import React, { useState, useRef, useEffect, createContext, useContext, useCallback, forwardRef, useMemo, Children, cloneElement, Fragment } from 'react';
import { css, tw, animation, theme, keyframes } from 'twind/css';
import { style, tw as tw$1, css as css$1 } from 'twind/style';
import { useUdsContext, useUdsTranslate, useResponsiveProp, useNavigate, useResponsiveValue, useSharedRef, useIsomorphicLayoutEffect as useIsomorphicLayoutEffect$1, useMediaQuery, useDelayUnmount, useResponsiveMap, useIntersectionObserver, useVisibilityChange, useResizeObserver, useHorizontalScroll } from '@global-ecom/nitro-uds/hooks';
import { tw as tw$2, apply } from 'twind';
import { Slot } from '@radix-ui/react-slot';
import { useId } from '@reach/auto-id';
import { tw as tw$3 } from 'twind/shim';
import * as RadixAccordion from '@radix-ui/react-accordion';
import { is, isNumber, isServer } from '@global-ecom/nitro-uds/utils';
import useEmblaCarousel from 'embla-carousel-react';
import * as RadixCollapsible from '@radix-ui/react-collapsible';
import * as RadixTabs from '@radix-ui/react-tabs';
import * as RadixDialog from '@radix-ui/react-dialog';
import * as RadixAlertDialog from '@radix-ui/react-alert-dialog';
import * as RadixPopover from '@radix-ui/react-popover';
import * as RadixTooltip from '@radix-ui/react-tooltip';
import * as RadixToast from '@radix-ui/react-toast';
import { Toast, ToastProvider as ToastProvider$1, ToastViewport as ToastViewport$1, ToastDescription as ToastDescription$1, ToastTitle as ToastTitle$1, ToastClose as ToastClose$1, ToastAction as ToastAction$1, Provider, Viewport } from '@radix-ui/react-toast';
import { announce } from '@react-aria/live-announcer';
import { Toggle } from '@radix-ui/react-toggle';
import { ToggleGroup, ToggleGroupItem as ToggleGroupItem$1 } from '@radix-ui/react-toggle-group';
import { useCallbackRef } from '@radix-ui/react-use-callback-ref';

const sizeStyle = style({
    variants: {
        size: {
            sm: 'text-xxs',
            base: 'text-xs',
            lg: 'text-base',
        },
    },
});
const dot = css `
  position: relative;
  border-radius: 50%;
  background-color: currentColor;
  transform: scale(0);
  width: 1em;
  height: 1em;

  &:nth-child(1) {
    animation-delay: 0.15s;
  }
  &:nth-child(2) {
    animation-delay: 0.35s;
  }
  &:nth-child(3) {
    animation-delay: 0.55s;
  }
`;
const dotPlaying = css `
  animation: dotPulse 1.3s infinite linear;
  @keyframes dotPulse {
    0% {
      transform: scale(0);
    }
    40% {
      transform: scale(1);
    }
    80%,
    100% {
      transform: scale(0);
    }
  }
`;
const DotAnimation = ({ className, on = true, size = 'base', compact = false, ...rest }) => {
    const [playing, setPlaying] = useState(false);
    const playingTimeoutRef = useRef();
    useEffect(() => {
        playingTimeoutRef.current && clearTimeout(playingTimeoutRef.current);
        playingTimeoutRef.current = setTimeout(() => {
            setPlaying(on);
        }, on ? 0 : 300);
        return () => {
            playingTimeoutRef.current && clearTimeout(playingTimeoutRef.current);
        };
    }, [on, setPlaying]);
    return playing ? (React.createElement("div", { "data-uds-child": "dot-animation", className: tw(className, sizeStyle({ size }), 'flex space-x-1.5 transition-opacity duration-300', on ? 'opacity-100' : 'opacity-0'), ...rest },
        React.createElement("div", { "data-uds-child": "dot", className: tw(dot, playing && dotPlaying) }),
        React.createElement("div", { "data-uds-child": "dot", className: tw(dot, playing && dotPlaying) }),
        compact === false ? (React.createElement("div", { "data-uds-child": "dot", className: tw(dot, playing && dotPlaying) })) : null)) : null;
};

const colorStyle = style({
    variants: {
        /**
         * Semantic color options.
         *
         * @defaultValue base
         */
        color: {
            current: '',
            inherit: 'text-inherit',
            base: 'text-neutral-100',
            muted: 'text-neutral-60',
            brand: 'text-brand-60',
            success: 'text-success-60',
            info: 'text-info-60',
            error: 'text-error-60',
            warning: 'text-warning-70',
        },
        /**
         * Use `invert` mode when rendering on dark backgrounds.
         *
         * @defaultValue false
         */
        invert: {
            true: '',
            false: '',
        },
    },
    matches: [
        {
            invert: true,
            color: 'base',
            use: 'text-neutral-0',
        },
        {
            invert: true,
            color: 'muted',
            use: 'text-neutral-50',
        },
        {
            invert: true,
            color: 'brand',
            use: 'text-brand-30',
        },
        {
            invert: true,
            color: 'success',
            use: 'text-success-40',
        },
        {
            invert: true,
            color: 'info',
            use: 'text-info-40',
        },
        {
            invert: true,
            color: 'warning',
            use: 'text-warning-50',
        },
        {
            invert: true,
            color: 'error',
            use: 'text-error-40',
        },
    ],
    defaults: {
        color: 'current',
    },
});
const fontStyle = style({
    variants: {
        /**
         * Font weight.
         *
         * @defaultValue 400
         */
        weight: {
            400: 'font-normal',
            normal: 'font-normal',
            700: 'font-bold',
            bold: 'font-bold',
        },
        condensed: {
            false: 'font-stretch-normal',
            true: 'font-stretch-condensed',
        },
        /**
         * Transform text case.
         *
         * @defaultValue none
         */
        transform: {
            none: '',
            uppercase: 'uppercase',
            lowercase: 'lowercase',
            capitalize: 'capitalize',
        },
        /**
         * Render the text as visually hidden for screen readers.
         *
         * - For visually hidden text, it is recommended to use
         * plain tailwind styling `<p className="sr-only"/>`.
         *
         * @defaultValue false
         */
        srOnly: {
            true: 'sr-only',
            false: 'not-sr-only	',
        },
    },
});

const ICONS_BIDI = ["arrow-left", "arrow-right", "chevron-left", "chevron-right"];

const ICON_SIZES = {
    inherit: 'text-[1.2em]',
    xxs: 'text-xxs',
    xs: 'text-xs',
    sm: 'text-sm',
    base: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl',
    '2xl': 'text-2xl',
    '3xl': 'text-3xl',
    '4xl': 'text-4xl',
    '5xl': 'text-5xl',
    '6xl': 'text-6xl',
    '7xl': 'text-7xl',
    '8xl': 'text-8xl',
    '9xl': 'text-9xl',
    '10xl': 'text-10xl',
};
const iconSizeStyle = style({
    variants: {
        /**
         * Set the size of the icon.
         * - Uses the text size scale
         * - If a size is not provided, the icon will inherit the width and height of it's container
         * - Set to `inherit` to inherit the font-size of parent text
         */
        size: ICON_SIZES,
    },
});
/**
 * `Icon` is a wrapper designed to be used around svg icons. It applies some necessary
 * accessibility attributes, and provides the option to render an accessible label.
 *
 * @example
 * ```
 * <Icon name="cart" size="base" label="Cart" />
 * ```
 */
const Icon = ({ name, children, className, label, dataTestId, size, aspectRatio = 1, bidi, color, invert, ...rest }) => {
    const { iconDirectory } = useUdsContext();
    const isBidi = ICONS_BIDI.includes(name);
    const sizeClassName = size
        ? tw$1(iconSizeStyle({ size }), `w-[1em] h-[${aspectRatio}em]`)
        : `w-full h-full`;
    const svgProps = {
        'aria-hidden': 'true',
        focusable: 'false',
        className: tw$1(sizeClassName, colorStyle({ color, invert }), (bidi || isBidi) && 'rtl:(transform -scale-x-100 origin-center)', className),
        'data-test-id': dataTestId,
        ...rest,
    };
    const href = `${iconDirectory}/${name}.svg#icon`;
    //.only to ensure only one child has been passed
    const child = children && React.Children.only(children);
    return (React.createElement(React.Fragment, null,
        name ? (React.createElement("svg", { "data-uds-child": "icon", ...svgProps },
            React.createElement("use", { href: href, 
                // xlinkHref attribute is needed for backwards compatability
                // https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/xlink:href
                xlinkHref: href }))) : child ? (React.cloneElement(child, svgProps)) : null,
        label && (React.createElement("span", { "data-test-id": dataTestId ? `${dataTestId}-label` : null, className: "sr-only", "data-uds-child": "icon-label" }, label))));
};

const ctaStyle = style({
    base: 'pointer-events-all relative inline-flex whitespace-normal items-center justify-center h-10 min-w-max border-1 text-white bg-neutral-100 border-neutral-100 hover:(bg-neutral-80 border-neutral-80) focus-visible:(bg-neutral-80 border-neutral-80 ring-0 outline-none)',
    variants: {
        /**
         * Choose one of the core CTA styles.
         *
         * - primary: a solid rectangle
         * - secondary: outline rectangle
         * - ghost: transparent rectangle
         * - underline: deprecated - use `ButtonUnderline` or `ButtonText` instead
         *
         * @defaultValue primary
         *
         * @example
         * ```
         * <ButtonCta variant="primary" label="Shop Running" />
         * ```
         */
        variant: {
            primary: 'text-white bg-neutral-100 border-neutral-100 hover:(bg-neutral-80 border-neutral-80) focus-visible:(bg-neutral-80 border-neutral-80)',
            secondary: 'text-neutral-100 border-neutral-40 bg-transparent hover:(bg-transparent border-neutral-100) focus-visible:(bg-transparent border-neutral-100)',
            ghost: 'bg-neutral-100 border-transparent bg-opacity-0 text-neutral-100 hover:(border-transparent bg-opacity-20) focus-visible:(border-transparent bg-opacity-20)',
            underline: 'h-auto rounded-none border-0 bg-transparent border-transparent text-neutral-100 hover:(bg-transparent) focus-visible:(bg-transparent)',
        },
        /**
         * Use to render elements on dark backgrounds
         *
         * @defaultValue false
         *
         * @example
         * ```
         * <ButtonCta
         *   variant="primary"
         *   label="Shop Running"
         *   invert={true}
         * />
         * ```
         */
        invert: {
            true: 'bg-white border-white text-neutral-100 hover:(bg-neutral-20 border-neutral-20) focus-visible:(bg-neutral-20 border-neutral-20)',
            false: '',
        },
        /**
         * Set rounded corners
         *
         * @defaultValue false
         */
        round: {
            true: 'rounded-full',
            false: 'rounded-sm',
        },
        /**
         * Set to full width
         *
         * @defaultValue false
         */
        bleed: {
            true: 'w-full',
            false: '',
        },
        /**
         * Semantic color property. Currently only supports `brand`
         */
        color: {
            default: '',
            brand: 'bg-brand-60  border-brand-60 text-white hover:(border-neutral-80 bg-neutral-80) focus-visible:(border-neutral-80 bg-neutral-80)',
        },
        /**
         * Set the element size.
         *
         * @defaultValue base
         */
        size: {
            sm: 'h-8 px-3',
            base: 'h-10 px-4',
            lg: 'h-14 px-6',
        },
        /**
         * The mode of the button
         * - text: renders a text button
         * - icon: renders an icon button
         *
         * @defaultValue text
         *
         * @deprecated use `ButtonIcon` instead for icon buttons
         */
        mode: {
            icon: 'px-0 w-10 h-10 rounded-full',
            text: '',
        },
        dimmed: {
            true: 'cursor-not-allowed',
            false: 'cursor-pointer',
        },
        loading: {
            true: 'cursor-not-allowed',
            false: 'cursor-pointer',
        },
    },
    matches: [
        {
            mode: 'icon',
            size: 'sm',
            use: 'w-8 h-8',
        },
        {
            mode: 'icon',
            size: 'base',
            use: 'w-10 h-10',
        },
        {
            mode: 'icon',
            size: 'lg',
            use: 'w-14 h-14 px-0',
        },
        {
            variant: 'underline',
            use: 'px-0',
        },
        {
            variant: 'underline',
            mode: 'icon',
            size: 'sm',
            use: 'w-4 h-6',
        },
        {
            variant: 'underline',
            mode: 'icon',
            size: 'base',
            use: 'w-6 h-6',
        },
        {
            variant: 'underline',
            mode: 'icon',
            size: 'lg',
            use: 'w-8 h-6',
        },
        {
            variant: 'primary',
            dimmed: true,
            use: 'bg-neutral-30 border-neutral-30 text-neutral-80 hover:(bg-neutral-30 border-neutral-30) focus-visible:(bg-neutral-30 border-neutral-30)',
        },
        {
            variant: 'primary',
            dimmed: true,
            invert: true,
            use: 'bg-neutral-30 border-neutral-30 text-neutral-80 hover:(bg-neutral-30 border-neutral-30) focus-visible:(bg-neutral-30 border-neutral-30)',
        },
        {
            variant: 'secondary',
            invert: true,
            use: 'bg-transparent border-neutral-60 text-white hover:(border-white bg-transparent) focus-visible:(border-white bg-transparent)',
        },
        {
            variant: 'secondary',
            dimmed: true,
            use: 'bg-transparent text-neutral-60 border-current hover:(bg-transparent border-current) focus-visible:(bg-transparent border-current)',
        },
        {
            variant: 'secondary',
            dimmed: true,
            invert: true,
            use: 'bg-transparent text-neutral-50 border-current hover:(bg-transparent border-current) focus-visible:(bg-transparent border-current)',
        },
        {
            variant: 'ghost',
            invert: true,
            use: 'bg-neutral-0 bg-opacity-0 border-transparent text-neutral-10 hover:(border-transparent bg-opacity-20) focus-visible:(border-transparent bg-opacity-20)',
        },
        {
            variant: 'ghost',
            dimmed: true,
            invert: false,
            use: 'bg-neutral-0 bg-opacity-0 border-transparent text-neutral-60 hover:bg-transparent focus-visible:bg-transparent',
        },
        {
            variant: 'ghost',
            dimmed: true,
            invert: true,
            use: 'bg-neutral-0 bg-opacity-0 border-transparent text-neutral-50 hover:bg-transparent focus-visible:bg-transparent',
        },
        {
            color: 'brand',
            invert: true,
            use: 'bg-brand-60 border-brand-60 text-white hover:(border-neutral-80 bg-neutral-80) focus-visible:(border-neutral-80 bg-neutral-80)',
        },
        {
            color: 'brand',
            dimmed: true,
            use: 'bg-neutral-30 border-neutral-30 text-neutral-80 hover:(bg-neutral-30 border-neutral-30) focus-visible:(bg-neutral-30 border-neutral-30)',
        },
        {
            variant: 'underline',
            invert: true,
            use: 'bg-transparent border-none text-white hover:(bg-transparent border-transparent) focus-visible:(bg-transparent border-transparent)',
        },
        {
            variant: 'underline',
            dimmed: true,
            use: 'bg-transparent border-none text-neutral-50 hover:(bg-transparent border-transparent) focus-visible:(bg-transparent border-transparent)',
        },
    ],
    defaults: {
        variant: 'primary',
        size: 'base',
        color: 'default',
        round: false,
    },
});
const iconSizeMap$1 = {
    sm: 'base',
    base: '2xl',
    lg: '4xl',
};
const getIconSizeByActionSize$1 = (size) => {
    let iconSize;
    if (typeof size === 'string') {
        iconSize = iconSizeMap$1[size];
    }
    else {
        iconSize = Object.assign({});
        for (const [key, value] of Object.entries(size)) {
            iconSize[key] = iconSizeMap$1[value];
        }
    }
    return iconSize;
};
const useActionCta = ({ className, label, srOnlyLabel, variant = 'primary', invert = false, round = false, bleed = false, size = 'base', color = 'default', mode = 'text', dimmed = false, dimmedReason, icon, iconEnd, loading, }) => {
    const t = useUdsTranslate();
    const actionClassName = tw$1('group', ctaStyle({
        variant: variant,
        invert: invert,
        size: size,
        round: round,
        bleed: bleed,
        color: color,
        mode: mode,
        dimmed: dimmed,
        loading: loading,
    }), iconEnd ? 'flex-row-reverse' : 'flex-row', `after::(absolute hidden pointer-events-none -left-1 -top-1 -right-1 -bottom-1)`, `after::(border-2 ${invert ? 'border-outline-invert' : 'border-outline'} ${mode === 'icon' || round ? 'rounded-full' : 'rounded-sm'} )`, variant !== 'underline' && 'focus-visible:after::block', css `
      &::after {
        content: '';
      }
    `, className);
    const labelSizeClass = useResponsiveProp(size, size => {
        if (size === 'sm')
            return ['px-1.5', 'text-sm', 'tracking-wider'];
        if (size === 'base')
            return ['px-2', 'text-base'];
        if (size === 'lg')
            return ['px-3', 'text-lg'];
    });
    const labelMode = useResponsiveProp(mode, mode => {
        return mode === 'icon' ? ['sr-only', 'px-0!'] : '';
    });
    const labelStyle = tw$1('relative font-bold uppercase', labelSizeClass, labelMode);
    const underLineLabelSize = useResponsiveProp(size, size => {
        if (size === 'sm')
            return ['text-xs', 'tracking-wider', 'leading-4'];
        if (size === 'base')
            return ['text-sm', 'leading-5'];
        if (size === 'lg')
            return ['text-base', 'leading-6'];
    });
    const underLineLabelMode = useResponsiveProp(mode, mode => {
        return mode === 'icon' ? ['sr-only'] : '';
    });
    const underLineLabelStyle = tw$1('relative font-bold uppercase px-0', underLineLabelSize, underLineLabelMode);
    const iconWrapperMode = useResponsiveProp(mode, mode => {
        return mode === 'icon' ? ['mr-0', 'ml-0', 'rtl:ml-0', 'rtl:mr-0'] : '';
    });
    const iconWrapperStyle = tw$1('relative', iconWrapperMode, loading ? 'opacity-0' : 'opacity-100');
    const iconSize = variant === 'underline' ? 'base' : getIconSizeByActionSize$1(size);
    const actionChildren = [
        icon ? (React.createElement("div", { key: "icon", "data-uds-child": "icon", className: iconWrapperStyle },
            React.createElement(Icon, { name: icon, size: iconSize }))) : null,
        loading ? (React.createElement("div", { key: "loading", className: tw$1('absolute', 'text-current') },
            React.createElement(DotAnimation, { key: "loading", on: loading, size: size, compact: mode === 'icon' }))) : null,
        srOnlyLabel ? (React.createElement("span", { key: "sr-label", "data-uds-child": "sr-only-label", className: "sr-only" }, srOnlyLabel)) : null,
        React.createElement("div", { key: "label", "data-uds-child": "text-label", "aria-hidden": srOnlyLabel ? 'true' : 'false', className: variant === 'underline' ? underLineLabelStyle : labelStyle },
            React.createElement("div", { className: loading ? 'opacity-0' : 'opacity-100' }, label),
            variant === 'underline' ? (React.createElement("div", { key: "underline", className: tw$1('h-0.5 absolute -bottom-0.5 w-full z-0', !invert && 'bg-neutral-40', !dimmed &&
                    'group-hover:bg-neutral-100 group-focus-visible:bg-neutral-100', invert && 'bg-neutral-50', invert &&
                    !dimmed &&
                    'group-hover:bg-white group-focus-visible:bg-white', className) })) : null),
        loading ? (React.createElement("span", { key: "loading-sr-text", className: "sr-only" }, ` ${t('loading').toLowerCase()}`)) : null,
        dimmed && dimmedReason ? (React.createElement("span", { "data-uds-child": "dimmed-reason", key: "dimmed-reason", className: tw$1(`absolute bottom-full whitespace-nowrap flex-none mb-2 px-2 py-1 bg-neutral-100 rounded-sm text-white font-bold text-xs uppercase`, 'opacity-0 invisible group-hover:(opacity-100 visible) group-focus-visible:(opacity-100 visible)') }, dimmedReason)) : null,
    ];
    return {
        className: actionClassName,
        children: actionChildren,
        'aria-disabled': dimmed || undefined,
    };
};

/**
 * The `ButtonCta` component renders a `button` tag, and is used to
 * submit forms, or receive user input. For navigation it is recommended
 * to use the `LinkCta` component.
 *
 * @example
 * ```
 * <ButtonCta label="Shop Running" />
 * ```
 */
const ButtonCta = React.forwardRef(function ButtonCta(props, forwardedRef) {
    const { dataTestId, label, srOnlyLabel, variant, invert, size, color, mode, bleed, round, dimmed, dimmedReason, disabled, icon, iconEnd, loading, className, onClick, type = 'button', ...rest } = props;
    const actionCtaProps = useActionCta({
        className,
        label,
        srOnlyLabel,
        variant,
        invert,
        size,
        color,
        mode,
        round,
        bleed,
        dimmed: dimmed || disabled,
        dimmedReason,
        icon,
        iconEnd,
        loading,
    });
    const handleOnClick = (e) => {
        if (loading) {
            e.preventDefault();
            return;
        }
        if (onClick) {
            onClick(e);
        }
    };
    return (React.createElement("button", { ref: forwardedRef, "data-test-id": dataTestId, onClick: handleOnClick, disabled: disabled, type: type, ...actionCtaProps, ...rest }));
});
// Temporarily export both ButtonCta and Button whilst we migrate to ButtonCta
const Button = ButtonCta;

/**
 * LinkCta is a link that is styled as a rectangle call-to-action, identical to the `ButtonCta` element
 *
 * @example
 * ```
 * <LinkCta label="Shop Running" href="/running" />
 * ```
 */
const LinkCta = React.forwardRef(function LinkCta(props, forwardedRef) {
    const { label, srOnlyLabel, className, variant, invert, size, color, mode, round, bleed, dimmed, icon, iconEnd, loading, dataTestId, ...rest } = props;
    const actionCtaProps = useActionCta({
        label,
        srOnlyLabel,
        className,
        variant,
        invert,
        size,
        color,
        mode,
        round,
        bleed,
        dimmed,
        icon,
        iconEnd,
        loading,
    });
    const navigateProps = useNavigate(rest);
    return (React.createElement("a", { ref: forwardedRef, ...actionCtaProps, ...navigateProps, "data-test-id": dataTestId }));
});
// Temporarily export both LinkCta and Link whilst we migrate to LinkCta
const Link = LinkCta;

const iconStyle = style({
    base: 'relative flex items-center justify-center border-1',
    variants: {
        /**
         * The visual variant of the button. Follows the same pattern as ButtonCta and LinkCta
         *
         * @defaultValue primary
         * @example
         * ```jsx
         * <ButtonIcon variant="secondary" />
         * ```
         */
        variant: {
            primary: 'text-white bg-neutral-100 border-neutral-100 group-hover:(bg-neutral-80 border-neutral-80) group-focus-visible:(bg-neutral-80 border-neutral-80)',
            secondary: 'text-neutral-100 border-neutral-40 bg-transparent group-hover:(bg-transparent border-neutral-100) group-focus-visible:(bg-transparent border-neutral-100)',
            ghost: 'bg-neutral-100 border-transparent bg-opacity-0 text-neutral-100 group-hover:(border-transparent bg-opacity-20) group-focus-visible:(border-transparent bg-opacity-20)',
        },
        /**
         * Use on dark backgrounds
         *
         * @defaultValue false
         * @example
         * ```jsx
         * <ButtonIcon invert />
         * ```
         */
        invert: {
            true: '',
            false: '',
        },
        /**
         * Position the icon on the end of the text orientation
         *
         * @defaultValue false
         *
         * @example
         *
         * ```jsx
         * <ButtonIcon iconEnd />
         * ```
         */
        iconEnd: {
            false: '',
            true: '',
        },
        /**
         * Set rounded corners
         *
         * @defaultValue false
         */
        round: {
            true: 'rounded-full',
            false: 'rounded-sm',
        },
        /**
         * Applies a transition to the icon and label
         * @defaultValue labek
         * @example
         * ```jsx
         * <ButtonIcon animate="icon" />
         * ```
         */
        animate: {
            label: '',
            icon: '',
        },
        /**
         * Float the label next to the icon, or visually hide tha label
         * @defaultValue icon
         * @example
         * ```jsx
         * <ButtonIcon mode="float" />
         * ```
         */
        mode: {
            icon: 'group-focus-visible:(transform translate-x-0) group-hover:(transform translate-x-0)',
            float: '',
        },
        dimmed: {
            true: '',
            false: '',
        },
        /**
         * Responsive size of the button
         * @defaultValue base
         * @example
         * ```jsx
         * <ButtonIcon size="sm" />
         * ```
         */
        size: {
            xs: 'w-6 h-6',
            sm: 'w-8 h-8',
            base: 'w-10 h-10',
            lg: 'w-14 h-14',
        },
    },
    matches: [
        {
            dimmed: true,
            invert: false,
            use: 'text-neutral-60',
        },
        {
            dimmed: true,
            invert: true,
            use: 'text-neutral-50',
        },
        {
            variant: 'primary',
            invert: true,
            use: 'bg-white border-white text-neutral-100 group-hover:(bg-neutral-20 border-neutral-20) group-focus-visible:(bg-neutral-20 border-neutral-20)',
        },
        {
            variant: 'primary',
            dimmed: true,
            use: 'bg-neutral-30 border-neutral-30 text-neutral-80 group-hover:(bg-neutral-30 border-neutral-30) group-focus-visible:(bg-neutral-30 border-neutral-30)',
        },
        {
            variant: 'primary',
            dimmed: true,
            invert: true,
            use: 'bg-neutral-30 border-neutral-30 text-neutral-80 group-hover:(bg-neutral-30 border-neutral-30) group-focus-visible:(bg-neutral-30 border-neutral-30)',
        },
        {
            variant: 'secondary',
            invert: true,
            use: 'bg-transparent border-neutral-60 text-white group-hover:(border-white bg-transparent) group-focus-visible:(border-white bg-transparent)',
        },
        {
            variant: 'secondary',
            dimmed: true,
            use: 'bg-transparent text-neutral-60 border-current group-hover:(bg-transparent border-current) group-focus-visible:(bg-transparent border-current)',
        },
        {
            variant: 'secondary',
            dimmed: true,
            invert: true,
            use: 'bg-transparent text-neutral-50 border-current group-hover:(bg-transparent border-current) group-focus-visible:(bg-transparent border-current)',
        },
        {
            variant: 'ghost',
            invert: true,
            use: 'bg-neutral-0 bg-opacity-0 border-transparent text-neutral-10 group-hover:(border-transparent bg-opacity-20) group-focus-visible:(border-transparent bg-opacity-20)',
        },
        {
            variant: 'ghost',
            dimmed: true,
            use: 'bg-neutral-0 bg-opacity-0 border-transparent text-neutral-60 group-hover:bg-transparent group-focus-visible:bg-transparent',
        },
        {
            variant: 'ghost',
            dimmed: true,
            invert: true,
            use: 'bg-neutral-0 bg-opacity-0 border-transparent text-neutral-50 group-hover:bg-transparent group-focus-visible:bg-transparent',
        },
        {
            iconEnd: true,
            animate: 'icon',
            dimmed: false,
            mode: 'float',
            use: 'group-focus-visible:(transform translate-x-1) group-hover:(transform translate-x-1) group-focus-visible:rtl:(transform -translate-x-1) group-hover:rtl:(transform -translate-x-1)',
        },
        {
            iconEnd: false,
            animate: 'icon',
            mode: 'float',
            dimmed: false,
            use: 'group-focus-visible:(transform -translate-x-1) group-hover:(transform -translate-x-1) group-focus-visible:rtl:(transform translate-x-1) group-hover:rtl:(transform translate-x-1)',
        },
    ],
    defaults: {
        variant: 'primary',
        iconEnd: 'false',
        animate: 'label',
        size: 'base',
        round: true,
    },
});
const labelStyle$1 = style({
    base: '',
    variants: {
        size: {
            xs: 'text-sm mt-px',
            sm: 'text-base mt-px',
            base: 'text-base mt-0.5',
            lg: 'text-lg mt-1.5',
        },
        iconEnd: {
            false: '',
            true: '',
        },
        animate: {
            icon: 'group-focus-visible:(transform translate-x-0) group-hover:(transform translate-x-0)',
            label: '',
        },
        dimmed: {
            true: '',
            false: '',
        },
        invert: {
            true: '',
            false: '',
        },
        mode: {
            icon: 'sr-only',
            float: '',
        },
    },
    matches: [
        {
            dimmed: true,
            invert: false,
            use: 'text-neutral-60',
        },
        {
            dimmed: true,
            invert: true,
            use: 'text-neutral-50',
        },
        {
            iconEnd: true,
            animate: 'label',
            dimmed: false,
            mode: 'float',
            use: 'group-focus-visible:(transform -translate-x-1) group-hover:(transform -translate-x-1) group-focus-visible:rtl:(transform translate-x-1) group-hover:rtl:(transform translate-x-1)',
        },
        {
            iconEnd: false,
            animate: 'label',
            dimmed: false,
            mode: 'float',
            use: 'group-focus-visible:(transform translate-x-1) group-hover:(transform translate-x-1) group-focus-visible:rtl:(transform -translate-x-1) group-hover:rtl:(transform -translate-x-1)',
        },
    ],
    defaults: {
        size: 'base',
        iconEnd: 'false',
        animate: 'label',
    },
});
const groupStyle = style({
    base: 'relative flex items-center focus-visible:(ring-0 outline-none)',
    variants: {
        invert: {
            true: 'text-neutral-0',
            false: 'text-neutral-100',
        },
        iconEnd: {
            false: 'flex-row',
            true: 'flex-row-reverse',
        },
        animate: {
            label: '',
            icon: '',
        },
        mode: {
            icon: '',
            float: '',
        },
        dimmed: {
            true: 'cursor-not-allowed',
            false: 'cursor-pointer',
        },
        size: {
            xs: 'gap-1.5',
            sm: 'gap-2',
            base: 'gap-2',
            lg: 'gap-2.5',
        },
    },
    matches: [{ mode: 'icon', use: 'gap-none' }],
    defaults: {
        iconEnd: 'false',
        animate: 'label',
        size: 'base',
    },
});
const iconSizeMap = {
    xs: 'base',
    sm: 'base',
    base: '2xl',
    lg: '4xl',
};
const getIconSizeByActionSize = (size) => {
    let iconSize;
    if (typeof size === 'string') {
        iconSize = iconSizeMap[size];
    }
    else {
        iconSize = Object.assign({});
        for (const [key, value] of Object.entries(size)) {
            iconSize[key] = iconSizeMap[value];
        }
    }
    return iconSize;
};
const useActionIcon = (props) => {
    const { className, label, variant = 'primary', invert = false, size = 'base', mode = 'icon', iconEnd = false, animate = 'label', round = true, dimmed = false, dimmedReason, icon, } = props;
    const buttonClassName = tw$1('group', groupStyle({ size, invert, mode, iconEnd, animate, dimmed }), className);
    const buttonChildren = [
        React.createElement("div", { key: "icon", "data-uds-child": "action-icon-icon", className: tw$1(iconStyle({
                variant,
                invert,
                size,
                mode,
                iconEnd,
                animate,
                round,
                dimmed,
            }), 'flex-none transition ease-out-expo duration-300', `after::(absolute hidden pointer-events-none -left-1 -top-1 -right-1 -bottom-1)`, `after::(border-2 ${invert ? 'border-outline-invert' : 'border-outline'} ${round ? 'rounded-full' : 'rounded-sm'} )`, 'group-focus-visible:after::block', css `
          &::after {
            content: '';
          }
        `) },
            React.createElement(Icon, { name: icon, size: getIconSizeByActionSize(size) })),
        React.createElement("span", { key: "label", "data-uds-child": "action-icon-label", className: tw$1('font-bold leading-[1.15]', 'transition ease-out-expo duration-300', labelStyle$1({ size, mode, iconEnd, animate, dimmed, invert })) }, label),
        dimmed && dimmedReason ? (React.createElement("span", { "data-uds-child": "dimmed-reason", key: "dimmed-reason", className: tw$1(`absolute left-1/2 transform -translate-x-1/2 bottom-full whitespace-nowrap flex-none mb-2 px-2 py-1 bg-neutral-100 rounded-sm text-white font-bold text-xs uppercase`, 'opacity-0 invisible group-hover:(opacity-100 visible) group-focus-visible:(opacity-100 visible)') }, dimmedReason)) : null,
    ];
    return {
        className: buttonClassName,
        children: buttonChildren,
        'aria-disabled': dimmed || undefined,
    };
};

/**
 * The `ButtonIcon` component renders a rounded icon button, withan optional floating label model.
 *
 * The API is similar to the `Button` component, and will eventually replace the `<Button mode="icon"/>`
 *
 * Use this compoenent to submit forms, or receive user input. For navigation it is recommended
 * to use the `LinkIcon` component.
 *
 * @example
 * ```jsx
 * <ButtonIcon label="Shop Running" mode="float" icon="puma-logo" />
 * ```
 */
const ButtonIcon = React.forwardRef(function ButtonIcon(props, forwardedRef) {
    const { dataTestId, label, variant, invert, size, disabled, icon, mode, dimmed, dimmedReason, iconEnd, round, animate, className, type = 'button', ...rest } = props;
    const { children, ...ctaProps } = useActionIcon({
        className,
        label,
        variant,
        invert,
        size,
        mode,
        dimmed,
        dimmedReason,
        icon,
        iconEnd,
        round,
        animate,
    });
    return (React.createElement(React.Fragment, null,
        React.createElement("button", { ref: forwardedRef, "data-test-id": dataTestId, disabled: disabled, type: type, "data-uds-child": "button-icon", ...ctaProps, ...rest }, children)));
});

/**
 * The `LinkIcon` component renders a rounded icon anchor element, withan optional floating label model.
 *
 * The API is similar to the `Link` component, and will eventually replace the `<Link mode="icon"/>`
 *
 *
 * @example
 * ```jsx
 * <LinkIcon label="Shop Running" mode="float" icon="puma-logo" />
 * ```
 */
const LinkIcon = React.forwardRef(function LinkIcon(props, forwardedRef) {
    const { label, className, variant, invert, size, mode, dimmed, dimmedReason, icon, iconEnd, round, animate, dataTestId, ...rest } = props;
    const actionIconProps = useActionIcon({
        label,
        className,
        variant,
        invert,
        size,
        mode,
        dimmed,
        dimmedReason,
        icon,
        iconEnd,
        round,
        animate,
    });
    const navigateProps = useNavigate(rest);
    return (React.createElement("a", { ref: forwardedRef, ...actionIconProps, ...navigateProps, "data-uds-child": "link-icon", "data-test-id": dataTestId }));
});

const LinkInvBlock = ({ className, children, dataTestId, ...rest }) => {
    const navigateProps = useNavigate(rest);
    return (React.createElement("a", { className: tw$2('absolute inset-0 z-1', className), ...navigateProps, "data-test-id": dataTestId, "data-uds-child": "link-inv-block" },
        React.createElement("span", { className: "sr-only" }, children)));
};

const ButtonInvBlock = ({ className, dataTestId, focusable = true, children, ...rest }) => {
    return (React.createElement("button", { ...rest, className: tw$2('absolute inset-0 z-1', className), "data-uds-child": "button-inv-block", "data-test-id": dataTestId, tabIndex: focusable ? 0 : -1, "aria-hidden": focusable ? 'false' : 'true' },
        React.createElement("span", { className: "sr-only" }, children)));
};

const SYMBOL_KEY = '__wrap_b';
const SYMBOL_OBSERVER_KEY = '__wrap_o';
const IS_SERVER = typeof window === 'undefined';
const useIsomorphicLayoutEffect = IS_SERVER
    ? React.useEffect
    : React.useLayoutEffect;
const relayout = (id, ratio, wrapper) => {
    wrapper =
        wrapper || document.querySelector(`[data-br="${id}"]`);
    const container = wrapper.parentElement;
    const update = (width) => (wrapper.style.maxWidth = width + 'px');
    // Reset wrapper width
    wrapper.style.maxWidth = '';
    // Get the initial container size
    const width = container.clientWidth;
    const height = container.clientHeight;
    // Synchronously do binary search and calculate the layout
    let lower = width / 2 - 0.25;
    let upper = width + 0.5;
    let middle;
    if (width) {
        while (lower + 1 < upper) {
            middle = Math.round((lower + upper) / 2);
            update(middle);
            if (container.clientHeight === height) {
                upper = middle;
            }
            else {
                lower = middle;
            }
        }
        // Update the wrapper width
        update(upper * ratio + width * (1 - ratio));
    }
    // Create a new observer if we don't have one.
    // Note that we must inline the key here as we use `toString()` to serialize
    // the function.
    if (!wrapper['__wrap_o']) {
        (wrapper['__wrap_o'] = new ResizeObserver(() => {
            window.requestAnimationFrame(() => {
                self.__wrap_b(0, +wrapper.dataset.brr, wrapper);
            });
        })).observe(container);
    }
};
const RELAYOUT_STR = relayout.toString();
const createScriptElement = (injected, suffix = '') => (React.createElement("script", { suppressHydrationWarning: true, dangerouslySetInnerHTML: {
        // Calculate the balance initially for SSR
        __html: (injected ? '' : `self.${SYMBOL_KEY}=${RELAYOUT_STR};`) + suffix,
    } }));
/**
 * An optional provider to inject the global relayout function, so all children
 * Balancer components can share it.
 */
const BalancerContext = React.createContext(false);
const BalancerProvider = ({ children }) => {
    return (React.createElement(BalancerContext.Provider, { value: true },
        createScriptElement(false),
        children));
};
const Balancer = ({ ratio = 1, children, ...props }) => {
    const id = useId();
    const wrapperRef = React.useRef();
    const hasProvider = React.useContext(BalancerContext);
    // Re-balance on content change and on mount/hydration.
    useIsomorphicLayoutEffect(() => {
        if (wrapperRef.current) {
            // Re-assign the function here as the component can be dynamically rendered, and script tag won't work in that case.
            (self[SYMBOL_KEY] = relayout)(0, ratio, wrapperRef.current);
        }
    }, [children, ratio]);
    // Remove the observer when unmounting.
    useIsomorphicLayoutEffect(() => {
        return () => {
            if (!wrapperRef.current)
                return;
            const resizeObserver = wrapperRef.current[SYMBOL_OBSERVER_KEY];
            if (!resizeObserver)
                return;
            resizeObserver.disconnect();
            delete wrapperRef.current[SYMBOL_OBSERVER_KEY];
        };
    }, []);
    return (React.createElement(React.Fragment, null,
        React.createElement(Slot, { ...props, "data-br": id, "data-brr": ratio, ref: wrapperRef, style: {
                display: 'inline-block',
                verticalAlign: 'top',
                textDecoration: 'inherit',
            }, suppressHydrationWarning: true }, children),
        createScriptElement(hasProvider, `self.${SYMBOL_KEY}("${id}",${ratio})`)));
};
// As Next.js adds `display: none` to `body` for development, we need to trigger
// a re-balance right after the style is removed, synchronously.
if (!IS_SERVER && process.env.NODE_ENV !== 'production') {
    const next_dev_style = document.querySelector('[data-next-hide-fouc]');
    if (next_dev_style) {
        const callback = mutationList => {
            for (const mutation of mutationList) {
                for (const node of Array.from(mutation.removedNodes)) {
                    if (node !== next_dev_style)
                        continue;
                    observer.disconnect();
                    const elements = document.querySelectorAll('[data-br]');
                    for (const element of Array.from(elements)) {
                        self[SYMBOL_KEY](0, +element.dataset.brr, element);
                    }
                }
            }
        };
        const observer = new MutationObserver(callback);
        observer.observe(document.head, { childList: true });
    }
}

const TEXT_SIZES = {
    inherit: 'text-inherit',
    xxs: 'text-xxs',
    xs: 'text-xs',
    sm: 'text-sm',
    base: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl',
    '2xl': 'text-2xl',
};
const useTextStyle = ({ size }) => {
    return useResponsiveProp(size, size => TEXT_SIZES[size]);
};
/**
 * Text only identifies a text size hierarchy, used for running text, and
 * other text styles.
 *
 * - By default renders a span. To render as a custom element, use the `asChild` prop
 *
 * @example
 * ```
 * <Text asChild>
 *   <p>New Collection</p>
 * </Text>
 * ```
 */
const Text = React.forwardRef(function Text(props, forwardedRef) {
    const { asChild, dataTestId, balance = false, size, prose, weight, condensed = false, srOnly, transform, className, children, color, invert, ...rest } = props;
    const Comp = balance
        ? Balancer
        : typeof children !== 'string' && asChild
            ? Slot
            : 'span';
    const sizeClassName = useTextStyle({ size });
    return (React.createElement(Comp, { ref: forwardedRef, className: tw$1(sizeClassName, prose && 'max-w-[65ch]', fontStyle({
            condensed,
            weight,
            transform,
            srOnly,
        }), colorStyle({
            color,
            invert,
        }), className), "data-test-id": dataTestId, "data-uds-child": "text", ...rest }, asChild ? children : balance ? React.createElement("span", null, children) : children));
});

const HEADING_SIZES = {
    xxxs: 'heading-xxxs',
    xxs: 'heading-xxs',
    xs: 'heading-xs',
    sm: 'heading-sm',
    base: 'heading-base',
    lg: 'heading-lg',
    xl: 'heading-xl',
    '2xl': 'heading-2xl',
    '3xl': 'heading-3xl',
    '4xl': 'heading-4xl',
    '5xl': 'heading-5xl',
    '6xl': 'heading-6xl',
};
/**
 * Heading is implements text size hierarchy, used for large and display text.
 *
 * - By default renders a span. To render as a custom element, use the `asChild` prop.
 *
 * @example
 * ```
 * <Heading
 *   color="success"
 *   size="xl"
 *   asChild
 * >
 *   <h2>New Collection</h2>
 * </Heading>
 * ```
 */
const Heading = React.forwardRef(function Heading(props, forwardedRef) {
    const { asChild, dataTestId, balance, size = 'base', weight = 700, condensed = false, transform, srOnly, className, children, color, invert, ...rest } = props;
    const Comp = balance
        ? Balancer
        : typeof children !== 'string' && asChild
            ? Slot
            : 'span';
    const sizeClassName = useResponsiveProp(size, size => HEADING_SIZES[size]);
    return (React.createElement(Comp, { ref: forwardedRef, className: tw$1(sizeClassName, fontStyle({
            condensed,
            weight,
            transform,
            srOnly,
        }), colorStyle({
            color,
            invert,
        }), className), "data-test-id": dataTestId, "data-uds-child": "heading", ...rest }, asChild ? children : balance ? React.createElement("span", null, children) : children));
});

const DISPLAY_SIZE_CONFIG = {
    1: css$1 `
    --min: 48;
    --max: 96;
    --lh: 1;
    --ls: -0.02;

    @screen lg {
      --lh: 0.9;
    }
  `,
    2: css$1 `
    --min: 40;
    --max: 72;
    --lh: 1.1;
    --ls: -0.02;

    @screen md {
      --lh: 1;
    }
  `,
    3: css$1 `
    --min: 32;
    --max: 60;
    --lh: 1.13;
    --ls: -0.01;

    @screen md {
      --lh: 1;
    }
  `,
    4: css$1 `
    --min: 28;
    --max: 48;
    --lh: 1.14;
    --ls: -0.01;

    @screen md {
      --lh: 1;
    }
  `,
    5: css$1 `
    --min: 24;
    --max: 40;
    --lh: 1.17;
    --ls: 0;

    @screen md {
      --lh: 1.1;
    }
  `,
    6: css$1 `
    --min: 20;
    --max: 32;
    --lh: 1.2;
    --ls: 0;

    @screen md {
      --lh: 1.13;
    }
  `,
    7: css$1 `
    --min: 18;
    --max: 28;
    --lh: 1.3;
    --ls: 0;

    @screen md {
      --lh: 1.14;
    }
  `,
    8: css$1 `
    --min: 16;
    --max: 24;
    --lh: 1.5;
    --ls: 0;

    @screen md {
      --lh: 1.17;
    }
  `,
};
const DISPLAY_CALC = css$1 `
  --bp-min: 480;
  --bp-max: 1600;

  --preferred-scale: calc(
    (100 * (var(--max) - var(--min))) / (var(--bp-max) - var(--bp-min))
  );

  --preferred-base: calc(
    ((var(--bp-min) * var(--max)) - (var(--bp-max) * var(--min))) /
      (var(--bp-min) - var(--bp-max))
  );

  font-size: clamp(
    calc(var(--min) * 1px),
    calc((var(--preferred-scale) * 1vw) + (var(--preferred-base) * 1px)),
    calc(var(--max) * 1px)
  );

  line-height: calc(var(--lh) * 1em);
`;
// only apply letter spacing to condensed variant
const DISPLAY_LS = css$1 `
  letter-spacing: calc(var(--ls) * 1em);
`;
/**
 * Display
 *
 * - By default renders a span. To render as a custom element, use the `asChild` prop
 *
 * @example
 * ```
 * <Display level="1" asChild>
 *   <p>New Collection</p>
 * </Display>
 * ```
 */
const Display = React.forwardRef(function Display(props, forwardedRef) {
    const { asChild, dataTestId, balance = false, level = 3, condensed = false, weight = 'bold', transform = 'uppercase', srOnly, className, children, color, invert, ...rest } = props;
    const Comp = balance
        ? Balancer
        : typeof children !== 'string' && asChild
            ? Slot
            : 'span';
    return (React.createElement(Comp, { ref: forwardedRef, className: tw$1(DISPLAY_CALC, condensed ? DISPLAY_LS : '', DISPLAY_SIZE_CONFIG[level], fontStyle({
            weight,
            condensed,
            transform,
            srOnly,
        }), colorStyle({
            color,
            invert,
        }), className), "data-test-id": dataTestId, "data-uds-child": "display", ...rest }, asChild ? children : balance ? React.createElement("span", null, children) : children));
});

const actionTextStyle = style({
    base: 'group relative inline flex-none rounded-sm appearance-none text-left focus-visible:outline-2',
    variants: {
        color: {
            current: '',
            base: '',
            muted: '',
            brand: '',
            success: '',
            info: '',
            error: '',
            warning: '',
        },
        invert: {
            true: 'hover:(bg-neutral-0 bg-opacity-[0.15])',
            false: 'hover:(bg-neutral-100 bg-opacity-10) ',
        },
    },
    matches: [
        {
            color: 'muted',
            invert: true,
            use: 'hover:(text-neutral-0)',
        },
        {
            color: 'muted',
            invert: false,
            use: 'hover:(text-neutral-100)',
        },
    ],
});
const useActionText = (props) => {
    const { label, srOnlyLabel, color, invert = false, size, condensed, transform, weight, className, external, } = props;
    const buttonChildren = [
        srOnlyLabel ? (React.createElement("span", { key: "sr-label", "data-uds-child": "sr-only-label", className: "sr-only" }, srOnlyLabel)) : null,
        React.createElement("span", { key: "label", "data-uds-child": "text-label", className: "font-inherit decoration-underline decoration-1 underline-offset-[0.18em]", "aria-hidden": srOnlyLabel ? 'true' : 'false' }, label),
        external ? (React.createElement(Icon, { size: "inherit", className: "mx-1 inline align-bottom mb-0.5", name: "external-link" })) : null,
    ];
    const textStyle = useTextStyle({ size });
    return {
        children: buttonChildren,
        className: tw$2(colorStyle({ color, invert }), fontStyle({ condensed, transform, weight }), actionTextStyle({ color, invert }), textStyle, className),
    };
};

const ButtonText = React.forwardRef(function ButtonText(props, forwardedRef) {
    const { dataTestId, label, srOnlyLabel, invert, size, color, transform, weight, condensed, external, disabled, className, onClick, type = 'button', ...rest } = props;
    const { children, ...ctaProps } = useActionText({
        className,
        label,
        srOnlyLabel,
        invert,
        size,
        color,
        transform,
        weight,
        condensed,
        external,
    });
    const handleOnClick = (e) => {
        if (onClick) {
            onClick(e);
        }
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("button", { ref: forwardedRef, "data-test-id": dataTestId, onClick: handleOnClick, disabled: disabled, type: type, ...ctaProps, ...rest }, children)));
});

/**
 * LinkText is a link that is styled as a rectangle call-to-action, identical to the `ButtonText` element
 *
 * @example
 * ```
 * <LinkText label="Shop Running" href="/running" />
 * ```
 */
const LinkText = React.forwardRef(function LinkText(props, forwardedRef) {
    const { label, srOnlyLabel, className, invert, size, color, transform, weight, condensed, external, dataTestId, ...rest } = props;
    const actionTextProps = useActionText({
        label,
        srOnlyLabel,
        className,
        invert,
        size,
        color,
        transform,
        weight,
        condensed,
        external,
    });
    const navigateProps = useNavigate(rest);
    return (React.createElement("a", { ref: forwardedRef, ...actionTextProps, ...navigateProps, "data-test-id": dataTestId }));
});

const labelStyle = css `
  &::before {
    content: '';
    transition: transform 350ms cubic-bezier(0.76, 0, 0.24, 1) 150ms;
    @apply absolute left-0 bottom-0 right-0 h-[2px] bg-current transform scale-x-0 origin-left pointer-none;
  }

  :hover > &::before {
    @apply scale-x-100;
  }

  &::after {
    content: '';
    transition: transform 350ms cubic-bezier(0.76, 0, 0.24, 1);
    @apply absolute left-0 bottom-0 right-0 h-[2px] bg-current transform scale-x-100 origin-right pointer-none;
  }

  :hover > &::after {
    @apply scale-x-0;
  }
`;
const actionUnderlineStyle = style({
    base: 'group relative flex whitespace-nowrap gap-2 items-center max-w-max rounded-sm text-left focus-visible:outline-2',
    variants: {
        size: {
            inherit: '',
            sm: 'text-sm',
            base: 'text-base',
            lg: 'text-lg',
        },
        invert: {
            true: '',
            false: '',
        },
    },
});
const useActionUnderline = (props) => {
    const { icon, iconEnd, label, srOnlyLabel, invert, size, transform, className, } = props;
    const buttonChildren = [
        icon ? (React.createElement(Icon, { key: "icon", size: "inherit", className: "flex-shrink-0 inline align-bottom", name: icon })) : null,
        srOnlyLabel ? (React.createElement("span", { key: "sr-label", "data-uds-child": "sr-only-label", className: "sr-only" }, srOnlyLabel)) : null,
        React.createElement("span", { key: "label", "data-uds-child": "text-label", className: tw$2('relative overflow-hidden truncate font-bold min-w-0 flex-1', labelStyle), "aria-hidden": srOnlyLabel ? 'true' : 'false' }, label),
    ];
    return {
        children: buttonChildren,
        className: tw$2(iconEnd ? 'flex-row-reverse' : 'flex-row', colorStyle({ color: 'base', invert }), fontStyle({ transform }), actionUnderlineStyle({ size, invert }), className),
    };
};

const ButtonUnderline = React.forwardRef(function ButtonUnderline(props, forwardedRef) {
    const { dataTestId, label, srOnlyLabel, invert, size, transform, disabled, icon, iconEnd, className, onClick, type = 'button', ...rest } = props;
    const { children, ...ctaProps } = useActionUnderline({
        className,
        label,
        srOnlyLabel,
        invert,
        size,
        transform,
        icon,
        iconEnd,
    });
    const handleOnClick = (e) => {
        if (onClick) {
            onClick(e);
        }
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("button", { ref: forwardedRef, "data-test-id": dataTestId, onClick: handleOnClick, disabled: disabled, type: type, ...ctaProps, ...rest }, children)));
});

/**
 * LinkUnderline is a link that is styled as an underlined call-to-action
 *
 * @example
 * ```
 * <LinkUnderline label="Shop Running" href="/running" />
 * ```
 */
const LinkUnderline = React.forwardRef(function LinkUnderline(props, forwardedRef) {
    const { label, srOnlyLabel, className, invert, size, transform, icon, iconEnd, dataTestId, ...rest } = props;
    const linkCtaProps = useActionUnderline({
        label,
        srOnlyLabel,
        className,
        invert,
        size,
        transform,
        icon,
        iconEnd,
    });
    const navigateProps = useNavigate(rest);
    return (React.createElement("a", { ref: forwardedRef, ...linkCtaProps, ...navigateProps, "data-test-id": dataTestId }));
});

/**
 * A plain text link, that inherits font-size and color from the parent context
 */
const Anchor = React.forwardRef(function Anchor(props, forwardedRef) {
    const { className, dataTestId, ...rest } = props;
    const navigateProps = useNavigate(rest);
    return (React.createElement("a", { ref: forwardedRef, className: tw$1('font-bold text-inherit cursor-pointer focus:(ring-0 text-inherit)', 'hover:(bg-gradient-to-b) from-current to-current bg-no-repeat', css$1 `
            background-size: 100% 2px;
            background-position: 100% 100%;
          `, className), ...navigateProps, "data-test-id": dataTestId }));
});

const liItemBase = 'text-neutral-100 whitespace-nowrap flex items-center h-5 leading-5';
const liItemCurrent = 'flex-1 truncate font-normal leading-none px-2';
const Breadcrumb = ({ path, dataTestId, ...args }) => {
    const ulStyle = 'capitalize flex items-center font-medium w-full';
    const itemSmScreenHidden = 'mobile:hidden tablet:hidden';
    const listItems = path.map((step, index) => {
        const isLastItem = index === path.length - 1;
        const shouldHideOnSmall = index < path.length - 2 || isLastItem;
        const isLastLink = index === path.length - 2;
        if (isLastItem) {
            return (React.createElement(BreadcrumbListItem, { key: step.label, label: step.label, className: tw$3(itemSmScreenHidden) }));
        }
        else {
            return (React.createElement(BreadcrumbLinkListItem, { key: step.label, step: step, hasBackArrow: isLastLink, className: tw$3(shouldHideOnSmall && itemSmScreenHidden) }));
        }
    });
    return (React.createElement("nav", { "data-test-id": dataTestId ?? 'breadcrumb-nav', "data-uds-child": "breadcrumb", "aria-label": "Breadcrumb", className: "flex flex-row items-center ", ...args },
        React.createElement("ul", { className: ulStyle, "data-uds-child": "breadcrumb-list" }, listItems)));
};
//breadcrumb item components
const BreadcrumbListItem = ({ label, className, }) => (React.createElement("li", { "data-uds-child": "breadcrumb-list-item", className: tw$3(liItemBase, liItemCurrent, className) }, label));
const BreadcrumbLinkListItem = ({ step, hasBackArrow, className, }) => {
    const liItemAnchor = 'group flex items-center font-bold border-1 border-white rounded-sm px-2 focus-visible:(border-neutral-100 ring) hover:text-neutral-80';
    const listItemSeparator = tw$3('after::(hidden flex-none bg-neutral-50 w-1 h-1 transform translate-y-0.5 rounded-full mx-2) lg:after::(block)', css `
      &::after {
        content: '';
      }
    `);
    const navigateProps = useNavigate({ href: step.url });
    return (React.createElement("li", { className: tw$3(liItemBase, className, listItemSeparator), "data-uds-child": "breadcrumb-list-item" },
        React.createElement("a", { className: liItemAnchor, ...navigateProps, "data-uds-child": "breadcrumb-link" },
            hasBackArrow ? (React.createElement("div", { className: "block lg:hidden flex-none mt-0.5 mr-1 rtl:(mr-0 ml-0.5) transition-transform ease-out group-hover:(transform -translate-x-1 rtl:translate-x-1) group-focus-visible:(transform -translate-x-1 rtl:translate-x-1)" },
                React.createElement(Icon, { size: "sm", name: "chevron-left" }))) : null,
            step.label)));
};

const SidenavContext = createContext({});
/**
 * The `Sidenav` component is used to render a secondary navigation on the side of the main page.
 *
 * @example
 * ```
 * <Sidenav>
 *   <SidenavLink label="Overview" icon="info" href="/my-account" />
 *   <SidenavLink label="Account Settings" icon="filter" href="/my-account/settings" />
 * </Sidenav>
 * ```
 */
const Sidenav = ({ gap = true, dataTestId, className, children, asChild, ...rest }) => {
    const Comp = asChild ? Slot : 'nav';
    return (React.createElement(SidenavContext.Provider, { value: { dataTestId } },
        React.createElement(Comp, { ...rest, "data-test-id": dataTestId, "data-uds-child": "sidebav", className: tw$1('flex flex-col', gap && 'gap-2', className) }, children)));
};
const linkStyle = style({
    variants: {
        /**
         * Render a chevron at the end of the link
         */
        chevronEnd: {
            true: 'display-block',
            false: 'hidden',
        },
    },
});
/**
 * The `SidenavLink` component usually within the Sidenav component, but can be used independently
 *
 * @example
 * ```
 * <Sidenav>
 *   <SidenavLink label="Overview" icon="info" href="/my-account" />
 *   <SidenavLink label="Account Settings" icon="filter" href="/my-account/settings" />
 * </Sidenav>
 * ```
 *
 *  * @example
 * ```
 * <Stack asChild>
 *   <nav
 *     <SidenavLink label="Overview" icon="info" href="/my-account" />
 *     <SidenavLink label="Account Settings" icon="filter" href="/my-account/settings" />
 *   </nav>
 * </SideStacknav>
 * ```
 */
const SidenavLink = (props) => {
    const { dataTestId, selected, className, label, icon, chevronEnd = false, ...rest } = props;
    const navigateProps = useNavigate(rest);
    const { dataTestId: contextDataTestId } = React.useContext(SidenavContext);
    const appliedDataTestId = dataTestId || contextDataTestId;
    return (React.createElement("a", { ...navigateProps, "data-test-id": `${appliedDataTestId}-link-${rest.href}`, className: tw$1('relative py-4 pl-5 pr-4', 'flex justify-between gap-4', 'border-l-2 rtl:(border-r-2 border-l-0))', selected
            ? 'border-neutral-100 bg-neutral-0'
            : 'border-transparent bg-transparent ', 'z-1 hover:(bg-neutral-20 z-[2]) focus:(bg-neutral-20 z-[2])', className) },
        React.createElement("div", { className: "flex gap-4" },
            icon ? (React.createElement(Icon, { size: "3xl", name: icon, dataTestId: `${appliedDataTestId}-link-icon`, className: "flex-shrink-0 -ml-px rtl:(ml-auto -mr-px)" })) : null,
            React.createElement(Text, { size: "base", weight: "bold", className: "py-0.5" }, label)),
        React.createElement(Icon, { size: "3xl", name: "chevron-right", dataTestId: `${appliedDataTestId}-link-chevron`, className: tw$1(linkStyle({ chevronEnd })) })));
};

const UDS_GAP = {
    min: '[0.8vmax]',
    max: '[3.2vmax]',
    auto: '[1.6vmax]',
    none: '0',
    px: 'px',
    xxs: '1',
    xs: '2',
    sm: '4',
    base: '6',
    lg: '8',
    xl: '12',
    '2xl': '16',
    '3xl': '20',
    '4xl': '28',
    '5xl': '36',
};
const UDS_GAP_VALUES = {
    min: '1vmax',
    auto: '3.2vmax',
    max: '4.8vmax',
    none: '0px',
    px: '1px',
    xs: '0.5rem',
    sm: '1rem',
    base: '1.5rem',
    lg: '2rem',
    xl: '3.5rem',
    '2xl': '4rem',
    '3xl': '5rem',
    '4xl': '7rem',
    '5xl': '9rem',
};
const gapStyle = style({
    variants: {
        /**
         * Gap, applies both horizontally and vertically. Alternatively use `gapX` and `gapY` to configure each space separately.
         */
        gap: {
            min: `gap-${UDS_GAP.min}`,
            auto: `gap-${UDS_GAP.auto}`,
            max: `gap-${UDS_GAP.max}`,
            none: `gap-${UDS_GAP.none}`,
            px: `gap-${UDS_GAP.px}`,
            xxs: `gap-${UDS_GAP.xxs}`,
            xs: `gap-${UDS_GAP.xs}`,
            sm: `gap-${UDS_GAP.sm}`,
            base: `gap-${UDS_GAP.base}`,
            lg: `gap-${UDS_GAP.lg}`,
            xl: `gap-${UDS_GAP.xl}`,
            '2xl': `gap-${UDS_GAP['2xl']}`,
            '3xl': `gap-${UDS_GAP['3xl']}`,
            '4xl': `gap-${UDS_GAP['4xl']}`,
            '5xl': `gap-${UDS_GAP['5xl']}`,
        },
        /**
         * Gap X, applies only horizontally. Alternatively use `gap` to configure gaps on both axis combined
         */
        gapX: {
            min: `gap-x-${UDS_GAP.min}`,
            auto: `gap-x-${UDS_GAP.auto}`,
            max: `gap-x-${UDS_GAP.max}`,
            none: `gap-x-${UDS_GAP.none}`,
            px: `gap-x-${UDS_GAP.px}`,
            xxs: `gap-x-${UDS_GAP.xxs}`,
            xs: `gap-x-${UDS_GAP.xs}`,
            sm: `gap-x-${UDS_GAP.sm}`,
            base: `gap-x-${UDS_GAP.base}`,
            lg: `gap-x-${UDS_GAP.lg}`,
            xl: `gap-x-${UDS_GAP.xl}`,
            '2xl': `gap-x-${UDS_GAP['2xl']}`,
            '3xl': `gap-x-${UDS_GAP['3xl']}`,
            '4xl': `gap-x-${UDS_GAP['4xl']}`,
            '5xl': `gap-x-${UDS_GAP['5xl']}`,
        },
        /**
         * Gap Y, applies only vertically. Alternatively use `gap` to configure gaps on both axis combined.
         */
        gapY: {
            min: `gap-y-${UDS_GAP.min}`,
            auto: `gap-y-${UDS_GAP.auto}`,
            max: `gap-y-${UDS_GAP.max}`,
            none: `gap-y-${UDS_GAP.none}`,
            px: `gap-y-${UDS_GAP.px}`,
            xxs: `gap-y-${UDS_GAP.xxs}`,
            xs: `gap-y-${UDS_GAP.xs}`,
            sm: `gap-y-${UDS_GAP.sm}`,
            base: `gap-y-${UDS_GAP.base}`,
            lg: `gap-y-${UDS_GAP.lg}`,
            xl: `gap-y-${UDS_GAP.xl}`,
            '2xl': `gap-y-${UDS_GAP['2xl']}`,
            '3xl': `gap-y-${UDS_GAP['3xl']}`,
            '4xl': `gap-y-${UDS_GAP['4xl']}`,
            '5xl': `gap-y-${UDS_GAP['5xl']}`,
        },
    },
});

const AccordionContext = createContext(null);
const useAccordionContext = () => {
    return useContext(AccordionContext);
};
const AccordionItemContext = createContext(null);
const useAccordionItemContext = () => {
    return useContext(AccordionItemContext);
};

const ACCORDION_VARIANTS = {
    invert: {
        false: 'text-neutral-100 border-neutral-20 bg-neutral-10',
        true: 'text-neutral-0 border-neutral-80 bg-neutral-90',
    },
    fill: {
        false: 'bg-opacity-0',
        true: 'bg-opacity-100',
    },
    outline: {
        false: 'border-0',
        true: 'border-1',
    },
};
const accordionRootStyle = style({
    variants: ACCORDION_VARIANTS,
});
const AccordionRoot = ({ id: propsId, children, type, value, defaultValue, onValueChange, gap = 'none', transition = 'reveal', speed = 'base', fill = false, outline = false, invert = false, className, dataTestId, ...rest }) => {
    const id = useId(propsId);
    const [internalValue, setInternalValue] = React.useState(defaultValue);
    useEffect(() => {
        setInternalValue(value);
    }, [value]);
    const handleOpenChange = (value) => {
        setInternalValue(value);
        onValueChange?.(value);
    };
    const rootProps = type === 'single'
        ? {
            type: 'single',
            value: internalValue,
            defaultValue,
            onValueChange: handleOpenChange,
        }
        : {
            type: 'multiple',
            onValueChange: handleOpenChange,
            defaultValue,
            value: internalValue,
        };
    return (React.createElement(AccordionContext.Provider, { value: {
            id,
            invert,
            transition,
            speed,
            dataTestId,
            value: internalValue,
        } },
        React.createElement(RadixAccordion.Root, { ...rest, ...rootProps, id: id, "data-test-id": dataTestId, "data-uds-child": "accordion", className: tw('w-full flex flex-col', accordionRootStyle({ fill, invert, outline }), gapStyle({ gap }), className) }, children)));
};

const ACCORDION_ITEM_VARIANTS = {
    invert: {
        false: 'text-neutral-100 border-neutral-20 bg-neutral-10',
        true: 'text-neutral-0 border-neutral-80 bg-neutral-90',
    },
    fill: {
        false: 'bg-opacity-0',
        true: 'bg-opacity-100',
    },
    outline: {
        false: 'border-0',
        true: 'border-1',
    },
};
const accordionItemStyle = style({
    variants: ACCORDION_ITEM_VARIANTS,
});
const AccordionItem = (props) => {
    const { value, children, invert, fill = false, outline = false, ...rest } = props;
    const { invert: rootInvert } = useAccordionContext();
    const { value: valueContext = [] } = useAccordionContext();
    const isItemOpen = valueContext === value || valueContext.includes(value);
    const appliedInvert = is.exists(invert) ? invert : rootInvert;
    return (React.createElement(AccordionItemContext.Provider, { value: { open: isItemOpen, invert: appliedInvert } },
        React.createElement(RadixAccordion.Item, { value: value, key: value, "data-uds-child": "accordion-item", className: tw$1(accordionItemStyle({ invert, fill, outline }), 'flex flex-col leading-[1]'), ...rest }, children)));
};

const animFadeIn$1 = (curve) => {
    return animation(curve, {
        from: {
            opacity: 0,
        },
        to: {
            opacity: 1,
        },
    });
};
const animFadeOut$1 = (curve) => {
    return animation(curve, {
        from: {
            opacity: 1,
        },
        to: {
            opacity: 0,
        },
    });
};
const animRevealIn = (curve, heightVar) => {
    return animation(curve, {
        from: {
            opacity: 0,
            height: '0px',
        },
        to: {
            opacity: 1,
            height: `var(${heightVar})`,
        },
    });
};
const animRevealOut = (curve, heightVar) => {
    return animation(curve, {
        from: {
            opacity: 1,
            height: `var(${heightVar})`,
        },
        to: {
            opacity: 0,
            height: '0px',
        },
    });
};
const animSlideIn = (curve, heightVar) => {
    return animation(curve, {
        from: {
            height: '0px',
        },
        to: {
            height: `var(${heightVar})`,
        },
    });
};
const animSlideOut = (curve, heightVar) => {
    return animation(curve, {
        from: {
            height: `var(${heightVar})`,
        },
        to: {
            height: '0px',
        },
    });
};
const getCollapsibleTransition = (param) => {
    return {
        in: getCollapsibleTransitionIn(param),
        out: getCollapsibleTransitionOut(param),
    };
};
const speedMap = {
    base: 0.6,
    fast: 0.3,
    slow: 1,
};
const getCurve = (speed) => {
    return `${speedMap[speed]}s cubic-bezier(0.16, 1, 0.3, 1) forwards`;
};
const getCollapsibleTransitionIn = (param) => {
    const curve = getCurve(param.speed);
    switch (param.type) {
        case 'fade':
            return animFadeIn$1(curve);
        case 'slide':
            return animSlideIn(curve, param.heightVar);
        case 'reveal':
            return animRevealIn(curve, param.heightVar);
        case 'none':
            return '';
    }
};
const getCollapsibleTransitionOut = (param) => {
    const curve = getCurve(param.speed);
    switch (param.type) {
        case 'fade':
            return animFadeOut$1(curve);
        case 'slide':
            return animSlideOut(curve, param.heightVar);
        case 'reveal':
            return animRevealOut(curve, param.heightVar);
        case 'none':
            return '';
    }
};

const insetStyle = style({
    variants: {
        inset: {
            none: '',
            xxxs: '',
            xxs: '',
            xs: '',
            sm: '',
            base: '',
            md: '',
            lg: '',
            xl: '',
            '2xl': '',
            '3xl': '',
            '4xl': '',
            '5xl': '',
        },
        insetX: {
            none: 'px-0',
            xxxs: 'px-1',
            xxs: 'px-2',
            xs: 'px-3',
            sm: 'px-4',
            base: 'px-6',
            md: 'px-8',
            lg: 'px-12',
            xl: 'px-14',
            '2xl': 'px-16',
            '3xl': 'px-20',
            '4xl': 'px-28',
            '5xl': 'px-36',
        },
        insetY: {
            none: 'py-0',
            xxxs: 'py-1',
            xxs: 'py-2',
            xs: 'py-3',
            sm: 'py-4',
            base: 'py-6',
            md: 'py-8',
            lg: 'py-12',
            xl: 'py-14',
            '2xl': 'py-16',
            '3xl': 'py-20',
            '4xl': 'py-28',
            '5xl': 'py-36',
        },
        insetStart: {
            none: 'pl-0 rtl:pr-0',
            xxxs: 'pl-1 rtl:pr-1',
            xxs: 'pl-2 rtl:pr-2',
            xs: 'pl-3 rtl:pr-3',
            sm: 'pl-4 rtl:pr-4',
            base: 'pl-6 rtl:pr-6',
            md: 'pl-8 rtl:pr-8',
            lg: 'pl-12 rtl:pr-12',
            xl: 'pl-14 rtl:pr-14',
            '2xl': 'pl-16 rtl:pr-16',
            '3xl': 'pl-20 rtl:pr-20',
            '4xl': 'pl-28 rtl:pr-28',
            '5xl': 'pl-36 rtl:pr-36',
        },
        insetEnd: {
            none: 'pr-0 rtl:pl-0',
            xxxs: 'pr-1 rtl:pl-1',
            xxs: 'pr-2 rtl:pl-2',
            xs: 'pr-3 rtl:pl-3',
            sm: 'pr-4 rtl:pl-4',
            base: 'pr-6 rtl:pl-6',
            md: 'pr-8 rtl:pl-8',
            lg: 'pr-12 rtl:pl-12',
            xl: 'pr-14 rtl:pl-14',
            '2xl': 'pr-16 rtl:pl-16',
            '3xl': 'pr-20 rtl:pl-20',
            '4xl': 'pr-28 rtl:pl-28',
            '5xl': 'pr-36 rtl:pl-36',
        },
        insetTop: {
            none: 'pt-0',
            xxxs: 'pt-1',
            xxs: 'pt-2',
            xs: 'pt-3',
            sm: 'pt-4',
            base: 'pt-6',
            md: 'pt-8',
            lg: 'pt-12',
            xl: 'pt-14',
            '2xl': 'pt-16',
            '3xl': 'pt-20',
            '4xl': 'pt-28',
            '5xl': 'pt-36',
        },
        insetBottom: {
            none: 'pb-0',
            xxxs: 'pb-1',
            xxs: 'pb-2',
            xs: 'pb-3',
            sm: 'pb-4',
            base: 'pb-6',
            md: 'pb-8',
            lg: 'pb-12',
            xl: 'pb-14',
            '2xl': 'pb-16',
            '3xl': 'pb-20',
            '4xl': 'pb-28',
            '5xl': 'pb-36',
        },
    },
});
/**
 * The `Inset` component is a padded container for content.
 *
 * @example
 * ```jsx
 * <Inset inset="md"/>
 * <Inset inset={{_:'base,md:'2xl'}}/>
 * ```
 */
const Inset = React.forwardRef((props, forwadedRef) => {
    const { asChild, dataTestId, inset = 'none', insetX, insetY, insetTop, insetStart, insetBottom, insetEnd, className, ...rest } = props;
    const Comp = asChild ? Slot : 'div';
    return (React.createElement(Comp, { ref: forwadedRef, "data-test-id": dataTestId, "data-uds-child": "inset", className: tw$1('w-full', insetStyle({
            insetX: insetX ?? inset,
            insetY: insetY ?? inset,
            insetStart: insetStart,
            insetEnd: insetEnd,
            insetTop: insetTop,
            insetBottom: insetBottom,
        }), className), ...rest }));
});
Inset.displayName = 'Inset';

const AccordionContent = ({ inset = 'none', insetX, insetY, insetTop, insetEnd, insetBottom, insetStart, className, children, ...props }) => {
    const { id, dataTestId, transition, speed } = useAccordionContext();
    const animation = getCollapsibleTransition({
        type: transition,
        speed: speed,
        heightVar: '--radix-accordion-content-height',
    });
    const animationCss = css$1 `
    &[data-state='open'] {
      ${animation.in}
    }

    &[data-state='closed'] {
      ${animation.out}
    }

    @media (prefers-reduced-motion) {
      &[data-state='open'] {
        animation: none;
      }

      &[data-state='closed'] {
        animation: none;
      }
    }
  `;
    return (React.createElement(RadixAccordion.Content, { ...props, "data-uds-child": "accordion-content", "data-test-id": dataTestId ? `${dataTestId}-content` : null, className: tw$2(className, 'overflow-hidden', animationCss) },
        React.createElement(Inset, { inset: inset, insetX: insetX, insetY: insetY, insetTop: insetTop, insetEnd: insetEnd, insetBottom: insetBottom, insetStart: insetStart }, children)));
};

const accordionTriggerStyle = style({
    variants: {
        hover: {
            bg: '',
            underline: '',
            none: '',
        },
        invert: {
            false: '',
            true: '',
        },
    },
    matches: [
        {
            hover: 'bg',
            invert: 'true',
            use: 'hover:bg-neutral-80 focus-visible:bg-neutral-80',
        },
        {
            hover: 'bg',
            invert: 'false',
            use: 'hover:bg-neutral-20 focus-visible:bg-neutral-20',
        },
        {
            hover: 'underline',
            use: 'focus:outline-none hover:(decoration-underline decoration-1) focus-visible:(decoration-underline decoration-1)',
        },
    ],
});
const AccordionTrigger = ({ className, hover = 'bg', inset = 'none', insetX, insetY, insetTop, insetEnd, insetBottom, insetStart, asChild, ...rest }) => {
    const { dataTestId } = useAccordionContext();
    const { invert } = useAccordionItemContext();
    const wrapProps = asChild
        ? {}
        : {
            asChild: true,
            inset: inset,
            insetX: insetX,
            insetY: insetY,
            insetTop: insetTop,
            insetEnd: insetEnd,
            insetBottom: insetBottom,
            insetStart: insetStart,
        };
    const Wrap = asChild ? React.Fragment : Inset;
    return (React.createElement(Wrap, { ...wrapProps },
        React.createElement(RadixAccordion.Trigger, { ...rest, asChild: asChild, "data-uds-child": "accordion-trigger", "data-test-id": dataTestId ? `${dataTestId}-trigger` : null, className: tw$1(accordionTriggerStyle({ hover, invert }), 'w-full text-left rtl:text-right', 'transition-colors duration-[800ms] ease-out', 'hover:(transition-colors duration-300 ease-out)', className) })));
};

const ToggleChevron = ({ size = 'xl', open = false, className, ...rest }) => {
    return (React.createElement(Icon, { "data-uds-child": "toggle-chevron", ...rest, name: "chevron-down", "data-state": open ? 'open' : 'closed', size: size, className: tw$1('transition-transform transform', css$1 `
          &[data-state='open'] {
            transform: rotate(180deg);
          }
        `, className) }));
};

const AccordionChevron = (props) => {
    const { open } = useAccordionItemContext();
    return (React.createElement(ToggleChevron, { ...props, open: open, "data-uds-child": "accordion-chevron" }));
};

const CarouselContext = createContext(undefined);
const CarouselProvider = ({ children, value, }) => (React.createElement(CarouselContext.Provider, { value: value }, children));

/**
 * The `CarouselRoot` component is the root component for the carousel. It provides the context for the carousel.
 *
 * To render slides within the carousel, you will need to wrap them in a `CarouselViewport` component, which provides the scrolling functionality.
 *
 * The pattern also exposes other components to help with navigation, such as `CarouselChevronButton`. `CarouselDots`, `Caroueland `CarouselProgress`
 *
 * ```
 * import {
 *   CarouselRoot,
 *   CarouselViewport,
 *   CarouselChevronButton,
 *   CarouselDots,
 *   CarouselProgress,
 *   CarouselThumbList,
 *   CarouselThumb,
 *   CarouselHeading,
 *   CarouselPageOf
 * } from '@global-ecom/nitro-uds/elements';
 *```
 *
 * @example
 * ```tsx
 * <CarouselRoot
 *   align="center"
 *   columns={1}
 * >
 * <CarouselChevronButton
 *    direction="prev"
 *  />
 *   CarouselChevronButton
 *    direction="next"
 *  />
 *
 *  <CarouselHeading>Trending content</CarouselHeading>
 *
 *  <CarouselViewport>
 *    <CarouselSlide/>
 *    <CarouselSlide/>
 *    <CarouselSlide/>
 *  </CarouselViewport>
 *
 *  <CarouselPeekButton
 *    direction="prev"
 *  />
 *   CarouselPeekButton
 *    direction="next"
 *  />
 *
 *  <CarouselDots />
 *  <CarouselProgress />
 *  <CarouselPageOf />
 *
 *  <CarouselThumbList>
 *    <CarouselThumb>
 *      <img />
 *    </CarouselThumb>
 *    <CarouselThumb>
 *      <img />
 *    </CarouselThumb>
 *    <CarouselThumb>
 *      <img />
 *    </CarouselThumb>
 *  </CarouselThumbList>
 * </CarouselRoot>
 * ```
 */
const CarouselRoot = ({ id: propsId, columns = 1, peek = false, limit = 999, gap = 'auto', loop = true, snap = true, animate = true, dataTestId, className, children, onSlideChange, ...rest }) => {
    const id = useId(propsId);
    const { devicePreferences } = useUdsContext();
    const prefersReducedMotion = devicePreferences.prefersReducedMotion;
    const columnValue = useResponsiveValue(columns);
    const rootRef = useRef(null);
    const [emblaRef, api] = useEmblaCarousel({
        containScroll: 'keepSnaps',
        skipSnaps: snap,
        dragFree: !snap,
        align: peek === true ? 'center' : 'start',
        inViewThreshold: 0.5,
        duration: animate && prefersReducedMotion !== true ? 20 : 0,
        slidesToScroll: columnValue,
        loop: loop,
    }, []);
    const handleSlideSelect = useCallback(api => {
        onSlideChange && onSlideChange(api.selectedScrollSnap());
    }, []);
    useEffect(() => {
        if (api) {
            api.on('init', handleSlideSelect);
            api.on('select', handleSlideSelect);
        }
        return () => {
            if (api) {
                api.off('init', handleSlideSelect);
                api.off('select', handleSlideSelect);
            }
        };
    }, [api, handleSlideSelect]);
    const handleKeyDown = event => {
        /* istanbul ignore next */
        if (!api || !rootRef.current.contains(document.activeElement))
            return;
        if (event.key === 'ArrowRight') {
            api.scrollNext();
        }
        if (event.key === 'ArrowLeft') {
            api.scrollPrev();
        }
        if (event.key === 'Home') {
            api.scrollTo(0);
        }
        if (event.key === 'End') {
            api.scrollTo(api.scrollSnapList().length - 1);
        }
    };
    useEffect(() => {
        /* istanbul ignore next */
        if (!rootRef.current)
            return;
        rootRef.current.addEventListener('keydown', handleKeyDown);
        return () => {
            if (!rootRef.current)
                return;
            rootRef.current.removeEventListener('keydown', handleKeyDown);
        };
    }, [rootRef, handleKeyDown]);
    return (React.createElement("div", { id: id, ref: rootRef, className: tw$2('relative', className), "data-uds-child": "carousel", "data-test-id": dataTestId, ...rest },
        React.createElement(CarouselProvider, { value: {
                id,
                dataTestId,
                peek,
                slidesToScroll: columnValue,
                columns,
                limit,
                loop,
                gap,
                rootRef: rootRef,
                viewportRef: emblaRef,
                api: api,
            } }, children)));
};

const LiveRegion = ({ id, children, className, dataTestId, role = 'alert', type = 'polite', atomic = true, ...rest }) => {
    return (React.createElement("div", { id: id, "data-test-id": dataTestId, role: role, "aria-live": type, "aria-atomic": atomic, className: tw$2('w-full', className, !children && 'hidden'), ...rest }, children));
};

const CAROUSEL_GAP = {
    min: `-ml-${UDS_GAP['min']} children:pl-${UDS_GAP['min']}`,
    auto: `-ml-${UDS_GAP['auto']} children:pl-${UDS_GAP['auto']}`,
    max: `-ml-${UDS_GAP['max']} children:pl-${UDS_GAP['max']}`,
    none: `-ml-${UDS_GAP['none']} children:pl-${UDS_GAP['none']}`,
    px: `-ml-${UDS_GAP['px']} children:pl-${UDS_GAP['px']}`,
    xxs: `-ml-${UDS_GAP['xxs']} children:pl-${UDS_GAP['xxs']}`,
    xs: `-ml-${UDS_GAP['xs']} children:pl-${UDS_GAP['xs']}`,
    sm: `-ml-${UDS_GAP['sm']} children:pl-${UDS_GAP['sm']}`,
    base: `-ml-${UDS_GAP['base']} children:pl-${UDS_GAP['base']}`,
    lg: `-ml-${UDS_GAP['lg']} children:pl-${UDS_GAP['lg']}`,
    xl: `-ml-${UDS_GAP['xl']} children:pl-${UDS_GAP['xl']}`,
    '2xl': `-ml-${UDS_GAP['2xl']} children:pl-${UDS_GAP['2xl']}`,
    '3xl': `-ml-${UDS_GAP['3xl']} children:pl-${UDS_GAP['3xl']}`,
    '4xl': `-ml-${UDS_GAP['4xl']} children:pl-${UDS_GAP['4xl']}`,
    '5xl': `-ml-${UDS_GAP['5xl']} children:pl-${UDS_GAP['5xl']}`,
};
const THUMB_SIZE$2 = {
    xs: 'h-6 w-6 p-px',
    sm: 'h-8 w-8 p-px',
    base: 'h-10 w-10 p-px',
    lg: 'h-12 w-12 p-px',
};
const THUMB_SIZE_RAW = {
    xs: 20,
    sm: 28,
    base: 36,
    lg: 44,
};
const THUMBS_GAP_RAW = {
    xs: 4,
    sm: 4,
    base: 6,
    lg: 8,
};
const THUMBS_GAP = {
    xs: '-ml-1 children:pl-1',
    sm: '-ml-1 children:pl-1',
    base: '-ml-1.5 children:pl-1.5',
    lg: '-ml-2 children:pl-2',
};

/**
 * useCarouselContext is used to access the CarouselContext. It's mounted internally by the CarouselRoot, and will not be used directly by consumers.
 *
 * @internal
 */
const useCarouselContext = () => {
    const context = useContext(CarouselContext);
    if (!context) {
        throw new Error('useCarouselContext must be used within a CarouselRoot');
    }
    return context;
};

/**
 * A utility hook, that returns the scroll progress of the parent carousel
 *
 * @example
 * ```tsx
 * useCarouselProgress(({ progressPercent, selectedIndex }) => {
 *  console.log(progressPercent, selectedIndex);
 * });
 * ```
 */
const useCarouselProgress = (onUpdate) => {
    const { api } = useCarouselContext();
    const progress = React.useRef(null);
    const onScroll = useCallback(() => {
        /* istanbul ignore next */
        if (!api)
            return;
        const progressPercent = Math.max(0.005, Math.min(1, api.scrollProgress()));
        progress.current = progressPercent;
        onUpdate({
            api,
            progressPercent,
            slidesInView: api.slidesInView(),
            selectedIndex: api.selectedScrollSnap(),
        });
    }, [api]);
    useEffect(() => {
        /* istanbul ignore next */
        if (!api)
            return;
        onScroll();
        api.on('reInit', onScroll);
        api.on('scroll', onScroll);
        api.on('select', onScroll);
        return () => {
            api.off('reInit', onScroll);
            api.off('scroll', onScroll);
            api.off('select', onScroll);
        };
    }, [api, onScroll]);
    return progress;
};

/**
 * A utility hook, that returns the scrolll positions, the current selected index and an array of indexes, representing the slides currently in view.
 *
 * @example
 * ```tsx
 * const { scrollSnapList, selectedIndex, slidesInView } = useCarouselSnaps();
 * ```
 */
const useCarouselSnaps = () => {
    const { api } = useCarouselContext();
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [scrollSnapList, setSnaps] = useState([]);
    const [slidesInView, setSlidesInView] = useState([]);
    const onInit = useCallback(() => {
        /* istanbul ignore next */
        if (!api)
            return;
        setSnaps(api.scrollSnapList());
        setSlidesInView(api.slidesInView());
        setSelectedIndex(api.selectedScrollSnap());
    }, [api]);
    const onSelect = useCallback(() => {
        setSlidesInView(api.slidesInView());
        setSelectedIndex(api.selectedScrollSnap());
    }, [api]);
    const onSettle = useCallback(() => {
        setSlidesInView(api.slidesInView());
        setSelectedIndex(api.selectedScrollSnap());
    }, [api]);
    useEffect(() => {
        /* istanbul ignore next */
        if (!api)
            return;
        api.on('init', onInit);
        api.on('reInit', onInit);
        api.on('select', onSelect);
        api.on('settle', onSettle);
        onInit();
        return () => {
            api.off('init', onInit);
            api.off('reInit', onInit);
            api.off('select', onSelect);
            api.off('settle', onSettle);
        };
    }, [api, onInit]);
    return { scrollSnapList, selectedIndex, slidesInView };
};

/**
 * The viewport of the carousel. This is the container that holds the slides, and is responsible for scrolling.. It does not receive any props, and injerits all the configurations from the CarouselRoot.
 *
 * @example
 * ```tsx
 * <CarouselRoot>
 *  <CarouselViewport>
 *    <CarouselSlide/>
 *    <CarouselSlide/>
 *    <CarouselSlide/>
 *  </CarouselViewport>
 * </CarouselRoot>
 * ```
 */
const CarouselViewport = ({ children, ...rest }) => {
    const t = useUdsTranslate();
    const { id, dataTestId, viewportRef, peek, columns, limit, gap } = useCarouselContext();
    const { scrollSnapList, selectedIndex } = useCarouselSnaps();
    const containerRef = React.useRef(null);
    const columnsClassName = useResponsiveProp(columns, col => {
        if (peek === true) {
            col += 0.2 + (col > 1 ? col * 0.12 : 0.06);
        }
        return `auto-cols-[${100 / col}%]`;
    });
    const limitValue = useResponsiveValue(limit);
    useCarouselProgress(({ slidesInView }) => {
        const tileList = containerRef.current.querySelectorAll('[data-uds-child="carousel-slide"]');
        const tileTargets = Array.prototype.slice.call(tileList);
        tileTargets.forEach((target, index) => {
            const isInView = slidesInView.includes(index);
            target.setAttribute('inert', `${!isInView}`);
            // target.dataset.udsAttrItemActive = isInView ? 'true' : 'false';
            target.children[0].dataset.udsAttrInView = isInView
                ? 'true'
                : 'false';
        });
    });
    const gapClassName = useResponsiveProp(gap, value => {
        return CAROUSEL_GAP[value].split(' ');
    });
    const containerClassName = tw$2('grid grid-flow-col', 'children:grid', gapClassName, columnsClassName);
    return (React.createElement(React.Fragment, null,
        React.createElement(LiveRegion, { type: "polite", "data-uds-child": "carousel-announcer" },
            React.createElement("p", { className: "sr-only" }, t('pageXofY', {
                page: selectedIndex + 1,
                total: scrollSnapList.length,
            }))),
        React.createElement("div", { tabIndex: 0, role: "region", "data-uds-child": "carousel-viewport", ref: viewportRef, "data-test-id": dataTestId ? `${dataTestId}-viewport` : undefined, className: tw$2('transition-transform ease-out duration-300', 'focus:outline-none focus-visible:(ring ring-2 ring-offset-2) overflow-hidden'), ...rest },
            React.createElement("div", { "data-uds-child": "carousel-slides-track", className: tw$2(containerClassName), ref: containerRef, role: "none" }, React.Children.map(children, (child, index) => index < limitValue ? child : null)))));
};

const CarouselSlide = ({ children, ...rest }) => {
    return (React.createElement("div", { role: "none", "data-uds-child": "carousel-slide", ...rest }, children));
};

/**
 *
 * A utility hook, that returns a object containing the carousel navigation functions and state.
 *
 * @example
 * ```tsx
 * const { scrollTo, scrollNext, scrollPrev, canScrollNext, canScrollPrev } = useCarouselNav();
 * ```
 */
const useCarouselNav = () => {
    const { api } = useCarouselContext();
    const [canScrollNext, setScrollNext] = useState(false);
    const [canScrollPrev, setScrollPrev] = useState(false);
    const onSelect = () => {
        setScrollNext(api.canScrollNext());
        setScrollPrev(api.canScrollPrev());
    };
    const scrollNext = useCallback(() => api && api.scrollNext(), [api]);
    const scrollPrev = useCallback(() => api && api.scrollPrev(), [api]);
    const scrollTo = useCallback((index) => api && api.scrollTo(index), [api]);
    useEffect(() => {
        /* istanbul ignore next */
        if (!api)
            return;
        api.on('init', onSelect);
        api.on('reInit', onSelect);
        api.on('select', onSelect);
        onSelect();
        return () => {
            api.off('init', onSelect);
            api.off('reInit', onSelect);
            api.off('select', onSelect);
        };
    }, [api, onSelect]);
    return {
        scrollNext,
        scrollPrev,
        scrollTo,
        canScrollNext,
        canScrollPrev,
    };
};

/**
 * A component that renders an icon button that can be used to navigate the carousel to the next or previous page.
 *
 * The CarouselChevronButton is a convenience component that wraps the ButtonIcon component, and inherits some of its props.
 *
 * You can render this component, withiin the CarouselRoot, and position it however you like.
 *
 * Alternatively, you can use the useCarouselNav hook, which provides all the information you need to render your own custom component.
 *
 * @example
 * ```tsx
 * <CarouselRoot>
 *  <CarouselChevronButton direction="next" className="absolute"/>
 *  <CarouselChevronButton direction="prev"  className="absolute"/>
 *  <CarouselViewport>
 *    <CarouselSlide/>
 *    <CarouselSlide/>
 *   </CarouselViewport>
 * </CarouselRoot>
 * ```
 *
 * @example Button props
 * ```tsx
 * <CarouselChevronButton variant="primary" size="md">
 * <CarouselChevronButton variant="primary" invert>
 * <CarouselChevronButton variant="ghost" size="xs">
 * <CarouselChevronButton icon="arrow-right">
 * ```
 */
const CarouselChevronButton = forwardRef(function CarouselChevronButton(props, ref) {
    const { className, direction, variant, label, size, icon, invert } = props;
    const { dataTestId } = useCarouselContext();
    const t = useUdsTranslate();
    const { scrollNext, scrollPrev, canScrollPrev, canScrollNext } = useCarouselNav();
    const handleClick = e => {
        if (direction === 'prev') {
            scrollPrev();
        }
        if (direction === 'next') {
            scrollNext();
        }
    };
    const disabled = direction === 'prev' ? !canScrollPrev : !canScrollNext;
    const appliedIcon = icon || direction === 'prev' ? 'chevron-left' : 'chevron-right';
    const appliedLabel = label || direction === 'prev' ? t('scrollPrevious') : t('scrollNext');
    return (React.createElement(ButtonIcon, { ref: ref, icon: appliedIcon, label: appliedLabel, size: size, invert: invert, variant: variant, mode: "icon", onClick: handleClick, disabled: disabled, className: className, "data-uds-child": `carousel-chevron-button-${direction}`, 
        /* istanbul ignore next */
        dataTestId: dataTestId ? `${dataTestId}-chevron-button-${direction}` : undefined }));
});

/**
 * A variation of the CarouselChevronButton that is designed to be used with the peek feature.
 *
 * Is automatically resized to the size of the peek area, and based on the direction
 *
 */
const CarouselPeekButton = forwardRef(function CarouselChevronButton(props, ref) {
    const { variant, size = { _: 'xs', md: 'sm', xl: 'base' }, direction, label, invert, offsetY = 0, } = props;
    const { dataTestId, columns, gap } = useCarouselContext();
    const t = useUdsTranslate();
    const { scrollNext, scrollPrev, canScrollPrev, canScrollNext } = useCarouselNav();
    const handleClick = () => {
        if (direction === 'prev') {
            scrollPrev();
        }
        if (direction === 'next') {
            scrollNext();
        }
    };
    const widthClassName = useResponsiveProp(columns, col => {
        let columnSize = col;
        columnSize += 0.2 + (col > 1 ? col * 0.12 : 0.06);
        return [`w-[${(100 - (100 / columnSize) * col) / 2}%]`];
    });
    const paddingClassName = useResponsiveProp(gap, value => {
        return direction === 'prev'
            ? `pr-${UDS_GAP[value]}`
            : `pl-${UDS_GAP[value]}`;
    });
    const disabled = direction === 'prev' ? canScrollPrev === false : canScrollNext === false;
    const appliedLabel = label || direction === 'prev' ? t('scrollPrevious') : t('scrollNext');
    const buttonOffsetY = useResponsiveValue(offsetY);
    const { children: buttonChildren, className: buttonClassName } = useActionIcon({
        label: appliedLabel,
        variant: variant,
        invert: invert,
        size: size,
        mode: 'icon',
        icon: direction === 'prev' ? 'chevron-left' : 'chevron-right',
    });
    return (React.createElement("div", { className: tw$2(paddingClassName, widthClassName, 'min-w-12', 'absolute h-full top-0 z-10 flex text-white', direction === 'prev' ? 'left-0' : 'right-0') },
        React.createElement("button", { ref: ref, onClick: handleClick, "data-uds-child": `carousel-peek-button-${direction}`, disabled: disabled, "data-test-id": dataTestId ? `${dataTestId}-peek-button-${direction}` : undefined, className: tw$2(buttonClassName, 'w-full flex-1', 'focus:outline-none') },
            React.createElement("div", { className: tw$2('absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2', css `
              top: calc(
                50% + ${buttonOffsetY}${isNumber(buttonOffsetY) ? 'px' : ''}
              );
            `) }, buttonChildren))));
});

/**
 * A component that renders a a progress bar, that represents the current scroll position of the carousel.
 *
 * You can render this component, withiin the CarouselRoot, and position it however you like.
 *
 * Alternatively, you can use the useCarouselProgress hook, which provides all the information you need to render your own custom progress bar.
 *
 * @example
 * ```tsx
 * <CarouselRoot>
 *   <CarouselProgress className="absolute bottom-2" />
 *  <CarouselViewport>
 *    <CarouselSlide/>
 *    <CarouselSlide/>
 *   </CarouselViewport>
 * </CarouselRoot>
 * ```
 */
const CarouselProgress = ({ invert, className, }) => {
    const spreadRed = React.useRef(null);
    const { dataTestId } = useCarouselContext();
    useCarouselProgress(({ progressPercent }) => {
        spreadRed.current.style.transform = `scaleX(${progressPercent})`;
    });
    return (React.createElement("div", { "data-uds-child": "carousel-progress", "data-test-id": dataTestId ? `${dataTestId}-progress` : undefined, className: tw$2('relative h-1.5 rounded-full', invert ? 'bg-neutral-80' : 'bg-neutral-30', className) },
        React.createElement("div", { className: tw$2('origin-top-left h-1.5 w-full', invert ? 'bg-neutral-0' : 'bg-neutral-90'), ref: spreadRed })));
};

const PAGE_OF_SIZES = {
    xs: '',
    sm: '',
    base: '',
    lg: '',
    xl: '',
    '2xl': '',
};
const LINE_SIZES = {
    xs: 'h-px w-4 mx-1.5 mt-0.5',
    sm: 'h-px w-6 mx-1.5 mt-0.5',
    base: 'h-px w-6 mx-2 mt-0.5',
    lg: 'h-px w-8 mx-2 mt-0.5',
    xl: 'h-px w-8 mx-2 mt-0.5',
    '2xl': 'h-px w-8 mx-2 mt-0.5',
};
/**
 * A text component that renders the current page number and the total number of pages in the carousel.
 *
 * Its size can be customized using a small subset of the size scale, and it inherits the color, transform, and weight props from the Heading component.
 *
 * The text is hidden from screen readers, as the engine, internally announces the same content. to screen readers.
 *
 * @example
 * ```tsx
 * <CarouselRoot>
 *  <CarouselViewport>
 *    <CarouselSlide/>
 *    <CarouselSlide/>
 *   </CarouselViewport>
 *   <CarouselPageOf size="lg" weight="bold"/>
 * </CarouselRoot>
 * ```
 */
const CarouselPageOf = ({ className, color = 'base', weight = '400', size = 'base', ...rest }) => {
    const t = useUdsTranslate();
    const { dataTestId } = useCarouselContext();
    const { scrollSnapList, selectedIndex } = useCarouselSnaps();
    const lineClassName = useResponsiveProp(size, value => {
        return `${LINE_SIZES[value]}`;
    });
    return (React.createElement(Heading, { "data-uds-child": "carousel-page-of", 
        /* istanbul ignore next */
        dataTestId: dataTestId ? `${dataTestId}-page-of` : undefined, className: tw$2('tabular-nums	flex items-center', className), "aria-hidden": "true", size: size, color: color, weight: weight, ...rest, asChild: true, "aria-label": t('pageXofY', {
            page: selectedIndex + 1,
            total: scrollSnapList.length,
        }) },
        React.createElement("p", null,
            React.createElement("span", null, `${selectedIndex + 1}`.padStart(2, '0')),
            React.createElement("span", { role: "presentation", "aria-hidden": "true", className: tw$2('block flex-none bg-current', lineClassName) }),
            React.createElement("span", null, `${scrollSnapList.length}`.padStart(2, '0')))));
};

const stackStyle = style({
    base: 'w-full flex',
    variants: {
        /**
         * Flex Direction.
         *
         * - column, stacks vertically,
         * - row stacks horizontally, with row wrap,
         * - inline is a flex row, that doesn wrap the children
         *
         * defaultValue column
         *
         * @example
         * ```
         * <Stack direction="column" />
         * ```
         */
        direction: {
            column: '',
            row: 'flex-wrap',
            inline: 'children:flex-auto',
        },
        /**
         * Reverse the order of the children
         * defaultValue false
         */
        reverse: {
            true: '',
            false: '',
        },
        /**
         * Flex items. Align items across the stack direction
         */
        items: {
            start: 'items-start',
            end: 'items-end',
            center: 'items-center',
            stretch: 'items-stretch',
            baseline: 'items-baseline',
        },
        /**
         * Flex justify. Align items along the stack direction
         *
         * - stretch applies flex-1 to it's children
         */
        justify: {
            start: 'justify-start',
            end: 'justify-end',
            center: 'justify-center',
            stretch: 'children:flex-1',
            between: 'justify-between',
        },
    },
    matches: [
        {
            direction: 'column',
            reverse: false,
            use: 'flex-col',
        },
        {
            direction: 'row',
            reverse: false,
            use: 'flex-row',
        },
        {
            direction: 'inline',
            reverse: false,
            use: 'flex-row',
        },
        {
            direction: 'column',
            reverse: true,
            use: 'flex-col-reverse',
        },
        {
            direction: 'row',
            reverse: true,
            use: 'flex-row-reverse ',
        },
        {
            direction: 'inline',
            reverse: true,
            use: 'flex-row-reverse ',
        },
    ],
});
/**
 * The Stack is a layout component wrapped around css flexbox. It is used as a way to
 * declaratively define a flex layout.
 *
 * @example
 * ```
 * <Stack
 *   direction="row"
 *   gap={{_: 'base', sm: 'xl'}}
 * >
 *  ...
 * </Stack>
 * ```
 *
 * Can be configured responsively
 *
 *  @example
 * ```
 * <Stack
 *   direction={{_: 'column', sm: 'row'}}
 *   gap={{_: 'base', sm: 'xl'}}
 * >
 *  ...
 * </Stack>
 * ```
 *
 * - By default renders a div. To render as a custom element, use the `asChild` prop
 *
 * @example
 * ```
 * <Stack
 *   asChild
 *   gap="xl"
 * >
 *  <section>...</section>
 * </Stack>
 * ```
 */
const Stack = React.forwardRef((props, forwardedRef) => {
    const { asChild, dataTestId, css: whatIsThis, // TODO find out why this is here
    gap = 'base', gapX, gapY, direction = 'column', reverse = false, items, justify, className, children, ...rest } = props;
    const Comp = asChild ? Slot : 'div';
    return (React.createElement(Comp, { ref: forwardedRef, className: tw$1(gapStyle({ gap, gapX, gapY }), stackStyle({
            items,
            justify,
            direction,
            reverse,
        }), className), "data-uds-child": "stack", "data-test-id": dataTestId, ...rest }, children));
});
Stack.displayName = 'Stack';

/**
 * A component that renders a set of dot buttons, that represent the number of pages in the carousel, and scroll to the corresponding page when clicked.
 *
 * You can render this component, withiin the CarouselRoot, and position it however you like.
 *
 * Alternatively, you can use the useCarouselSnaps hook, which provides all the information you need to render your own custom pagination component.
 *
 * @example
 * ```tsx
 * <CarouselRoot>
 *   <CarouselDots invert/>
 *  <CarouselViewport>
 *    <CarouselSlide/>
 *    <CarouselSlide/>
 *   </CarouselViewport>
 * </CarouselRoot>
 * ```
 *
 * @example Responsive invert
 * ```tsx
 * <CarouselRoot invert={{_:false, md:true}}/>
 * ```
 */
const CarouselDots = ({ invert, className }) => {
    const { dataTestId } = useCarouselContext();
    const ref = useRef(null);
    const { scrollSnapList, selectedIndex } = useCarouselSnaps();
    const { scrollTo } = useCarouselNav();
    useEffect(() => {
        if (!ref.current.contains(document.activeElement))
            return;
        const nodelist = ref.current?.querySelectorAll('button');
        const targets = Array.prototype.slice.call(nodelist);
        targets.forEach((target, index) => {
            if (index === selectedIndex) {
                target.focus();
                target.tabIndex = 0;
                target.setAttribute('ariaHidden', 'true');
                target.setAttribute('ariaSelected', 'true');
            }
            else {
                target.tabIndex = -1;
                target.setAttribute('ariaHidden', 'false');
                target.setAttribute('ariaSelected', 'false');
            }
        });
    }, [selectedIndex]);
    const selectedDotClassName = useResponsiveProp(invert, value => {
        return value ? 'bg-neutral-0' : 'bg-neutral-100';
    });
    const dotClassName = useResponsiveProp(invert, value => {
        return value
            ? 'bg-neutral-60 group-hover:bg-neutral-40'
            : 'bg-neutral-40 group-hover:bg-neutral-60';
    });
    return (React.createElement(Stack, { ref: ref, direction: "row", justify: "center", gap: "xs", className: className, "data-uds-child": "carousel-dots", dataTestId: dataTestId ? `${dataTestId}-dots` : undefined, asChild: true },
        React.createElement("div", { role: "tablist" }, scrollSnapList.map((_, index) => (React.createElement("button", { key: index, role: "tab", "data-uds-child": "carousel-dot-button", onClick: () => scrollTo(index), tabIndex: selectedIndex === index ? 0 : -1, "aria-selected": selectedIndex === index ? 'true' : 'false', className: tw$2('group flex-none relative px-0.5 py-0.5 rounded-full', 'focus:outline-none focus-visible:(ring-2 ring-offset-0)', css `
                &:after {
                  position: absolute;
                  top: -6px;
                  left: -4px;
                  right: -4px;
                  bottom: -6px;
                  content: '';
                  display: block;
                  opacity: 1;
                  background-color: rgba(255, 25, 255, 0);
                }
              `) },
            React.createElement("div", { className: tw$2('flex-none h-2.5 w-2.5 rounded-full', 'transition-bg duration-400 ease-in', selectedIndex === index ? selectedDotClassName : dotClassName) }),
            React.createElement("span", { className: "sr-only" }, `Page ${index + 1}`)))))));
};

/**
 * A component that renders a list of thumb buttons, that represent the number of pages in the carousel, and scroll to the corresponding page when clicked.
 *
 * Provide as may items as the carousel slides, and they will be rendered as the thumbs.
 *
 * @example
 *
 * ```tsx
 * <CarouselRoot>
 *
 *  <CarouselThumbList>
 *    <CarouselThumb>
 *      <img />
 *    </CarouselThumb>
 *    <CarouselThumb>
 *      <img />
 *    </CarouselThumb>
 *    <CarouselThumb>
 *      <img />
 *    </CarouselThumb>
 *  </CarouselThumbList>
 *
 *  <CarouselViewport>
 *    <CarouselSlide/>
 *    <CarouselSlide/>
 *    <CarouselSlide/>
 *   </CarouselViewport>
 *
 * </CarouselRoot>
 * ```
 */
const CarouselThumbList = ({ children, size = 'base', scroll: scrollThumbs, invert, className, }) => {
    const t = useUdsTranslate();
    const trackRef = useRef(null);
    const { dataTestId, slidesToScroll, columns, limit, api, loop } = useCarouselContext();
    const { slidesInView, selectedIndex } = useCarouselSnaps();
    const { scrollTo } = useCarouselNav();
    const sizeValue = useResponsiveValue(size);
    const columnValue = useResponsiveValue(columns);
    const limitValue = useResponsiveValue(limit);
    const scrollThumbsValue = useResponsiveValue(scrollThumbs);
    const visibleThumbs = Math.max(slidesToScroll, scrollThumbsValue);
    const viewportWidth = visibleThumbs * THUMB_SIZE_RAW[sizeValue] +
        (visibleThumbs - 1) * THUMBS_GAP_RAW[sizeValue];
    const thumbsCarouselEnabled = isNumber(scrollThumbsValue) &&
        scrollThumbsValue &&
        scrollThumbsValue < React.Children.count(children);
    const [thumbsViewportRef, thumbApi] = useEmblaCarousel({
        active: thumbsCarouselEnabled,
        containScroll: 'keepSnaps',
        dragFree: true,
        slidesToScroll: slidesToScroll,
        align: 'center',
        loop: loop,
    }, []);
    const onSelect = useCallback(() => {
        /* istanbul ignore next */
        if (!api || !thumbApi)
            return;
        thumbApi.scrollTo(api.selectedScrollSnap());
    }, [api, thumbApi]);
    const gapClassName = useResponsiveProp(size, value => {
        return THUMBS_GAP[value];
    });
    const scrollThumbsNext = useCallback(() => {
        thumbApi.scrollNext();
    }, [thumbApi]);
    const scrollThumbsPrev = useCallback(() => {
        thumbApi.scrollPrev();
    }, [thumbApi]);
    useEffect(() => {
        if (!trackRef.current.contains(document.activeElement))
            return;
        const targetIndex = selectedIndex * columnValue;
        const nodelist = trackRef.current.querySelectorAll('[data-uds-child="carousel-thumb"]');
        const targets = Array.prototype.slice.call(nodelist);
        targets[targetIndex].focus();
    }, [selectedIndex]);
    const syncVisibleThumbs = (slidesInView) => {
        const nodelist = trackRef.current.querySelectorAll('[data-uds-child="carousel-thumb"]');
        const targets = Array.prototype.slice.call(nodelist);
        targets.forEach((target, index) => {
            const isInView = slidesInView.includes(index);
            target.tabIndex = isInView ? 0 : -1;
            target.setAttribute('ariaSelected', `${isInView}`);
        });
    };
    useCarouselProgress(({ slidesInView }) => {
        syncVisibleThumbs(slidesInView);
    });
    useEffect(() => {
        /* istanbul ignore next */
        if (!api || !thumbApi)
            return;
        onSelect();
        api.on('init', onSelect);
        api.on('reInit', onSelect);
        api.on('select', onSelect);
        return () => {
            /* istanbul ignore next */
            if (!api)
                return;
            api.off('init', onSelect);
            api.off('reInit', onSelect);
            api.off('select', onSelect);
        };
    }, [api, thumbApi, onSelect]);
    const thumbsLength = limitValue ?? React.Children.count(children);
    const arrowsSize = size === 'xs' || size === 'sm' ? 'xs' : 'sm';
    return (React.createElement("div", { className: tw$2(className, 'flex gap-3'), "data-uds-child": "carousel-thumbs" },
        thumbsCarouselEnabled ? (React.createElement(ButtonIcon, { icon: "chevron-left", label: t('scrollPrevious'), size: arrowsSize, variant: "ghost", mode: "icon", invert: invert, onClick: scrollThumbsPrev, "data-uds-child": "carousel-thumbs-chevron-prev" })) : null,
        React.createElement("div", { ref: thumbsViewportRef, className: tw$2('py-0.5 px-0.5', thumbsCarouselEnabled &&
                `flex-none overflow-hidden w-[${viewportWidth}px]`) },
            React.createElement("div", { role: "tablist", "data-uds-child": "carousel-thumbs-track", ref: trackRef, "data-test-id": dataTestId ? `${dataTestId}-thumbs` : undefined, className: tw$2('grid grid-flow-col', gapClassName, css `
              grid-template-columns: repeat(${thumbsLength}, max-content);
            `, className) }, React.Children.map(children, (child, index) => !limitValue || index < limitValue ? (React.createElement("div", { role: "none" }, React.cloneElement(child, {
                role: 'tab',
                size: size,
                invert: invert,
                'data-uds-child': 'carousel-thumb',
                'aria-label': `Slide ${index + 1}`,
                active: slidesInView.includes(index),
                onClick: () => {
                    scrollTo(Math.floor(index / columnValue));
                },
            }))) : null))),
        thumbsCarouselEnabled ? (React.createElement(ButtonIcon, { icon: "chevron-right", label: t('scrollNext'), size: arrowsSize, variant: "ghost", mode: "icon", invert: invert, onClick: scrollThumbsNext, "data-uds-child": "carousel-thumbs-chevron-next" })) : null));
};

/**
 * The AspectRatio component is used to wrap content in a fixed aspect ratio.
 *
 * Use the `overflow` property, to set the layout to a minimum-aspect-ratio, that grows depending on the length of its contents.
 *
 * @example
 * ```jsx
 * <AspectRatio ratio={16/9}/>
 * ```
 *
 */
const fixedRatios = {
    landscape: 0.42,
    wide: 0.66,
    square: 1,
    tall: 1.3333,
};
const AspectRatio = React.forwardRef((props, forwadedRef) => {
    const { asChild, ratio = 1, overflow, className, dataTestId, ...rest } = props;
    const Comp = asChild ? Slot : 'div';
    const callback = (value) => {
        const ratio = fixedRatios[value] ?? value;
        return overflow
            ? `min-aspect-ratio-[${ratio}]`
            : ['overflow-hidden', `aspect-ratio-[${ratio}]`];
    };
    const ratioClassName = useResponsiveProp(ratio, callback);
    return (React.createElement(Comp, { ref: forwadedRef, "data-test-id": dataTestId, "data-uds-child": "aspect-ratio", className: tw$2(ratioClassName, className), ...rest }));
});
AspectRatio.displayName = 'AspectRatio';

/**
 *
 * A single thumbnail button for the CarouselThumbViewport component.
 *
 * @example
 *
 * ```tsx
 * <CarouselRoot>
 *   <CarouselViewport>
 *   </CarouselViewport>
 *   <CarouselThumbList>
 *    <CarouselThumb size="sm" ratio="wide"><img/></CarouselThumb>
 *  </CarouselThumbList ratio={1}>
 * </CarouselRoot>
 * ```
 */
const CarouselThumb = ({ size = 'base', ratio = 1, grayscale = true, invert, children, active, ...rest }) => {
    const buttonSize = useResponsiveProp(size, value => `w-${THUMB_SIZE$2[value]} h-${THUMB_SIZE$2[value]}`);
    const overlayBgColor = invert ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.4)';
    return (React.createElement("button", { ...rest, tabIndex: active ? 0 : -1, "data-uds-child": "carousel-thumb", "aria-hidden": active ? 'false' : 'true', "aria-selected": active ? 'true' : 'false', className: tw$2(buttonSize, 'relative block', 'flex-none', 'focus:outline-none focus-visible:(ring-offset-1 ring-2)', 'transition-all duration-300', 'border-1 p-px', invert
            ? 'bg-neutral-90 border-neutral-60'
            : 'bg-neutral-10 border-neutral-40', css `
          &[aria-selected='true'] {
            @apply ${invert ? 'border-neutral-0' : 'border-neutral-100'};
          }
          &:hover,
          &:focus-visible {
            @apply ${invert ? 'border-neutral-30' : 'border-neutral-60'};
          }
        `, grayscale &&
            css `
            filter: grayscale(100%);
            &[aria-selected='true'] {
              filter: none;
            }

            &:hover,
            &:focus-visible {
              filter: none;
            }
          `, css `
          &:after {
            position: absolute;
            top: 1px;
            left: 1px;
            right: 1px;
            bottom: 1px;
            content: '';
            display: block;
            opacity: 1;
            background-color: ${overlayBgColor};
          }
          &[aria-selected='true'] {
            &:after {
              opacity: 0;
            }
          }
          &:hover,
          &:focus-visible {
            &:after {
              opacity: 0;
            }
          }
        `) },
        React.createElement(AspectRatio, { ratio: ratio },
            React.createElement("div", { className: tw$2('absolute inset-0 w-full h-full', css `
              & img,
              & video {
                object-fit: cover;
                object-position: center;
                position: absolute;
                inset: 0;
                width: 100%;
                height: 100%;
              }
            `) }, children))));
};

const CollapsibleContext = createContext(null);
const useCollapsibleContext = () => {
    return useContext(CollapsibleContext);
};

const COLLAPSIBLE_VARIANTS = {
    invert: {
        false: 'text-neutral-100 border-neutral-20 bg-neutral-10',
        true: 'text-neutral-0 border-neutral-80 bg-neutral-90',
    },
    fill: {
        false: 'bg-opacity-0',
        true: 'bg-opacity-100',
    },
    outline: {
        false: 'border-0',
        true: 'border-1',
    },
};
const collapsibleRootStyle = style({
    variants: COLLAPSIBLE_VARIANTS,
});
const CollapsibleRoot = ({ fill = false, outline = false, invert = false, transition = 'reveal', speed = 'base', open, defaultOpen, onOpenChange, id: propsId, className, dataTestId, ...rest }) => {
    const id = useId(propsId);
    const [internalOpen, setInternalOpen] = React.useState(defaultOpen);
    const handleOpenChange = (open) => {
        setInternalOpen(open);
        onOpenChange?.(open);
    };
    return (React.createElement(CollapsibleContext.Provider, { value: {
            id,
            invert,
            transition,
            speed,
            dataTestId,
            open: internalOpen,
        } },
        React.createElement(RadixCollapsible.Root, { ...rest, id: id, "data-test-id": dataTestId, "data-uds-child": "collapsible", open: open, defaultOpen: defaultOpen, onOpenChange: handleOpenChange, className: tw('w-full leading-[1]', collapsibleRootStyle({ fill, outline, invert }), className) })));
};

const CollapsibleTriggerStyle = style({
    variants: {
        hover: {
            bg: '',
            underline: '',
            none: '',
        },
        invert: {
            false: '',
            true: '',
        },
    },
    matches: [
        {
            hover: 'bg',
            invert: 'true',
            use: 'hover:bg-neutral-80 focus-visible:bg-neutral-80',
        },
        {
            hover: 'bg',
            invert: 'false',
            use: 'hover:bg-neutral-20 focus-visible:bg-neutral-20',
        },
        {
            hover: 'underline',
            use: 'focus:outline-none hover:(decoration-underline decoration-1) focus-visible:(decoration-underline decoration-1)',
        },
    ],
});
const CollapsibleTrigger = React.forwardRef(({ className, hover = 'bg', inset = 'none', insetX, insetY, insetTop, insetEnd, insetBottom, insetStart, asChild, ...rest }, ref) => {
    const { invert, dataTestId } = useCollapsibleContext();
    const appliedHover = asChild ? 'none' : hover;
    const wrapProps = asChild
        ? {}
        : {
            asChild: true,
            inset: inset,
            insetX: insetX,
            insetY: insetY,
            insetTop: insetTop,
            insetEnd: insetEnd,
            insetBottom: insetBottom,
            insetStart: insetStart,
        };
    const Wrap = asChild ? React.Fragment : Inset;
    return (React.createElement(Wrap, { ...wrapProps },
        React.createElement(RadixCollapsible.Trigger, { ...rest, ref: ref, asChild: asChild, "data-uds-child": "collapsible-trigger", "data-test-id": dataTestId ? `${dataTestId}-trigger` : null, "data-invert": invert, className: tw$1(CollapsibleTriggerStyle({ hover: appliedHover, invert }), !asChild && 'w-full text-left rtl:text-right', !asChild && 'transition-bg duration-[800ms] ease-out', !asChild &&
                'hover:(transition-bg duration-300 ease-out) focus:(transition-bg duration-300 ease-out)', className) })));
});
CollapsibleTrigger.displayName = 'CollapsibleTrigger';

const CollapsibleContent = (props) => {
    const { inset = 'none', insetX, insetY, insetTop, insetEnd, insetBottom, insetStart, className, children, ...rest } = props;
    const { transition, speed, dataTestId } = useCollapsibleContext();
    const animationCss = useMemo(() => {
        const animation = getCollapsibleTransition({
            type: transition,
            speed: speed,
            heightVar: '--radix-collapsible-content-height',
        });
        return css$1 `
      &[data-state='open'] {
        ${animation.in}
      }

      &[data-state='closed'] {
        ${animation.out}
      }

      @media (prefers-reduced-motion) {
        &[data-state='open'] {
          animation: none;
        }

        &[data-state='closed'] {
          animation: none;
        }
      }
    `;
    }, [transition, speed]);
    return (React.createElement(RadixCollapsible.Content, { ...rest, "data-uds-child": "collapsible-content", "data-test-id": dataTestId ? `${dataTestId}-content` : null, className: tw$2(className, 'overflow-hidden', animationCss) },
        React.createElement(Inset, { inset: inset, insetX: insetX, insetY: insetY, insetTop: insetTop, insetEnd: insetEnd, insetBottom: insetBottom, insetStart: insetStart }, children)));
};

const CollapsibleChevron = (props) => {
    const { open } = useCollapsibleContext();
    return (React.createElement(ToggleChevron, { ...props, open: open, "data-uds-child": "collapsible-chevron" }));
};

/**
 * Render and control a list of panels, and display one panel of content at a time
 *
 * @example
 * ```tsx
 * <TabsRoot defaultValue="1" activationMode={"automatic" | "manual"}>
 *  <TabsList bleed>
 *    <TabsTrigger size="lg" value="1">One</TabsTrigger>
 *    <TabsTrigger size="lg" value="2">Two</TabsTrigger>
 *   </TabsList>
 *   <TabsContent value="1">Content 1</TabsContent>
 *   <TabsContent value="2">Content 2</TabsContent>
 * </TabsRoot>
 * ```
 */
const TabsRoot = (props) => {
    const { dataTestId, activationMode = 'manual', children, ...rest } = props;
    return (React.createElement(RadixTabs.Root, { "data-uds-child": "tabs", "data-test-id": dataTestId, activationMode: activationMode, ...rest }, children));
};
/**
 * Render and control a list of panels, and display one panel of content at a time
 *
 * @example
 * ```tsx
 * <TabsRoot defaultValue="1">
 *  <TabsList>
 *    <TabsTrigger value="1">One</TabsTrigger>
 *    <TabsTrigger value="2">Two</TabsTrigger>
 *   </TabsList>
 *   <TabsContent value="1">Content 1</TabsContent>
 *   <TabsContent value="2">Content 2</TabsContent>
 * </TabsRoot>
 * ```
 */
const TabsList = (props) => {
    const { bleed, className, ...rest } = props;
    return (React.createElement(RadixTabs.List, { ...rest, "data-uds-child": "tabs-list", className: tw$2(bleed
            ? 'w-full flex children:(flex flex-1)'
            : 'w-auto flex children:(flex flex-none max-w-content)', className) }));
};
const SizeVariants = {
    sm: 'text-sm min-h-8 px-3',
    base: 'text-base min-h-10 px-4',
    lg: 'text-lg min-h-14 px-6',
};
/**
 * Render a button that controls a tap panel. The value prop is required
 *
 * @example
 * ```tsx
 * <TabsRoot defaultValue="1">
 *  <TabsList>
 *    <TabsTrigger value="1">One</TabsTrigger>
 *    <TabsTrigger value="2">Two</TabsTrigger>
 *   </TabsList>
 *   <TabsContent value="1">Content 1</TabsContent>
 *   <TabsContent value="2">Content 2</TabsContent>
 * </TabsRoot>
 * ```
 */
const TabsTrigger = (props) => {
    const { children, size = 'base', invert, className, ...rest } = props;
    const sizeClassName = useResponsiveProp(size, value => SizeVariants[value]);
    return (React.createElement(RadixTabs.TabsTrigger, { ...rest, "data-uds-child": "tabs-trigger", className: tw$2('relative whitespace-nowrap rounded-sm', 'flex items-center justify-center', 'focus:outline-none', 'focus-visible:(ring-2)', invert ? 'text-neutral-0' : 'text-neutral-100', sizeClassName, css `
          & > [data-uds-child='tabs-trigger-outline'] {
            display: none;
          }
          &:focus-visible > [data-uds-child='tabs-trigger-outline'] {
            display: block;
          }
        `, invert
            ? css `
              & > [data-uds-child='tabs-trigger-underline'] {
                @apply bg-neutral-70;
              }
              &[data-state='active'] {
                @apply font-bold;
                & > [data-uds-child='tabs-trigger-underline'] {
                  @apply bg-neutral-0;
                }
              }
            `
            : css `
              & > [data-uds-child='tabs-trigger-underline'] {
                @apply bg-neutral-30;
              }
              &[data-state='active'] {
                @apply font-bold;
                & > [data-uds-child='tabs-trigger-underline'] {
                  @apply bg-neutral-100;
                }
              }
            `, className) },
        children,
        React.createElement("div", { className: "block w-full h-0.5 absolute left-0 bottom-0", "data-uds-child": "tabs-trigger-underline" }),
        React.createElement("div", { className: tw$2('border-1 block absolute inset-0 rounded-sm', invert ? 'border-neutral-0' : 'border-neutral-100'), "data-uds-child": "tabs-trigger-outline" })));
};
/**
 * Render content panels that are controlled by TabsTrigger
 *
 * @example
 * ```tsx
 * <TabsRoot defaultValue="1">
 *  <TabsList>
 *    <TabsTrigger value="1">One</TabsTrigger>
 *    <TabsTrigger value="2">Two</TabsTrigger>
 *   </TabsList>
 *   <TabsContent value="1">Content 1</TabsContent>
 *   <TabsContent value="2">Content 2</TabsContent>
 * </TabsRoot>
 * ```
 */
const TabsContent = (props) => {
    const { className, ...rest } = props;
    return (React.createElement(RadixTabs.Content, { ...rest, "data-uds-child": "tabs-content", className: tw$2(className, css `
          &:focus-visible {
            outline-offset: 6px;
          }
        `) }));
};

/**
 * Shared input label component. Renders a `label` tag and is reused on all inputs.
 */
const InputLabel = ({ htmlFor, children, invert, required, optional, className, ...rest }) => {
    const t = useUdsTranslate();
    const style = tw$2('flex font-bold text-xs uppercase', invert ? 'text-white' : 'text-neutral-100');
    return (React.createElement("label", { className: tw$2(className, style), htmlFor: htmlFor, ...rest },
        children,
        required ? (React.createElement("div", { "data-uds-child": "asterisk-required", "aria-hidden": true, className: "text-error ml-1 rtl:mr-1" },
            ' ',
            "*")) : null,
        !required && optional ? (React.createElement("div", { "aria-hidden": true, "data-uds-child": "label-optional", className: tw$2('ml-1 rtl:mr-1 font-normal normal-case', invert ? 'text-neutral-40' : 'text-neutral-60') },
            ' ',
            "(",
            t('optional'),
            ")")) : null));
};

const ErrorText = ({ children, invert, ...rest }) => {
    const className = tw$2('text-sm', invert ? 'text-error-40' : 'text-error-60');
    return (React.createElement("p", { className: className, ...rest }, children));
};
const SupportTextContainer = ({ id, dataTestId, children, }) => (React.createElement("div", { id: id, "data-test-id": dataTestId }, children));
const SupportText = ({ invert, children, ...rest }) => {
    const style = tw$2('text-sm', invert ? 'text-white' : 'text-neutral-100');
    return (React.createElement("p", { className: style, ...rest }, children));
};
const InputWrapper = ({ dataTestIdPrefix, childId, supportText, label, srOnlyLabel, srOnlyDescription, errorText, showLabel, minMaxText, invert, required, optional, children, className, ...rest }) => {
    const labelClass = showLabel === false ? 'sr-only' : '';
    return (React.createElement("div", { "data-test-id": dataTestIdPrefix ? `${dataTestIdPrefix}-wrapper` : null, ...rest, className: tw$2('w-full', className) },
        React.createElement("div", { className: "flex justify-between" }, label && (React.createElement(InputLabel, { id: `${childId}-label`, htmlFor: childId, className: labelClass, invert: invert, required: required, optional: optional, "data-test-id": dataTestIdPrefix ? `${dataTestIdPrefix}-label` : null },
            React.createElement("span", { "data-label": "visible", "aria-hidden": srOnlyLabel ? 'true' : 'false' }, label),
            srOnlyLabel && (React.createElement("span", { "data-uds-child": "sr-only-label", className: "sr-only" },
                ' ',
                srOnlyLabel))))),
        children,
        React.createElement(LiveRegion, { id: errorText?.id, type: "polite" }, errorText?.text ? (React.createElement(ErrorText, { "data-uds-child": "error-text", "data-test-id": dataTestIdPrefix ? `${dataTestIdPrefix}-error` : undefined, invert: invert }, errorText?.text)) : null),
        supportText && !errorText?.text ? (React.createElement(SupportTextContainer, { id: supportText.id },
            React.createElement(SupportText, { "data-uds-child": "support-text", "data-test-id": dataTestIdPrefix ? `${dataTestIdPrefix}-support` : undefined, invert: invert }, supportText.text))) : null,
        srOnlyDescription ? (React.createElement("p", { className: "sr-only", "data-uds-child": "sr-only-description", id: `${childId}_sr-description`, "data-test-id": dataTestIdPrefix ? `${dataTestIdPrefix}-sr-description` : undefined }, srOnlyDescription)) : null,
        minMaxText?.id ? (React.createElement(LiveRegion, { id: minMaxText.id, type: "polite" },
            React.createElement("p", { className: "sr-only", "data-test-id": dataTestIdPrefix ? `${dataTestIdPrefix}-minmax` : undefined }, minMaxText.text))) : null));
};

const useInputDivStyle = ({ error, disabled, invert, hasSlot, descriptionText, showLabel, }) => {
    const wrapperStyle = tw$2('w-full inline-flex items-center border-1 text-base rounded-sm relative', disabled && 'cursor-not-allowed', error && 'border-error border-2', error && invert && 'border-error-40', disabled
        ? invert
            ? 'text-neutral-30 bg-neutral-80 border-neutral-80 hover:border-neutral-80'
            : 'text-neutral-70 border-neutral-20 bg-neutral-20 hover:border-neutral-20'
        : invert
            ? 'text-white bg-neutral-100 border-neutral-50 focus-within:(border-white ring) hover:(border-white)'
            : 'text-neutral-100 bg-white border-neutral-80 focus-within:(border-neutral-100 ring) hover:(border-neutral-100)', descriptionText && 'mb-2', showLabel && 'mt-2');
    const inputStyle = tw$2('w-full h-14 bg-transparent px-3 relative focus:outline-none', hasSlot && 'pr-12 rtl:pl-12', disabled && 'cursor-not-allowed', css$1({
        '&::placeholder': {
            '@apply': 'text-neutral-60',
        },
        '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus': {
            '-webkit-text-fill-color': `${invert ? 'white' : 'black'} `,
            '-webkit-box-shadow': '0 0 0px 1000px transparent inset',
            transition: 'background-color 5000s ease-in-out 0s',
        },
    }));
    const prefixSuffixStyles = tw$2('px-3 h-full flex items-center', disabled
        ? invert
            ? 'bg-neutral-80 border-neutral-70'
            : 'bg-neutral-20 border-neutral-30'
        : invert
            ? 'text-neutral-50 bg-neutral-100 border-neutral-50'
            : 'text-neutral-60 bg-white border-neutral-60');
    return { wrapperStyle, inputStyle, prefixSuffixStyles };
};

const useMinMaxLength = ({ minLength, maxLength, value, }) => {
    const [minMaxText, setMinMaxText] = useState();
    const [length, setLength] = useState();
    const t = useUdsTranslate();
    useEffect(() => {
        setLength(String(value)?.length);
    }, [value]);
    useEffect(() => {
        const generateSrOnlyText = () => {
            if (!length) {
                return;
            }
            if (maxLength !== undefined || minLength !== undefined) {
                if (minLength !== undefined && minLength - length > 0) {
                    return `${t('genericFieldMinLength', {
                        length: minLength,
                    })}`;
                }
                else if (maxLength !== undefined && maxLength - length >= 0) {
                    return `${t('charactersRemaining', {
                        num: maxLength - length,
                    })}`;
                }
            }
        };
        if (minLength || maxLength) {
            const text = generateSrOnlyText();
            setMinMaxText(text);
        }
    }, [length, minLength, maxLength]);
    return minMaxText;
};

const ErrorIcon = ({ disabled, invert, size = '2xl', ...props }) => {
    const iconStyle = tw$2('flex-shrink-0', invert ? 'text-error-40' : 'text-error-60', disabled && 'text-neutral-60', invert && disabled && 'text-neutral-50');
    return (React.createElement(Icon, { name: "exclamation-filled", className: iconStyle, size: size, ...props }));
};

const inputIconDivStyle = style({
    base: `absolute right-1.5 rtl:right-auto rtl:left-1.5 gap-2 grid grid-flow-col auto-cols-fr justify-items-center items-center pr-2 rtl:(pl-2 pr-0)`,
    variants: {
        right: {
            sm: 'right-1 rtl:right-auto rtl:left-1',
            base: 'right-1.5 rtl:right-auto rtl:left-1.5',
            lg: 'right-2 rtl:right-auto rtl:left-2',
        },
        position: {
            top: 'top-3',
        },
    },
});
const inputSlotReturnsValue = (inputSlot) => {
    if (!inputSlot) {
        return false;
    }
    return Boolean(inputSlot({}));
};
const useInputIconSlots = ({ invert, disabled, inputSlot, error, right = 'base', position, className, }) => {
    const shouldRenderInputSlot = inputSlotReturnsValue(inputSlot);
    const iconSpace = shouldRenderInputSlot && error
        ? 'w-16'
        : shouldRenderInputSlot
            ? 'w-9'
            : error
                ? 'w-9'
                : 'w-0';
    const divStyle = tw$2(inputIconDivStyle({ right, position }), iconSpace, className);
    const slots = !error && !shouldRenderInputSlot ? null : (React.createElement("div", { className: divStyle },
        error && React.createElement(ErrorIcon, { invert: invert, disabled: disabled }),
        shouldRenderInputSlot && inputSlot ? (React.createElement("div", { "data-uds-child": "input-slot" }, inputSlot({ invert, disabled }))) : null));
    return {
        inputIconsSlot: slots,
        shouldRenderInputSlot: shouldRenderInputSlot,
    };
};

const Fieldset = React.forwardRef(function Fieldset(props, forwardRef) {
    const { id, invert, children, errorText, supportText, legend, className, showLegend = true, required, optional, dataTestId, ...rest } = props;
    const t = useUdsTranslate();
    const style = tw$2('flex font-bold text-xs uppercase mb-2', invert ? 'text-white' : 'text-neutral-100', !showLegend && 'sr-only');
    return (React.createElement("fieldset", { "data-test-id": dataTestId, ref: forwardRef, className: className, "aria-describedby": `${id}_error ${id}_support`, ...rest },
        React.createElement("legend", { className: style, id: `${id}_legend` },
            legend,
            required && (React.createElement("div", { "aria-hidden": true, className: "text-error ml-1 rtl:mr-1" }, "*")),
            !required && optional ? (React.createElement("div", { "aria-hidden": true, className: tw$2('ml-1 rtl:mr-1 font-normal normal-case', invert ? 'text-neutral-40' : 'text-neutral-60') },
                "(",
                t('optional'),
                ")")) : null),
        React.createElement(Stack, { gap: "xs" },
            children,
            supportText && !errorText ? (React.createElement(SupportTextContainer, { id: `${id}_support` },
                React.createElement(SupportText, { invert: invert, "data-test-id": dataTestId ? `${dataTestId}-support` : undefined }, supportText))) : null,
            React.createElement(LiveRegion, { id: `${id}_error`, type: "polite", atomic: true }, errorText && (React.createElement(ErrorText, { invert: invert, "data-test-id": dataTestId ? `${dataTestId}-error` : undefined }, errorText))))));
});

/**
 * Renders and `input` element, along with it's associated label, and support/error messages
 *
 * - Receives all the standard HTML `input` props
 *
 * @example
 * ```
 * <InputText
 *   required
 *   label="First Name"
 *   errorText="Field is required"
 * />
 * ```
 */
const InputText = React.forwardRef(function InputText(props, forwardRef) {
    const { id: propsId, dataTestId, name, disabled, children, error, errorText, showErrorIcon = true, supportText, className, label, srOnlyLabel, srOnlyDescription, prefix, suffix, invert, required, optional, type, autoComplete, minLength, maxLength, value, placeholder, onChange, inputSlot, defaultValue, showLabel = true, ...rest } = props;
    const id = useId(propsId);
    const shouldRenderErrorIcon = Boolean(showErrorIcon && (error || errorText));
    const inputRef = useRef(null);
    const ref = useSharedRef(forwardRef, inputRef);
    const slotClassName = suffix && 'mr-11 rtl:(ml-11)';
    const { inputIconsSlot, shouldRenderInputSlot } = useInputIconSlots({
        error: shouldRenderErrorIcon,
        inputSlot,
        invert,
        disabled,
        className: slotClassName,
    });
    const [uncontrolledValue, setUncontrolledValue] = useState(null);
    const descriptionIds = {
        error: `${id}_error`,
        support: `${id}_support`,
        minMax: `${id}_length`,
    };
    const minMaxText = useMinMaxLength({
        minLength,
        maxLength,
        value: value ?? uncontrolledValue,
    });
    const { wrapperStyle, inputStyle, prefixSuffixStyles } = useInputDivStyle({
        error: error || errorText ? true : false,
        disabled,
        invert,
        showLabel,
        hasSlot: shouldRenderInputSlot,
        descriptionText: Boolean(supportText || errorText),
    });
    const inputTextStyle = `pl-3 rtl:(pr-3)`;
    const inputWrapperProps = {
        childId: id,
        label,
        showLabel,
        srOnlyLabel,
        srOnlyDescription,
        supportText: {
            text: supportText,
            id: descriptionIds.support,
        },
        errorText: {
            text: errorText || undefined,
            id: descriptionIds.error,
        },
        minMaxText: {
            text: minMaxText,
            id: descriptionIds.minMax,
        },
        invert,
        required: required,
        optional: optional,
        dataTestIdPrefix: dataTestId,
        className,
    };
    const handleOnChange = (e) => {
        if (props.autoCapitalize && props.autoCapitalize !== 'off') {
            e.target.value = e.target.value.toUpperCase();
        }
        if (onChange) {
            onChange(e);
        }
        setUncontrolledValue(e.target.value);
    };
    const describedBySrOnly = srOnlyDescription ? `${id}_sr-description ` : '';
    return (React.createElement(React.Fragment, null,
        React.createElement(InputWrapper, { ...inputWrapperProps },
            React.createElement("div", { className: tw$1(wrapperStyle, 'h-14') },
                prefix && (React.createElement("div", { "data-uds-child": "prefix", "aria-hidden": "true", className: tw$1(prefixSuffixStyles, 'flex-none border-r-1 rtl:(border-r-0 border-l-1)') }, prefix)),
                React.createElement("input", { id: id, "data-test-id": dataTestId, ref: ref, type: type || 'text', name: name, disabled: disabled, required: required, className: tw$1(inputStyle, inputTextStyle), "aria-describedby": `${descriptionIds.error} ${descriptionIds.support} ${describedBySrOnly}${descriptionIds.minMax}`, "aria-invalid": error || errorText ? 'true' : 'false', autoComplete: autoComplete, minLength: minLength, maxLength: maxLength, placeholder: placeholder, onChange: handleOnChange, defaultValue: defaultValue, value: value, ...rest }),
                suffix && (React.createElement("div", { "aria-hidden": "true", "data-uds-child": "suffix", className: tw$1(prefixSuffixStyles, 'flex-none border-l-1 rtl:(border-l-0 border-r-1)') }, suffix)),
                inputIconsSlot))));
});

const InputPassword = React.forwardRef(function InputPassword(props, forwardRef) {
    const [showPassword, setShowPassword] = React.useState(false);
    const t = useUdsTranslate();
    return (React.createElement(InputText, { ref: forwardRef, ...props, type: showPassword ? 'text' : 'password', inputSlot: ({ invert, disabled }) => (React.createElement(ButtonCta, { "data-uds-child": "toggle-password", dataTestId: props.dataTestId ? `${props.dataTestId}-toggle-password` : undefined, label: showPassword ? t('hidePassword') : t('showPassword'), icon: showPassword ? 'eye-closed' : 'eye-open', mode: "icon", variant: "ghost", onClick: () => setShowPassword(!showPassword), role: "switch", "aria-pressed": showPassword, invert: invert, dimmed: disabled })) }));
});

/**
 * Renders and `input` element, along with it's associated label, and support/error messages
 *
 * - Receives all the standard HTML `input` props
 *
 * @example
 * ```
 * <InputTextArea
 *   required
 *   size="lg"
 *   label="First Name"
 *   errorText="Field is required"
 * />
 * ```
 */
const InputTextArea = React.forwardRef(function InputText(props, forwardRef) {
    const { id: propsId, dataTestId, name, disabled, children, error, errorText, showErrorIcon = true, supportText, className, label, srOnlyLabel, srOnlyDescription, showLabel = true, invert, required, optional, autoComplete, minLength, maxLength, value, placeholder, onChange, size = 'base', inputSlot, ...rest } = props;
    const id = useId(propsId);
    const shouldRenderErrorIcon = Boolean(showErrorIcon && (error || errorText));
    const inputRef = useRef(null);
    const ref = useSharedRef(forwardRef, inputRef);
    const { inputIconsSlot, shouldRenderInputSlot } = useInputIconSlots({
        error: shouldRenderErrorIcon,
        inputSlot,
        invert,
        disabled,
        position: 'top',
    });
    const [uncontrolledValue, setUncontrolledValue] = useState();
    const descriptionIds = {
        error: `${id}_error`,
        support: `${id}_support`,
        minMax: `${id}_length`,
    };
    const minMaxText = useMinMaxLength({
        minLength,
        maxLength,
        value: value ?? uncontrolledValue,
    });
    const { wrapperStyle, inputStyle } = useInputDivStyle({
        error: error || errorText ? true : false,
        disabled,
        invert,
        showLabel,
        descriptionText: Boolean(supportText || errorText),
    });
    const inputTextAreaStyle = tw$1('h-auto resize-none py-3', shouldRenderInputSlot && 'pr-8 rtl:(pl-8 pr-0)');
    const rowSize = size === 'sm' ? 2 : size === 'base' ? 4 : 6;
    const inputWrapperProps = {
        childId: id,
        label,
        srOnlyLabel,
        srOnlyDescription,
        showLabel,
        required: required,
        optional: optional,
        supportText: {
            text: supportText,
            id: descriptionIds.support,
        },
        errorText: {
            text: errorText || undefined,
            id: descriptionIds.error,
        },
        minMaxText: {
            text: minMaxText,
            id: descriptionIds.minMax,
        },
        invert,
        dataTestIdPrefix: dataTestId,
        className,
    };
    const describedBySrOnly = srOnlyDescription ? `${id}_sr-description ` : '';
    const handleOnChange = (e) => {
        if (onChange) {
            onChange(e);
        }
        setUncontrolledValue(e.target.value);
    };
    return (React.createElement(InputWrapper, { ...inputWrapperProps },
        React.createElement("div", { className: tw$1(wrapperStyle) },
            React.createElement("textarea", { id: id, name: name, ref: ref, disabled: disabled, "data-test-id": dataTestId, required: required, className: tw$1(inputStyle, inputTextAreaStyle), "aria-describedby": `${descriptionIds.error} ${descriptionIds.support} ${describedBySrOnly}${descriptionIds.minMax}`, "aria-invalid": error || errorText ? 'true' : 'false', autoComplete: autoComplete, minLength: minLength, maxLength: maxLength, placeholder: placeholder, value: value, rows: rowSize, onChange: handleOnChange, ...rest }),
            inputIconsSlot)));
});

/**
 * InputCheckbox renders a label, and within an input of `type=checkbox`.
 * The children of the component, are part of the label
 */
const InputCheckbox = React.forwardRef(function InputCheckbox(props, forwardRef) {
    const { id: propsId, disabled, children, invert, className, dataTestId, ...rest } = props;
    const id = useId(propsId);
    const inputRef = useRef(null);
    const ref = useSharedRef(forwardRef, inputRef);
    const styles = {
        checkboxWrapper: tw$1('relative flex group', disabled ? 'cursor-not-allowed' : 'cursor-pointer', className),
        checkButton: tw$1('relative block text-lg w-5 h-5 border-2 rounded-sm flex-none mt-0.5 mr-4 rtl:mr-0 rtl:ml-4', invert && !disabled && 'text-neutral-100', invert && disabled && 'text-neutral-50', !invert && disabled && 'text-neutral-60', !invert && !disabled && 'border-neutral-60 text-white', `group-hover:(${invert ? 'border-neutral-20' : 'border-neutral-80'})`),
        checkIndicator: tw$1('w-4 h-4 absolute left-1/2 top-1/2 -translate-y-1/2 -translate-x-1/2 w-full', css$1({
            display: 'none',
        })),
        input: tw$1('opacity-0 absolute left-0 top-0 w-full h-full', disabled ? 'cursor-not-allowed' : 'cursor-pointer', css$1({
            '&:focus + [data-uds-child="indicator"]': {
                '@apply': `ring-3 border-neutral-80 ${invert && 'ring-invert border-white'}`,
            },
            '&:checked + [data-uds-child="indicator"]': {
                '@apply': `${invert ? 'bg-white border-white' : 'bg-black border-neutral-100'}`,
                span: {
                    display: 'block',
                },
            },
            '&:disabled + [data-uds-child="indicator"]': {
                '@apply': `${invert
                    ? 'bg-neutral-80 border-neutral-50'
                    : 'bg-neutral-20 border-neutral-60'}`,
            },
        })),
        inputLabel: tw$1('text-base flex-1', disabled && `${invert ? 'text-neutral-50' : 'text-neutral-60'}`, invert && 'text-white'),
    };
    return (React.createElement("label", { htmlFor: id, className: styles.checkboxWrapper, "data-test-id": dataTestId },
        React.createElement("input", { className: styles.input, id: id, ref: ref, type: "checkbox", "data-uds-child": "checkbox", "data-test-id": dataTestId ? `${dataTestId}-checkbox` : undefined, disabled: disabled, ...rest }),
        React.createElement("div", { "data-uds-child": "indicator", "aria-hidden": true, className: styles.checkButton },
            React.createElement("span", { className: styles.checkIndicator },
                React.createElement(Icon, { name: "check" }))),
        children && (React.createElement("div", { "data-uds-child": "children", className: styles.inputLabel }, children))));
});

/**
 * InputRadio renders a label, and within an input of `type=radio`.
 * The children of the component, are part of the label
 */
const InputRadio = React.forwardRef(function InputRadio(props, forwardRef) {
    const { id: propsId, disabled, children, className, invert, dataTestId, ...rest } = props;
    const id = useId(propsId);
    const inputRef = useRef(null);
    const ref = useSharedRef(forwardRef, inputRef);
    const styles = {
        checkboxWrapper: tw$1('relative flex group', disabled ? 'cursor-not-allowed' : 'cursor-pointer', className),
        checkButton: tw$1('relative block text-lg w-5 h-5 rounded-full border-2 flex-none mt-0.5 mr-4 rtl:mr-0 rtl:ml-4', invert && !disabled && 'text-white', invert && disabled && 'text-neutral-50', !invert && disabled && 'text-neutral-60', !invert && !disabled && 'border-neutral-60 text-black', `group-hover:(${invert ? 'border-neutral-20' : 'border-neutral-80'})`),
        checkIndicator: tw$1('absolute left-1/2 top-1/2 -translate-y-1/2 -translate-x-1/2 w-full p-0.5 hidden'),
        input: tw$1('opacity-0 absolute left-0 top-0 w-full h-full', disabled ? 'cursor-not-allowed' : 'cursor-pointer', css$1({
            '&:focus + [data-uds-child="indicator"]': {
                '@apply': `ring border-neutral-80 ${invert && 'ring-invert border-white'}`,
            },
            '&:checked + [data-uds-child="indicator"]': {
                '@apply': `${invert ? 'bg-black border-white' : 'bg-white border-neutral-100'}`,
                span: {
                    display: 'block',
                },
            },
            '&:disabled + [data-uds-child="indicator"]': {
                '@apply': `${invert
                    ? 'bg-neutral-80 border-neutral-50'
                    : 'bg-neutral-20 border-neutral-60'}`,
            },
        })),
        inputLabel: tw$1('text-base flex-1', disabled && `${invert ? 'text-neutral-50' : 'text-neutral-60'}`, invert && 'text-white'),
    };
    return (React.createElement("label", { "data-test-id": dataTestId, htmlFor: id, className: styles.checkboxWrapper },
        React.createElement("input", { id: id, className: styles.input, ref: ref, type: "radio", disabled: disabled, "data-uds-child": "radio", "data-test-id": dataTestId ? `${dataTestId}-radio` : undefined, ...rest }),
        React.createElement("div", { "aria-hidden": true, className: styles.checkButton, "data-uds-child": "indicator" },
            React.createElement("span", { className: styles.checkIndicator },
                React.createElement(IndicatorRadio, null))),
        children && (React.createElement("div", { "data-uds-child": "children", className: styles.inputLabel }, children))));
});
const IndicatorRadio = () => {
    return (React.createElement("svg", { viewBox: "0 0 24 24" },
        React.createElement("circle", { r: 10, cx: 12, cy: 12, fill: "currentColor" })));
};

const ListBoxItem = (props) => {
    return (React.createElement("li", { role: "option", id: `${props.name}-option-${props.id}`, value: props.value, "data-index": props.index, "aria-selected": props.selected, className: tw$2('flex items-center w-full px-2 py-1', props.className) },
        React.createElement("span", { className: tw$2('flex-1', props.selected && 'font-bold') }, props.value),
        props.selected ? React.createElement(Icon, { size: "2xl", name: "check" }) : null));
};
const ListBox = React.forwardRef(function ListBox(props, forwardRef) {
    return (React.createElement("ul", { ref: forwardRef, id: `listbox-${props.id}`, role: "listbox", tabIndex: -1, "aria-labelledby": `${props.id}-label`, className: tw$2(props.hidden && 'hidden', props.className, 'border-1 overflow-auto w-full '), "aria-hidden": props.hidden }, props.options?.map((option, i) => (React.createElement(ListBoxItem, { key: option.value, index: i, id: option.id, value: option.value, name: props.id, selected: option.value === props.currentValue })))));
});
const getOptionElements = (node) => {
    if (!node)
        return [];
    const nodelist = node.querySelectorAll('li');
    const targets = Array.prototype.slice.call(nodelist);
    return targets;
};
function useCombobox(inputRef, params) {
    const { onOptionSelect, typeahead, filter } = params;
    const keypress = useRef('');
    const listboxRef = useRef(null);
    const keypressTimeout = useRef();
    const mouseMoveTimeout = useRef();
    const hideListboxTimeout = useRef();
    const [isActive, setActive] = useState(false);
    const [isExpanded, setExpanded] = useState(false);
    const [currentValue, setCurrentValue] = useState(null);
    const [activeDescendant, setActiveDescendant] = useState(-1);
    const [options, setOptions] = useState([]);
    const defaultFilter = (value) => {
        if (value && params.options) {
            const filteredOptions = params.options.filter(option => option.value
                .toString()
                .toLowerCase()
                .startsWith(value.toLowerCase().trim()));
            return setOptions(filteredOptions);
        }
        setOptions(params.options ?? []);
    };
    const filterOptions = (value) => {
        if (filter === true && params.options) {
            defaultFilter(value);
        }
        else if (typeof filter === 'function') {
            filter({
                value: value,
                setOptions: setOptions,
            });
        }
    };
    useEffect(() => {
        setOptions(params.options || []);
    }, [params.options]);
    const arrowRef = inputRef.current?.parentElement?.querySelector(`#action_${params.id}`);
    useEffect(() => {
        if (params.typeahead !== true) {
            inputRef.current?.removeAttribute('aria-autocomplete');
            inputRef.current?.setAttribute('readonly', 'true');
        }
        else {
            inputRef.current?.setAttribute('aria-autocomplete', 'list');
        }
        inputRef.current?.setAttribute('type', 'text');
        inputRef.current?.setAttribute('autocomplete', 'off');
        inputRef.current?.setAttribute('autocorrect', 'off');
        inputRef.current?.setAttribute('spellcheck', 'false');
        inputRef.current?.setAttribute('role', 'combobox');
        inputRef.current?.setAttribute('aria-haspopup', 'listbox');
    }, [params.id, params.typeahead]);
    useEffect(() => {
        if (isExpanded) {
            inputRef.current?.setAttribute('aria-controls', `listbox-${params.id}`);
        }
        else {
            inputRef.current?.removeAttribute('aria-controls');
        }
        inputRef.current?.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
    }, [isExpanded]);
    const listbox = useMemo(() => {
        return (React.createElement(ListBox, { hidden: !isExpanded, ref: listboxRef, id: params.id, className: params.classNameListbox, optionClassName: params.classNameListItem, currentValue: currentValue, options: options }));
    }, [listboxRef, currentValue, isExpanded, params.id, options]);
    const showListbox = () => {
        keypress.current = '';
        if (options.length > 0) {
            setCurrentValue(inputRef.current?.value);
            setExpanded(true);
        }
    };
    const hideListbox = () => {
        keypress.current = '';
        setExpanded(false);
        inputRef.current?.removeAttribute('aria-activedescendant');
        setActiveDescendant(-1);
        highlightOption(-1);
        listboxRef.current?.scrollTo({ top: 0 });
    };
    const setDefaultValue = (defaultValue) => {
        if (!inputRef.current)
            return;
        inputRef.current.value = defaultValue ? `${defaultValue}` : '';
        if (isActive) {
            const caretPos = defaultValue ? `${defaultValue}`.length : 0;
            inputRef.current.selectionStart = caretPos;
        }
        setCurrentValue(defaultValue);
    };
    const setSelectedOption = (selectedOptionIndex) => {
        if (!options)
            return;
        if (!inputRef.current)
            return;
        const selectedOption = options[selectedOptionIndex];
        /**
         * This valueSetter, and prototypeValueSetteris needed to fire `onKeyUp` event`
         * inputRef.current.value = selectedOption.label;
         *
         */
        const valueSetter = Object.getOwnPropertyDescriptor(inputRef.current, 'value')?.set;
        const prototype = Object.getPrototypeOf(inputRef.current);
        const prototypeValueSetter = Object.getOwnPropertyDescriptor(prototype, 'value')?.set;
        if (valueSetter && valueSetter !== prototypeValueSetter) {
            prototypeValueSetter?.call(inputRef.current, selectedOption.value);
        }
        else {
            valueSetter?.call(inputRef.current, selectedOption.value);
        }
        inputRef.current.dispatchEvent(new Event('input', { bubbles: true }));
        if (isActive) {
            const caretPos = `${selectedOption.value}`.length;
            inputRef.current.selectionStart = caretPos;
        }
        setCurrentValue(selectedOption.value);
        if (onOptionSelect)
            onOptionSelect(selectedOption);
        hideListboxTimeout.current = setTimeout(() => {
            hideListbox();
        }, 300);
    };
    const hoverClassName = tw$2(apply(params?.classNameListItemHover));
    const highlightOption = (optionIndex, scrollTo = false) => {
        if (!isActive || !options || !listboxRef.current)
            return;
        const optionElementsList = getOptionElements(listboxRef.current);
        if (activeDescendant >= 0) {
            const currentOptionElement = optionElementsList[activeDescendant];
            if (currentOptionElement) {
                currentOptionElement.classList.remove(hoverClassName);
                currentOptionElement.removeAttribute('data-highlight');
            }
        }
        if (optionIndex < 0)
            return;
        const activeOptionElement = optionElementsList[optionIndex];
        if (!activeOptionElement) {
            inputRef.current?.removeAttribute('aria-activedescendant');
            return;
        }
        activeOptionElement.classList.add(hoverClassName);
        activeOptionElement.setAttribute('data-highlight', 'true');
        const listboxScroll = listboxRef.current?.scrollTop;
        const listboxHeight = listboxRef.current?.getBoundingClientRect().height;
        const elementHeight = activeOptionElement.getBoundingClientRect().height;
        const elementTopPos = activeOptionElement.offsetTop;
        const elementBottomPos = elementTopPos + elementHeight;
        if (elementBottomPos > listboxScroll + listboxHeight)
            listboxRef.current?.scrollTo({
                top: elementBottomPos - listboxHeight,
            });
        if (elementTopPos < listboxScroll)
            listboxRef.current?.scrollTo({
                top: elementTopPos,
            });
        inputRef.current?.setAttribute('aria-activedescendant', `${activeOptionElement.getAttribute('id')}`);
        setActiveDescendant(optionIndex);
    };
    useIsomorphicLayoutEffect$1(() => {
        if (!params.defaultValue) {
            setDefaultValue('');
            return;
        }
        const selectedOptionIndex = options.findIndex(o => o.id === params.defaultValue || o.value === params.defaultValue);
        if (selectedOptionIndex >= 0) {
            setSelectedOption(selectedOptionIndex);
            return;
        }
        setDefaultValue(params.defaultValue);
    }, [params.defaultValue]);
    useEffect(() => {
        if (isActive && options && options.length > 0) {
            showListbox();
        }
        else {
            hideListbox();
        }
    }, [options]);
    useIsomorphicLayoutEffect$1(() => {
        const onFocus = (event) => {
            if (event.defaultPrevented) {
                return;
            }
            setCurrentValue(inputRef.current?.value);
            setActive(true);
        };
        const onBlur = (event) => {
            if (event.defaultPrevented) {
                return;
            }
            setActive(false);
            hideListbox();
        };
        const onArrowClick = (event) => {
            event.preventDefault();
            event.stopPropagation();
            setCurrentValue(inputRef.current?.value);
            inputRef.current?.focus();
            if (!isExpanded && options && options.length > 0) {
                showListbox();
            }
            else {
                hideListbox();
            }
        };
        const onClick = (event) => {
            if (event.defaultPrevented) {
                return;
            }
            setCurrentValue(inputRef.current?.value);
            if (params.typeahead === true)
                return;
            if (options && options.length > 0) {
                showListbox();
            }
            else {
                hideListbox();
            }
        };
        const onMouseMove = () => {
            clearTimeout(mouseMoveTimeout.current);
            mouseMoveTimeout.current = setTimeout(() => {
                mouseMoveTimeout.current = undefined;
            }, 500);
        };
        const onOptionHover = (event) => {
            if (!mouseMoveTimeout.current)
                return;
            event.preventDefault();
            event.stopPropagation();
            const hoverElement = event.currentTarget;
            const elementIndex = parseInt(`${hoverElement.dataset.index}`, 10);
            highlightOption(elementIndex);
        };
        const onOptionLeave = (event) => {
            event.preventDefault();
            event.stopPropagation();
            highlightOption(-1);
        };
        const onOptionClick = (event) => {
            event.preventDefault();
            event.stopPropagation();
            const selectedOptionElement = event.currentTarget;
            const selectedOptionIndex = parseInt(`${selectedOptionElement.dataset.index}`, 10);
            setSelectedOption(selectedOptionIndex);
            return false;
        };
        const onKeyUp = (event) => {
            if (!isActive || !options || !listboxRef.current)
                return;
            const target = event.target;
            let activeOption = activeDescendant;
            if ((typeahead && /^(Key|Digit)/.test(event.code)) ||
                event.key == 'Backspace' ||
                event.key === 'Delete') {
                filterOptions(target.value);
            }
            if (!isExpanded) {
                return;
            }
            /**
             * Listbox navigation
             */
            // TODO BACKSPACE
            if (!params.filter && /^(Key|Digit)/.test(event.code)) {
                clearTimeout(keypressTimeout.current);
                if (!typeahead) {
                    keypress.current = keypress.current + event.key;
                }
                else {
                    keypress.current = target?.value || '';
                }
                activeOption = options.findIndex(({ value }) => {
                    return `${value}`
                        .toLowerCase()
                        .startsWith(keypress.current.toLowerCase());
                });
                if (!typeahead) {
                    keypressTimeout.current = setTimeout(() => {
                        keypress.current = '';
                    }, 900);
                }
            }
            highlightOption(activeOption, true);
        };
        const onKeyDown = (event) => {
            if (!isActive || !options || !listboxRef.current)
                return;
            event.target;
            let activeOption = activeDescendant;
            const optionElementsList = getOptionElements(listboxRef.current);
            const totalOptions = optionElementsList.length;
            if (event.key === 'Escape') {
                hideListbox();
                return;
            }
            else if (!isExpanded && event.key === 'Tab') {
                return;
            }
            else if (!isExpanded && event.key === 'Enter') {
                event.preventDefault();
                event.stopPropagation();
                showListbox();
            }
            else if (!isExpanded &&
                (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
                event.preventDefault();
                event.stopPropagation();
                showListbox();
            }
            const blockKeys = ['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp'];
            if (!isExpanded)
                return;
            if (blockKeys.includes(event.key)) {
                event.preventDefault();
                event.stopPropagation();
            }
            if (event.code === 'Tab') {
                if (activeOption >= 0) {
                    setSelectedOption(activeOption);
                    return;
                }
            }
            if (event.code === 'Space' || event.key === 'Enter') {
                if (activeOption >= 0) {
                    event.preventDefault();
                    event.stopPropagation();
                    setSelectedOption(activeOption);
                    return;
                }
            }
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                event.stopPropagation();
                activeOption += 1;
                if (activeOption >= totalOptions) {
                    activeOption = 0;
                }
            }
            else if (event.key === 'ArrowUp') {
                event.preventDefault();
                event.stopPropagation();
                activeOption -= 1;
                if (activeOption < 0) {
                    activeOption = totalOptions - 1;
                }
            }
            else if (event.key === 'PageDown') {
                event.preventDefault();
                event.stopPropagation();
                activeOption += 10;
                if (activeOption >= totalOptions) {
                    activeOption = totalOptions - 1;
                }
            }
            else if (event.key === 'PageUp') {
                event.preventDefault();
                event.stopPropagation();
                activeOption -= 10;
                if (activeOption < 0) {
                    activeOption = 0;
                }
            }
            else if (event.key === 'Home') {
                event.preventDefault();
                event.stopPropagation();
                activeOption = 0;
            }
            else if (event.key === 'End') {
                event.preventDefault();
                event.stopPropagation();
                activeOption = totalOptions - 1;
            }
            highlightOption(activeOption, true);
        };
        const optionElementsList = listboxRef.current
            ? getOptionElements(listboxRef.current)
            : [];
        for (const item of optionElementsList) {
            item.addEventListener('mouseenter', onOptionHover);
            item.addEventListener('mouseleave', onOptionLeave);
            item.addEventListener('mousedown', onOptionClick);
        }
        arrowRef?.addEventListener('mousedown', onArrowClick);
        inputRef.current?.addEventListener('mousedown', onClick);
        inputRef.current?.addEventListener('keydown', onKeyDown);
        inputRef.current?.addEventListener('keyup', onKeyUp);
        inputRef.current?.addEventListener('focus', onFocus);
        inputRef.current?.addEventListener('blur', onBlur, true);
        document.addEventListener('mousemove', onMouseMove, true);
        return () => {
            clearTimeout(hideListboxTimeout.current);
            arrowRef?.removeEventListener('mousedown', onArrowClick);
            inputRef.current?.removeEventListener('mousedown', onClick);
            inputRef.current?.removeEventListener('keydown', onKeyDown);
            inputRef.current?.removeEventListener('keyup', onKeyUp);
            inputRef.current?.removeEventListener('focus', onFocus);
            inputRef.current?.removeEventListener('blur', onBlur, true);
            document.removeEventListener('mousemove', onMouseMove, true);
            for (const item of optionElementsList) {
                item.removeEventListener('mouseenter', onOptionHover);
                item.removeEventListener('mouseleave', onOptionLeave);
                item.removeEventListener('mousedown', onOptionClick);
            }
        };
    }, [options, activeDescendant, inputRef, isExpanded, isActive, typeahead]);
    return { listbox, isExpanded };
}

const defaultInputSplot = ({ expanded }) => (React.createElement(Icon, { name: expanded ? 'chevron-up' : 'chevron-down', className: "z-40", size: "2xl" }));
/**
 * The `InputCombobox` is a combination of a drop-down list or listbox and a single-line editable textbox,
 * allowing the user to either type a value directly or select a value from the list
 *
 * @example
 * ```
 *
 * <InputCombobox
 *   label="Combobox"
 *   typeahead={true}
 *   defaultValue="Tomato"
 *   options={[...]}
 * />
 *
 * ```
 */
const InputCombobox = React.forwardRef(function InputCombobox(props, forwardRef) {
    const inputRef = useRef(null);
    const { id: propsId, dataTestId, className, options, typeahead, filter, onOptionSelect, defaultValue, inputSlot, value, ...rest } = props;
    const id = useId(propsId);
    const { listbox, isExpanded } = useCombobox(inputRef, {
        id: id,
        filter: filter,
        options: options,
        typeahead: typeahead,
        defaultValue: defaultValue,
        onOptionSelect: onOptionSelect,
        classNameListbox: props.invert
            ? 'bg-neutral-100 border-neutral-50 max-h-64 rounded-sm'
            : 'bg-white border-neutral-60 max-h-64 rounded-sm',
        classNameListItem: props.invert ? 'text-white' : 'text-neutral-100',
        classNameListItemHover: props.invert
            ? 'bg-neutral-70 text-white'
            : 'bg-neutral-30 text-neutral-100',
    });
    const ref = useSharedRef(inputRef, forwardRef);
    const handleInputSlot = (props) => {
        return inputSlot
            ? inputSlot({ ...props, expanded: isExpanded })
            : defaultInputSplot({ ...props, expanded: isExpanded });
    };
    return (React.createElement("div", { className: tw$2('relative', className) },
        React.createElement(InputText, { ...rest, id: id, ref: ref, type: "text", dataTestId: dataTestId, inputSlot: handleInputSlot }),
        React.createElement("div", { "data-test-id": `${dataTestId}-listbox`, className: "z-[999] absolute top-20 mt-1.5 w-full" }, listbox)));
});

const PLACEHOLDER_VALUE = '';

const SelectContext = createContext(undefined);
/**
 * The `SelectProvider` component provides the select props to all of its children via context.
 *
 * @example
 * ```
 * <SelectProvider value={selectProps}>
 *   <SelectRoot>
 *     <SelectValue />
 *     <SelectHiddenInput />
 *     <SelectChevron />
 *   </SelectRoot>
 * </SelectProvider>
 * ```
 */
const SelectProvider = ({ children, value: contextValue, }) => {
    const { placeholder, defaultValue, value, options } = contextValue;
    if (value && defaultValue) {
        console.error('Select cannot be used as a controlled and uncontrolled component at the same time. Provide either a value or a defaultValue prop, not both.');
    }
    const getDefaultValue = () => {
        // Controlled value takes precedence over default value
        if (value || defaultValue) {
            return String(value || defaultValue);
        }
        // If no value is provided but a placeholder is, we want to show that
        if (placeholder) {
            return PLACEHOLDER_VALUE;
        }
        // Otherwise, show the first option as that is the default behaviour of a native select
        return options[0].value;
    };
    // We control the value of the select with state as it allows us to show a placeholder when no value is selected
    const [internalValue, setInternalValue] = useState(getDefaultValue());
    const displayValue = internalValue === PLACEHOLDER_VALUE
        ? placeholder
        : options.find(option => option.value === internalValue)?.label;
    // Keep local state up-to-date with controlled value
    useEffect(() => {
        if (value && value !== internalValue) {
            setInternalValue(String(value));
        }
    }, [value]);
    return (React.createElement(SelectContext.Provider, { value: {
            ...contextValue,
            // Only use default value if the Select is being used in an uncontrolled manner
            defaultValue: value ? undefined : internalValue,
            value,
            displayValue,
            setValue: setInternalValue,
        } }, children));
};
/**
 * A hook to access the `SelectContext` values.
 */
const useSelectContext = () => {
    const context = useContext(SelectContext);
    if (!context) {
        throw new Error('useSelectContext must be used within a SelectProvider');
    }
    return context;
};

/**
 * The `SelectRoot` component is used to render the wrapping element of the select.
 *
 * @example
 * ```
 * <SelectRoot>
 *   <SelectValue />
 *   <SelectHiddenInput />
 *   <SelectChevron />
 * </SelectRoot>
 * ```
 */
const SelectRoot = ({ className, children, ...rest }) => {
    const { dataTestId } = useSelectContext();
    return (React.createElement("div", { className: tw$2('relative inline-flex items-center gap-2', className), "data-test-id": dataTestId, ...rest }, children));
};

/**
 * The `SelectValue` component is used to render the placeholder text or selected value.
 *
 * @example
 * ```
 * <SelectValue />
 * ```
 */
const SelectValue = ({ className }) => {
    const { displayValue, dataTestId } = useSelectContext();
    return (React.createElement("span", { className: tw$2('flex-1 whitespace-nowrap text-left rtl:text-right', className), "aria-hidden": "true", "data-test-id": dataTestId ? `${dataTestId}-value` : undefined }, displayValue));
};

/**
 * The `SelectHiddenInput` component is the actual select element.
 * The native select is hidden from view but is used to handle the rendering of options and selection.
 * A ref should be passed to this component to allow the parent component to access the select element.
 *
 * @example
 * ```
 * <SelectHiddenInput ref={ref} />
 * ```
 */
const SelectHiddenInput = React.forwardRef(function Select(_, ref) {
    const { id, name, dataTestId, placeholder, options, disabled, invert, required, error, className, defaultValue, value, displayValue, 'aria-describedby': ariaDescribedBy, showErrorIcon, setValue, onChange, ...rest } = useSelectContext();
    const handleOnChange = (e) => {
        const val = e.target.value;
        setValue(val);
        if (onChange)
            onChange(val);
    };
    const selectStyle = tw$2('absolute w-full inset-0 opacity-0', // Hide the select so the span above shows the value - this lets the component be either controlled or uncontrolled depending on need.
    'text-neutral-100', // Necessary to ensure the select option text is legible on windows machines.
    disabled ? 'cursor-not-allowed' : 'cursor-pointer');
    return (React.createElement("select", { id: id, name: name, "data-test-id": dataTestId ? `${dataTestId}-input` : undefined, className: selectStyle, defaultValue: defaultValue, value: value, disabled: disabled, "aria-invalid": error ? 'true' : 'false', "aria-describedby": ariaDescribedBy, onChange: handleOnChange, required: required, ref: ref, ...rest },
        placeholder ? (React.createElement("option", { value: PLACEHOLDER_VALUE, disabled: true }, placeholder)) : null,
        options.map(option => (React.createElement("option", { key: option.value, value: option.value, disabled: option.disabled }, option.label)))));
});

/**
 * The `SelectChevron` component renders the chevron icon for a select.
 *
 * @example
 * ```
 * <SelectChevron />
 * ```
 */
const SelectChevron = ({ className = 'flex-shrink-0' }) => {
    const { dataTestId } = useSelectContext();
    return (React.createElement(ToggleChevron, { className: className, "data-test-id": dataTestId ? `${dataTestId}-chevron` : undefined }));
};

const InputSelectInner = React.forwardRef(function InputSelectInner({ label, showLabel, supportText, errorText }, ref) {
    const { error, disabled, invert, placeholder, displayValue, dataTestId } = useSelectContext();
    const { wrapperStyle } = useInputDivStyle({
        error,
        disabled,
        invert,
        descriptionText: Boolean(errorText || supportText),
        showLabel,
    });
    const isPlaceholder = displayValue === placeholder || displayValue === label;
    let valueColor = '';
    if (isPlaceholder && disabled) {
        valueColor = invert ? 'text-neutral-30' : 'text-neutral-70';
    }
    else if (isPlaceholder && !disabled) {
        valueColor = invert ? 'text-neutral-40' : 'text-neutral-60';
    }
    return (React.createElement(SelectRoot, { className: tw$2(wrapperStyle, 'h-14 pl-3 pr-4') },
        React.createElement(SelectValue, { className: tw$2('text-base truncate', valueColor) }),
        React.createElement(SelectHiddenInput, { ref: ref }),
        error && (React.createElement(ErrorIcon, { invert: invert, disabled: disabled, dataTestId: dataTestId ? `${dataTestId}-error-icon` : undefined })),
        React.createElement(SelectChevron, null)));
});
/**
 * The `InputSelect` renders a styled `select` tag and is used to create a drop-down list.
 * It is most often used in a form, to collect user input.
 *
 * @example
 * ```
 * <InputSelect
 *   label="Options"
 *   placeholder="Select an option"
 *   options={[ { value: "optionOne", label: "Option One" } ]}
 *   defaultValue="optionOne"
 * />
 *
 * ```
 */
const InputSelect = React.forwardRef(function InputSelect({ id: propsId, name, value, defaultValue, dataTestId, label, placeholder, options, disabled, invert, required, optional, className, error, errorText, supportText, showLabel = true, showErrorIcon = true, srOnlyLabel, srOnlyDescription, 'aria-describedby': ariaDescribedBy, onChange, onBlur, }, ref) {
    const id = useId(propsId);
    const descriptionIds = {
        error: `${id}_error`,
        support: `${id}_support`,
    };
    const inputWrapperProps = {
        label,
        childId: id,
        dataTestIdPrefix: dataTestId,
        supportText: supportText && descriptionIds?.support
            ? {
                text: supportText,
                id: descriptionIds.support,
            }
            : undefined,
        errorText: {
            text: errorText || undefined,
            id: descriptionIds.error,
        },
        required: required,
        optional: optional,
        invert,
        showLabel,
        className,
        srOnlyLabel,
        srOnlyDescription,
    };
    return (React.createElement(InputWrapper, { ...inputWrapperProps },
        React.createElement(SelectProvider, { value: {
                id,
                name,
                value,
                defaultValue,
                dataTestId,
                invert,
                options,
                placeholder,
                disabled,
                onChange,
                onBlur,
                required,
                showErrorIcon,
                'aria-describedby': ariaDescribedBy ||
                    `${descriptionIds.error}  ${descriptionIds.support}`,
                error: Boolean(error || errorText),
            } },
            React.createElement(InputSelectInner, { ref: ref, label: label, showLabel: showLabel, supportText: supportText, errorText: errorText }))));
});

const monthNames = [
    'january',
    'february',
    'march',
    'april',
    'may',
    'june',
    'july',
    'august',
    'september',
    'october',
    'november',
    'december',
];
const range = (length) => Array.from(Array(length).keys());
const initialDays = range(31).map((number) => {
    const value = `${number + 1}`;
    return { label: value, value };
});
const currentYear = new Date().getFullYear();
const yearList = range(100).reduce((result, current) => {
    const value = `${currentYear - current}`;
    return [...result, { label: value, value }];
}, []);
const createMonthsList = (months) => months.map((month, index) => ({ label: month, value: `${index + 1}` }));
const getCorrectDaysForMonth = (year, month) => {
    const monthLength = new Date(year, month, 0).getDate();
    const daysList = range(monthLength).map((number) => {
        const day = (number + 1).toString();
        return { label: day, value: day };
    });
    return daysList;
};
const useDateParts = () => {
    const t = useUdsTranslate();
    const [dayList, setDayList] = useState(initialDays);
    const [selectedMonth, setSelectedMonth] = useState();
    const [selectedYear, setSelectedYear] = useState();
    const months = monthNames.map(m => t(m));
    const monthList = createMonthsList(months);
    const handleYearChange = (year) => setSelectedYear(parseInt(year));
    const handleMonthChange = (month) => setSelectedMonth(parseInt(month));
    useEffect(() => {
        if (selectedMonth && selectedYear) {
            const days = getCorrectDaysForMonth(selectedYear, selectedMonth);
            setDayList(days);
        }
        else if (selectedMonth && !selectedYear) {
            const days = getCorrectDaysForMonth(new Date().getFullYear(), selectedMonth);
            setDayList(days);
        }
    }, [selectedMonth, selectedYear]);
    return { monthList, yearList, dayList, handleMonthChange, handleYearChange };
};

const defaultSelectNames = {
    year: 'year',
    month: 'month',
    day: 'day',
};
const containerStyle$1 = style({
    base: 'grid',
    variants: {
        direction: {
            column: 'grid-cols-1 gap-4',
            row: 'grid-cols-3 gap-4',
        },
    },
});
const isFieldError = (error, errorText) => (field) => {
    return typeof error === 'boolean'
        ? error
        : error?.[field] ||
            (typeof errorText === 'string'
                ? typeof error === 'undefined'
                : Boolean(errorText?.[field]));
};
/**
 * The `InputDateSelect` renders a fieldset, with three inputs for year, month, day.
 *
 * The inputs are three `InputSelect` components
 *
 * @example
 * ```
 * <InputDateSelect
 *   errorText={{
 *     year: 'Year is required',
 *   }}
 * />
 * ```
 */
const InputDateSelect = React.forwardRef((props, forwardRef) => {
    const { id: propsId, direction = { initial: 'column', sm: 'row' }, disabled, invert, className, selectNames = defaultSelectNames, error, errorText, supportText, currentItems, required, optional, label, dataTestId, refs, } = props;
    const id = useId(propsId);
    const t = useUdsTranslate();
    const day = t('day');
    const month = t('month');
    const year = t('year');
    const { dayList, monthList, yearList, handleMonthChange, handleYearChange, } = useDateParts();
    const yearRef = refs?.year ? refs.year : forwardRef;
    const monthRef = refs?.month ? refs.month : forwardRef;
    const dayRef = refs?.day ? refs.day : forwardRef;
    const getFieldError = isFieldError(error, errorText);
    const isYearError = getFieldError('year');
    const yearErrorText = typeof errorText !== 'string' ? errorText?.year : undefined;
    const isMonthError = getFieldError('month');
    const monthErrorText = typeof errorText !== 'string' ? errorText?.month : undefined;
    const isDayError = getFieldError('day');
    const dayErrorText = typeof errorText !== 'string' ? errorText?.day : undefined;
    const fieldsetErrorText = typeof errorText === 'string' ? errorText : undefined;
    const fieldsetErrorId = `${id}_error`;
    return (React.createElement(Fieldset, { id: id, dataTestId: dataTestId, invert: invert, legend: label, errorText: fieldsetErrorText, supportText: supportText, className: tw$2(className, 'w-full'), required: required, optional: optional, ref: forwardRef },
        React.createElement("div", { className: tw$2(containerStyle$1({ direction })) },
            React.createElement(InputSelect, { name: selectNames.year, showLabel: false, options: yearList, placeholder: year, id: `${id}-${selectNames.year}`, label: year, defaultValue: currentItems?.year, ref: yearRef, disabled: disabled, onChange: handleYearChange, error: isYearError, errorText: yearErrorText, invert: invert, required: required, "aria-describedby": fieldsetErrorText && isYearError ? fieldsetErrorId : undefined, dataTestId: dataTestId ? `${dataTestId}-${selectNames.year}` : undefined }),
            React.createElement(InputSelect, { name: selectNames.month, showLabel: false, options: monthList, placeholder: month, id: `${id}-${selectNames.month}`, label: month, ref: monthRef, error: isMonthError, errorText: monthErrorText, defaultValue: currentItems?.month, disabled: disabled, onChange: handleMonthChange, invert: invert, required: required, "aria-describedby": fieldsetErrorText && isMonthError ? fieldsetErrorId : undefined, dataTestId: dataTestId ? `${dataTestId}-${selectNames.month}` : undefined }),
            React.createElement(InputSelect, { name: selectNames.day, showLabel: false, options: dayList, placeholder: day, id: `${id}-${selectNames.day}`, label: day, error: isDayError, errorText: dayErrorText, ref: dayRef, defaultValue: currentItems?.day, disabled: disabled, invert: invert, required: required, "aria-describedby": fieldsetErrorText && isDayError ? fieldsetErrorId : undefined, dataTestId: dataTestId ? `${dataTestId}-${selectNames.day}` : undefined }))));
});
InputDateSelect.displayName = 'InputDateSelect';

/**
 * InputCheckbox renders a label, and within an input of `type=checkbox`.
 * The children of the component, are part of the label
 */
const InputSwitch = React.forwardRef(function InputCheckbox(props, forwardRef) {
    const { id: propsId, disabled, children, invert, className, dataTestId, showLabel = true, reverse, ...rest } = props;
    const id = useId(propsId);
    const inputRef = useRef(null);
    const ref = useSharedRef(forwardRef, inputRef);
    const styles = {
        switchWrapper: tw$1('group relative flex gap-4', disabled ? 'cursor-not-allowed' : 'cursor-pointer', reverse && 'flex-row-reverse', className),
        switch: tw$1('pointer-events-none transition transition-all ease-out relative block text-lg w-12 h-6 border-2 rounded-full flex-none', !invert && !disabled && 'bg-neutral-20 text-white border-neutral-100', !invert && disabled && 'bg-neutral-20 border-neutral-60', invert && !disabled && 'bg-neutral-90 text-neutral-100 border-white', invert && disabled && 'bg-neutral-80 border-neutral-50'),
        swichThumb: tw$1('pointer-events-none transition transition-all ease-out absolute transform -translate-x-0.5 left-0 -top-0.5 rtl:(left-auto right-0 translate-x-0.5)', 'w-6 h-6 rounded-full bg-white border-2 border-neutral-100', !invert && !disabled && 'bg-white border-neutral-100', !invert && disabled && 'bg-neutral-20 border-neutral-60', invert && !disabled && 'bg-neutral-100 border-white', invert && disabled && 'bg-neutral-80 border-neutral-50'),
        checkIndicator: tw$1('absolute w-4 h-4 top-0.5 left-1.5 transition transition-all ease-out opacity-0 transform -translate-x-2 rtl:(left-auto right-1.5 transform translate-x-2) ', invert ? 'text-neutral-100' : 'text-white'),
        input: tw$1('opacity-0 absolute left-0 top-0 w-full h-full', disabled ? 'cursor-not-allowed' : 'cursor-pointer', css$1({
            '&:focus ~ .switch, &:active ~ .switch': {
                '@apply': `ring-3 ${invert && 'ring-invert'}`,
            },
            '&:checked + .switch': {
                '@apply': `${invert ? 'bg-white' : 'bg-neutral-100'}`,
            },
            '&:checked + .switch > .thumb': {
                '@apply': `left-6 rtl:(left-auto right-6)`,
            },
            '&:checked + .switch > [data-indicator]': {
                '@apply': `translate-x-0 opacity-100`,
            },
        })),
        inputLabel: tw$1('text-base flex-1', disabled && `${invert ? 'text-neutral-50' : 'text-neutral-60'}`, invert && 'text-white', !showLabel && 'sr-only'),
    };
    return (React.createElement("label", { htmlFor: id, className: styles.switchWrapper, "data-test-id": dataTestId },
        React.createElement("input", { className: styles.input, id: id, ref: ref, type: "checkbox", disabled: disabled, ...rest }),
        React.createElement("div", { "data-uds-child": "switch", "aria-hidden": true, className: `${styles.switch} switch` },
            React.createElement("div", { "data-indicator": true, className: tw$1(styles.checkIndicator) },
                React.createElement(Icon, { name: "check" })),
            React.createElement("span", { className: tw$1(styles.swichThumb, 'thumb') })),
        children && (React.createElement("div", { "data-uds-child": "children", className: styles.inputLabel }, children))));
});

/**
 * The `SearchForm` component is used to render a form with a search input, clear button and submit button.
 *
 * @example
 * ```
 * const [value, setValue] = useState('');
 *
 * <SearchForm
 *   label="Search"
 *   placeholder="Search"
 *   value={value}
 *   onChange={setValue}
 *   onSubmit={handleSubmit}
 * />
 * ```
 */
const SearchForm = forwardRef(function SearchForm({ id: propsId, label, placeholder, name, dataTestId, value, invert, onChange, onSubmit, required = false, ...props }, forwardedRef) {
    const id = useId(propsId);
    const ref = useRef(null);
    const sharedRef = useSharedRef(forwardedRef, ref);
    const { dictionary } = useUdsContext();
    const isLargeDevice = useMediaQuery(`(min-width: ${tw$2.theme('screens.lg')})`);
    const handleClear = () => {
        onChange('');
        // Refocus input after clearing
        ref.current?.focus();
    };
    const clearButtonProps = {
        type: 'reset',
        label: dictionary.clear,
        onClick: handleClear,
        dataTestId: dataTestId ? `${dataTestId}-clear` : undefined,
        'data-uds-child': 'search-form-clear',
        invert,
    };
    return (React.createElement("form", { className: "relative flex-1", role: "search", "data-test-id": dataTestId, onSubmit: onSubmit },
        React.createElement("label", { htmlFor: id, className: "sr-only" }, label),
        React.createElement("input", { type: "search", id: id, name: name, placeholder: placeholder || label, value: value, className: tw$2('w-full h-14 rounded-sm border-1 focus-within:ring', invert
                ? 'bg-neutral-100 text-neutral-0 border-neutral-50 hover:border-neutral-0 focus-within:border-neutral-0'
                : 'bg-neutral-0 text-neutral-100 border-neutral-60 hover:border-neutral-100 focus-within:border-neutral-100', 
            // Padding to account for clear and submit buttons
            value
                ? 'pl-4 pr(28 lg:32) rtl:(pr-4 pl(28 lg:32))'
                : 'pl-4 pr-14 rtl:(pr-4 pl-14)'), autoComplete: "off", onChange: e => onChange(e.target.value), "data-uds-child": "search-form-input", "data-test-id": dataTestId ? `${dataTestId}-input` : undefined, ref: sharedRef, required: required, ...props }),
        React.createElement("div", { className: "absolute top-2 right-2 flex items-center gap-2 rtl:(right-auto left-2)" },
            value && (React.createElement(React.Fragment, null,
                isLargeDevice ? (React.createElement(ButtonUnderline, { ...clearButtonProps, className: "mr-2 rtl:mr-0 rtl:ml-2", transform: "uppercase", size: "sm" })) : (React.createElement(ButtonIcon, { ...clearButtonProps, icon: "close", variant: "ghost" })),
                React.createElement("div", { role: "separator", className: tw$2('h-6 w-px', invert ? 'bg-neutral-60' : 'bg-neutral-30') }))),
            React.createElement(ButtonIcon, { type: "submit", label: dictionary.search, icon: "search", variant: "ghost", "data-uds-child": "search-form-submit", "data-test-id": dataTestId ? `${dataTestId}-submit` : undefined, invert: invert }))));
});

/**
 * Context for the Callout component.
 *
 * @internal Used internally by the CalloutRoot component.
 */
const CalloutContext = createContext(null);
/**
 * Internal hook for consuming the Callout context.
 *
 * @internal
 */
const useCalloutContext = () => {
    return useContext(CalloutContext);
};

const CalloutIcon = (props) => {
    const { icon, size = '2xl', dataTestId, className, ...rest } = props;
    const { variant, invert, dataTestId: contextDataTestId, } = useCalloutContext();
    return (React.createElement(Icon, { ...rest, name: icon, color: variant, invert: invert, className: className, size: size, "data-uds-child": "callout-icon", dataTestId: dataTestId
            ? dataTestId
            : contextDataTestId
                ? `${contextDataTestId}-icon`
                : undefined }));
};
const CalloutBody = (props) => {
    const { dataTestId: contextDataTestId } = useCalloutContext();
    return (React.createElement("div", { "data-uds-child": "callout-body", "data-test-id": props.dataTestId
            ? props.dataTestId
            : contextDataTestId
                ? `${contextDataTestId}-body`
                : undefined, ...props }));
};
const CalloutText = ({ weight = 'bold', dataTestId, ...props }) => {
    const { variant, invert, dataTestId: contextDataTestId, } = useCalloutContext();
    return (React.createElement(Text, { color: variant, invert: invert, weight: weight, "data-uds-child": "callout-text", "data-test-id": dataTestId
            ? dataTestId
            : contextDataTestId
                ? `${contextDataTestId}-text`
                : undefined, ...props }));
};

const CalloutCloseButton = (props) => {
    const { className } = props;
    const { onCloseClick, invert } = useCalloutContext();
    const t = useUdsTranslate();
    const handleClick = e => {
        onCloseClick?.(e);
    };
    return (React.createElement(ButtonIcon, { "data-uds-child": "callout-close", label: t('close'), icon: "close", size: "xs", variant: "ghost", invert: invert, onClick: handleClick, className: className }));
};

animation('0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards', {
    '0%': {
        opacity: 0,
    },
    '100%': {
        opacity: 1,
    },
});
const fadeOut$2 = animation('0.3s cubic-bezier(0.5, 1, 0.89, 1) forwards', {
    '0%': {
        opacity: 1,
    },
    '100%': {
        opacity: 0,
    },
});
animation('0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards', {
    '0%': {
        transform: 'translate3d(0, -60px, 0)',
    },
    '100%': {
        transform: 'translate3d(0, 0, 0)',
    },
});

const baseLayout$1 = css `
  @apply grid;
  grid-auto-rows: max-content;

  & > [data-uds-child='callout-icon'] {
    grid-area: icon;
    @apply mt-px;
  }

  & > [data-uds-child='callout-body'] {
    grid-area: body;
    @apply justify-self-start self-start leading-normal;
  }

  & > [data-uds-child='callout-close'] {
    grid-area: close;
    @apply mt-0.5 justify-self-start self-start;
  }
`;
const baseLayoutIconStart = css `
  grid-template-columns: max-content auto max-content;
  grid-template-areas:
    'icon body close'
    'icon body close';
  & > [data-uds-child='callout-icon'] {
    @apply -ml-1 mr-2 rtl:(ml-2 -mr-1);
  }
  & > [data-uds-child='callout-close'] {
    @apply -mr-1 ml-3 rtl:(mr-3 -ml-1);
  }
`;
const baseLayoutIconEnd = css `
  grid-template-columns: auto max-content max-content;
  grid-template-areas:
    'body icon close'
    'body icon close';
  & > [data-uds-child='callout-icon'] {
    @apply -mr-1 ml-2 rtl:(mr-2 -ml-1);
  }
  & > [data-uds-child='callout-close'] {
    @apply -ml-1 mr-3 rtl:(ml-3 -mr-1);
  }
`;
const borderStyles = {
    base: 'border-neutral-20',
    muted: 'border-neutral-20',
    brand: 'border-brand-10',
    success: 'border-success-10',
    error: 'border-error-10',
    warning: 'border-warning-10',
    info: 'border-info-10',
};
const borderStylesInvert = {
    base: 'border-neutral-80',
    muted: 'border-neutral-80',
    brand: 'border-brand-70',
    success: 'border-success-80',
    error: 'border-error-80',
    warning: 'border-warning-80',
    info: 'border-info-80',
};
const fillStyles = {
    base: 'bg-neutral-10',
    muted: 'bg-neutral-5',
    brand: 'bg-brand-5',
    success: 'bg-success-5',
    error: 'bg-error-5',
    warning: 'bg-warning-5',
    info: 'bg-info-5',
};
const fillStylesInvert = {
    base: 'bg-neutral-90',
    muted: 'bg-neutral-100',
    brand: 'bg-brand-100',
    success: 'bg-success-100',
    error: 'bg-error-100',
    warning: 'bg-warning-100',
    info: 'bg-info-100',
};
/**

 */
const CalloutRoot = ({ variant = 'base', invert = false, outline = false, fill = false, inset = 'none', insetX, insetY, insetTop, insetEnd, insetBottom, insetStart, iconEnd = false, bleed = false, open = true, dismissible, onDismiss, asChild, dataTestId, className, children, ...rest }) => {
    const [isOpen, setIsOpen] = React.useState(open);
    const calloutMounted = useDelayUnmount(isOpen, 1000);
    const handleClose = (e) => {
        onDismiss && onDismiss(e);
        setIsOpen(false);
    };
    useEffect(() => {
        setIsOpen(open);
    }, [open]);
    return is.undefined(dismissible) || calloutMounted ? (React.createElement(CalloutContext.Provider, { value: {
            open: isOpen,
            invert,
            variant,
            outline,
            iconEnd,
            dataTestId,
            onCloseClick: handleClose,
        } },
        React.createElement(Inset, { "data-uds-child": "callout", asChild: asChild, inset: inset, insetX: insetX, insetY: insetY, insetTop: insetTop, insetEnd: insetEnd, insetBottom: insetBottom, insetStart: insetStart, dataTestId: dataTestId, className: tw$2(bleed ? 'w-full' : 'w-auto max-w-max', className, baseLayout$1, !isOpen && fadeOut$2, iconEnd ? baseLayoutIconEnd : baseLayoutIconStart, outline && 'relative border-1 rounded-sm', outline && !invert && borderStyles[variant], outline && invert && borderStylesInvert[variant], outline && fill && !invert && fillStyles[variant], outline && fill && invert && fillStylesInvert[variant]), ...rest },
            children,
            dismissible && React.createElement(CalloutCloseButton, null)))) : null;
};

/**
 * Callout message component.
 *
 * @example
 * ```
 * <Callout
 *   content="This order qualifies for free shipping"
 *   variant="success"
 *   icon="truck"
 * />
 * ```
 */
const Callout = ({ content, invert, variant, icon, ...rest }) => (React.createElement(CalloutRoot, { variant: variant, invert: invert, inset: "none", ...rest },
    icon && React.createElement(CalloutIcon, { icon: icon }),
    React.createElement(CalloutBody, null,
        React.createElement(CalloutText, { asChild: true, color: variant, invert: invert },
            React.createElement("p", null, content)))));

const defaultAlertIcons = {
    success: 'check-filled',
    error: 'exclamation-filled',
    warning: 'warning-filled',
    info: 'info-filled',
};
/**
 * Alert message component. Renders a box with the alert contents
 *
 * - The Alert is usually wrapped in a `LiveRegion` component. By itself, the alert
 * is not of `role=alert` but just text content.
 *
 * @example
 * ```
 * <LiveRegion role="assertive">
 *   <Alert title="Please fix the form errors" />
 * </LiveRegion>
 * ```
 */
const Alert = ({ title, content, variant = 'base', invert, icon = true, dataTestId, className, ref, ...rest }) => {
    const defaultAlertIcon = defaultAlertIcons[variant];
    const alertContent = Array.isArray(content) ? (React.createElement("ul", { className: "list-inside", "data-test-id": dataTestId ? `${dataTestId}-content` : undefined, "data-uds-child": "alert-content" }, content &&
        content.map(c => (React.createElement(CalloutText, { key: c, weight: title ? 'normal' : 'bold', asChild: true },
            React.createElement("li", { className: "list-disc" }, c)))))) : (React.createElement(CalloutText, { weight: title ? 'normal' : 'bold', dataTestId: dataTestId ? `${dataTestId}-content` : undefined, "data-uds-child": "alert-content", asChild: true },
        React.createElement("p", null, content)));
    const useIcon = icon === true && defaultAlertIcon
        ? defaultAlertIcon
        : is.string(icon)
            ? icon
            : null;
    return (React.createElement(CalloutRoot, { ref: ref, variant: variant, invert: invert, iconEnd: true, outline: true, insetX: "xs", insetY: "xxs", dataTestId: dataTestId, className: tw$2(className, 'w-full'), ...rest },
        useIcon ? (React.createElement(CalloutIcon, { icon: useIcon, "data-uds-child": "alert-icon", dataTestId: `${dataTestId}-icon` })) : null,
        React.createElement(CalloutBody, { "data-uds-child": "alert-body" },
            React.createElement(Stack, { gap: "px", justify: "start" },
                title ? (React.createElement(CalloutText, { "data-uds-child": "alert-title", dataTestId: dataTestId && `${dataTestId}-title`, asChild: true },
                    React.createElement("h4", null, title))) : null,
                content ? alertContent : null))));
};

const cardStyle = style({
    base: 'relative',
    variants: {
        variant: {
            fill: '',
            base: '',
            frost: '',
            transparent: '',
        },
        invert: {
            true: 'text-neutral-0',
            false: 'text-neutral-100',
        },
        outline: {
            true: '',
            false: '',
        },
        elevate: {
            true: 'shadow-glow-base',
            false: '',
        },
    },
    matches: [
        {
            invert: false,
            variant: 'base',
            use: 'bg-neutral-0',
        },
        {
            invert: false,
            variant: 'fill',
            use: 'bg-neutral-10',
        },
        {
            invert: false,
            variant: 'frost',
            use: 'bg-white bg-opacity-20',
        },
        {
            invert: true,
            variant: 'base',
            use: 'bg-neutral-100',
        },
        {
            invert: true,
            variant: 'fill',
            use: 'bg-neutral-90',
        },
        {
            invert: true,
            variant: 'frost',
            use: 'bg-neutral-100 bg-opacity-30',
        },
        {
            variant: 'transparent',
            use: 'border-1 border-transparent bg-transparent',
        },
        {
            variant: 'frost',
            use: css$1 `
        backdrop-filter: blur(16px);
      `,
        },
        {
            invert: false,
            outline: true,
            use: 'border-1 border-neutral-20',
        },
        {
            invert: true,
            outline: true,
            use: 'border-1 border-neutral-80',
        },
        {
            outline: false,
            use: 'border-1 border-transparent',
        },
    ],
    defaults: {
        variant: 'base',
        invert: false,
    },
});
/**
 * The `Card` component is a container for content. Internally uses the `Inset` component and inherits all its props.
 *
 * @example
 * ```tsx
 * <Card variant="frost" inset="md">
 * ```
 */
const Card = React.forwardRef((props, forwadedRef) => {
    const { variant = 'fill', outline = false, invert = false, elevate = false, inset = 'base', className, ...rest } = props;
    return (React.createElement(Inset, { ref: forwadedRef, "data-uds-child": "card", className: tw$1(cardStyle({
            invert,
            outline,
            variant,
            elevate,
        }), className), inset: inset, ...rest }));
});
Card.displayName = 'Card';

const badgeStyle = style({
    base: 'flex-shrink-0 inline-flex items-center justify-center gap-1 font-bold uppercase',
    variants: {
        /**
         * The color of the badge.
         * @defaultValue 'base'
         *
         * @example
         * ```tsx
         * <Badge color="error" />
         * ```
         */
        color: {
            base: '',
            muted: '',
            brand: '',
            success: '',
            info: '',
            error: '',
            warning: '',
        },
        /**
         * The size of the badge.
         * @defaultValue 'base'
         *
         * @example
         * ```tsx
         * <Badge size="sm" />
         * ```
         */
        size: {
            sm: 'text-xxs h-5 min-w-5',
            base: 'text-xs h-6 min-w-6',
        },
        /**
         * Set the badge to the round variant.
         * @defaultValue false
         *
         * @example
         * ```tsx
         * <Badge round />
         * ```
         */
        round: {
            true: 'rounded-full',
            false: 'rounded-sm ',
        },
        /**
         * Set the badge to the outlined variant.
         * @defaultValue false
         *
         * @example
         * ```tsx
         * <Badge outline />
         * ```
         */
        outline: {
            true: 'border border-1',
            false: '',
        },
        /**
         * Set the badge to the inverted variant.
         * @defaultValue false
         *
         * @example
         * ```tsx
         * <Badge invert />
         * ```
         */
        invert: {
            true: '',
            false: '',
        },
    },
    matches: [
        // Sizes
        {
            round: false,
            size: 'sm',
            use: 'px-3',
        },
        {
            round: false,
            size: 'base',
            use: 'px-4',
        },
        {
            round: true,
            size: 'sm',
            use: 'px-1.5',
        },
        {
            round: true,
            size: 'base',
            use: 'px-2',
        },
        // Solid colors
        {
            invert: false,
            outline: false,
            use: 'text-white',
        },
        {
            outline: false,
            color: 'base',
            invert: false,
            use: 'bg-neutral-100',
        },
        {
            outline: false,
            color: 'muted',
            invert: false,
            use: 'bg-neutral-60',
        },
        {
            outline: false,
            color: 'brand',
            invert: false,
            use: 'bg-brand-60',
        },
        {
            outline: false,
            color: 'success',
            invert: false,
            use: 'bg-success-60',
        },
        {
            outline: false,
            color: 'info',
            invert: false,
            use: 'bg-info-60',
        },
        {
            outline: false,
            color: 'error',
            invert: false,
            use: 'bg-error-60',
        },
        {
            outline: false,
            color: 'warning',
            invert: false,
            use: 'bg-warning-70',
        },
        // Solid colors (inverted)
        {
            invert: true,
            outline: false,
            use: 'text-neutral-100',
        },
        {
            outline: false,
            color: 'base',
            invert: true,
            use: 'bg-white',
        },
        {
            outline: false,
            color: 'muted',
            invert: true,
            use: 'bg-neutral-50',
        },
        {
            outline: false,
            color: 'brand',
            invert: true,
            use: 'bg-brand-30',
        },
        {
            outline: false,
            color: 'success',
            invert: true,
            use: 'bg-success-40',
        },
        {
            outline: false,
            color: 'info',
            invert: true,
            use: 'bg-info-40',
        },
        {
            outline: false,
            color: 'error',
            invert: true,
            use: 'bg-error-40',
        },
        {
            outline: false,
            color: 'warning',
            invert: true,
            use: 'bg-warning-50',
        },
        // Outline colors
        {
            outline: true,
            invert: false,
            use: 'bg-neutral-0',
        },
        {
            outline: true,
            invert: true,
            use: 'bg-neutral-100',
        },
        {
            outline: true,
            color: 'base',
            invert: false,
            use: 'text-neutral-100 border-neutral-20',
        },
        {
            outline: true,
            color: 'muted',
            invert: false,
            use: 'text-neutral-60 border-neutral-20',
        },
        {
            outline: true,
            color: 'brand',
            invert: false,
            use: 'text-brand-60 border-brand-20',
        },
        {
            outline: true,
            color: 'success',
            invert: false,
            use: 'text-success-60 border-success-20',
        },
        {
            outline: true,
            color: 'info',
            invert: false,
            use: 'text-info-60 border-info-20',
        },
        {
            outline: true,
            color: 'error',
            invert: false,
            use: 'text-error-60 border-error-20',
        },
        {
            outline: true,
            color: 'warning',
            invert: false,
            use: 'text-warning-70 border-warning-20',
        },
        // Outline colors (inverted)
        {
            outline: true,
            color: 'base',
            invert: true,
            use: 'text-white border-neutral-80',
        },
        {
            outline: true,
            color: 'muted',
            invert: true,
            use: 'text-neutral-50 border-neutral-80',
        },
        {
            outline: true,
            color: 'brand',
            invert: true,
            use: 'text-brand-30 border-brand-80',
        },
        {
            outline: true,
            color: 'success',
            invert: true,
            use: 'text-success-40 border-success-80',
        },
        {
            outline: true,
            color: 'info',
            invert: true,
            use: 'text-info-40 border-info-80',
        },
        {
            outline: true,
            color: 'error',
            invert: true,
            use: 'text-error-40 border-error-80',
        },
        {
            outline: true,
            color: 'warning',
            invert: true,
            use: 'text-warning-50 border-warning-80',
        },
    ],
});
/**
 * A badge component to highlight the status of an item or product.
 *
 * @example
 * ```
 * <Badge label="Coming Soon" color="success" icon="star-filled" />
 * ```
 */
const Badge = React.forwardRef(function Badge(props, forwardedRef) {
    const { dataTestId, color = 'base', size = 'base', icon, round = false, outline = false, invert = false, className, label, ...rest } = props;
    return (React.createElement("span", { ref: forwardedRef, className: tw$1('min-w-content', badgeStyle({
            color,
            outline,
            round,
            invert,
            size,
        }), className), "data-uds-child": "badge", "data-test-id": dataTestId, ...rest },
        icon && (React.createElement(Icon, { name: icon, size: "base", className: "flex-shrink-0 -ml-1 rtl:(-mr-1 ml-0))" })),
        label));
});

const ProgressStepperItemContext = createContext(undefined);
const ProgressStepperItemProvider = ({ children, value, }) => (React.createElement(ProgressStepperItemContext.Provider, { value: value }, children));
const useProgressStepperItemContext = () => {
    const context = useContext(ProgressStepperItemContext);
    if (!context) {
        throw new Error('useProgressStepperItemContext must be used within a ProgressStepperItemProvider');
    }
    return context;
};

/**
 * Step item bullet
 * @internal
 */
const ProgressStepperItemBullet = () => {
    const { isCurrent, layout, isFuture, isCompleted, number, isLast } = useProgressStepperItemContext();
    const style = useResponsiveProp(layout, value => {
        const bulletSize = 'w-8 h-8';
        const currentBullet = `bg-neutral-100 border-neutral-100 text-neutral-0 ${bulletSize}`;
        switch (value.compact) {
            case true:
                return [
                    isCurrent ? currentBullet : 'border-0 -indent-9999px w-2 h-2',
                    isCompleted && 'bg-neutral-100',
                    isFuture && 'bg-neutral-20',
                ];
            case false:
                return [
                    bulletSize,
                    isCurrent ? currentBullet : 'border-2 bg-neutral-0 indent-initial',
                    isFuture && 'text-neutral-60 border-neutral-20',
                    isCompleted && 'text-neutral-100 border-neutral-100',
                ];
        }
    });
    return (React.createElement("div", { className: "relative flex items-center justify-center w-8 h-8" },
        React.createElement("div", { className: "p-1 -m-1 bg-neutral-0 rounded-full" },
            React.createElement("div", { className: tw$2(`flex items-center justify-center rounded-full font-bold text-xs border-2 transition duration-300 ease-out-expo`, style), "aria-hidden": "true", "data-uds-child": "progress-stepper-item-bullet" }, isLast && isCurrent ? (React.createElement(Icon, { name: "check", size: "2xl" })) : (React.createElement(React.Fragment, null, number))))));
};

/**
 * Step item label
 *
 * @internal
 */
const ProgressStepperItemLabel = () => {
    const { isCurrent, layout, label } = useProgressStepperItemContext();
    const style = useResponsiveProp(layout, value => {
        const visibility = !isCurrent && value.compact ? `hidden` : `block`;
        switch (value.direction) {
            case `horizontal`:
                return ['text-xs', visibility];
            case 'vertical':
                return ['text-sm', visibility];
        }
    });
    return (React.createElement("span", { className: tw$2('font-bold uppercase whitespace-nowrap', !isCurrent && 'text-neutral-60', style), "data-uds-child": "progress-stepper-item-label" }, label));
};

/**
 * Item step count
 *
 * @internal
 */
const ProgressStepperItemStepCount = () => {
    const t = useUdsTranslate();
    const { isCurrent, layout, number, totalSteps } = useProgressStepperItemContext();
    const style = useResponsiveProp(layout, value => {
        return isCurrent && value.compact ? `block` : `hidden`;
    });
    return (React.createElement("span", { className: tw$2('font-bold text-xxs text-neutral-60 uppercase whitespace-nowrap', style), "data-uds-child": "progress-stepper-item-step-count" },
        ' ',
        t('stepCount', { step: number, total: totalSteps }),
        ' '));
};

/**
 * Step item text container
 *
 * @internal
 */
const ProgressStepperItemTextContainer = ({ children, }) => {
    const { layout, isFirst, isLast } = useProgressStepperItemContext();
    const style = useResponsiveProp(layout, value => {
        const out = [];
        switch (value.direction) {
            case `horizontal`:
                out.push('absolute top-10');
                isFirst && out.push('left-0 text-left');
                isLast && out.push('right-0 text-right');
                !isFirst &&
                    !isLast &&
                    out.push('left-1/2 -translate-x-1/2 text-center');
                break;
            case 'vertical':
                out.push('static translate-x-0 text-left');
                break;
        }
        return out;
    });
    return React.createElement("div", { className: tw$2('flex flex-col-reverse', style) }, children);
};

/**
 * Step item
 *
 * @internal
 */
const ProgressStepperItem = (props) => {
    const t = useUdsTranslate();
    const style = useResponsiveProp(props.layout, ({ direction, compact }) => {
        switch (direction) {
            case 'horizontal':
                return [
                    'flex gap-0 flex-col items-start',
                    // accounts for the absolutely positioned text container
                    compact ? 'pb-10' : 'pb-6',
                ];
            case 'vertical':
                return 'flex gap-3 flex-row items-center pb-0';
        }
    });
    return (React.createElement("li", { className: tw$2('relative z-[1]', style), "data-uds-child": "progress-stepper-item" },
        React.createElement(ProgressStepperItemProvider, { value: props },
            props.isCompleted && React.createElement("span", { className: "sr-only" },
                t('complete'),
                " "),
            props.isCurrent && React.createElement("span", { className: "sr-only" },
                t('current'),
                " "),
            React.createElement(ProgressStepperItemBullet, null),
            React.createElement(ProgressStepperItemTextContainer, null,
                React.createElement(ProgressStepperItemStepCount, null),
                React.createElement(ProgressStepperItemLabel, null)))));
};

const DEFAULT_VERTICAL_LAYOUT = {
    direction: 'vertical',
    compact: false,
    gap: 'base',
};
const DEFAULT_HORIZONTAL_LAYOUT = {
    direction: 'horizontal',
    compact: false,
    bleed: false,
    gap: 'base',
};
/** Formats the layout object into a responsive prop with default values */
const getLayout = (layout) => {
    const layoutObject = 'direction' in layout ? { _: layout } : layout;
    for (const breakpoint in layoutObject) {
        const { direction } = layoutObject[breakpoint];
        layoutObject[breakpoint] =
            direction === 'vertical'
                ? { ...DEFAULT_VERTICAL_LAYOUT, ...layoutObject[breakpoint] }
                : { ...DEFAULT_HORIZONTAL_LAYOUT, ...layoutObject[breakpoint] };
    }
    return layoutObject;
};
const HORIZONTAL_BAR_STYLES = 'left-4 top-4 right-4 bottom-auto h-[2px] w-auto';
const VERTICAL_BAR_STYLES = 'left-[15px] top-4 bottom-4 right-auto w-[2px] h-auto';
/**
 * Display the progress of a multi-step process.
 *
 * @example
 * ```
 * <ProgressStepper
 *   currentStep={0}
 *   steps={[
 *     "Ordered",
 *     "Processing",
 *     "Delivered",
 *   ]}
 *   layout={{
 *     direction: 'vertical', compact:false, gap:'base'
 *   }}
 * />
 * ```
 *
 * @example
 * ```
 * <ProgressStepper
 *  steps={[
 *    "Ordered",
 *    "Processing",
 *    "Packed",
 *    "Shipped",
 *    "Out for delivery",
 *    "Delivered",
 *  ]}
 *  currentStep={2}
 *  layout={{
 *    _: {
 *        direction: 'horizontal',
 *        gap: 'sm',
 *        compact: true
 *    },
 *    xl: {
 *        direction: 'horizontal',
 *        gap: 'lg',
 *        compact: false
 *    }
 *  }}
 * ```
 */
const ProgressStepper = ({ currentStep, steps, layout: layoutProp = DEFAULT_HORIZONTAL_LAYOUT, dataTestId, className, ...rest }) => {
    const layout = getLayout(layoutProp);
    const progressScale = Math.max((1 / (steps.length - 1)) * Math.min(currentStep + 0.5, steps.length - 1), 0);
    const style = useResponsiveProp(layout, ({ direction, gap, bleed }) => {
        switch (direction) {
            case 'horizontal':
                return [
                    `flex-row ${bleed ? 'w-full' : 'w-max'}`,
                    `${gap === 'sm' ? 'gap-8' : gap === 'lg' ? 'gap-24' : 'gap-16'}`,
                    `before:(${HORIZONTAL_BAR_STYLES})`,
                    `after:(${HORIZONTAL_BAR_STYLES} scale-x-[${progressScale}] scale-y-100)`,
                ];
            case 'vertical':
                return [
                    `flex-col w-auto ${bleed ? 'h-full' : 'h-max'}`,
                    `${gap === 'sm' ? 'gap-6' : gap === 'lg' ? 'gap-10' : 'gap-8'}`,
                    `before:(${VERTICAL_BAR_STYLES})`,
                    `after:(${VERTICAL_BAR_STYLES} scale-y-[${progressScale}] scale-x-100)`,
                ];
        }
    });
    return (React.createElement("ol", { className: tw$1('relative flex items-start justify-between min-w-max', `before:(content absolute bg-neutral-20)`, `after:(content absolute bg-neutral-100 origin-top-left transform transition-transform duration-300 ease-out-expo)`, style, className), "data-test-id": dataTestId, ...rest }, steps.map((item, i) => (React.createElement(ProgressStepperItem, { key: item, label: item, number: i + 1, totalSteps: steps.length, layout: layout, isFirst: i === 0, isLast: i === steps.length - 1, isCurrent: i === currentStep, isCompleted: i < currentStep, isFuture: i > currentStep })))));
};

const toPrecision = (number, precision = 2) => {
    const p = Math.pow(10, precision);
    const n = number * p * (1 + Number.EPSILON);
    return Math.round(n) / p;
};
const normalize = (value, minVal, maxVal, minTarget, maxTarget) => {
    return (minTarget + ((value - minVal) * (maxTarget - minTarget)) / (maxVal - minVal));
};

const GLOW_RGB = {
    base: [25, 25, 25],
    success: [32, 60, 0],
    error: [118, 25, 0],
    warning: [79, 56, 0],
    info: [0, 53, 118],
};
/**
 * Apply a subtle shaddow, and lift a surface off the background.
 *

 * @example
 * ```tsx
 * <Elevated asChild distance={0.4} angle={45}>
 *   <AvatarRoot/>
 * </Elevated>
 * ```
 */
const Elevated = (props) => {
    const { asChild, className, enabled = true, angle = 45, distance = 0.25, contrast = 0, color = 'base', invert, dataTestId, ...rest } = props;
    const Comp = asChild ? Slot : 'div';
    const elevationShadow = useMemo(() => {
        if (!enabled)
            return 'none';
        const opacityOffset = toPrecision(normalize(contrast, 0, 1, 0, 0.15), 2);
        const maxContrast = invert ? 0.65 : 0.25;
        const minContrast = invert ? 0.5 : 0.1;
        const opacity = toPrecision(normalize(distance, 0, 1, maxContrast + opacityOffset, minContrast + opacityOffset), 2);
        const minBlur = 5 + toPrecision(normalize(contrast, 0, 1, 0, -2), 2);
        const maxBlur = 18 + toPrecision(normalize(contrast, 0, 1, 0, -8));
        const blur = toPrecision(normalize(distance, 0, 1, minBlur, maxBlur));
        const spread = toPrecision(normalize(distance, 0, 1, -4, 0));
        const radians = (angle * Math.PI) / 180;
        const offset = 3;
        const minX = offset + Math.cos(radians);
        const maxX = offset + blur * Math.cos(radians);
        const minY = offset + Math.sin(radians);
        const maxY = offset + blur * Math.sin(radians);
        const offsetX = toPrecision(normalize(distance, 0, 1, minX, maxX));
        const offsetY = toPrecision(normalize(distance, 0, 1, minY, maxY));
        const rgba = color in GLOW_RGB
            ? `rgba(${GLOW_RGB[color].join(', ')}, ${opacity})`
            : color;
        return `${offsetX}px ${offsetY}px ${blur}px ${spread}px ${rgba}`;
    }, [enabled, invert, distance, contrast, angle, color]);
    return (React.createElement(Comp, { "data-test-id": dataTestId, "data-uds-child": "elevated", className: tw$2(className, css `
          box-shadow: ${elevationShadow};
        `), ...rest }));
};

const Bleed = props => {
    const { dataTestId, className, children, ...rest } = props;
    return (React.createElement("div", { className: tw$2('relative w-screen left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] px-0 sm:px-0', className), "data-uds-child": "bleed", "data-test-id": dataTestId, ...rest },
        React.createElement("div", { className: "max-w-screen-4xl mx-auto" }, children)));
};

const containerStyle = style({
    base: '',
    variants: {
        /**
         * Container Size (Responsive variant)
         *
         * @example
         *
         * ```
         * <Container  size={{_:md, md:'lg'}}>
         *   ...
         * </Container>
         * ```
         */
        size: {
            xs: 'max-w-screen-2xs',
            sm: 'max-w-[512px]',
            md: 'max-w-screen-md',
            lg: 'max-w-screen-lg',
            xl: 'max-w-[1312px]',
            base: 'max-w-screen-3xl',
            page: 'max-w-screen-3xl px-4 md:px-8 lg:px-10',
            bleed: 'max-w-screen-4xl',
        },
    },
});
/**
 * The `Container` is used to create a horizontally centered content container, and configure
 * its maximum width
 *
 * @example
 *
 * ```
 * <Container  size="md">
 *   ...
 * </Container>
 * ```
 */
const Container = React.forwardRef((props, forwadedRef) => {
    const { asChild, dataTestId, size = 'base', className, ...rest } = props;
    const Comp = asChild ? Slot : 'div';
    return (React.createElement(Comp, { ref: forwadedRef, "data-test-id": dataTestId, "data-uds-child": "container", className: tw$1('w-full mx-auto', containerStyle({ size }), className), ...rest }));
});
Container.displayName = 'Container';

const separatorStyle = style({
    variants: {
        /**
         * The visual and semantic orientation of the separator. Sets both the visual direction and the margin axis
         *
         * @defaultValue horizontal
         * @example
         * ```jsx
         * <Separator orientation="vertical" />
         * ```
         */
        orientation: {
            horizontal: 'w-full h-px',
            vertical: 'w-px h-auto self-stretch',
        },
        /**
         * Use on dark backgrounds
         */
        invert: {
            true: 'text-neutral-70 bg-neutral-80',
            false: 'text-neutral-30 bg-neutral-20',
        },
        variant: {
            transparent: css$1 `
        @apply bg-transparent;
        color: inherit;
      `,
            line: '',
            dashed: css$1 `
        background-color: transparent;
        background-image: linear-gradient(
          to right,
          currentColor 40%,
          rgba(255, 255, 255, 0) 0%
        );
        background-position: top;
        background-size: 12px 1px;
        background-repeat: repeat-x;
      `,
            dot: css$1 `
        position: relative;
        background-color: transparent;
        &:after {
          content: '';
          display: block;
          width: 4px;
          height: 4px;
          border-radius: 50%;
          background-color: currentColor;
          position: absolute;
          left: 50%;
          top: calc(50% + 3px);
          transform: translate(-50%, -50%);
        }
      `,
        },
        /**
         * Responsive margin before and after the separator
         *
         * @example
         * ```jsx
         * <Separator gap="none" />
         * ```
         *
         * @example
         * ```jsx
         * <Separator gap={{_:'sm', md:'base'}} />
         * ```
         */
        gap: {
            min: '',
            auto: '',
            max: '',
            none: '',
            px: '',
            xxs: '',
            xs: '',
            sm: '',
            base: '',
            md: '',
            lg: '',
            xl: '',
            '2xl': '',
            '3xl': '',
            '4xl': '',
            '5xl': '',
        },
        asChild: {
            true: 'h-auto',
            false: '',
        },
    },
    matches: [
        {
            orientation: 'horizontal',
            gap: 'min',
            use: `my-${UDS_GAP.min} mx-0`,
        },
        {
            orientation: 'horizontal',
            gap: 'auto',
            use: `my-${UDS_GAP.auto} mx-0`,
        },
        {
            orientation: 'horizontal',
            gap: 'max',
            use: `my-${UDS_GAP.max} mx-0`,
        },
        {
            orientation: 'horizontal',
            gap: 'none',
            use: `my-${UDS_GAP.none} mx-0`,
        },
        {
            orientation: 'horizontal',
            gap: 'px',
            use: `my-${UDS_GAP.px} mx-0`,
        },
        {
            orientation: 'horizontal',
            gap: 'xxs',
            use: `my-${UDS_GAP.xxs} mx-0`,
        },
        {
            orientation: 'horizontal',
            gap: 'xs',
            use: `my-${UDS_GAP.xs} mx-0`,
        },
        {
            orientation: 'horizontal',
            gap: 'sm',
            use: `my-${UDS_GAP.sm} mx-0`,
        },
        {
            orientation: 'horizontal',
            gap: 'base',
            use: `my-${UDS_GAP.base} mx-0`,
        },
        {
            orientation: 'horizontal',
            gap: 'lg',
            use: `my-${UDS_GAP.lg} mx-0`,
        },
        {
            orientation: 'horizontal',
            gap: 'xl',
            use: `my-${UDS_GAP.xl} mx-0`,
        },
        {
            orientation: 'horizontal',
            gap: '2xl',
            use: `my-${UDS_GAP['2xl']} mx-0`,
        },
        {
            orientation: 'horizontal',
            gap: '3xl',
            use: `my-${UDS_GAP['3xl']} mx-0`,
        },
        {
            orientation: 'horizontal',
            gap: '4xl',
            use: `my-${UDS_GAP['4xl']} mx-0`,
        },
        {
            orientation: 'horizontal',
            gap: '5xl',
            use: `my-${UDS_GAP['5xl']} mx-0`,
        },
        {
            orientation: 'vertical',
            gap: 'min',
            use: `mx-${UDS_GAP.min} my-0`,
        },
        {
            orientation: 'vertical',
            gap: 'auto',
            use: `mx-${UDS_GAP.auto} my-0`,
        },
        {
            orientation: 'vertical',
            gap: 'max',
            use: `mx-${UDS_GAP.max} my-0`,
        },
        {
            orientation: 'vertical',
            gap: 'none',
            use: `mx-${UDS_GAP.none} my-0`,
        },
        {
            orientation: 'vertical',
            gap: 'px',
            use: `mx-${UDS_GAP.px} my-0`,
        },
        {
            orientation: 'vertical',
            gap: 'xxs',
            use: `mx-${UDS_GAP.xxs} my-0`,
        },
        {
            orientation: 'vertical',
            gap: 'xs',
            use: `mx-${UDS_GAP.xs} my-0`,
        },
        {
            orientation: 'vertical',
            gap: 'sm',
            use: `mx-${UDS_GAP.sm} my-0`,
        },
        {
            orientation: 'vertical',
            gap: 'base',
            use: `mx-${UDS_GAP.base} my-0`,
        },
        {
            orientation: 'vertical',
            gap: 'lg',
            use: `mx-${UDS_GAP.lg} my-0`,
        },
        {
            orientation: 'vertical',
            gap: 'xl',
            use: `mx-${UDS_GAP.xl} my-0`,
        },
        {
            orientation: 'vertical',
            gap: '2xl',
            use: `mx-${UDS_GAP['2xl']} my-0`,
        },
        {
            orientation: 'vertical',
            gap: '3xl',
            use: `mx-${UDS_GAP['3xl']} my-0`,
        },
        {
            orientation: 'vertical',
            gap: '4xl',
            use: `mx-${UDS_GAP['4xl']} my-0`,
        },
        {
            orientation: 'vertical',
            gap: '5xl',
            use: `mx-${UDS_GAP['5xl']} my-0`,
        },
        {
            variant: 'transparent',
            orientation: 'horizontal',
            gap: 'min',
            use: `mt-${UDS_GAP.min} mb-0 mx-0`,
        },
        {
            variant: 'transparent',
            orientation: 'horizontal',
            gap: 'auto',
            use: `mt-${UDS_GAP.auto} mb-0 mx-0`,
        },
        {
            variant: 'transparent',
            orientation: 'horizontal',
            gap: 'max',
            use: `mt-${UDS_GAP.max} mb-0 mx-0`,
        },
        {
            variant: 'transparent',
            orientation: 'horizontal',
            gap: 'none',
            use: `mt-${UDS_GAP.none} mb-0 mx-0`,
        },
        {
            variant: 'transparent',
            orientation: 'horizontal',
            gap: 'px',
            use: `mt-${UDS_GAP.px} mb-0 mx-0`,
        },
        {
            variant: 'transparent',
            orientation: 'horizontal',
            gap: 'xxs',
            use: `mt-${UDS_GAP.xxs} mb-0 mx-0`,
        },
        {
            variant: 'transparent',
            orientation: 'horizontal',
            gap: 'xs',
            use: `mt-${UDS_GAP.xs} mb-0 mx-0`,
        },
        {
            variant: 'transparent',
            orientation: 'horizontal',
            gap: 'sm',
            use: `mt-${UDS_GAP.sm} mb-0 mx-0`,
        },
        {
            variant: 'transparent',
            orientation: 'horizontal',
            gap: 'base',
            use: `mt-${UDS_GAP.base} mb-0 mx-0`,
        },
        {
            variant: 'transparent',
            orientation: 'horizontal',
            gap: 'lg',
            use: `mt-${UDS_GAP.lg} mb-0 mx-0`,
        },
        {
            variant: 'transparent',
            orientation: 'horizontal',
            gap: 'xl',
            use: `mt-${UDS_GAP.xl} mb-0 mx-0`,
        },
        {
            variant: 'transparent',
            orientation: 'horizontal',
            gap: '2xl',
            use: `mt-${UDS_GAP['2xl']} mb-0 mx-0`,
        },
        {
            variant: 'transparent',
            orientation: 'horizontal',
            gap: '3xl',
            use: `mt-${UDS_GAP['3xl']} mb-0 mx-0`,
        },
        {
            variant: 'transparent',
            orientation: 'horizontal',
            gap: '4xl',
            use: `mt-${UDS_GAP['4xl']} mb-0 mx-0`,
        },
        {
            variant: 'transparent',
            orientation: 'horizontal',
            gap: '5xl',
            use: `mt-${UDS_GAP['5xl']} mb-0 mx-0`,
        },
        {
            variant: 'transparent',
            orientation: 'vertical',
            gap: 'min',
            use: `ml--${UDS_GAP.min} mr-0 rtl:(ml-0 mr--${UDS_GAP.min}) my-0`,
        },
        {
            variant: 'transparent',
            orientation: 'vertical',
            gap: 'auto',
            use: `ml--${UDS_GAP.auto} mr-0 rtl:(ml-0 mr--${UDS_GAP.auto}) my-0`,
        },
        {
            variant: 'transparent',
            orientation: 'vertical',
            gap: 'max',
            use: `ml--${UDS_GAP.max} mr-0 rtl:(ml-0 mr--${UDS_GAP.max}) my-0`,
        },
        {
            variant: 'transparent',
            orientation: 'vertical',
            gap: 'none',
            use: `ml-${UDS_GAP.none} mr-0 rtl:(ml-0 mr-${UDS_GAP.none}) my-0`,
        },
        {
            variant: 'transparent',
            orientation: 'vertical',
            gap: 'px',
            use: `ml-${UDS_GAP.px} mr-0 rtl:(ml-0 mr-${UDS_GAP.px}) my-0`,
        },
        {
            variant: 'transparent',
            orientation: 'vertical',
            gap: 'xxs',
            use: `ml-${UDS_GAP.xxs} mr-0 rtl:(ml-0 mr-${UDS_GAP.xxs}) my-0`,
        },
        {
            variant: 'transparent',
            orientation: 'vertical',
            gap: 'xs',
            use: `ml-${UDS_GAP.xs} mr-0 rtl:(ml-0 mr-${UDS_GAP.xs}) my-0`,
        },
        {
            variant: 'transparent',
            orientation: 'vertical',
            gap: 'sm',
            use: `ml-${UDS_GAP.sm} mr-0 rtl:(ml-0 mr-${UDS_GAP.sm}) my-0`,
        },
        {
            variant: 'transparent',
            orientation: 'vertical',
            gap: 'base',
            use: `ml-${UDS_GAP.base} mr-0 rtl:(ml-0 mr-${UDS_GAP.base}) my-0`,
        },
        {
            variant: 'transparent',
            orientation: 'vertical',
            gap: 'lg',
            use: `ml-${UDS_GAP.lg} mr-0 rtl:(ml-0 mr-${UDS_GAP.lg}) my-0`,
        },
        {
            variant: 'transparent',
            orientation: 'vertical',
            gap: 'xl',
            use: `ml-${UDS_GAP.xl} rtl:(ml-0 mr-${UDS_GAP.xl}) my-0`,
        },
        {
            variant: 'transparent',
            orientation: 'vertical',
            gap: '2xl',
            use: `ml-${UDS_GAP['2xl']} rtl:(ml-0 mr-${UDS_GAP['2xl']}) my-0`,
        },
        {
            variant: 'transparent',
            orientation: 'vertical',
            gap: '3xl',
            use: `ml-${UDS_GAP['3xl']} rtl:(ml-0 mr-${UDS_GAP['3xl']}) my-0`,
        },
        {
            variant: 'transparent',
            orientation: 'vertical',
            gap: '4xl',
            use: `ml-${UDS_GAP['4xl']} rtl:(ml-0 mr-${UDS_GAP['4xl']}) my-0`,
        },
        {
            variant: 'transparent',
            orientation: 'vertical',
            gap: '5xl',
            use: `ml-${UDS_GAP['5xl']} rtl:(ml-0 mr-${UDS_GAP['5xl']}) my-0`,
        },
        {
            orientation: 'vertical',
            variant: 'dashed',
            use: css$1 `
        background-color: transparent;
        background-image: linear-gradient(
          to bottom,
          currentColor 40%,
          rgba(255, 255, 255, 0) 0%
        );
        background-position: top;
        background-size: 1px 12px;
        background-repeat: repeat-y;
      `,
        },
        {
            asChild: true,
            use: 'h-auto w-auto bg-transparent',
        },
    ],
    defaults: {
        invert: false,
    },
});
/**
 * The `Separator` component is a visual, thematic and contextual content separator.
 *
 * It can be rendered as a line, dashed, or single dot
 *
 *
 * @example
 *
 * ```jsx
 * <Separator variant="dashed" orientation="horizontal"/>
 * ```
 *
 * @example
 * ```jsx
 * <Separator gap={{_:'sm', md:'base'}} />
 * ```
 */
const Separator = React.forwardRef((props, forwadedRef) => {
    const { variant = 'line', orientation = 'horizontal', role = 'separator', invert = false, gap = 'base', className, dataTestId, asChild, ...rest } = props;
    const Comp = asChild ? Slot : 'div';
    return (React.createElement(Comp, { ref: forwadedRef, role: role, "data-uds-child": "separator", "aria-orientation": orientation, "data-test-id": dataTestId, className: tw$1(className, separatorStyle({
            gap: gap,
            variant: variant,
            orientation: orientation,
            invert,
            asChild,
        })), ...rest }));
});
Separator.displayName = 'Separator';

/**
 * The `Divider` is a component, that doesn't render an HTML element itself, but instead renders a separator between each child.
 *
 * By default, it renders a `Separator` component, but you can also pass a custom react node using the `separator` prop.
 *
 * @example
 * ```tsx
 * <Divider variant="dash">
 *    <div>...</div>
 *    <div>...</div>
 * </Divider>
 * ```
 *
 */
const Divider = props => {
    const { separator, gap = 'none', variant, orientation, invert, role, children, } = props;
    return Children.map(children, (child, index) => [
        child,
        index < Children.count(children) - 1 &&
            cloneElement(React.createElement(Separator, null), {
                gap: gap,
                orientation: orientation,
                invert: invert,
                role: role,
                variant: variant,
                asChild: separator ? true : false,
            }, separator),
    ]);
};

const floatStyle = style({
    base: 'max-w-fit absolute z-[3] flex items-center justify-center',
    variants: {
        align: {
            top: 'top-0 bottom-auto',
            middle: 'top-1/2 transform -translate-y-1/2',
            bottom: '-bottom-0 top-auto',
        },
        justify: {
            start: 'left-0 right-auto text-left rtl:(right-0 left-auto text-right)',
            center: 'left-1/2 text-center transform -translate-x-1/2',
            end: 'right-0 left-auto text-right rtl:(left-0 right-auto text-left)',
        },
        invert: {
            true: 'text-white',
            false: 'text-neutral',
        },
        inset: {
            none: '',
            px: '',
            xs: '',
            sm: '',
            base: '',
            md: '',
            lg: '',
            xl: '',
            '2xl': '',
            '3xl': '',
            '4xl': '',
            '5xl': '',
        },
        insetX: {
            none: 'px-0',
            px: 'px-px',
            xs: 'px-2',
            sm: 'px-4',
            base: 'px-6',
            md: 'px-8',
            lg: 'px-12',
            xl: 'px-14',
            '2xl': 'px-16',
            '3xl': 'px-20',
            '4xl': 'px-28',
            '5xl': 'px-36',
        },
        insetY: {
            none: 'py-0',
            px: 'py-px',
            xs: 'py-2',
            sm: 'py-4',
            base: 'py-6',
            md: 'py-8',
            lg: 'py-12',
            xl: 'py-14',
            '2xl': 'py-16',
            '3xl': 'py-20',
            '4xl': 'py-28',
            '5xl': 'py-36',
        },
    },
    matches: [
        {
            align: 'middle',
            justify: 'center',
            use: 'text-center top-1/2 transform -translate-y-1/2 left-1/2 -translate-x-1/2',
        },
    ],
});
/**
 * Float is a floating content container, that can be positioned anywhere over another element.
 *
 * Use it to render badges, icons, buttons and other floating content.
 *
 * @example
 * ```jsx
 * <Float>
 *   <Badge icon="start" label="Exclusive"/>
 * </Float>
 * ```
 */
const Float = ({ children, align = 'top', justify = 'start', interactive = true, inset, insetX, insetY, invert, className, dataTestId, asChild, ...rest }) => {
    const Comp = asChild ? Slot : 'div';
    return (React.createElement(Comp, { "data-uds-child": "float", "data-test-id": dataTestId, ...rest, className: tw$1(!interactive && 'pointer-events-none', floatStyle({
            invert,
            align,
            justify,
            inset,
            insetX: insetX ?? inset,
            insetY: insetY ?? inset,
        }), className) }, children));
};

const gridStyle$1 = style({
    base: 'w-full grid',
    variants: {
        /**
         * Responsive align grid children along the Y axis
         */
        align: {
            start: 'items-start',
            center: 'items-center',
            auto: 'items-stretch',
            end: 'items-end',
        },
        /**
         * Responsive justify grid children along the X axis
         */
        justify: {
            start: 'justify-items-start',
            center: 'justify-items-center',
            auto: 'justify-items-auto',
            end: 'justify-items-end',
        },
        /**
         * Responsive Grid columns. By default, `columns=auto` is selected, and grid children will be distributed equally.
         *
         * defaultValue auto
         *
         * @example
         * Default use case, three equal columns
         * ```
         * <Grid asChild>
         *   <ul>
         *     <li>Et mollit voluptate ad do quis.</li>
         *     <li>Et mollit voluptate ad do quis.</li>
         *     <li>Et mollit voluptate ad do quis.</li>
         *   </ul>
         * </Grid>
         *
         * @example
         * Responsive Grid columns
         * <Grid columns={{_: "2", md: "6"}} />
         * ```
         */
        columns: {
            auto: 'grid-flow-col auto-cols-fr',
            '1': 'grid-cols-1',
            '2': 'grid-cols-2',
            '3': 'grid-cols-3',
            '4': 'grid-cols-4',
            '5': 'grid-cols-5',
            '6': 'grid-cols-6',
            '7': 'grid-cols-7',
            '8': 'grid-cols-8',
            '9': 'grid-cols-9',
            '10': 'grid-cols-10',
            '11': 'grid-cols-11',
            '12': 'grid-cols-12',
        },
    },
});
/**
 * The Grid is a simply layout component wrapped around css grid specification. It is used as a way to
 * declaratively define a grid system.
 *
 * @example auto flow
 * ```
 * <Grid>
 *   <li>Et mollit voluptate ad do quis.</li>
 *   <li>Et mollit voluptate ad do quis.</li>
 *   <li>Et mollit voluptate ad do quis.</li>
 * </Grid>
 * ```
 *
 * @example responsive columns
 * ```
 * <Grid columns={{_:1, md:3}}>
 *   <li>Et mollit voluptate ad do quis.</li>
 *   <li>Et mollit voluptate ad do quis.</li>
 *   <li>Et mollit voluptate ad do quis.</li>
 * </Grid>
 * ```
 *
* - By default renders a div. To render as a custom element, use the `asChild` prop

 */
const Grid = React.forwardRef((props, forwardedRef) => {
    const { asChild, dataTestId, css: whatIsThis, // TODO find out why this is here
    gap = 'base', gapX, gapY, columns = 'auto', align, justify, className, children, ...rest } = props;
    const Comp = asChild ? Slot : 'div';
    return (React.createElement(Comp, { ref: forwardedRef, className: tw$1(gridStyle$1({
            columns,
            align,
            justify,
        }), gapStyle({ gap, gapX, gapY }), className), "data-test-id": dataTestId, ...rest }, children));
});
Grid.displayName = 'Grid';

const gridStyle = style({
    variants: {
        /**
         * Align cell along both axes.
         *
         * @example
         * use `place=center` to center the grid cell.
         * ```
         * <GridCell place="center />
         * ```
         */
        place: {
            start: 'place-self-start',
            center: 'place-self-center',
            auto: 'place-self-auto',
            end: 'place-self-end',
        },
        /**
         * Align grid children along the Y axis
         */
        align: {
            start: 'self-start',
            center: 'self-center',
            auto: 'self-auto',
            end: 'self-end',
        },
        /**
         * Align grid children along the X axis
         */
        justify: {
            start: 'justify-self-start',
            center: 'justify-self-center',
            auto: 'justify-self-auto',
            end: 'justify-self-end',
        },
        /**
         * GridCell column span, configure a cell to span to a custom number of columns.
         *
         * @example
         *
         * ```jsx
         * <Grid>
         *  <GridCell colSpan="2}/>
         * </Grid>
         * ```
         *
         * @example responsive
         *
         * ```jsx
         * <GridCell colSpan={{ _:'auto', md: 2}}/>
         * ```
         */
        colSpan: {
            auto: 'col-span-auto',
            full: 'col-span-full',
            '1': 'col-span-1',
            '2': 'col-span-2',
            '3': 'col-span-3',
            '4': 'col-span-4',
            '5': 'col-span-5',
            '6': 'col-span-6',
            '7': 'col-span-7',
        },
        /**
         * GridCell column start, configure a cell to render at a particular column index
         *
         * @example
         *
         * ```jsx
         * <Grid>
         *  <GridCell colStart={2} />
         * </Grid>
         * ```
         *
         * @example responsive
         *
         * ```jsx
         * <GridCell colStart={{ _:'auto', md: 2}}/>
         * ```
         */
        colStart: {
            auto: 'col-start-auto',
            '1': 'col-start-1',
            '2': 'col-start-2',
            '3': 'col-start-3',
            '4': 'col-start-4',
            '5': 'col-start-5',
            '6': 'col-start-6',
            '7': 'col-start-7',
        },
        /**
         * GridCell row span, configure a cell to span to a custom number of rows.
         *
         * @example
         *
         * ```jsx
         * <Grid>
         *  <GridCell rowSpan={2} />
         * </Grid>
         * ```
         *
         * @example responsive
         *
         * ```jsx
         * <GridCell rowSpan={{ _:'auto', md: 2}}/>
         * ```
         */
        rowSpan: {
            auto: 'row-span-auto',
            full: 'row-span-full',
            '1': 'row-span-1',
            '2': 'row-span-2',
            '3': 'row-span-3',
            '4': 'row-span-4',
            '5': 'row-span-5',
            '6': 'row-span-6',
            '7': 'row-span-7',
        },
        /**
         * GridCell row start, configure a cell to start at a specific row index.
         *
         * @example
         *
         * ```jsx
         * <Grid>
         *  <GridCell rowStart={2} />
         * </Grid>
         * ```
         *
         * @example responsive
         *
         * ```jsx
         * <GridCell rowStart={{ _:'auto', md: 2}}/>
         * ```
         */
        rowStart: {
            auto: 'row-start-auto',
            '1': 'row-start-1',
            '2': 'row-start-2',
            '3': 'row-start-3',
            '4': 'row-start-4',
            '5': 'row-start-5',
            '6': 'row-start-6',
            '7': 'row-start-7',
        },
    },
});
/**
 * The `GridCell` is always found directly nested within a `Grid` component.
 *
 * @example
 * Usage in a 2:1 grid
 * ```
 * <Grid columns="3" >
 *   <GridCell colSpan="2"/>
 *   <GridCell colSpan="1"/>
 * </Grid>
 * ```
 */
const GridCell = React.forwardRef((props, forwardedRef) => {
    const { asChild, dataTestId, css: whatIsThis, // TODO find out why this is here
    justify, align, place, colSpan, colStart, rowSpan, rowStart, className, children, ...rest } = props;
    const Comp = asChild ? Slot : 'div';
    return (React.createElement(Comp, { ref: forwardedRef, className: tw$1(gridStyle({
            justify,
            align,
            place,
            colSpan,
            colStart,
            rowSpan,
            rowStart,
        }), className), "data-uds-child": "grid-cell", "data-test-id": dataTestId, ...rest }, children));
});
GridCell.displayName = 'GridCell';

const spacerStyle = style({
    variants: {
        /**
         * The visual orientation of the divider. Sets the margin axis
         *
         * @defaultValue horizontal
         * @example
         * ```jsx
         * <Spacer orientation="vertical" />
         * ```
         */
        orientation: {
            horizontal: 'w-full h-0',
            vertical: 'w-0',
        },
        /**
         * Responsive margin before and after the spacer
         *
         * @example
         * ```jsx
         * <Spacer gap="none" />
         * ```
         *
         * @example
         * ```jsx
         * <Spacer gap={{_:'sm', md:'base'}} />
         * ```
         */
        gap: {
            min: '',
            auto: '',
            max: '',
            none: '',
            px: '',
            xxs: '',
            xs: '',
            sm: '',
            base: '',
            md: '',
            lg: '',
            xl: '',
            '2xl': '',
            '3xl': '',
            '4xl': '',
            '5xl': '',
        },
    },
    matches: [
        {
            orientation: 'horizontal',
            gap: 'min',
            use: `mt-${UDS_GAP.min} mb-0 mx-0`,
        },
        {
            orientation: 'horizontal',
            gap: 'auto',
            use: `mt-${UDS_GAP.auto} mb-0 mx-0`,
        },
        {
            orientation: 'horizontal',
            gap: 'max',
            use: `mt-${UDS_GAP.max} mb-0 mx-0`,
        },
        {
            orientation: 'horizontal',
            gap: 'none',
            use: `mt-${UDS_GAP.none} mb-0 mx-0`,
        },
        {
            orientation: 'horizontal',
            gap: 'px',
            use: `mt-${UDS_GAP.px} mb-0 mx-0`,
        },
        {
            orientation: 'horizontal',
            gap: 'xxs',
            use: `mt-${UDS_GAP.xxs} mb-0 mx-0`,
        },
        {
            orientation: 'horizontal',
            gap: 'xs',
            use: `mt-${UDS_GAP.xs} mb-0 mx-0`,
        },
        {
            orientation: 'horizontal',
            gap: 'sm',
            use: `mt-${UDS_GAP.sm} mb-0 mx-0`,
        },
        {
            orientation: 'horizontal',
            gap: 'base',
            use: `mt-${UDS_GAP.base} mb-0 mx-0`,
        },
        {
            orientation: 'horizontal',
            gap: 'lg',
            use: `mt-${UDS_GAP.lg} mb-0 mx-0`,
        },
        {
            orientation: 'horizontal',
            gap: 'xl',
            use: `mt-${UDS_GAP.xl} mb-0 mx-0`,
        },
        {
            orientation: 'horizontal',
            gap: '2xl',
            use: `mt-${UDS_GAP['2xl']} mb-0 mx-0`,
        },
        {
            orientation: 'horizontal',
            gap: '3xl',
            use: `mt-${UDS_GAP['3xl']} mb-0 mx-0`,
        },
        {
            orientation: 'horizontal',
            gap: '4xl',
            use: `mt-${UDS_GAP['4xl']} mb-0 mx-0`,
        },
        {
            orientation: 'horizontal',
            gap: '5xl',
            use: `mt-${UDS_GAP['5xl']} mb-0 mx-0`,
        },
        {
            orientation: 'vertical',
            gap: 'min',
            use: `ml--${UDS_GAP.min} mr-0 rtl:(ml-0 mr--${UDS_GAP.min}) my-0`,
        },
        {
            orientation: 'vertical',
            gap: 'auto',
            use: `ml--${UDS_GAP.auto} mr-0 rtl:(ml-0 mr--${UDS_GAP.auto}) my-0`,
        },
        {
            orientation: 'vertical',
            gap: 'max',
            use: `ml--${UDS_GAP.max} mr-0 rtl:(ml-0 mr--${UDS_GAP.max}) my-0`,
        },
        {
            orientation: 'vertical',
            gap: 'none',
            use: `ml-${UDS_GAP.none} mr-0 rtl:(ml-0 mr-${UDS_GAP.none}) my-0`,
        },
        {
            orientation: 'vertical',
            gap: 'px',
            use: `ml-${UDS_GAP.px} mr-0 rtl:(ml-0 mr-${UDS_GAP.px}) my-0`,
        },
        {
            orientation: 'vertical',
            gap: 'xxs',
            use: `ml-${UDS_GAP.xxs} mr-0 rtl:(ml-0 mr-${UDS_GAP.xxs}) my-0`,
        },
        {
            orientation: 'vertical',
            gap: 'xs',
            use: `ml-${UDS_GAP.xs} mr-0 rtl:(ml-0 mr-${UDS_GAP.xs}) my-0`,
        },
        {
            orientation: 'vertical',
            gap: 'sm',
            use: `ml-${UDS_GAP.sm} mr-0 rtl:(ml-0 mr-${UDS_GAP.sm}) my-0`,
        },
        {
            orientation: 'vertical',
            gap: 'base',
            use: `ml-${UDS_GAP.base} mr-0 rtl:(ml-0 mr-${UDS_GAP.base}) my-0`,
        },
        {
            orientation: 'vertical',
            gap: 'lg',
            use: `ml-${UDS_GAP.lg} mr-0 rtl:(ml-0 mr-${UDS_GAP.lg}) my-0`,
        },
        {
            orientation: 'vertical',
            gap: 'xl',
            use: `ml-${UDS_GAP.xl} rtl:(ml-0 mr-${UDS_GAP.xl}) my-0`,
        },
        {
            orientation: 'vertical',
            gap: '2xl',
            use: `ml-${UDS_GAP['2xl']} rtl:(ml-0 mr-${UDS_GAP['2xl']}) my-0`,
        },
        {
            orientation: 'vertical',
            gap: '3xl',
            use: `ml-${UDS_GAP['3xl']} rtl:(ml-0 mr-${UDS_GAP['3xl']}) my-0`,
        },
        {
            orientation: 'vertical',
            gap: '4xl',
            use: `ml-${UDS_GAP['4xl']} rtl:(ml-0 mr-${UDS_GAP['4xl']}) my-0`,
        },
        {
            orientation: 'vertical',
            gap: '5xl',
            use: `ml-${UDS_GAP['5xl']} rtl:(ml-0 mr-${UDS_GAP['5xl']}) my-0`,
        },
    ],
});
/**
 * The `Spacer` component is vertical or hozitonal spacer between elements.
 *
 * @example
 * ```jsx
 * <Spacer gap="base"/>
 * ```
 *
 * @example
 * ```jsx
 * <Spacer gap={{_:'sm', md:'base'}} />
 * ```
 */
const Spacer = React.forwardRef((props, forwadedRef) => {
    const { orientation = 'horizontal', gap = 'base', className, dataTestId, ...rest } = props;
    return (React.createElement("div", { ref: forwadedRef, "data-uds-child": "spacer", "data-test-id": dataTestId, className: tw$1('flex-none', className, spacerStyle({
            gap: gap,
            orientation: orientation,
        })), ...rest }));
});
Spacer.displayName = 'Spacer';

const DialogContext = createContext(undefined);
const DialogProvider = ({ children, value, }) => React.createElement(DialogContext.Provider, { value: value }, children);
const useDialogContext = () => {
    const context = useContext(DialogContext);
    if (!context) {
        throw new Error('useDialogContext must be used within a DialogProvider');
    }
    return context;
};

/**
 * The `DialogRoot` component is the root component needed to render a dialog.
 * It wraps the trigger that opens the dialog and the dialog content itself.
 *
 * @example
 * ```
 * <DialogRoot>
 *   <DialogTrigger>Open Dialog</DialogTrigger>
 *   <DialogContent>
 *     <DialogHeader>
 *       <DialogHeading>Title</DialogHeading>
 *     </DialogHeader>
 *     <DialogBody>
 *       <DialogDescription>Description</DialogDescription>
 *     </DialogBody>
 *     <DialogFooter>
 *       <ButtonCta label="Submit" />
 *     </DialogFooter>
 *   </DialogContent>
 * </DialogRoot>
 * ```
 */
const DialogRoot = ({ children, modal = true, autoClose, autoFocus = 'content', invert = false, size = 'md', align = 'center', justify = 'center', drawer = false, dataTestId, open, onOpenChange, onEscapeKeyDown, ...rest }) => {
    const closeButtonRef = useRef();
    const contextProps = {
        modal,
        invert,
        size,
        justify,
        align,
        drawer,
        autoClose,
        autoFocus,
        dataTestId,
        closeButtonRef,
        onOpenChange,
        onEscapeKeyDown,
    };
    return (React.createElement(RadixDialog.Root, { modal: modal, open: open, onOpenChange: onOpenChange, ...rest },
        React.createElement(DialogProvider, { value: contextProps }, children)));
};

const fadeIn$1 = animation('0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards', {
    '0%': {
        opacity: 0,
    },
    '100%': {
        opacity: 1,
    },
});
animation('0.3s cubic-bezier(0.5, 1, 0.89, 1) forwards', {
    '0%': {
        opacity: 1,
    },
    '100%': {
        opacity: 0,
    },
});
const slideInBottom = animation('0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards', {
    '0%': {
        transform: 'translate3d(0, 5%, 0)',
    },
    '100%': {
        transform: 'translate3d(0, 0, 0)',
    },
});
const slideInBottomCentered = animation('0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards', {
    '0%': {
        transform: 'translate3d(-50%, -45%, 0)',
    },
    '100%': {
        transform: 'translate3d(-50%, -50%, 0)',
    },
});
const slideInBottomCenteredX = animation('0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards', {
    '0%': {
        transform: 'translate3d(-50%, 5%, 0)',
    },
    '100%': {
        transform: 'translate3d(-50%, 0, 0)',
    },
});
const slideInBottomCenteredY = animation('0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards', {
    '0%': {
        transform: 'translate3d(0, -45%, 0)',
    },
    '100%': {
        transform: 'translate3d(0, -50%, 0)',
    },
});
const slideInLeft = animation('0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards', {
    '0%': {
        transform: 'translate3d(-20%, 0, 0)',
    },
    '100%': {
        transform: 'translate3d(0, 0, 0)',
    },
});
const slideInRight = animation('0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards', {
    '0%': {
        transform: 'translate3d(20%, 0, 0)',
    },
    '100%': {
        transform: 'translate3d(0, 0, 0)',
    },
});

const maxAvailableHeight = `
  height: 100vh;
  /* Fixes height of dialog on iOS Safari */
  max-height: -webkit-fill-available;
`;
const overlayStyle = style({
    base: css$1 `
    ${fadeIn$1}
    @apply fixed z-dialog inset-0 flex overflow-y-auto bg-black bg-opacity-50;
  `,
    variants: {
        size: {
            xs: '',
            sm: '',
            md: '',
            lg: '',
            xl: '',
            full: '',
        },
        drawer: {
            true: '',
            false: '',
        },
    },
    matches: [
        {
            drawer: false,
            size: 'xs',
            use: 'p-4 md:p-6',
        },
        {
            drawer: false,
            size: 'sm',
            use: 'p-4 md:p-6',
        },
        {
            drawer: false,
            size: 'md',
            use: 'p-4 md:p-6',
        },
        {
            drawer: false,
            size: 'lg',
            use: 'p-4 md:p-6',
        },
        {
            drawer: false,
            size: 'xl',
            use: 'p-4 md:p-6',
        },
        {
            drawer: false,
            size: 'full',
            use: 'p-0 md:p-0',
        },
        // Drawer
        {
            drawer: true,
            size: 'xs',
            use: 'p-0 md:p-0',
        },
        {
            drawer: true,
            size: 'sm',
            use: 'p-0 md:p-0',
        },
        {
            drawer: true,
            size: 'md',
            use: 'p-0 md:p-0',
        },
        {
            drawer: true,
            size: 'lg',
            use: 'p-0 md:p-0',
        },
        {
            drawer: true,
            size: 'xl',
            use: 'p-0 md:p-0',
        },
        {
            drawer: true,
            size: 'full',
            use: 'p-0 md:p-0',
        },
    ],
});
const contentStyle$2 = style({
    base: 'relative flex flex-col w-full overflow-hidden focus-visible:(outline-none ring ring-1 ring-inset ring-offset-1)',
    variants: {
        size: {
            xs: 'max-w-screen-2xs max-h-auto h-auto rounded-md',
            sm: 'max-w-screen-xs max-h-auto h-auto rounded-md',
            md: 'max-w-screen-sm max-h-auto h-auto rounded-md',
            lg: 'max-w-screen-md max-h-auto h-auto rounded-md',
            xl: 'max-w-screen-lg max-h-auto h-auto rounded-md',
            full: css$1 `
        ${maxAvailableHeight}
        @apply rounded-none max-w-full;
      `,
        },
        modal: {
            true: 'relative w-full',
            // Minus the gutter width either side to prevent dialog from overflowing
            // on smaller screens - necessary as it is a fixed element.
            false: 'fixed w-[calc(100%-2rem)] lg:w-[calc(100%-3rem)] shadow-xl border-1',
        },
        drawer: {
            true: css$1 `
        ${maxAvailableHeight}
        @apply rounded-none;
      `,
            false: '',
        },
        invert: {
            true: 'bg-neutral-100 text-white border-neutral-80',
            false: 'bg-white text-neutral-100 border-neutral-20',
        },
        align: {
            start: '',
            center: '',
            end: '',
        },
        justify: {
            start: '',
            center: '',
            end: '',
        },
    },
    matches: [
        {
            invert: true,
            size: 'full',
            use: 'bg-neutral-100',
        },
        {
            invert: false,
            size: 'full',
            use: 'bg-white',
        },
        /**
         * Modal dialog
         * - Aligned using margin as it is in a fixed, flex container
         */
        {
            align: 'start',
            modal: true,
            use: 'mb-auto mt-0',
        },
        {
            align: 'center',
            modal: true,
            use: 'my-auto',
        },
        {
            align: 'end',
            modal: true,
            use: 'mt-auto mb-0',
        },
        {
            justify: 'start',
            modal: true,
            drawer: false,
            use: css$1 `
        ${slideInBottom}
        @apply mr-auto ml-0;
      `,
        },
        {
            justify: 'center',
            modal: true,
            drawer: false,
            use: css$1 `
        ${slideInBottom}
        @apply mx-auto;
      `,
        },
        {
            justify: 'end',
            modal: true,
            drawer: false,
            use: css$1 `
        ${slideInBottom}
        @apply ml-auto mr-0;
      `,
        },
        // drawer
        {
            modal: true,
            drawer: true,
            justify: 'start',
            use: css$1 `
        ${slideInLeft}
        @apply mr-auto ml-0;
      `,
        },
        {
            modal: true,
            drawer: true,
            justify: 'center',
            use: css$1 `
        ${slideInLeft}
        @apply mr-auto ml-0;
      `,
        },
        {
            modal: true,
            justify: 'end',
            drawer: true,
            use: css$1 `
        ${slideInRight}
        @apply ml-auto mr-0;
      `,
        },
        /**
         * Non-modal dialog
         * - Fixed positioning
         */
        {
            align: 'start',
            modal: false,
            drawer: false,
            use: css$1 `
        ${slideInBottom}
        @apply top-6 bottom-auto;
      `,
        },
        {
            align: 'center',
            modal: false,
            drawer: false,
            use: css$1 `
        ${slideInBottomCenteredY}
        @apply top-1/2 -translate-y-1/2 bottom-auto;
      `,
        },
        {
            align: 'end',
            modal: false,
            drawer: false,
            use: css$1 `
        ${slideInBottom}
        @apply bottom-6 top-auto;
      `,
        },
        {
            justify: 'start',
            modal: false,
            drawer: false,
            use: 'left-6 right-auto',
        },
        {
            justify: 'center',
            modal: false,
            drawer: false,
            use: css$1 `
        ${slideInBottomCenteredX}
        @apply left-1/2 -translate-x-1/2 right-auto;
      `,
        },
        {
            justify: 'end',
            modal: false,
            drawer: false,
            use: 'right-6 left-auto',
        },
        {
            align: 'center',
            justify: 'center',
            modal: false,
            drawer: false,
            use: css$1 `
        ${slideInBottomCentered}
        @apply top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 right-auto bottom-auto;
      `,
        },
        // drawer
        {
            modal: false,
            justify: 'start',
            drawer: true,
            use: css$1 `
        ${slideInLeft}
        @apply top-0 left-0 right-auto border-r border-t-0 border-b-0 border-l-0',
      `,
        },
        {
            modal: false,
            justify: 'center',
            drawer: true,
            use: css$1 `
        ${slideInLeft}
        @apply top-0 left-0 right-auto border-r border-l-0 border-t-0 border-b-0',
      `,
        },
        {
            modal: false,
            justify: 'end',
            drawer: true,
            use: css$1 `
        ${slideInRight}
        @apply top-0 right-0 left-auto border-l border-r-0 border-t-0 border-b-0',
      `,
        },
    ],
});

const DialogContentInner = forwardRef(function DialogContentInner({ children, className, asChild, ...rest }, forwardedRef) {
    const { size, align, justify, modal, invert, drawer, autoClose, dataTestId, autoFocus, closeButtonRef, onOpenChange, onEscapeKeyDown, } = useDialogContext();
    const contentRef = useRef();
    const sharedRef = useSharedRef(forwardedRef, contentRef);
    const handleAutoFocus = (e) => {
        if (autoFocus !== 'close') {
            e.preventDefault();
        }
        if (autoFocus === 'content') {
            contentRef.current.focus();
        }
    };
    // Auto close useEffect
    useEffect(() => {
        let timeout;
        if (autoClose && open) {
            timeout = setTimeout(() => {
                if (onOpenChange) {
                    // Controlled dialogs
                    // Call the onOpenChange callback to close the dialog
                    onOpenChange(false);
                }
                else {
                    // Uncontrolled dialogs
                    // Simulate a click event on the close button to close the dialog
                    closeButtonRef.current.click();
                }
            }, autoClose);
        }
        else {
            // Reset timeout if open={false}
            clearTimeout(timeout);
        }
        // Clean up timeout on unmount
        return () => {
            if (timeout) {
                clearTimeout(timeout);
            }
        };
    }, [autoClose, open, onOpenChange]);
    const currentSize = useResponsiveValue(size);
    return (React.createElement(RadixDialog.Content, { "data-test-id": dataTestId && `${dataTestId}-content`, "data-uds-child": "dialog-content", className: tw$1(contentStyle$2({ size, modal, invert, align, justify, drawer }), currentSize === 'full' && 'animate-none', className), onOpenAutoFocus: handleAutoFocus, onEscapeKeyDown: onEscapeKeyDown, asChild: asChild, ref: sharedRef, ...rest }, children));
});
/**
 * The `DialogContent` component is used to wrap the content of a dialog
 * It renders a close button and any children provided, including body and footer.
 *
 * @example
 * ```
 * <DialogContent>
 *   <DialogHeader>
 *     <DialogHeading>Dialog Heading</DialogHeading>
 *   </DialogHeader>
 *   <DialogBody>
 *     <DialogDescription>Dialog Description</DialogDescription>
 *   </DialogBody>
 *   <DialogFooter>
 *     <ButtonCta label="Submit" />
 *   </DialogFooter>
 * </DialogContent>
 * ```
 */
const DialogContent = forwardRef(function DialogContent({ children, ...rest }, forwardedRef) {
    const { size, drawer, modal, dataTestId } = useDialogContext();
    return (React.createElement(RadixDialog.Portal, null, modal ? (React.createElement(RadixDialog.Overlay, { className: tw$1(overlayStyle({ size, drawer })), "data-test-id": dataTestId && `${dataTestId}-overlay`, "data-uds-child": "dialog-overlay" },
        React.createElement(DialogContentInner, { ...rest, ref: forwardedRef }, children))) : (React.createElement(DialogContentInner, { ...rest, ref: forwardedRef }, children))));
});

/**
 * The `DialogTrigger` component should wrap the element opens the dialog.
 * It will render as the child element.
 *
 * @example
 * ```
 * <DialogTrigger>
 *   <ButtonCta label="Open Dialog" />
 * </DialogTrigger>
 * ```
 */
const DialogTrigger = ({ children }) => {
    const { dataTestId } = useDialogContext();
    return (React.createElement(RadixDialog.DialogTrigger, { "data-test-id": dataTestId && `${dataTestId}-trigger`, asChild: true }, children));
};

/**
 * The `DialogHeader` component is used to render a header specifically for Dialogs.
 *
 * @example
 * ```
 * <DialogHeader>
 *   <DialogHeading>Title</DialogHeading>
 * </DialogHeader>
 * ```
 */
const DialogHeader = ({ children, className }) => {
    const { dataTestId, invert, closeButtonRef } = useDialogContext();
    const t = useUdsTranslate();
    return (React.createElement(Container, { asChild: true },
        React.createElement("header", { className: tw$2('flex items-start pt-5 pb-4 px-6', className), "data-test-id": dataTestId && `${dataTestId}-header`, "data-uds-child": "dialog-header" },
            children,
            React.createElement(RadixDialog.Close, { asChild: true },
                React.createElement(ButtonIcon, { className: "absolute flex-shrink-0 top-3.5 right-3 rtl:(left-3 mr-auto)", icon: "close", label: t('close'), variant: "ghost", invert: invert, ref: closeButtonRef, dataTestId: dataTestId && `${dataTestId}-close` })))));
};

/**
 * The `DialogBody` component is used to wrap the body content of a dialog, below the header.
 * It can contain a `DialogDescription` component, forms or any other content.
 *
 * @example
 * ```
 * <DialogBody>
 *   <DialogDescription>Dialog Description</DialogDescription>
 * </DialogBody>
 * ```
 */
const DialogBody = ({ children, className }) => {
    const { dataTestId } = useDialogContext();
    return (React.createElement(Container, { asChild: true },
        React.createElement("div", { "data-test-id": dataTestId && `${dataTestId}-body`, "data-uds-child": "dialog-body", className: tw$2('flex-1 px-6', className) }, children)));
};

const footerStyle$2 = style({
    base: 'flex gap-4 p-6 children:flex-grow-1',
    variants: {
        size: {
            xs: 'flex-col',
            sm: 'flex-row-reverse flex-wrap sm:max-w-max sm:ml-auto sm:rtl:(mr-auto ml-0)',
            md: 'flex-row-reverse flex-wrap sm:max-w-max sm:ml-auto sm:rtl:(mr-auto ml-0)',
            lg: 'flex-row-reverse flex-wrap sm:max-w-max sm:ml-auto sm:rtl:(mr-auto ml-0) ',
            xl: 'flex-row-reverse flex-wrap sm:max-w-max sm:ml-auto sm:rtl:(mr-auto ml-0)',
            full: 'flex-row-reverse flex-wrap sm:max-w-max sm:ml-auto sm:rtl:(mr-auto ml-0)',
        },
        bleed: {
            true: 'w-full sm:max-w-full',
            false: '',
        },
    },
});
/**
 * The `DialogFooter` component wraps the call to actions of a dialog.
 * It can contain `ButtonCta` and `LinkCta` components.
 *
 * @example
 * ```
 * <DialogFooter>
 *   <ButtonCta label="Cancel" />
 *   <LinkCta label="Go to Homepage" href="/" />
 * </DialogFooter>
 * ```
 */
const DialogFooter = ({ children, bleed, className, }) => {
    const { size, dataTestId } = useDialogContext();
    return (React.createElement(Container, null,
        React.createElement("div", { "data-test-id": dataTestId && `${dataTestId}-footer`, className: tw$2(footerStyle$2({ size, bleed }), className) }, children)));
};

/**
 * The `DialogHeading` component is used to render a heading specifically for Dialogs.
 *
 * It's a wrapper around the Heading component with some different defaults.
 *
 * By default renders a h1. To render as a custom element, use the `asChild` prop.
 *
 * @example
 * ```
 * <DialogHeading>Title</DialogHeading>
 * ```
 */
const DialogHeading = React.forwardRef(function DialogHeading({ children, size = 'sm', asChild, ...props }, ref) {
    const { dataTestId, invert } = useDialogContext();
    return (React.createElement(RadixDialog.Title, { "data-test-id": dataTestId && `${dataTestId}-heading`, asChild: true },
        React.createElement(Heading, { size: size, invert: invert, ref: ref, asChild: true, ...props }, asChild ? children : React.createElement("h1", null, children))));
});

/**
 * The `DialogDescription` component is used to render a description specifically for Dialogs.
 *
 * It's a wrapper around the Text component with some different defaults.
 *
 * By default renders a `p`. To render as a custom element, use the `asChild` prop.
 *
 * @example
 * ```
 * <DialogDescription>Description</DialogDescription>
 * ```
 */
const DialogDescription = React.forwardRef(function DialogDescription({ children, size = 'base', asChild, ...props }, ref) {
    const { dataTestId, invert } = useDialogContext();
    return (React.createElement(RadixDialog.Description, { "data-test-id": dataTestId && `${dataTestId}-description`, asChild: true },
        React.createElement(Text, { size: size, invert: invert, ref: ref, asChild: true, ...props }, asChild ? children : React.createElement("p", null, children))));
});

const AlertDialogContext = createContext(undefined);
const AlertDialogProvider = ({ children, value, }) => (React.createElement(AlertDialogContext.Provider, { value: value }, children));
const useAlertDialogContext = () => {
    const context = useContext(AlertDialogContext);
    if (!context) {
        throw new Error('useAlertDialogContext must be used within a AlertDialogProvider');
    }
    return context;
};

/**
 * The `AlertDialogAction` component should wrap each positive action in an alert dialog.
 * It will render as the child element.
 *
 * @example
 * ```
 * <AlertDialogAction>
 *   <ButtonCta label="Permanently Delete" />
 * </AlertDialogAction>
 * ```
 */
const AlertDialogAction = ({ children }) => {
    const { dataTestId } = useAlertDialogContext();
    return (React.createElement(RadixAlertDialog.Action, { "data-test-id": dataTestId && `${dataTestId}-action`, asChild: true }, children));
};

/**
 * The `AlertDialogBody` component is used to wrap the body content of a dialog, below the header and before the footer.
 * Should be used to provide additional context to the user.
 *
 * @example
 * ```
 * <AlertDialogBody>
 *   <Text>Description</Text>
 * </AlertDialogBody>
 * ```
 */
const AlertDialogBody = ({ children, className, asChild, }) => {
    const { dataTestId } = useAlertDialogContext();
    const Comp = asChild ? Slot : 'div';
    return (React.createElement(Comp, { "data-test-id": dataTestId && `${dataTestId}-body`, "data-uds-child": "alert-dialog-body", className: tw$2('px-6', className) }, children));
};

/**
 * The `AlertDialogCancel` component should wrap the cancel action in an alert dialog.
 * It will render as the child element.
 *
 * @example
 * ```
 * <AlertDialogCancel>
 *   <ButtonCta label="Cancel" />
 * </AlertDialogCancel>
 * ```
 */
const AlertDialogCancel = ({ children }) => {
    const { dataTestId } = useAlertDialogContext();
    return (React.createElement(RadixAlertDialog.Cancel, { "data-test-id": dataTestId && `${dataTestId}-cancel`, asChild: true }, children));
};

const contentStyle$1 = style({
    base: 'flex flex-col w-full rounded-md overflow-hidden animate-slide-in-bottom relative m-auto focus-visible:(outline-none ring ring-1 ring-inset ring-offset-1)',
    variants: {
        size: {
            xs: 'max-w-screen-2xs',
            sm: 'max-w-screen-xs',
            md: 'max-w-screen-sm',
            lg: 'max-w-screen-md',
            xl: 'max-w-screen-lg',
        },
        invert: {
            true: 'bg-neutral-100 text-white border-neutral-80',
            false: 'bg-white text-neutral-100 border-neutral-20',
        },
    },
});
/**
 * The `AlertDialogContent` component is used to wrap the content of a dialog.
 *
 * @example
 * ```
 * <AlertDialogContent>
 *   <AlertDialogHeader>
 *     <AlertDialogHeading>Title</AlertDialogHeading>
 *   </AlertDialogHeader>
 *   <AlertDialogBody>
 *     <AlertDialogDescription>Description</AlertDialogDescription>
 *   </AlertDialogBody>
 *   <AlertDialogFooter>
 *     <AlertDialogAction>
 *        <ButtonCta label="Accept" />
 *      </AlertDialogAction>
 *     <AlertDialogCancel>
 *        <ButtonCta label="Cancel" />
 *      </AlertDialogCancel>
 *   </AlertDialogFooter>
 * </AlertDialogContent>
 * ```
 */
const AlertDialogContent = ({ children, className, asChild, ...rest }) => {
    const { autoFocus, size, invert, dataTestId, onEscapeKeyDown } = useAlertDialogContext();
    const contentRef = useRef(null);
    const handleAutoFocus = (e) => {
        if (autoFocus !== 'cancel') {
            e.preventDefault();
        }
        if (autoFocus === 'content') {
            contentRef.current.focus();
        }
    };
    return (React.createElement(RadixAlertDialog.Portal, null,
        React.createElement(RadixAlertDialog.Overlay, { "data-test-id": dataTestId && `${dataTestId}-overlay`, "data-uds-child": "alert-dialog-overlay", className: "fixed z-dialog inset-0 p-6 flex overflow-y-auto bg-black bg-opacity-50 animate-fade-in" },
            React.createElement(RadixAlertDialog.Content, { "data-test-id": dataTestId && `${dataTestId}-content`, "data-uds-child": "alert-dialog-content", className: tw$1(contentStyle$1({ size, invert }), className), onOpenAutoFocus: handleAutoFocus, onEscapeKeyDown: onEscapeKeyDown, asChild: asChild, ref: contentRef, ...rest }, children))));
};

/**
 * The `AlertDialogDescription` component is used to render a description specifically for AlertDialogs.
 *
 * It's a wrapper around the Text component with some different defaults.
 *
 * By default renders a `p`. To render as a custom element, use the `asChild` prop.
 *
 * @example
 * ```
 * <AlertDialogDescription>Description</AlertDialogDescription>
 * ```
 */
const AlertDialogDescription = React.forwardRef(function AlertDialogDescription({ children, size = 'base', asChild, ...props }, ref) {
    const { dataTestId, invert } = useAlertDialogContext();
    return (React.createElement(RadixAlertDialog.Description, { "data-test-id": dataTestId && `${dataTestId}-description`, asChild: true },
        React.createElement(Text, { size: size, invert: invert, ref: ref, asChild: true, ...props }, asChild ? children : React.createElement("p", null, children))));
});

const footerStyle$1 = style({
    base: 'flex gap-4 p-6 children:flex-grow-1',
    variants: {
        size: {
            xs: 'flex-col',
            sm: 'flex-row-reverse flex-wrap sm:max-w-max sm:ml-auto sm:rtl:(mr-auto ml-0)',
            md: 'flex-row-reverse flex-wrap sm:max-w-max sm:ml-auto sm:rtl:(mr-auto ml-0)',
            lg: 'flex-row-reverse flex-wrap sm:max-w-max sm:ml-auto sm:rtl:(mr-auto ml-0) ',
            xl: 'flex-row-reverse flex-wrap sm:max-w-max sm:ml-auto sm:rtl:(mr-auto ml-0)',
        },
    },
});
/**
 * The `AlertDialogFooter` component wraps the actions of an alert dialog.
 * It should contain a positive action (e.g. "Confirm") and a negative action (e.g. "Cancel").
 *
 * @example
 * ```
 * <AlertDialogFooter>
 *   <AlertDialogAction>
 *      <ButtonCta label="Confirm" />
 *    </AlertDialogAction>
 *   <AlertDialogCancel>
 *      <ButtonCta label="Cancel" />
 *    </AlertDialogCancel>
 * </AlertDialogFooter>
 * ```
 */
const AlertDialogFooter = ({ children, className, }) => {
    const { size, dataTestId } = useAlertDialogContext();
    return (React.createElement("footer", { "data-test-id": dataTestId && `${dataTestId}-footer`, "data-uds-child": "alert-dialog-footer", className: tw$2(footerStyle$1({ size }), className) }, children));
};

/**
 * The `AlertDialogHeader` component is used to render a header specifically for AlertDialogs.
 *
 * @example
 * ```
 * <AlertDialogHeader>
 *   <AlertDialogHeading>Title</AlertDialogHeading>
 * </AlertDialogHeader>
 * ```
 */
const AlertDialogHeader = ({ children, className, }) => {
    const { dataTestId } = useAlertDialogContext();
    return (React.createElement("header", { className: tw$2('flex flex-col gap-4 p-6', className), "data-test-id": dataTestId && `${dataTestId}-header`, "data-uds-child": "alert-dialog-header" }, children));
};

/**
 * The `AlertDialogHeading` component is used to render a heading specifically for AlertDialogs.
 *
 * It's a wrapper around the Heading component with some different defaults.
 *
 * By default renders a h1. To render as a custom element, use the `asChild` prop.
 *
 * @example
 * ```
 * <AlertDialogHeading>Title</AlertDialogHeading>
 * ```
 */
const AlertDialogHeading = React.forwardRef(function AlertDialogHeading({ children, size = 'sm', asChild, ...props }, ref) {
    const { dataTestId, invert } = useAlertDialogContext();
    return (React.createElement(RadixAlertDialog.Title, { "data-test-id": dataTestId && `${dataTestId}-heading`, asChild: true },
        React.createElement(Heading, { size: size, invert: invert, ref: ref, asChild: true, ...props }, asChild ? children : React.createElement("h1", null, children))));
});

/**
 * The `AlertDialogRoot` component is the root component needed to render an alert dialog.
 * It wraps the trigger that opens the alert dialog and the alert dialog content itself.
 *
 * @example
 * ```
 * <AlertDialogRoot>
 *   <AlertDialogTrigger>Open Dialog</AlertDialogTrigger>
 *   <AlertDialogContent>
 *     <AlertDialogHeader>
 *       <AlertDialogHeading>Title</AlertDialogHeading>
 *     </AlertDialogHeader>
 *     <AlertDialogBody>
 *       <Text>Description</Text>
 *     </AlertDialogBody>
 *     <AlertDialogFooter>
 *       <AlertDialogAction>
 *          <ButtonCta label="Accept" />
 *        </AlertDialogAction>
 *       <AlertDialogCancel>
 *          <ButtonCta label="Cancel" />
 *        </AlertDialogCancel>
 *     </AlertDialogFooter>
 *   </AlertDialogContent>
 * </AlertDialogRoot>
 * ```
 */
const AlertDialogRoot = ({ children, invert = false, size = 'md', open, autoFocus = 'cancel', dataTestId, onOpenChange, onEscapeKeyDown, ...rest }) => {
    const contextProps = {
        invert,
        size,
        open,
        autoFocus,
        dataTestId,
        onOpenChange,
        onEscapeKeyDown,
    };
    return (React.createElement(RadixAlertDialog.Root, { open: open, onOpenChange: onOpenChange, ...rest },
        React.createElement(AlertDialogProvider, { value: contextProps }, children)));
};

/**
 * The `AlertDialogTrigger` component should wrap the element opens the alert dialog.
 * It will render as the child element.
 *
 * @example
 * ```
 * <AlertDialogTrigger>
 *   <ButtonCta label="Remove Card" />
 * </AlertDialogTrigger>
 * ```
 */
const AlertDialogTrigger = ({ children }) => {
    const { dataTestId } = useAlertDialogContext();
    return (React.createElement(RadixAlertDialog.Trigger, { "data-test-id": dataTestId && `${dataTestId}-trigger`, asChild: true }, children));
};

const PopoverContext = createContext(undefined);
const PopoverProvider = ({ children, value, }) => (React.createElement(PopoverContext.Provider, { value: value }, children));
const usePopoverContext = () => {
    const context = useContext(PopoverContext);
    if (!context) {
        throw new Error('usePopoverContext must be used within a PopoverProvider');
    }
    return context;
};

/**
 * The `PopoverRoot` component is the root component needed to render a popover.
 * It wraps the trigger that opens the popover and the popover content itself.
 *
 * @example
 * ```
 * <PopoverRoot>
 *   <PopoverTrigger>Open Popover</PopoverTrigger>
 *   <PopoverContent>
 *     <PopoverBody>
 *        <PopoverHeading srOnly>...</PopoverHeading>
 *        ...
 *     </PopoverBody>
 *     <PopoverClose />
 *   </PopoverContent>
 * </PopoverRoot>
 * ```
 */
const PopoverRoot = ({ id, size = 'base', align = 'start', side = 'bottom', arrow = true, distance = 12, autoFocus, dataTestId, children, ...rest }) => {
    const popId = useId(id);
    return (React.createElement(PopoverProvider, { value: {
            id: popId,
            dataTestId,
            size,
            align,
            side,
            arrow,
            distance,
            autoFocus: autoFocus ?? 'auto',
            ...rest,
        } },
        React.createElement(RadixPopover.Root, { ...rest }, children)));
};

const POPOVER_SIZE_MAP = {
    xs: [
        css `
      --popover-max-width: 12rem;
    `,
    ],
    sm: [
        css `
      --popover-max-width: 15rem;
    `,
    ],
    base: [
        css `
      --popover-max-width: 20rem;
    `,
    ],
    lg: [
        css `
      --popover-max-width: 24rem;
    `,
    ],
    xl: [
        css `
      --popover-max-width: 28rem;
    `,
    ],
};
const POPOVER_CLOSE_BUTTON_SIZE_MAP = {
    xs: ['top-2', 'right-1.5', 'rtl:(right-auto left-1.5)'],
    sm: ['top-3', 'right-2.5', 'rtl:(right-auto left-2.5)'],
    base: ['top-3', 'right-2.5', 'rtl:(right-auto left-2.5)'],
    lg: ['top-4', 'right-3.5', 'rtl:(right-auto left-3.5)'],
    xl: ['top-4', 'right-3.5', 'rtl:(right-auto left-3.5)'],
};
const POPOVER_HEADER_SIZE_MAP = {
    xs: 'p-3 pb-0',
    sm: 'p-4 pb-0',
    base: 'p-4 pb-0',
    lg: 'p-6 pb-0',
    xl: 'p-6 pb-0',
};
const POPOVER_BODY_SIZE_MAP = {
    xs: 'p-3',
    sm: 'p-4',
    base: 'p-4',
    lg: 'p-6',
    xl: 'p-6',
};

/**
 * The `PopoverContent` component is used to wrap the contents of a popover.
 *
 * @example
 * ```
 * <PopoverContent arrow>
 *   <PopoverClose />
 *   <PopoverBody>...</PopoverBody>
 * </PopoverContent>
 * ```
 */
const PopoverContent = ({ children, className, zIndex = 20, ...rest }) => {
    const popoverContent = useRef(null);
    const { id, size, invert, side, align, distance, offset, arrow, autoFocus } = usePopoverContext();
    const { dataTestId } = usePopoverContext();
    const colorStyle = useResponsiveProp(invert, invert => invert
        ? 'bg-neutral-100 border-neutral-80 text-neutral-0'
        : 'bg-neutral-0 border-neutral-20 text-neutral-100');
    const sizeStyle = useResponsiveProp(size, size => tw$2(POPOVER_SIZE_MAP[size]));
    const handleAutoFocus = (e) => {
        if (!is.undefined(autoFocus) && autoFocus !== 'auto') {
            e.preventDefault();
        }
        if (popoverContent.current && autoFocus === 'content') {
            popoverContent.current.focus();
        }
        if (autoFocus === 'close') {
            popoverContent.current.querySelector('[data-uds-child="popover-close-button"]')?.focus();
        }
    };
    return (React.createElement(RadixPopover.Portal, null,
        React.createElement(RadixPopover.Content, { ref: popoverContent, "data-uds-child": "popover-content", "data-test-id": dataTestId && `${dataTestId}-content`, side: side, align: align, sideOffset: arrow ? distance - 12 : distance, alignOffset: offset, "aria-labelledby": `popover-${id}-heading`, className: tw$2('relative shadow-glow-base animate-slide-in-bottom rounded-sm', 'focus-visible:(outline-none ring ring-1)'), onOpenAutoFocus: handleAutoFocus, style: {
                zIndex: zIndex,
            }, ...rest },
            arrow && (React.createElement(RadixPopover.Arrow, { asChild: true },
                React.createElement("span", { className: tw$2('w-3 h-3 rotate-45 -translate-y-1/2 border-b-1 border-r-1', colorStyle), "data-uds-child": "popover-arrow", "data-test-id": dataTestId && `${dataTestId}-arrow` }))),
            React.createElement("div", { className: tw$2(css `
            max-height: 50vh;
            overflow-y: auto;
          `) },
                React.createElement("div", { className: tw$2('flex flex-col rounded-sm border-1', sizeStyle, css `
                width: min(
                  var(--radix-popper-available-width),
                  var(--popover-max-width)
                );
              `, colorStyle, className) }, children)))));
};

/**
 * The `PopoverTrigger` component should wrap the element opens the popover.
 * It will render as the child element.
 *
 * @example
 * <PopoverTrigger>
 *   <ButtonCta label="Open Popover" />
 * </PopoverTrigger>
 */
const PopoverTrigger = ({ children, ...rest }) => {
    const { dataTestId } = usePopoverContext();
    return (React.createElement(RadixPopover.Trigger, { "data-uds-child": "popover-trigger", "data-test-id": dataTestId && `${dataTestId}-trigger`, ...rest, asChild: true }, children));
};

/**
 * The body content of a popover.
 *
 * @example
 * ```
 * <PopoverHeader>
 *   <Heading>Some content</Heading>
 *   <PopoverClose />
 * </PopoverHeader>
 * ```
 */
const PopoverHeader = ({ children, className }) => {
    const { size } = usePopoverContext();
    const headerSizeStyle = useResponsiveProp(size, size => POPOVER_HEADER_SIZE_MAP[size]);
    return (React.createElement("header", { className: tw$2('flex items-start gap-2', headerSizeStyle, css `
          > [data-uds-child='popover-close-button'] {
            @apply relative transform top-auto right-auto -translate-y-1 translate-x-1.5 rtl:(-translate-x-1.5);
          }
        `, className) }, children));
};

/**
 * The body content of a popover.
 *
 * @example
 * ```
 * <PopoverBody>
 *   <Heading>Some content</Heading>
 * </PopoverBody>
 * ```
 */
const PopoverBody = ({ children, className }) => {
    const { size, dataTestId } = usePopoverContext();
    const bodySizeStyle = useResponsiveProp(size, size => POPOVER_BODY_SIZE_MAP[size]);
    return (React.createElement("div", { className: tw$2('order-2 flex-1', bodySizeStyle, className), "data-uds-child": "popover-body", "data-test-id": dataTestId && `${dataTestId}-body` }, children));
};

const PopoverClose = forwardRef(function PopoverClose({ className }, ref) {
    const t = useUdsTranslate();
    const { size, dataTestId, invert } = usePopoverContext();
    const sizeStyle = useResponsiveProp(size, size => POPOVER_CLOSE_BUTTON_SIZE_MAP[size]);
    return (React.createElement(RadixPopover.Close, { asChild: true },
        React.createElement(ButtonIcon, { ref: ref, className: tw$2('absolute top-0 right-0 order-1 ml-auto', sizeStyle, className), mode: "icon", icon: "close", label: t('close'), size: "xs", variant: "ghost", invert: invert, "data-uds-child": "popover-close-button", "data-test-id": dataTestId && `${dataTestId}-close-button` })));
});

const TooltipInnerContext = createContext(undefined);
const TooltipInnerProvider = ({ children, value, }) => (React.createElement(TooltipInnerContext.Provider, { value: value }, children));
const useTooltipContext = () => {
    const context = useContext(TooltipInnerContext);
    if (!context) {
        throw new Error('useTooltipContext must be used within a TooltipProvider');
    }
    return context;
};

const animTooltipRight = animation('0.4s cubic-bezier(0.16, 1, 0.3, 1)', {
    '0%': {
        transform: 'translateX(8px)',
        opacity: 0,
    },
    '100%': {
        opacity: 100,
        transform: 'translateX(0)',
    },
});
const animTooltipLeft = animation('0.4s cubic-bezier(0.16, 1, 0.3, 1)', {
    '0%': {
        transform: 'translateX(-8px)',
        opacity: 0,
    },
    '100%': {
        opacity: 100,
        transform: 'translateX(0)',
    },
});
const animTooltipTop = animation('0.4s cubic-bezier(0.16, 1, 0.3, 1)', {
    '0%': {
        transform: 'translateY(-8px)',
        opacity: 0,
    },
    '100%': {
        opacity: 100,
        transform: 'translateY(0)',
    },
});
const animTooltipBottom = animation('0.4s cubic-bezier(0.16, 1, 0.3, 1)', {
    '0%': {
        transform: 'translateY(8px)',
        opacity: 0,
    },
    '100%': {
        opacity: 100,
        transform: 'translateY(0)',
    },
});

/**
 * The `TooltipContent` component is used to wrap the contents of a Tooltip.
 *
 * @example
 * ```
 * ```
 * <TooltipProvider delayDuration={300}>
 *   <TooltipRoot side="right" align="start">
 *     <TooltipTrigger>
 *       <button>Open Tooltip</button>
 *     </TooltipTrigger>
 *     <TooltipContent>
 *       Eiusmod non cillum fugiat qui eu culpa
 *     </TooltipContent>
 *   </TooltipRoot>
 * <TooltipProvider>
 * ```
 */
const TooltipContent = ({ children, className, zIndex = 20, ...rest }) => {
    const TooltipContent = useRef(null);
    const { dataTestId, invert, side, align, distance, offset } = useTooltipContext();
    const colorStyle = useResponsiveProp(invert, invert => invert ? 'bg-neutral-10 text-neutral-100' : 'bg-neutral-90 text-neutral-0');
    return (React.createElement(RadixTooltip.Portal, null,
        React.createElement(RadixTooltip.Content, { ref: TooltipContent, "data-uds-child": "tooltip-content", "data-test-id": dataTestId && `${dataTestId}-content`, side: side, align: align, sideOffset: distance + 4, alignOffset: offset, className: tw$2('relative shadow-glow-base animate-slide-in-bottom rounded-sm', css `
            --tooltip-max-width: 260px;
            max-width: min(
              var(--radix-tooltip-content-available-width),
              var(--tooltip-max-width)
            );
          `, css `
            &[data-side='left'] {
              ${animTooltipLeft};
            }
            &[data-side='right'] {
              ${animTooltipRight};
            }
            &[data-side='top'] {
              ${animTooltipTop};
            }
            &[data-side='bottom'] {
              ${animTooltipBottom};
            }
          `), style: {
                zIndex: zIndex,
            }, ...rest },
            React.createElement(RadixTooltip.Arrow, { asChild: true },
                React.createElement("div", { "data-test-id": dataTestId && `${dataTestId}-arrow`, className: tw$2('w-2.5 h-2.5 rotate-45 -translate-y-1/2', colorStyle), "data-uds-child": "tooltip-arrow" })),
            React.createElement("div", { className: tw$2('flex flex-col rounded-sm', 'px-3 py-2', 'text-sm', colorStyle, className) }, children))));
};

/**
 * The `TooltipRoot` component is the root component needed to render a Tooltip.
 * It wraps the trigger that opens the Tooltip and the Tooltip content itself.
 *
 * @example
 * ```
 * <TooltipProvider delayDuration={300}>
 *   <TooltipRoot side="right" align="start">
 *     <TooltipTrigger>
 *       <button>Open Tooltip</button>
 *     </TooltipTrigger>
 *     <TooltipContent>
 *       Eiusmod non cillum fugiat qui eu culpa
 *     </TooltipContent>
 *   </TooltipRoot>
 * <TooltipProvider>
 * ```
 */
const TooltipRoot = ({ align = 'start', side = 'bottom', distance = 0, dataTestId, children, ...rest }) => {
    return (React.createElement(TooltipInnerProvider, { value: {
            dataTestId,
            align,
            side,
            distance,
            ...rest,
        } },
        React.createElement(RadixTooltip.Root, { ...rest }, children)));
};

/**
 * The `TooltipTrigger` component should wrap the element opens the Tooltip.
 * It will render as the child element.
 *
 * @example
 * <TooltipTrigger>
 *   <ButtonCta label="Open Tooltip" />
 * </TooltipTrigger>
 */
const TooltipTrigger = ({ children, ...rest }) => {
    const { dataTestId } = useTooltipContext();
    return (React.createElement(RadixTooltip.Trigger, { "data-uds-child": "tooltip-trigger", "data-test-id": dataTestId && `${dataTestId}-trigger`, ...rest, asChild: true }, children));
};

const TooltipProvider = ({ children, delayDuration = 200, ...rest }) => {
    return (React.createElement(RadixTooltip.Provider, { delayDuration: delayDuration, ...rest }, children));
};

const ToastPrimitives = {
    Toast: Toast,
    Provider: ToastProvider$1,
    Viewport: ToastViewport$1,
    Description: ToastDescription$1,
    Title: ToastTitle$1,
    Close: ToastClose$1,
    Action: ToastAction$1,
};

/**
 * Context for the Toast component.
 *
 * @internal Used internally by the ToastRoot component.
 */
const ToastContext = createContext(null);
/**
 * Internal hook for consuming the Toast context.
 *
 * @internal
 */
const useToastContext = () => {
    return useContext(ToastContext);
};

const defaultIcons = {
    success: 'check-filled',
    error: 'exclamation-filled',
    warning: 'warning-filled',
    info: 'info-filled',
    base: 'info-filled',
};
const ToastIcon = (props) => {
    const { icon, size = '2xl', dataTestId, className, ...rest } = props;
    const { variant, dataTestId: contextDataTestId } = useToastContext();
    return (React.createElement(Icon, { ...rest, name: icon ?? defaultIcons[variant], "data-test-id": dataTestId ?? `${contextDataTestId}-icon`, "data-uds-child": "toast-icon", className: className, size: size }));
};
const ToastTitle = ({ size = 'xxs', className, children, dataTestId, asChild, ...rest }) => {
    const { dataTestId: contextDataTestId } = useToastContext();
    const Comp = asChild ? React.Fragment : 'h4';
    return (React.createElement(ToastPrimitives.Title, { asChild: true },
        React.createElement(Heading, { ...rest, size: size, asChild: true, "data-uds-child": "toast-title", className: className, "data-test-id": dataTestId ?? `${contextDataTestId}-title`, "data-title": true },
            React.createElement(Comp, null, children))));
};
const ToastDescription = ({ size = 'base', className, children, dataTestId, asChild, ...rest }) => {
    const { dataTestId: contextDataTestId } = useToastContext();
    const Comp = asChild ? React.Fragment : 'p';
    return (React.createElement(ToastPrimitives.Description, { asChild: true },
        React.createElement(Text, { ...rest, asChild: true, size: size, "data-uds-child": "toast-description", "data-test-id": dataTestId ?? `${contextDataTestId}-description`, className: className, "data-description": true },
            React.createElement(Comp, null, children))));
};
const ToastAction = ({ altText, className, children, dataTestId, ...rest }) => {
    const { dataTestId: contextDataTestId } = useToastContext();
    return (React.createElement(ToastPrimitives.Action, { ...rest, asChild: true, altText: altText, "data-test-id": dataTestId ?? `${contextDataTestId}-action`, "data-uds-child": "toast-action", className: className }, children));
};
const ToastClose = ({ className, dataTestId, ...rest }) => {
    const { dictionary } = useUdsContext();
    const { invert, dataTestId: contextDataTestId } = useToastContext();
    return (React.createElement(ToastPrimitives.Close, { asChild: true },
        React.createElement(ButtonCta, { ...rest, className: tw$2(className), mode: "icon", "data-uds-child": "toast-close", label: dictionary.close, icon: "close", size: "sm", variant: "ghost", "data-test-id": dataTestId ?? `${contextDataTestId}-close`, invert: invert })));
};

const sizeStyles = {
    sm: 'w-full w-64',
    base: 'w-full w-[26rem]',
    md: 'w-full w-[32rem]',
};
const variantStyles = {
    base: 'shadow-glow-base border border-neutral-40 bg-neutral-10 text-neutral',
    success: 'shadow-glow-success border border-success-20 bg-white text-success',
    error: 'shadow-glow-error border border-error-10 bg-white text-error',
    warning: 'shadow-glow-warning border border-warning-20 bg-white text-warning-70',
    info: 'shadow-glow-info border border-info-10 bg-white text-info',
};
const invertVariantStyles = {
    base: 'shadow-glow-base-invert border border-neutral-80 bg-neutral-100 text-neutral-0',
    success: 'shadow-glow-success-invert border border-success-90 bg-neutral-100 text-success-40',
    error: 'shadow-glow-error-invert border border-error-80 bg-neutral-100 text-error-40',
    warning: 'shadow-glow-warning-invert border border-warning-90 bg-neutral-100 text-warning-50',
    info: 'shadow-glow-info-invert border border-info-80 bg-neutral-100 text-info-40',
};
const baseLayout = css `
  @apply grid;
  grid-auto-rows: max-content;
  grid-template-columns: max-content auto max-content;
  grid-template-areas:
    'icon title'
    'icon description'
    'icon action';

  & > [data-uds-child='toast-icon'] {
    grid-area: icon;
    @apply -ml-1 mr-2 rtl:(ml-2 -mr-1);
  }

  & > [data-uds-child='toast-title'] {
    grid-area: title;
    @apply justify-self-start self-start leading-normal;
  }

  & > [data-uds-child='toast-description'] {
    grid-area: description;
    @apply justify-self-start self-start;
  }

  & > [data-uds-child='toast-action'] {
    grid-area: action;
    @apply mt-2 justify-self-start self-start;
  }

  & > [data-uds-child='toast-close'] {
    @apply absolute right-2 top-2 mt-0 z-10 rtl:(-ml-1.5 mr-0 left-3 right-auto);
  }
`;
/**
 * A ToastRoot is a non-modal, unobtrusive window element used to display brief, auto-expiring windows of information to a user.
 *
 * A toast must appear within a ToastProvider, usually found at the root of the application. See the documentation for ToastProvider and ToastViewport.
 *
 *
 * @example
 * ```
 * <ToastRoot
 *   size="base"
 *   variant="base"
 *   invert={false}
 *   dismissible={true}
 *   onOpenChange={setOpen}
 * >
 *   <ToastIcon icon="info-filled" />
 *   <ToastTitle>Title</ToastTitle>
 *   <ToastDescription>Content</ToastDescription>
 *   <ToastAction altText="Action">
 *     <ButtonCta size="sm" label="Action" />
 *   </ToastAction>
 * </ToastRoot>
 * ```
 */
const ToastRoot = ({ size = 'base', variant = 'base', invert = false, assertive = false, dismissible = true, duration, className, dataTestId, children, ref, }) => {
    const toastStyle = tw$2(sizeStyles[size], invert ? invertVariantStyles[variant] : variantStyles[variant]);
    return (React.createElement(RadixToast.Root, { "data-test-id": dataTestId, "data-uds-child": "toast", duration: duration, ref: ref, className: tw$2(toastStyle, className, baseLayout, 'rounded-sm py-3 px-3', dismissible && 'pr-10 rtl:(pl-10 pr-3)'), type: assertive ? 'foreground' : 'background' },
        React.createElement(ToastContext.Provider, { value: { variant, invert, dataTestId, dismissible } },
            children,
            dismissible && React.createElement(ToastClose, null))));
};

/**
 * Top level provider, that's responsible for rendering the toast viewport, announcing the contents, and managing the toast state.
 *
 * Usually, you'll want to wrap your entire app with this provider, so that you can use the toast primitives anywhere in your app.
 *
 * You can have multiple ToastProviderInstances in your app, if you need to render context specific toasts.
 *
 * @example
 * ```jsx
 * <ToastProvider>
 *   <YourApp>
 *     <ToastRoot />
 *   </YourApp>
 * <ToastViewport position="bottom_end" animationIn="fade"/>
 * <ToastProvider>
 * ```
 */
const ToastProvider = (props) => {
    const { dataTestId, children, label = 'Notifications', duration = 5000, } = props;
    return (React.createElement(Provider, { label: label, duration: duration, "data-test-id": dataTestId }, children));
};

const animSlideInRight = animation('0.3s cubic-bezier(0.16, 1, 0.3, 1)', {
    '0%': {
        transform: 'translateX(20%)',
        opacity: 0,
    },
    '100%': {
        opacity: 100,
        transform: 'translateX(0)',
    },
});
const animSlideInLeft = animation('0.3s cubic-bezier(0.16, 1, 0.3, 1)', {
    '0%': {
        transform: 'translateX(-20%)',
        opacity: 0,
    },
    '100%': {
        opacity: 100,
        transform: 'translateX(0)',
    },
});
const animSlideOutRight = animation('0.3s cubic-bezier(0.55, 0, 1, 0.45)', {
    '0%': {
        transform: '0%',
        opacity: 100,
    },
    '100%': {
        opacity: 0,
        transform: 'translateX(20%)',
    },
});
const animSlideOutLeft = animation('0.3s cubic-bezier(0.55, 0, 1, 0.45)', {
    '0%': {
        transform: '0%',
        opacity: 100,
    },
    '100%': {
        opacity: 0,
        transform: 'translateX(-20%)',
    },
});
const animSlideInUp = animation('0.3s cubic-bezier(0.16, 1, 0.3, 1)', {
    '0%': {
        transform: 'translateY(50%)',
        opacity: 0,
    },
    '100%': {
        opacity: 100,
        transform: 'translateY(0)',
    },
});
const animSlideInDown = animation('0.3s cubic-bezier(0.16, 1, 0.3, 1)', {
    '0%': {
        transform: 'translateY(-50%)',
        opacity: 0,
    },
    '100%': {
        opacity: 100,
        transform: 'translateY(0)',
    },
});
const animSlideOutUp = animation('0.3s cubic-bezier(0.55, 0, 1, 0.45)', {
    '0%': {
        transform: '0%',
        opacity: 100,
    },
    '100%': {
        opacity: 0,
        transform: 'translateY(-50%)',
    },
});
const animSlideOutDown = animation('0.3s cubic-bezier(0.55, 0, 1, 0.45)', {
    '0%': {
        transform: '0%',
        opacity: 100,
    },
    '100%': {
        opacity: 0,
        transform: 'translateY(50%)',
    },
});
const animFadeIn = animation('0.4s cubic-bezier(0.16, 1, 0.3, 1)', {
    '0%': {
        opacity: 0,
    },
    '100%': {
        opacity: 100,
    },
});
const animFadeOut = animation('0.4s cubic-bezier(0.55, 0, 1, 0.45)', {
    '0%': {
        opacity: 100,
    },
    '100%': {
        opacity: 0,
    },
});

const positions = {
    top_end: 'fixed z-[99999] top-6 right-6 rtl:right-auto rtl:left-6',
    bottom_end: 'fixed z-[99999] bottom-6 right-6 rtl:right-auto rtl:left-6',
    top_start: 'fixed z-[99999] top-6 left-6 rtl:left-auto rtl:right-6',
    bottom_start: 'fixed z-[99999] bottom-6 left-6 rtl:left-auto rtl:right-6',
    top_middle: 'fixed z-[99999] top-6 left-1/2 -translate-x-1/2',
    bottom_middle: 'fixed z-[99999] bottom-6 left-1/2 -translate-x-1/2',
    middle_end: 'fixed z-[99999] top-1/2 -translate-y-1/2 right-6 rtl:right-auto rtl:left-6',
    middle_start: 'fixed z-[99999] top-1/2 -translate-y-1/2 left-6 rtl:left-auto rtl:right-6',
    inline: '',
};
const animationsIn = {
    left: css `
    & > [data-state='open'] {
      ${animSlideInLeft}
    }
  `,
    right: css `
    & > [data-state='open'] {
      ${animSlideInRight}
    }
  `,
    up: css `
    & > [data-state='open'] {
      ${animSlideInUp}
    }
  `,
    down: css `
    & > [data-state='open'] {
      ${animSlideInDown}
    }
  `,
    fade: css `
    & > [data-state='open'] {
      ${animFadeIn}
    }
  `,
};
const animationsOut = {
    left: css `
    & > [data-state='closed'] {
      ${animSlideOutLeft}
    }
  `,
    right: css `
    & > [data-state='closed'] {
      ${animSlideOutRight}
    }
  `,
    up: css `
    & > [data-state='closed'] {
      ${animSlideOutUp}
    }
  `,
    down: css `
    & > [data-state='closed'] {
      ${animSlideOutDown}
    }
  `,
    fade: css `
    & > [data-state='closed'] {
      ${animFadeOut}
    }
  `,
};
const toastStylesCommon = css `
  & > li {
    transform: translate3d(0, 0, 0);
    backface-visibility: hidden;
    perspective: 1000;
  }
`;
const defaultAnimationIn = {
    top_start: 'left',
    top_end: 'right',
    top_middle: 'down',
    bottom_end: 'right',
    bottom_start: 'left',
    bottom_middle: 'up',
    middle_start: 'left',
    middle_end: 'right',
    inline: 'fade',
};
const defaultAnimationOut = {
    top_end: 'right',
    bottom_end: 'right',
    top_start: 'left',
    bottom_start: 'left',
    top_middle: 'up',
    bottom_middle: 'down',
    middle_start: 'left',
    middle_end: 'right',
    inline: 'fade',
};
/**
 * The ToastViewport is the html element, whithin which, the Toasts will be rendered. It is responsible for positioning the Toasts and animating them in and out.
 *
 * Toasts are not direct children of the Viewport, but are rendered in a portal, so they can be positioned anywhere in the dom. The viewport is determined by the parent ToastProvider instance.
 *
 *
 *
 * @example
 * ```
 * <ToastProvider>
 *   <YourApp>
 *     <ToastRoot />
 *   </YourApp>
 * // The Toasts will be rendered here
 * <ToastViewport position="bottom_end" animationIn="fade"/>
 * <ToastProvider>
 * ```
 */
const ToastViewport = (props) => {
    const { position = 'bottom_end', label = 'Notifications', animationIn, animationOut, offsetX = 0, offsetY = 0, hotkey = ['F8'], dataTestId, } = props;
    const animationInConfig = animationIn || defaultAnimationIn[position];
    const animationOutConfig = animationOut || defaultAnimationOut[position];
    const hotKeyString = `(${hotkey.join(' + ')})`;
    const viewportLabel = `${label} ${hotKeyString}`;
    const offsetPositionX = [
        'top_start',
        'bottom_start',
        'top_middle',
        'middle_start',
        'bottom_middle',
    ].includes(position)
        ? `ml-[${offsetX}px]`
        : `mr-[${offsetX}px]`;
    const offsetPositionY = [
        'top_start',
        'top_end',
        'top_middle',
        'middle_start',
        'middle_end',
    ].includes(position)
        ? `mt-[${offsetY}px]`
        : `mb-[${offsetY}px]`;
    return (React.createElement(React.Fragment, null,
        React.createElement(Viewport, { label: viewportLabel, hotkey: hotkey, "data-test-id": dataTestId, "data-uds-child": "toast-viewport", "data-viewport": true, className: tw$1('outline:none flex flex-col gap-6 outline-none', positions[position], toastStylesCommon, animationsIn[animationInConfig], animationsOut[animationOutConfig], offsetPositionX, offsetPositionY) })));
};

const SearchContext = createContext(undefined);
/**
 * Provider for the search context, used to register search options and provide state and behaviour to the search pattern.
 *
 * @internal Mounted internally by the SearchRoot.
 */
const SearchProvider = ({ children, value, }) => React.createElement(SearchContext.Provider, { value: value }, children);
const useSearchContext = () => {
    const context = useContext(SearchContext);
    if (!context) {
        throw new Error('useSearchContext must be used within a SearchProvider');
    }
    return context;
};

/**
 * The `SearchRoot` component is the root component needed to render a search overlay.
 * It wraps and provides context to the trigger that opens the search overlay and the search overlay content.
 *
 * @example
 * ```
 * const [value, setValue] = useState('');
 * const [open, setOpen] = useState(false);
 *
 * <SearchRoot
 *   label="Search"
 *   placeholder="Type to search"
 *   value={value}
 *   open={open}
 *   onChange={setValue}
 *   onOpenChange={setOpen}
 *   onSubmit={handleSubmit}
 * >
 *   <SearchTrigger>
 *     <ButtonCta label="Search" />
 *   </SearchTrigger>
 *   <SearchContent>
 *     <SearchListbox>
 *       <ul>
 *         <li>
 *           <SearchOption>
 *             <a href="/option-1">Option 1</a>
 *           </SearchOption>
 *         </li>
 *         <li>
 *           <SearchOption>
 *             <a href="/option-2">Option 2</a>
 *           </SearchOption>
 *         </li>
 *         <li>
 *           <SearchOption>
 *             <a href="/option-3">Option 3</a>
 *           </SearchOption>
 *         </li>
 *       </ul>
 *     </SearchListbox>
 *   </SearchContent>
 * </SearchRoot>
 * ```
 */
const SearchRoot = ({ value, label, name, placeholder, children, dataTestId, open, loading, invert, onOpenChange, onEscapeKeyDown, onChange, onSubmit, }) => {
    const [options, setOptions] = useState([]);
    const [visuallyFocusedOption, setVisuallyFocusedOption] = useState(null);
    const t = useUdsTranslate();
    const listboxId = useId();
    const contentRef = useRef(null);
    const inputRef = useRef(null);
    const registeredTriggers = useRef([]);
    const activeTrigger = useRef(null);
    const onTriggerClick = (ref) => {
        activeTrigger.current = ref;
        onOpenChange(true);
    };
    const registerTrigger = (ref) => {
        const hasTrigger = registeredTriggers.current.some(t => t.current === ref.current);
        if (!hasTrigger) {
            registeredTriggers.current.push(ref);
        }
    };
    const unregisterTrigger = (ref) => {
        const triggerIndex = registeredTriggers.current.findIndex(t => t.current === ref.current);
        if (triggerIndex > -1) {
            registeredTriggers.current.splice(triggerIndex, 1);
        }
    };
    const registerOption = (id) => {
        setOptions(prev => [...prev, id]);
    };
    const unregisterOption = (id) => {
        setOptions(prev => prev.filter(optionId => optionId !== id));
    };
    const handleKeyDown = (e) => {
        switch (e.key) {
            case 'Escape':
                if (onEscapeKeyDown) {
                    onEscapeKeyDown(e);
                }
                else {
                    onOpenChange(false);
                }
                break;
            case 'Tab':
                // Ensure focus does not leave the search overlay
                const focusableElements = Array.from(contentRef.current.querySelectorAll('a:not([tabindex="-1"]), button:not([tabindex="-1"]), input')).filter(element => {
                    // Elements that have no offsetWidth, offsetHeight, or getClientRects()
                    // values are considered hidden
                    /* istanbul ignore next */
                    const isHidden = element.offsetWidth ||
                        element.offsetHeight ||
                        element.getClientRects().length;
                    return isHidden;
                });
                const firstElement = focusableElements[0];
                const lastElement = focusableElements[focusableElements.length - 1];
                if (!e.shiftKey && document.activeElement === lastElement) {
                    firstElement.focus();
                    e.preventDefault();
                }
                if (e.shiftKey && document.activeElement === firstElement) {
                    lastElement.focus();
                    e.preventDefault();
                }
                break;
        }
    };
    /**
     * Return focus to trigger on close
     */
    const isFirstRender = useRef(true);
    useEffect(() => {
        // Don't focus on initial render
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        if (!open) {
            activeTrigger.current?.current?.focus();
        }
    }, [open]);
    /**
     * Handle event listeners
     */
    useEffect(() => {
        if (open) {
            document.addEventListener('keydown', handleKeyDown);
        }
        else {
            document.removeEventListener('keydown', handleKeyDown);
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [open, handleKeyDown]);
    /**
     * Announce search results to screen readers
     */
    const prevOptions = useRef(JSON.stringify(options));
    useEffect(() => {
        if (open &&
            value &&
            !loading &&
            prevOptions.current !== JSON.stringify(options)) {
            prevOptions.current = JSON.stringify(options);
            const announcement = options.length
                ? t('srNewSearchResults', { query: value, num: options.length })
                : t('srNoSearchResults', { query: value });
            announce(announcement, 'assertive');
        }
    }, [open, value, loading, options]);
    return (React.createElement(SearchProvider, { value: {
            value,
            label,
            placeholder,
            name,
            dataTestId,
            open,
            options,
            invert,
            visuallyFocusedOption,
            listboxId,
            contentRef,
            inputRef,
            onSubmit,
            onOpenChange,
            onChange,
            registerTrigger,
            unregisterTrigger,
            onTriggerClick,
            registerOption,
            unregisterOption,
            setVisuallyFocusedOption,
        } }, children));
};

/**
 * The `SearchFormInternal` component is used to render the input of the search overlay.
 *
 *
 * @internal This component is not meant to be used outside of the SearchContent component. A standalone version of this component is available as `SearchForm`.
 */
const SearchFormInternal = () => {
    const { label, placeholder, name, listboxId, dataTestId, open, value, options, invert, visuallyFocusedOption, inputRef, onChange, onSubmit, setVisuallyFocusedOption, } = useSearchContext();
    const handleChange = (value) => {
        onChange(value);
        setVisuallyFocusedOption(null);
    };
    const handleKeyDown = (e) => {
        const index = options.indexOf(visuallyFocusedOption);
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                if (index < options.length - 1) {
                    setVisuallyFocusedOption(options[index + 1]);
                }
                else {
                    setVisuallyFocusedOption(options[0]);
                }
                break;
            case 'ArrowUp':
                e.preventDefault();
                if (index > 0) {
                    setVisuallyFocusedOption(options[index - 1]);
                }
                else {
                    setVisuallyFocusedOption(options[options.length - 1]);
                }
                break;
            case 'Enter':
                if (visuallyFocusedOption) {
                    e.preventDefault();
                    const element = document.getElementById(visuallyFocusedOption);
                    if (element)
                        element.click();
                }
                break;
            case 'Home':
                e.preventDefault();
                setVisuallyFocusedOption(options[0]);
                break;
            case 'End':
                e.preventDefault();
                setVisuallyFocusedOption(options[options.length - 1]);
                break;
            case 'Escape':
                // If there is a value or selected option, clear input and deselect option
                // Otherwise, the event will bubble up to the SearchRoot and close the search overlay
                if (value || visuallyFocusedOption) {
                    e.preventDefault();
                    e.stopPropagation();
                    setVisuallyFocusedOption(null);
                    onChange('');
                }
                break;
            case 'ArrowLeft':
            case 'ArrowRight':
                setVisuallyFocusedOption(null);
                break;
            case 'Delete':
            case 'Backspace':
                setVisuallyFocusedOption(null);
                break;
        }
    };
    return (React.createElement(SearchForm, { role: "combobox", name: name, label: label, placeholder: placeholder, value: value, invert: invert, "aria-controls": listboxId, "aria-expanded": open, "aria-autocomplete": "list", "aria-activedescendant": visuallyFocusedOption, onChange: handleChange, onKeyDown: handleKeyDown, onSubmit: onSubmit, onBlur: () => setVisuallyFocusedOption(null), dataTestId: dataTestId ? `${dataTestId}-form` : undefined, ref: inputRef, autoFocus: true }));
};

const fadeIn = animation('0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards', {
    '0%': {
        opacity: 0,
    },
    '100%': {
        opacity: 1,
    },
});
const fadeOut$1 = animation('0.3s cubic-bezier(0.5, 1, 0.89, 1) forwards', {
    '0%': {
        opacity: 1,
    },
    '100%': {
        opacity: 0,
    },
});
const slideInTop = animation('0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards', {
    '0%': {
        transform: 'translate3d(0, -60px, 0)',
    },
    '100%': {
        transform: 'translate3d(0, 0, 0)',
    },
});

/**
 * The `SearchContent` component is used to render the content of the search overlay e.g. the search result options.
 *
 * @example
 * <SearchContent>
 *   <SearchListbox>
 *     <ul>
 *       <li>
 *         <SearchOption>
 *           <a href="/option-1">Option 1</a>
 *         </SearchOption>
 *       </li>
 *       <li>
 *         <SearchOption>
 *           <a href="/option-2">Option 2</a>
 *         </SearchOption>
 *       </li>
 *       <li>
 *         <SearchOption>
 *           <a href="/option-3">Option 3</a>
 *         </SearchOption>
 *       </li>
 *     </ul>
 *   </SearchListbox>
 * </SearchContent>
 */
const SearchContent = ({ children, className, ...props }) => {
    const { dictionary } = useUdsContext();
    const { dataTestId, open, invert, contentRef, onOpenChange } = useSearchContext();
    const shouldMount = useDelayUnmount(open, 300);
    const isLargeDevice = useMediaQuery(`(min-width: ${tw$2.theme('screens.lg')})`);
    if (!shouldMount)
        return null;
    return (React.createElement("div", { role: "dialog", "aria-label": dictionary.search, className: tw$2('fixed inset-0 z-search', open ? fadeIn : fadeOut$1, className), "data-uds-child": "search-content", "data-test-id": dataTestId ? `${dataTestId}` : undefined, ref: contentRef, ...props },
        React.createElement("div", { "data-uds-child": "search-backdrop", className: "absolute inset-0 bg-black bg-opacity-50", onClick: () => onOpenChange(false), "aria-hidden": "true" }),
        React.createElement("div", { className: tw$2('flex flex-col relative h-full lg:h-auto lg:max-h-full', css `
            @screen lg {
              ${slideInTop}
            }
          `, invert ? 'bg-neutral-100' : 'bg-neutral-0'), style: {
                backfaceVisibility: 'hidden',
                perspective: 1000,
            } },
            React.createElement("div", { className: tw$2('flex flex-shrink-0 sh items-center w-full h([104px] lg:[120px])', invert ? 'bg-neutral-90' : 'bg-neutral-10') },
                React.createElement(Container, { size: "page", className: "flex flex-row-reverse items-center lg:flex-row gap(2 lg:6)" },
                    React.createElement(SearchFormInternal, null),
                    React.createElement(ButtonIcon, { label: dictionary.close, icon: isLargeDevice ? 'close' : 'chevron-left', className: isLargeDevice ? '' : '-ml-3 rtl:ml-0 rtl:-mr-3', variant: "ghost", onClick: () => onOpenChange(false), "data-uds-child": "search-close-button", invert: invert }))),
            React.createElement(Container, { className: "py-8 overflow-auto", size: "page" }, children))));
};

/**
 * The `SearchTrigger` component should wrap the element that opens the search overlay.
 * It will render as the child element.
 *
 * @example
 * <SearchTrigger>
 *   <ButtonCta label="Search" icon="search" />
 * </SearchTrigger>
 */
const SearchTrigger = ({ className, children }) => {
    const { open, onTriggerClick, registerTrigger, unregisterTrigger } = useSearchContext();
    const ref = useRef(null);
    useEffect(() => {
        registerTrigger(ref);
        return () => {
            unregisterTrigger(ref);
        };
    }, [open]);
    const handleClick = () => {
        onTriggerClick(ref);
    };
    return (React.createElement(Slot, { ref: ref, onClick: handleClick, className: className, "data-uds-child": "search-trigger" }, children));
};

/**
 * The `SearchListbox` component is used to render the listbox of the search overlay, wrapping the selectable options.
 *
 * @example
 * <SearchListbox>
 *   <ul>
 *     <li>
 *       <SearchOption>
 *         <a href="/option-1">Option 1</a>
 *       </SearchOption>
 *     </li>
 *     <li>
 *       <SearchOption>
 *         <a href="/option-2">Option 2</a>
 *       </SearchOption>
 *     </li>
 *     <li>
 *       <SearchOption>
 *         <a href="/option-3">Option 3</a>
 *       </SearchOption>
 *     </li>
 *   <ul>
 * </SearchListbox>
 */
const SearchListbox = ({ children, ...props }) => {
    const { listboxId, visuallyFocusedOption, dataTestId } = useSearchContext();
    return (React.createElement("div", { role: "listbox", "aria-activedescendant": visuallyFocusedOption, "data-test-id": dataTestId ? `${dataTestId}-listbox` : undefined, "data-uds-child": "search-listbox", id: listboxId, ...props }, children));
};

/**
 * Wrapper component for search result options, will register the anchor with the search context and attach keyboard navigation events, and any necessary behaviour.
 */
const SearchOption = ({ children, closeOnClick = true, }) => {
    const { dataTestId, visuallyFocusedOption, registerOption, unregisterOption, onOpenChange, } = useSearchContext();
    const id = useId();
    const isVisuallyFocused = id === visuallyFocusedOption;
    const handleClick = () => {
        if (closeOnClick)
            onOpenChange(false);
    };
    useEffect(() => {
        registerOption(id);
        return () => {
            unregisterOption(id);
        };
    }, []);
    return (React.createElement(Slot, { tabIndex: -1, id: id, className: isVisuallyFocused ? 'ring' : '', "data-focused": isVisuallyFocused, "data-test-id": dataTestId ? `${dataTestId}-option` : undefined, role: "option", "data-uds-child": "search-option", "aria-selected": "false", onClick: handleClick }, children));
};

const fadeOut = animation('0.3s cubic-bezier(0.5, 1, 0.89, 1) forwards', {
    '0%': {
        opacity: 1,
    },
    '100%': {
        opacity: 0,
    },
});
const Chip = props => {
    const { label, dismissible = false, invert = false, round = false, className, onDismiss, dataTestId, ...rest } = props;
    const t = useUdsTranslate();
    const [isOpen, setIsOpen] = useState(true);
    const calloutMounted = useDelayUnmount(isOpen, 1000);
    const handleDismiss = e => {
        onDismiss && onDismiss(e);
        setIsOpen(false);
    };
    return is.undefined(dismissible) || calloutMounted ? (React.createElement("span", { ...rest, "data-uds-child": "chip", "data-test-id": dataTestId, className: tw$2('max-w-max relative', 'flex items-center flex-none', 'h-8 gap-1.5', round ? 'rounded-full px-px' : 'rounded-sm px-0', !isOpen && fadeOut, invert
            ? 'bg-neutral-80 text-neutral-0'
            : 'bg-neutral-20 text-neutral-100', 'text-sm font-bold tracking-wide leading-tight', 'focus-within:outline-none focus-within:ring-2', className) },
        React.createElement("span", { className: tw$2('mx-3', dismissible && 'pr-4') }, label),
        dismissible && (React.createElement("button", { onClick: handleDismiss, "data-uds-child": "chip-dismiss-button", "data-test-id": `${dataTestId}-dismiss-button`, className: tw$2('absolute inset-0', 'focus:outline-none') },
            React.createElement("span", { className: "sr-only" }, `${t('clear')} ${label}`),
            React.createElement("div", { className: tw$2('absolute mt-px top-1/2 -translate-y-1/2', round ? 'right-2' : 'right-1.5') },
                React.createElement(Icon, { name: "close", size: "lg" })))))) : null;
};

const TOGGLE_SIZE = {
    sm: 'h-8 text-xs',
    base: 'h-10 text-base',
    lg: 'h-14 text-xl',
};
const TOGGLE_PADDING = {
    sm: 'py-1 px-3',
    base: 'py-2 px-5',
    lg: 'py-3 px-7',
};
const ToggleTextBase = React.forwardRef(function ToggleTextBase(props, ref) {
    const { size = 'base', bleed, invert, disabled, className, label, children, dataTestId, ...rest } = props;
    const buttonSize = useResponsiveProp(size, value => TOGGLE_SIZE[value]);
    const thumbStyle = tw$2('block min-w-max relative border-1 rounded-[1px]', !disabled && invert && 'border-neutral-60 text-neutral-0', !disabled && !invert && 'border-neutral-20 text-neutral-100', !invert && disabled && 'border-neutral-20', invert && disabled && 'border-neutral-80', disabled && 'cursor-not-allowed', 'focus:outline-none focus-visible:outline-none', invert ? 'focus-visible:(ring-2 ring-invert)' : 'focus-visible:(ring-2)', bleed ? 'flex-1' : 'max-w-max flex-none', css `
      &:hover,
      &:focus-visible {
        &:not([data-disabled]) {
          & [data-uds-child='toggle-text-overlay'] {
            @apply bg-opacity-100;
          }
        }
      }
      &[data-state='on'] {
        & [data-uds-child='toggle-text-outline'] {
          --tw-border-opacity: 1;
        }
        & [data-uds-child='toggle-text-overlay'] {
          @apply bg-opacity-0;
        }
      }
    `, css `
      &[data-state='on'] {
        @apply border-opacity-0;
      }
    `);
    const contentPadding = useResponsiveProp(size, value => TOGGLE_PADDING[value]);
    const contentStyle = tw$2('relative z-[5] my-px flex items-center justify-center', '!leading-[1em]', bleed ? 'px-3' : contentPadding, disabled && !invert && 'text-neutral-60', disabled && invert && 'text-neutral-40');
    const outlineStyle = tw$2('absolute -inset-px z-[10] rounded-[1px] transition-colors duration-100 transition-ease-out', 'border-2 border-opacity-0', invert ? 'border-neutral-0' : 'border-neutral-100 border-opacity-0');
    const overlayStyle = tw$2('absolute inset-0 z-[0]', 'bg-opacity-0', !disabled && invert && 'bg-neutral-90', !disabled && !invert && 'bg-neutral-10', !invert && disabled && 'bg-neutral-20 bg-opacity-100', invert && disabled && 'bg-neutral-80 bg-opacity-100');
    return (React.createElement("button", { ...rest, ref: ref, disabled: disabled, "data-uds-child": "toggle-text", "data-test-id": dataTestId, className: tw$2(thumbStyle, buttonSize, className) },
        React.createElement("div", { className: contentStyle, "data-uds-child": "toggle-text-label" }, label),
        React.createElement("div", { "data-uds-child": "toggle-text-overlay", className: overlayStyle }),
        React.createElement("div", { "data-uds-child": "toggle-text-outline", className: outlineStyle })));
});

const THUMB_SIZE$1 = {
    sm: 'w-8 h-8',
    base: 'w-10 h-10',
    lg: 'w-14 h-14',
};
const ICON_SIZE$2 = {
    sm: 'w-6 h-6',
    base: 'w-8 h-8',
    lg: 'w-10 h-10',
};
const ToggleIconBase = forwardRef(function ToggleIconBase(props, ref) {
    const { label, size = 'base', round, icon, iconOn, invert, disabled, className, children, dataTestId, ...rest } = props;
    const buttonSize = useResponsiveProp(size, value => THUMB_SIZE$1[value]);
    const thumbStyle = tw$2('flex items-center justify-center relative flex-none border-1 rounded-[1px]', round && 'rounded-full', disabled && 'cursor-not-allowed', !disabled && invert && 'border-neutral-60 text-neutral-0', !disabled && !invert && 'border-neutral-20 text-neutral-100', !invert && disabled && 'border-neutral-20', invert && disabled && 'border-neutral-80', 'focus:outline-none focus-visible:outline-none', invert ? 'focus-visible:(ring-2 ring-invert)' : 'focus-visible:(ring-2)', css `
      &:hover,
      &:focus-visible {
        &:not([data-disabled]) {
          & [data-uds-child='toggle-icon-overlay'] {
            @apply bg-opacity-100;
          }
        }
      }
      &:not([data-disabled]) {
        & [data-uds-child='toggle-icon-content'] {
          opacity: 1;
          @apply ${invert ? 'text-neutral-70' : 'text-neutral-30'};
        }
      }

      &[data-state='on'] {
        & [data-uds-child='toggle-icon-content'] {
          opacity: 1;
          @apply ${invert ? 'text-neutral-0' : 'text-neutral-100'};
        }
        & [data-uds-child='toggle-icon-outline'] {
          --tw-border-opacity: 1;
        }
        & [data-uds-child='toggle-icon-overlay'] {
          @apply bg-opacity-0;
        }
      }
    `, css `
      &[data-state='on'] {
        @apply border-opacity-0;
      }
    `, iconOn &&
        css `
        & [data-uds-child='toggle-icon-svg-on'] {
          display: none;
        }
        &[data-state='on'] {
          & [data-uds-child='toggle-icon-svg'] {
            display: none;
          }
          & [data-uds-child='toggle-icon-svg-on'] {
            display: block;
          }
        }
      `);
    const iconSize = useResponsiveProp(size, value => ICON_SIZE$2[value]);
    const iconStyle = tw$2('relative z-[10]', disabled && invert && 'text-neutral-40', disabled && !invert && 'text-neutral-60', iconSize);
    const outlineStyle = tw$2('border-2 border-opacity-0', 'absolute -inset-px z-[10] rounded-[1px] transition-colors duration-100 transition-ease-out', round && 'rounded-full', invert ? 'border-neutral-0' : 'border-neutral-100 border-opacity-0');
    const overlayStyle = tw$2('absolute inset-0 z-[0]', 'bg-opacity-0', round && 'rounded-full', !disabled && invert && 'bg-neutral-90', !disabled && !invert && 'bg-neutral-10', !invert && disabled && 'bg-neutral-20 bg-opacity-100', invert && disabled && 'bg-neutral-80 bg-opacity-100');
    return (React.createElement("button", { ...rest, ref: ref, disabled: disabled, "data-uds-child": "toggle-icon", "data-test-id": dataTestId, className: tw$2(thumbStyle, buttonSize, className) },
        React.createElement("span", { className: "sr-only" }, label),
        React.createElement("div", { className: "aspect-1-1" },
            React.createElement("div", { className: iconStyle, "data-uds-child": "toggle-icon-content" },
                React.createElement(Icon, { invert: invert, "data-uds-child": "toggle-icon-svg", name: icon }, children),
                iconOn && (React.createElement(Icon, { invert: invert, "data-uds-child": "toggle-icon-svg-on", name: iconOn }))),
            React.createElement("div", { "data-uds-child": "toggle-icon-overlay", className: overlayStyle }),
            React.createElement("div", { "data-uds-child": "toggle-icon-outline", className: outlineStyle }))));
});

const ICON_SIZE$1 = {
    sm: 'w-4 h-4',
    base: 'w-6 h-6',
    lg: 'w-7 h-7',
};
const CONTENT_STYLE = {
    sm: 'py-1 px-2 gap-1.5',
    base: 'py-2 px-3 gap-2',
    lg: 'py-3 px-4 gap-2.5',
};
const BUTTON_SIZE = {
    sm: 'h-8 min-w-6 text-xs',
    base: 'h-10 min-w-10 text-base',
    lg: 'h-14 min-w-14 text-xl',
};
const ToggleSwitchBase = forwardRef(function ToggleSwitchBase(props, ref) {
    const { size = 'base', icon = 'check-filled', iconEnd, invert, disabled, className, label, children, dataTestId, ...rest } = props;
    const buttonSize = useResponsiveProp(size, value => BUTTON_SIZE[value]);
    const thumbStyle = tw$2('block relative border-1 rounded-[1px]', 'flex justify-center items-center', !disabled && invert && 'border-neutral-60 text-neutral-0', !disabled && !invert && 'border-neutral-20 text-neutral-100', !invert && disabled && 'border-neutral-20', invert && disabled && 'border-neutral-80', disabled && 'cursor-not-allowed', 'focus:outline-none focus-visible:outline-none', invert ? 'focus-visible:(ring-2 ring-invert)' : 'focus-visible:(ring-2)', css `
      &:hover,
      &:focus-visible {
        &:not([data-disabled]) {
          & [data-uds-child='toggle-switch-overlay'] {
            @apply bg-opacity-100;
          }
        }
      }

      &:not([data-disabled]) {
        & [data-uds-child='toggle-switch-icon'] {
          opacity: 1;
          @apply ${invert ? 'text-neutral-80' : 'text-neutral-20'};
        }
      }

      &[data-state='on'] {
        & [data-uds-child='toggle-switch-icon'] {
          opacity: 1;
          @apply ${invert ? 'text-neutral-0' : 'text-neutral-100'};
        }

        & [data-uds-child='toggle-switch-outline'] {
          --tw-border-opacity: 1;
        }
        & [data-uds-child='toggle-switch-overlay'] {
          @apply bg-opacity-0;
        }
      }
    `, css `
      &[data-state='on'] {
        @apply border-opacity-0;
      }
    `);
    const iconSize = useResponsiveProp(size, value => ICON_SIZE$1[value]);
    const iconStyle = tw$2('transition-colors duration-250 transition-ease-out', disabled && invert && 'text-neutral-40', disabled && !invert && 'text-neutral-60', iconSize);
    const contentPadding = useResponsiveProp(size, value => {
        return [CONTENT_STYLE[value]];
    });
    const contentStyle = tw$2('relative z-[5] flex items-center justify-center', iconEnd ? 'flex-row-reverse' : 'flex-row', contentPadding);
    const labelStyle = tw$2('!leading-[1em]', disabled && invert && 'text-neutral-40', disabled && !invert && 'text-neutral-60');
    const outlineStyle = tw$2('absolute -inset-px z-[10] rounded-[1px] transition-colors duration-100 transition-ease-out', 'border-2 border-opacity-0', invert ? 'border-neutral-0' : 'border-neutral-100 border-opacity-0');
    const overlayStyle = tw$2('absolute inset-0 z-[0]', 'bg-opacity-0', !disabled && invert && 'bg-neutral-90', !disabled && !invert && 'bg-neutral-10', !invert && disabled && 'bg-neutral-20 bg-opacity-100', invert && disabled && 'bg-neutral-80 bg-opacity-100');
    return (React.createElement("button", { ...rest, ref: ref, disabled: disabled, "data-uds-child": "toggle-switch", "data-test-id": dataTestId, className: tw$2(thumbStyle, buttonSize, className) },
        React.createElement("div", { className: tw$2(contentStyle) },
            React.createElement("div", { "data-uds-child": "toggle-switch-icon", className: iconStyle },
                React.createElement(Icon, { invert: invert, name: icon }, children)),
            React.createElement("div", { className: labelStyle, "data-uds-child": "toggle-switch-label" }, label)),
        React.createElement("div", { "data-uds-child": "toggle-switch-overlay", className: overlayStyle }),
        React.createElement("div", { "data-uds-child": "toggle-switch-outline", className: outlineStyle })));
});

const THUMB_SIZE = {
    auto: 'w-full',
    sm: 'w-8',
    base: 'w-10',
    lg: 'w-14',
};
const LABEL_SIZE$1 = {
    auto: 'text-sm mt-2',
    sm: 'text-xxs mt-1',
    base: 'text-xs mt-1.5',
    lg: 'text-sm mt-2',
};
const ToggleThumbBase = forwardRef(function ToggleThumbBase(props, ref) {
    const { label, showLabel = false, size = 'base', ratio = 1, round = false, invert, disabled, className, children, dataTestId, ...rest } = props;
    const buttonSize = useResponsiveProp(size, value => THUMB_SIZE[value]);
    const labelSize = useResponsiveProp(size, value => LABEL_SIZE$1[value]);
    const thumbStyle = tw$2('group block relative flex-none rounded-[1px]', 'focus:outline-none focus-visible:outline-none', disabled && 'cursor-not-allowed', round && 'rounded-full', css `
      &:hover,
      &:focus-visible {
        &:not([data-disabled]) {
          & [data-uds-child='toggle-thumb-overlay'] {
            @apply bg-opacity-20;
          }
        }
      }
      &[data-disabled] {
        & [data-uds-child='toggle-thumb-media'] {
          filter: grayscale(100%);
        }
      }
      &[data-state='on'] {
        & [data-uds-child='toggle-thumb-outline'] {
          --tw-border-opacity: 1;
        }
        & [data-uds-child='toggle-thumb-overlay'] {
          @apply bg-opacity-0;
        }
      }
    `, css `
      &[data-state='on'] {
        & [data-uds-child='toggle-thumb-media'] {
          @apply border-opacity-0;
        }
      }
    `);
    const ratioStyle = tw$2(round && 'rounded-full', invert
        ? 'group-focus-visible:(ring-2 ring-invert)'
        : 'group-focus-visible:(ring-2)');
    const contentStyle = tw$2('absolute inset-0 border-1 z-0  rounded-[1px]', 'children:(object-cover object-center w-full h-full)', round && 'rounded-full overflow-hidden', invert ? 'border-neutral-80' : 'border-neutral-20');
    const outlineStyle = tw$2('absolute inset-0 z-[10] rounded-[1px] transition-colors duration-100 transition-ease-out', 'border-2 border-opacity-0', round && 'rounded-full', invert ? 'border-neutral-0' : 'border-neutral-100 border-opacity-0');
    const overlayStyle = tw$2('absolute inset-0 z-[1]', 'bg-neutral-20', 'bg-opacity-0', round && 'rounded-full', !invert && disabled && 'bg-opacity-80', invert && disabled && 'bg-neutral-80 bg-opacity-80');
    return (React.createElement("button", { ...rest, ref: ref, disabled: disabled, "data-uds-child": "toggle-thumb", "data-test-id": dataTestId, className: tw$2(thumbStyle, buttonSize, className) },
        React.createElement(AspectRatio, { ratio: ratio, overflow: true, className: ratioStyle },
            React.createElement("div", { "data-uds-child": "toggle-thumb-media", className: contentStyle }, children),
            React.createElement("div", { "data-uds-child": "toggle-thumb-overlay", className: overlayStyle }),
            React.createElement("div", { "data-uds-child": "toggle-thumb-outline", className: outlineStyle })),
        React.createElement("span", { className: showLabel
                ? tw$2('block leading-none text-center', labelSize)
                : 'sr-only' }, label)));
});

const CONTENT_SIZE = {
    sm: 'pl-3 pr-8 rtl:pl-8 rtl:pr-3 pb-2 pt-4',
    base: 'pl-4 pr-10 rtl:pl-10 rtl:pr-4 pb-3 pt-6',
    lg: 'pl-6 pr-12 rtl:pl-12 rtl:pr-6  pb-4 pt-7',
};
const LABEL_SIZE = {
    sm: 'text-xs',
    base: 'text-sm ',
    lg: 'text-base ',
};
const ICON_SIZE = {
    sm: 'xl',
    base: '2xl',
    lg: '3xl',
};
const ICON_OFFSET = {
    sm: '-mt-px',
    base: '-mt-0.5',
    lg: '-mt-0.5',
};
const ToggleImageBase = forwardRef(function ToggleImageBase(props, ref) {
    const { size = 'base', label, overlay = 'dark', ratio = 2 / 3, invert, disabled, className, children, dataTestId, ...rest } = props;
    const thumbStyle = tw$2('w-full block relative rounded-[1px]', 'focus:outline-none focus-visible:outline-none', disabled && 'cursor-not-allowed', invert ? 'focus-visible:(ring-3 ring-invert)' : 'focus-visible:(ring-3)', css `
      &:hover,
      &:focus-visible {
        &:not([data-disabled]) {
          & [data-uds-child='toggle-image-filter'] {
            backdrop-filter: blur(0px) opacity(100);
          }
        }
      }
      &[data-disabled] {
        & [data-uds-child='toggle-image-media'] {
          filter: grayscale(100%);
        }
      }

      & [data-uds-child='toggle-image-icon'] {
        opacity: 0;
      }

      &[data-state='on'] {
        & [data-uds-child='toggle-image-filter'] {
          backdrop-filter: blur(0px) opacity(100);
        }

        & [data-uds-child='toggle-image-overlay'] {
          opacity: 0;
        }
        & [data-uds-child='toggle-image-icon'] {
          opacity: 1;
        }
        &:hover,
        &:focus-visible {
          &:not([data-disabled]) {
            & [data-uds-child='toggle-image-overlay'] {
              opacity: 0;
            }
          }
        }
      }
    `);
    const filterStyle = tw$2('pointer-events-none absolute inset-0 z-[1]', css `
      transition: backdrop-filter 200ms ease-out;
      backdrop-filter: blur(4px) opacity(1);
    `, disabled && 'bg-opacity-0');
    const overlayStyle = tw$2('pointer-events-none absolute inset-0 z-[1]', 'opacity-50', 'transition-opacity duration-200 transition-ease-out', overlay === 'dark' ? 'bg-neutral-100' : 'bg-neutral-10', invert && disabled && 'bg-neutral-100 opacity-50', !invert && disabled && 'bg-neutral-40 opacity-50');
    const mediaStyle = tw$2('pointer-events-none absolute inset-0 z-0 rounded-[1px]', 'children:(object-cover object-center w-full h-full)');
    const contentSize = useResponsiveProp(size, value => CONTENT_SIZE[value]);
    const contentStyle = tw$2(contentSize, 'leading-[1] ', 'absolute z-[10] w-full bottom-0 left-0 text-left rtl:text-right', 'flex justify-between items-start gap-3', overlay === 'dark' ? 'text-neutral-0' : 'text-neutral-100', css `
      & > span {
        position: relative;
        z-index: 10;
      }
      &:before {
        content: '';
        position: absolute;
        z-index: 0;
        top: 0;
        left: 0;
        width: 100%;
        height: calc(100% + 1px);
        --media-vignette-angle: 0deg;
        --grad-start: 0.6;
        --grad-end: 0;
        --grad-spread: 80%;
      }
    `, overlay === 'dark'
        ? css `
          &:before {
            background: linear-gradient(
              var(--media-vignette-angle),
              rgba(25, 25, 25, var(--grad-start)) 0%,
              rgba(25, 25, 25, var(--grad-end)) var(--grad-spread)
            );
          }
        `
        : css `
          &:before {
            background: linear-gradient(
              var(--media-vignette-angle),
              rgba(255, 255, 255, var(--grad-start)) 0%,
              rgba(255, 255, 255, var(--grad-end)) var(--grad-spread)
            );
          }
        `);
    const labelSize = useResponsiveProp(size, value => LABEL_SIZE[value]);
    const labelStyle = tw$2(labelSize, 'uppercase font-bold');
    const iconOffset = useResponsiveProp(size, value => ICON_OFFSET[value]);
    const iconStyle = tw$2('absolute left-full rtl:right-full rtl:left-auto top-0', 'transition-colors duration-300 transition-ease-out', iconOffset);
    const iconSize = useResponsiveMap(size, ICON_SIZE);
    return (React.createElement("button", { ...rest, ref: ref, disabled: disabled, "data-uds-child": "toggle-image", "data-test-id": dataTestId, className: tw$2(thumbStyle, className) },
        React.createElement(AspectRatio, { ratio: ratio, overflow: true },
            React.createElement("div", { "data-uds-child": "toggle-image-media", className: mediaStyle }, children),
            React.createElement("div", { "data-uds-child": "toggle-image-filter", className: filterStyle }),
            React.createElement("div", { "data-uds-child": "toggle-image-overlay", className: overlayStyle }),
            React.createElement("div", { "data-uds-child": "toggle-image-content", className: contentStyle },
                React.createElement("div", { className: "relative w-full" },
                    React.createElement("span", { className: labelStyle }, label),
                    React.createElement("span", { "data-aria-hidden": "true", "data-uds-child": "toggle-image-icon", className: iconStyle },
                        React.createElement(Icon, { size: iconSize, name: "check-filled" })))))));
});

const ToggleChipBase = React.forwardRef(function ToggleChipBase(props, ref) {
    const { round, invert, disabled, className, label, children, dataTestId, ...rest } = props;
    const thumbStyle = tw$2('max-w-max relative', 'flex items-center flex-none', 'h-8', round ? 'rounded-full px-px' : 'rounded-sm px-0', !disabled && invert && 'bg-neutral-80', !disabled && !invert && 'bg-neutral-20', !invert && disabled && 'bg-neutral-20 bg-opacity-100', invert && disabled && 'bg-neutral-80 bg-opacity-100', !disabled && invert && 'border-neutral-60', !disabled && !invert && 'border-neutral-20', !invert && disabled && 'border-neutral-20', invert && disabled && 'border-neutral-80', disabled && 'cursor-not-allowed', 'focus:outline-none focus-visible:outline-none', invert ? 'focus-visible:(ring-2 ring-invert)' : 'focus-visible:(ring-2)', css `
      &:hover,
      &:focus-visible {
        &:not([data-disabled]) {
          & [data-uds-child='toggle-chip-overlay'] {
            @apply bg-opacity-60;
          }
        }
      }
      &[data-state='on'] {
        & [data-uds-child='toggle-chip-outline'] {
          --tw-border-opacity: 1;
        }
        & [data-uds-child='toggle-chip-overlay'] {
          @apply bg-opacity-0;
        }
      }
    `, css `
      &[data-state='on'] {
        @apply border-opacity-0;
      }
    `);
    const contentStyle = tw$2('relative z-[5] my-px px-3', 'text-sm font-bold tracking-wide leading-tight', disabled && !invert && 'text-neutral-60', disabled && invert && 'text-neutral-40');
    const outlineStyle = tw$2('absolute -inset-px z-[10] transition-colors duration-100 transition-ease-out', 'border-2 border-opacity-0', round ? 'rounded-full' : 'rounded-sm', invert ? 'border-neutral-0' : 'border-neutral-100 border-opacity-0');
    const overlayStyle = tw$2('absolute inset-0 z-[0]', 'bg-opacity-0', round ? 'rounded-full' : 'rounded-sm', !invert ? 'bg-neutral-30' : 'bg-neutral-90');
    return (React.createElement("button", { ...rest, ref: ref, disabled: disabled, "data-uds-child": "toggle-chip", "data-test-id": dataTestId, className: tw$2(thumbStyle, className) },
        React.createElement("div", { className: contentStyle, "data-uds-child": "toggle-chip-label" }, label),
        React.createElement("div", { "data-uds-child": "toggle-chip-overlay", className: overlayStyle }),
        React.createElement("div", { "data-uds-child": "toggle-chip-outline", className: outlineStyle })));
});

const ToggleButtonChip = forwardRef((props, ref) => {
    const { label, ...rest } = props;
    return (React.createElement(Toggle, { asChild: true, ...rest },
        React.createElement(ToggleChipBase, { ref: ref, label: label })));
});
ToggleButtonChip.displayName = 'ToggleButtonChip';

const ToggleButtonIcon = forwardRef((props, ref) => {
    const { label, icon, ...rest } = props;
    return (React.createElement(Toggle, { asChild: true, ...rest },
        React.createElement(ToggleIconBase, { ref: ref, icon: icon, label: label })));
});
ToggleButtonIcon.displayName = 'ToggleButtonIcon';

const ToggleButtonImage = forwardRef((props, ref) => {
    const { label, children, ...rest } = props;
    return (React.createElement(Toggle, { asChild: true, ...rest },
        React.createElement(ToggleImageBase, { ref: ref, label: label }, children)));
});
ToggleButtonImage.displayName = 'ToggleButtonImage';

const ToggleButtonSwitch = forwardRef((props, ref) => {
    const { label, ...rest } = props;
    return (React.createElement(Toggle, { asChild: true, ...rest },
        React.createElement(ToggleSwitchBase, { ref: ref, label: label })));
});
ToggleButtonSwitch.displayName = 'ToggleButtonSwitch';

const ToggleButtonText = forwardRef((props, ref) => {
    const { label, ...rest } = props;
    return (React.createElement(Toggle, { asChild: true, ...rest },
        React.createElement(ToggleTextBase, { ref: ref, label: label })));
});
ToggleButtonText.displayName = 'ToggleButtonText';

const ToggleButtonThumb = forwardRef((props, ref) => {
    const { label, children, ...rest } = props;
    return (React.createElement(Toggle, { asChild: true, ...rest },
        React.createElement(ToggleThumbBase, { ref: ref, label: label }, children)));
});
ToggleButtonThumb.displayName = 'ToggleButtonThumb';

const ToggleButton = {
    Icon: ToggleButtonIcon,
    Text: ToggleButtonText,
    Switch: ToggleButtonSwitch,
    Thumb: ToggleButtonThumb,
    Image: ToggleButtonImage,
    Chip: ToggleButtonChip,
};

const ToggleGroupRoot = (props) => {
    const { optional, type, value, defaultValue, onValueChange, className, ...rest } = props;
    const appliedOptional = optional ?? type === 'multiple';
    const [internalValue, setInternalValue] = useState(defaultValue || value);
    useEffect(() => {
        if (value)
            setInternalValue(value);
    }, [value]);
    const handleValueChange = (value) => {
        if (!appliedOptional && type === 'single' && !value)
            return;
        if (!appliedOptional && value.length === 0)
            return;
        setInternalValue(value);
        onValueChange?.(value);
    };
    return (React.createElement(ToggleGroup, { type: type, value: internalValue, onValueChange: handleValueChange, ...rest, className: className }));
};

const ToggleGroupItemChip = forwardRef((props, ref) => {
    const { label, ...rest } = props;
    return (React.createElement(ToggleGroupItem$1, { asChild: true, ...rest },
        React.createElement(ToggleChipBase, { ref: ref, label: label })));
});
ToggleGroupItemChip.displayName = 'ToggleGroupItemChip';

const ToggleGroupItemIcon = forwardRef((props, ref) => {
    const { label, icon, ...rest } = props;
    return (React.createElement(ToggleGroupItem$1, { asChild: true, ...rest },
        React.createElement(ToggleIconBase, { ref: ref, icon: icon, label: label })));
});
ToggleGroupItemIcon.displayName = 'ToggleGroupItemIcon';

const ToggleGroupItemImage = forwardRef((props, ref) => {
    const { label, children, ...rest } = props;
    return (React.createElement(ToggleGroupItem$1, { asChild: true, ...rest },
        React.createElement(ToggleImageBase, { ref: ref, label: label }, children)));
});
ToggleGroupItemImage.displayName = 'ToggleGroupItemImage';

const ToggleGroupItemSwitch = forwardRef((props, ref) => {
    const { label, ...rest } = props;
    return (React.createElement(ToggleGroupItem$1, { asChild: true, ...rest },
        React.createElement(ToggleSwitchBase, { ref: ref, label: label })));
});
ToggleGroupItemSwitch.displayName = 'ToggleGroupItemSwitch';

const ToggleGroupItemText = forwardRef((props, ref) => {
    const { label, ...rest } = props;
    return (React.createElement(ToggleGroupItem$1, { asChild: true, ...rest },
        React.createElement(ToggleTextBase, { ref: ref, label: label })));
});
ToggleGroupItemText.displayName = 'ToggleGroupItemText';

const ToggleGroupItemThumb = forwardRef((props, ref) => {
    const { label, children, ...rest } = props;
    return (React.createElement(ToggleGroupItem$1, { asChild: true, ...rest },
        React.createElement(ToggleThumbBase, { ref: ref, label: label }, children)));
});
ToggleGroupItemThumb.displayName = 'ToggleGroupItemThumb';

const ToggleGroupItem = {
    Text: ToggleGroupItemText,
    Icon: ToggleGroupItemIcon,
    Switch: ToggleGroupItemSwitch,
    Thumb: ToggleGroupItemThumb,
    Image: ToggleGroupItemImage,
    Chip: ToggleGroupItemChip,
};

const selectStyle = style({
    base: 'h-10 px-3 border-1 rounded-sm text-sm',
    variants: {
        invert: {
            true: 'text-neutral-0',
            false: 'text-neutral-100',
        },
        disabled: {
            true: '',
            false: '',
        },
        filled: {
            true: '',
            false: '',
        },
    },
    matches: [
        // Default state
        {
            invert: false,
            disabled: false,
            filled: false,
            use: 'border-neutral-40 hover:border-neutral-100',
        },
        // Default state (inverted)
        {
            invert: true,
            disabled: false,
            filled: false,
            use: 'border-neutral-50 hover:border-neutral-0',
        },
        // Focused state
        {
            invert: false,
            disabled: false,
            use: 'focus-within:(border-neutral-100 ring-2)',
        },
        // Focused state (inverted)
        {
            invert: true,
            disabled: false,
            use: 'focus-within:(border-neutral-0 ring-2)',
        },
        // Filled state
        {
            invert: false,
            disabled: false,
            filled: true,
            use: 'border-neutral-100',
        },
        // Filled state (inverted)
        {
            invert: true,
            disabled: false,
            filled: true,
            use: 'border-neutral-0',
        },
        // Disabled state
        {
            disabled: true,
            invert: false,
            use: 'bg-neutral-20 border-neutral-20 text-neutral-70',
        },
        // Disabled state (inverted)
        {
            disabled: true,
            invert: true,
            use: 'bg-neutral-80 border-neutral-80 text-neutral-30',
        },
    ],
});
const FilterSelectInner = React.forwardRef(function FilterSelectInner({ label }, ref) {
    const { id, value, invert, disabled, className, dataTestId } = useSelectContext();
    return (React.createElement(SelectRoot, { className: tw$1(selectStyle({
            invert,
            disabled,
            filled: !!value && value !== PLACEHOLDER_VALUE,
        }), className) },
        React.createElement("label", { className: "sr-only", "data-test-id": dataTestId && `${dataTestId}-label`, htmlFor: id }, label),
        React.createElement(SelectValue, { className: "text-base font-bold" }),
        React.createElement(SelectHiddenInput, { ref: ref }),
        React.createElement(SelectChevron, { className: "mt-0.5" })));
});
/**
 * The `FilterSelect` renders a styled `select` tag and is used to create a drop-down list.
 *
 * - The component is implemented with backwards compatibility in mind, and is likely to change
 *
 * @example
 * ```
 * <FilterSelect
 *   label="Select an option"
 *   placeholder="Select an option"
 *   options={[ { value: "optionOne", label: "Option One" } ]}
 *   defaultValue="optionOne"
 * />
 * ```
 */
const FilterSelect = React.forwardRef(function FilterSelect({ label, id: propsId, invert = false, disabled = false, ...props }, ref) {
    const id = useId(propsId);
    return (React.createElement(SelectProvider, { value: {
            id,
            invert,
            disabled,
            ...props,
        } },
        React.createElement(FilterSelectInner, { label: label, ref: ref })));
});

const FilterToggle = React.forwardRef(function FilterToggle(props, ref) {
    const { dataTestId, highlighted, invert, defaultPressed, pressed, onPressedChange, children, ...rest } = props;
    const [internalPressed, setInternalPressed] = React.useState(defaultPressed);
    useEffect(() => {
        if (is.exists(pressed)) {
            setInternalPressed(pressed);
        }
    }, [pressed]);
    const handleClick = () => {
        setInternalPressed(!internalPressed);
        onPressedChange && onPressedChange(!internalPressed);
    };
    const baseColors = tw$2(!internalPressed && !highlighted && 'border-neutral-30', internalPressed && 'border-neutral-100', highlighted && 'border-neutral-100', 'hover:border-neutral-100', 'focus:outline-none focus-visible:(border-neutral-100 ring-2)');
    const invertColors = tw$2(!internalPressed && !highlighted && 'border-neutral-70', internalPressed && 'border-neutral-0', highlighted && 'border-neutral-0', 'hover:border-neutral-0', 'focus:outline-none focus-visible:(border-neutral-0 ring-invert ring-2)');
    return (React.createElement("button", { ref: ref, type: "button", "data-uds-child": "filter-toggle", "aria-pressed": internalPressed ? 'true' : 'false', "data-test-id": dataTestId, onClick: handleClick, className: tw$2('relative h-10 px-3', 'flex gap-2 items-center', 'px-3 rounded-sm border-1', 'whitespace-nowrap text-base font-bold leading-none', invert ? invertColors : baseColors), ...rest },
        children,
        React.createElement(ToggleChevron, { open: internalPressed, size: "lg", className: "mt-0.5" })));
});

const Image = forwardRef((props, forwardedRef) => {
    const { src, onLoaded, onLoadingStatusChange, ...rest } = props;
    const imageLoadingStatus = useImageLoadingStatus(src);
    const handleLoadingStatusChange = useCallbackRef((status) => {
        onLoadingStatusChange?.(status);
    });
    useIsomorphicLayoutEffect$1(() => {
        if (imageLoadingStatus !== 'idle') {
            handleLoadingStatusChange?.(imageLoadingStatus);
        }
        if (imageLoadingStatus === 'loaded') {
            onLoaded?.();
        }
    }, [imageLoadingStatus, handleLoadingStatusChange]);
    return imageLoadingStatus === 'loaded' ? (React.createElement("img", { "data-uds-child": "image", ...rest, ref: forwardedRef, src: src })) : null;
});
Image.displayName = 'Image';
const useImageLoadingStatus = (src) => {
    const image = useRef();
    const [loadingStatus, setLoadingStatus] = useState('idle');
    useEffect(() => {
        /* istanbul ignore next */
        if (isServer)
            return;
        if (!src) {
            setLoadingStatus('error');
            return;
        }
        let isMounted = true;
        const updateStatus = (status) => () => {
            /* istanbul ignore next */
            if (!isMounted)
                return;
            setLoadingStatus(status);
        };
        setLoadingStatus('loading');
        image.current = new window.Image();
        image.current.onload = updateStatus('loaded');
        image.current.onerror = updateStatus('error');
        image.current.src = src;
        return () => {
            isMounted = false;
        };
    }, [src]);
    return loadingStatus;
};

const AVATAR_VARIANTS = {
    size: {
        sm: 'w-8 h-8 text-sm border-3',
        base: 'w-12 h-12 heading-sm border-4',
        lg: 'w-20 h-20 heading-base border-5',
    },
    invert: {
        true: 'bg-neutral-80 text-neutral-0 border-neutral-70',
        false: 'bg-neutral-10 text-neutral-100 border-neutral-0',
    },
};
const avatarStyle = style({ variants: AVATAR_VARIANTS });
const AvatarContext = React.createContext(null);
const useAvatarContext = () => useContext(AvatarContext);
/**
 * Avatar compound pattern. Render a rounded image, graphic or icon with an optional label.
 *
 * @example
 * ```tsx
 * <AvatarRoot>
 *   <AvatarImage src="img.png" />
 *   <AvatarLabel>JD</AvatarLabel>
 * </AvatarRoot>
 * ```
 *
 *  * @example
 * ```tsx
 * <AvatarRoot aria-label="John Doe">
 *   <AvatarImage src="img.png" />
 * </AvatarRoot>
 * ```
 */
const AvatarRoot = (props) => {
    const { dataTestId, children, className, size = 'base', invert = false, delay = 600, asChild, ...rest } = props;
    const Comp = asChild ? Slot : 'span';
    const timeoutRef = useRef();
    const [imageLoaded, setImageLoaded] = React.useState(false);
    const onImageLoaded = React.useCallback(() => {
        timeoutRef.current = setTimeout(() => {
            setImageLoaded(true);
        }, delay);
        /* istanbul ignore next */
        return () => {
            /* istanbul ignore next */
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);
    return (React.createElement(AvatarContext.Provider, { value: { size, invert, onImageLoaded, imageLoaded } },
        React.createElement(Comp, { "data-test-id": dataTestId, "data-uds-attr-media-ready": imageLoaded ? 'true' : 'false', className: tw$2('relative inline-grid grid-cols-1 rounded-full overflow-hidden items-center justify-items-center align-middle', 'flex-none min-w-8 min-h-8', 'children:(col-start-1 row-start-1)', avatarStyle({ size, invert }), css `
            & > [data-uds-child='avatar-image'],
            & > [data-uds-child='avatar-graphic'] {
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              object-fit: cover;
              object-position: center;
              z-index: 0;
              opacity: 0;
            }

            & > [data-uds-child='avatar-image'],
            & > [data-uds-child='avatar-icon'],
            & > [data-uds-child='avatar-graphic'] {
              z-index: 0;
              opacity: 0;
            }

            & > [data-uds-child='avatar-label'] {
              z-index: 1;
              opacity: 1;
              margin-top: -0.075em;
            }

            &[data-uds-attr-media-ready='true'] {
              & > [data-uds-child='avatar-image'],
              & > [data-uds-child='avatar-icon'],
              & > [data-uds-child='avatar-graphic'] {
                opacity: 1;
              }
              & > [data-uds-child='avatar-label'] {
                opacity: 0;
              }
            }
          `, className), ...rest, "data-uds-child": "avatar" }, children)));
};
/**
 * Render an image inside the avatar.
 *
 * @example
 * ```tsx
 * <AvatarRoot>
 *   <AvatarImage src="img.png" />
 *   <AvatarLabel>JD</AvatarLabel>
 * </AvatarRoot>
 * ```
 */
const AvatarImage = (props) => {
    const { onImageLoaded } = useAvatarContext();
    return (React.createElement(Image, { "data-uds-child": "avatar-image", onLoaded: onImageLoaded, ...props }));
};
/**
 * Render an svg graphic inside the avatar
 *
 * @example
 * ```tsx
 * <AvatarRoot>
 *   <AvatarGraphic>
 *     <svg viewBox="0 0 24 24" fill="currentColor"/>
 *   </AvatarGraphic>
 *   <AvatarLabel>JD</AvatarLabel>
 * </AvatarRoot>
 * ```
 */
const AvatarGraphic = (props) => {
    const { className, ...rest } = props;
    const { onImageLoaded } = useAvatarContext();
    useEffect(() => {
        onImageLoaded();
    }, []);
    return (React.createElement("div", { "data-uds-child": "avatar-graphic", className: tw$2(className, 'children:(w-full aspect-ratio-1/1)'), ...rest }));
};
/**
 * Render a UDS icon inside the avatar. Extends the `Icon` component, and inherits all of its props.
 *
 * @example
 * ```tsx
 * <AvatarRoot>
 *   <AvatarIcon name="user"/>
 *   <AvatarLabel>JD</AvatarLabel>
 * </AvatarRoot>
 * ```
 */
const AvatarIcon = ({ size, ...rest }) => {
    const { onImageLoaded, size: contextSize } = useAvatarContext();
    useEffect(() => {
        onImageLoaded();
    }, []);
    const autoSize = useResponsiveMap(contextSize, {
        sm: 'lg',
        base: '2xl',
        lg: '4xl',
    });
    return (React.createElement(Icon, { name: "filter", size: size ?? autoSize, "data-uds-child": "avatar-icon", ...rest }));
};
/**
 * Render a text label within the avatar. If an icon, image or graphic is present, the label will be hidden, as soon as the asset is loaded.
 *
 * The Label inherits the following props from the Text component: transform, weight, condensed, color
 *
 * @example
 * ```tsx
 * <AvatarRoot>
 *   <AvatarLabel color="info">John Doe</AvatarLabel>
 * </AvatarRoot>
 * ```
 */
const AvatarLabel = (props) => {
    return (React.createElement(Text, { "data-uds-child": "avatar-label", transform: "uppercase", ...props }));
};

const vignetteStyle = style({
    base: css$1 `
    pointer-events: none;
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
    mix-blend-mode: multiply;
    background: linear-gradient(
      var(--media-vignette-angle),
      rgba(0, 0, 0, var(--grad-start)) 0%,
      rgba(0, 0, 0, var(--grad-end)) var(--grad-spread)
    );
  `,
    variants: {
        vignette: {
            cover: css$1 `
        display: block;
        --media-vignette-angle: 0deg;
        --grad-start: 0.3;
        --grad-end: 0.3;
        --grad-spread: 100%;
      `,
            bottom: css$1 `
        display: block;
        --media-vignette-angle: 0deg;
        --grad-start: 0.45;
        --grad-end: 0;
        --grad-spread: 60%;
      `,
            bottom_left: css$1 `
        display: block;
        --media-vignette-angle: 45deg;
        --grad-start: 0.45;
        --grad-end: 0;
        --grad-spread: 60%;
      `,
            left: css$1 `
        display: block;
        --media-vignette-angle: 90deg;
        --grad-start: 0.45;
        --grad-end: 0;
        --grad-spread: 60%;
      `,
            top_left: css$1 `
        display: block;
        --media-vignette-angle: 135deg;
        --grad-start: 0.45;
        --grad-end: 0;
        --grad-spread: 60%;
      `,
            top: css$1 `
        display: block;
        --media-vignette-angle: 180deg;
        --grad-start: 0.45;
        --grad-end: 0;
        --grad-spread: 60%;
      `,
            top_right: css$1 `
        display: block;
        --media-vignette-angle: 225deg;
        --grad-start: 0.45;
        --grad-end: 0;
        --grad-spread: 60%;
      `,
            right: css$1 `
        display: block;
        --media-vignette-angle: 270deg;
        --grad-start: 0.45;
        --grad-end: 0;
        --grad-spread: 60%;
      `,
            bottom_right: css$1 `
        display: block;
        --media-vignette-angle: 315deg;
        --grad-start: 0.45;
        --grad-end: 0;
        --grad-spread: 60%;
      `,
            true: css$1 `
        display: block;
        --media-vignette-angle: 0deg;
        --grad-start: 0.45;
        --grad-end: 0;
        --grad-spread: 60%;
      `,
            false: css$1 `
        display: none;
        -grad-start: 0;
        --grad-end: 0;
      `,
        },
    },
});
/**
 * A container for image and video content that maintains a fixed aspect ratio. Optionally applies a vignette to improve readability.
 *
 * @example
 * ```jsx
 * <MediaContainer>
 *   <img src="https://cdn.jpg" />
 * </MediaContainer>
 * ```
 */
const MediaContainer = React.forwardRef(function MediaContainer({ ratio = 1, vignette = false, className, children, dataTestId, ...rest }, ref) {
    return (React.createElement(AspectRatio, { ...rest, ref: ref, ratio: ratio, "data-test-id": dataTestId, "data-uds-child": "media-container", className: className },
        React.createElement("div", { className: tw$2('absolute inset-0 w-full h-full', css$1 `
            & img,
            & video {
              object-fit: cover;
              object-position: center;
              position: absolute;
              inset: 0;
              width: 100%;
              height: 100%;
            }
          `, className) },
            children,
            vignette ? (React.createElement("div", { className: tw$2(vignetteStyle({
                    vignette,
                })), "data-uds-child": "media-container-vignette" })) : null)));
});

const VideoPlayerBigPlayBtn = ({ disabled, playing, onClick, ...rest }) => {
    const mouseDeltaRef = useRef(0);
    const mouseDeltaTimeoutRef = useRef();
    const buttonRef = useRef(null);
    const fadeOutTimeoutRef = useRef();
    const fadeInThenOut = () => {
        buttonRef.current?.classList.remove('opacity-0');
        clearTimeout(fadeOutTimeoutRef.current);
        fadeOutTimeoutRef.current = setTimeout(fadeout, 2500);
    };
    useEffect(() => {
        clearTimeout(fadeOutTimeoutRef.current);
        if (!playing) {
            fadeOutTimeoutRef.current = setTimeout(fadein, 100);
        }
        else {
            fadeOutTimeoutRef.current = setTimeout(fadeout, 500);
        }
    }, [playing]);
    const fadein = () => {
        buttonRef.current?.classList.remove('opacity-0');
    };
    const fadeout = () => {
        buttonRef.current?.classList.add('opacity-0');
    };
    const handleMouseEnter = () => {
        if (!playing)
            return;
        fadeInThenOut();
    };
    const handleMouseMove = e => {
        if (!playing)
            return;
        clearTimeout(mouseDeltaTimeoutRef.current);
        const { movementX, movementY } = e;
        mouseDeltaRef.current += Math.abs(movementX) + Math.abs(movementY);
        mouseDeltaTimeoutRef.current = setTimeout(() => {
            mouseDeltaRef.current = 0;
        }, 400);
        if (mouseDeltaRef.current > 20) {
            fadeInThenOut();
        }
    };
    const handleMouseLeave = () => {
        if (!playing)
            return;
        clearTimeout(fadeOutTimeoutRef.current);
        fadeout();
    };
    useEffect(() => {
        return () => clearTimeout(fadeOutTimeoutRef.current);
    }, []);
    return (React.createElement("button", { "aria-label": "Play", onClick: onClick, ref: buttonRef, onMouseMove: handleMouseMove, onMouseOver: handleMouseEnter, onMouseOut: handleMouseLeave, onFocus: handleMouseEnter, onBlur: handleMouseLeave, className: tw('opacity-0', 'transition-opacity duration-700 absolute inset-0 flex items-center justify-center z-20', disabled && 'pointer-events-none opacity-0', 'focus:(outline-none) focus-visible:(ring-2)'), ...rest },
        React.createElement("div", { className: tw(playing && 'opacity-0', 'absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-1/4 min-w-12 max-w-24') },
            React.createElement(Icon, { name: 'play-filled', aspectRatio: 1, color: "base", invert: true })),
        React.createElement("div", { className: tw(!playing && 'opacity-0', 'absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-1/4 min-w-12 max-w-24') },
            React.createElement(Icon, { name: 'pause-filled', aspectRatio: 1, color: "base", invert: true }))));
};

/**
 * Video player with built in accessibility features. When set to `autoplay` the video won't load until the player intersects with the viewport
 *
 * @example
 * ```
 * <Video
 *   autoplay
 *   src="video.mp4"
 *   poster="/poster.png"
 *   aspectRatio={9/16}
 *   controls="none"
 * />
 * ```
 */
const VideoPlayer = (props) => {
    const { src, className, aspectRatio, autoPlay = false, autoPause = true, controls = 'plain', muted = false, loop, poster, lqip, onPlay, onPause, onLoad, onEnded, dataTestId, ...rest } = props;
    const player = useRef(null);
    const [ref, inView] = useIntersectionObserver({ threshold: 0.5 });
    const isTabVisible = useVisibilityChange();
    const isPlayerVisible = inView && isTabVisible;
    const [playing, setPlaying] = useState(false);
    const [source, setSource] = useState();
    const [started, setStarted] = useState(false);
    const [isMuted, setIsMuted] = useState(autoPlay || muted);
    const { devicePreferences } = useUdsContext();
    const prefersReducedData = devicePreferences.prefersReducedData;
    const prefersReducedMotion = devicePreferences.prefersReducedMotion;
    const hasPoster = is.exists(poster);
    const isAutoplayEnabled = autoPlay || (controls === 'none' && !hasPoster);
    const videoWrapClassName = aspectRatio
        ? `aspect-ratio-[${aspectRatio}]`
        : `absolute w-full h-full object-cover`;
    // a video can not autoplay if user prefers reduced data or reduced motion
    const canAutoPlay = isAutoplayEnabled && !prefersReducedData && !prefersReducedMotion;
    // inview autoplay/autopause
    const shouldAutoPlay = isPlayerVisible && canAutoPlay;
    const shouldStopPlaying = !isPlayerVisible && playing && autoPause;
    const [renderBigPlay, setRenderBigPlay] = useState(true);
    const bigPlayMounted = useDelayUnmount(renderBigPlay, 1000);
    const [renderPoster, setRenderPoster] = useState(false);
    const posterMounted = useDelayUnmount(renderPoster, 1000);
    const shouldUnmountBigPlay = controls === 'full'; // || (controls === 'none' && !hasPoster && !canAutoPlay);
    useEffect(() => {
        if (hasPoster && inView && !shouldAutoPlay && !playing) {
            setRenderPoster(true);
        }
    }, [inView, playing, hasPoster, shouldAutoPlay]);
    const hasBigPlayButton = (controls === 'none' && !hasPoster && !canAutoPlay) ||
        (controls === 'full' && !hasPoster) ||
        controls !== 'none';
    const videoUrl = src;
    const load = () => {
        setSource(videoUrl);
        player.current.src = videoUrl;
        player.current.load();
    };
    const play = () => {
        if (!source) {
            load();
        }
        setPlaying(true);
        const promise = player.current.play();
        if (promise) {
            promise.catch(e => console.log(e));
        }
    };
    const pause = () => {
        setPlaying(false);
        player.current.pause();
    };
    const handlePlay = e => {
        if (onPlay)
            onPlay(e);
        setStarted(true);
        setRenderPoster(false);
        if (shouldUnmountBigPlay) {
            setRenderBigPlay(false);
        }
    };
    const handleBigPlayClick = () => {
        playing ? pause() : play();
    };
    useEffect(() => {
        if (shouldStopPlaying) {
            pause();
        }
        else if (shouldAutoPlay) {
            play();
        }
        else if (!hasPoster && isPlayerVisible) {
            load();
        }
    }, [isPlayerVisible, hasPoster, shouldAutoPlay, shouldStopPlaying]);
    return (React.createElement("div", { ...rest, ref: ref, className: tw(className, videoWrapClassName, 'z-0 bg-cover bg-no-repeat bg-center'), style: { backgroundImage: lqip ? `url(${lqip})` : 'none' }, "data-test-id": dataTestId, "data-uds-child": "video-player-wrapper" },
        posterMounted ? (React.createElement("div", null,
            React.createElement("img", { src: poster, "data-uds-child": "video-player-poster", "data-test-id": dataTestId ? `${dataTestId}-poster` : undefined, className: tw('transition-opacity object-cover object-center w-full h-full absolute z-10', started ? 'pointer-events-none opacity-0' : 'opacity-100') }))) : null,
        hasBigPlayButton && bigPlayMounted ? (React.createElement(VideoPlayerBigPlayBtn, { onClick: handleBigPlayClick, playing: playing, disabled: !renderBigPlay, "data-uds-child": "video-player-big-play", "data-test-id": dataTestId ? `${dataTestId}-big-play` : undefined })) : null,
        React.createElement("video", { ref: player, autoPlay: false, controls: controls === 'full', muted: isMuted, loop: loop, playsInline: true, preload: "none", className: tw('w-full h-full object-cover object-center transition-opacity duration-500', source ? 'opacity-100' : 'opacity-0'), onPlay: handlePlay, onPause: onPause, onLoad: onLoad, onEnded: onEnded, "data-uds-child": "video-player-source", "data-test-id": dataTestId ? `${dataTestId}-source` : undefined })));
};

const collabStyle$1 = style({
    base: 'flex items-center',
    variants: {
        size: {
            sm: 'text-sm gap-2',
            base: 'text-base gap-3',
            lg: 'text-lg gap-4',
            xl: 'text-xl gap-4',
        },
        invert: {
            true: 'text-white',
            false: 'text-neutral',
        },
    },
});
const imageStyle = style({
    variants: {
        size: {
            sm: 'h-6',
            base: 'h-8',
            lg: 'h-10',
            xl: 'h-12',
        },
    },
});
const logoWrapper = style({
    base: 'flex after:(w-px)',
    variants: {
        size: {
            sm: 'gap-2 after:(h-6 ml-1)',
            base: 'gap-3 after:(h-8 ml-1)',
            lg: 'gap-4 after:(h-10 ml-1)',
            xl: 'gap-4 after:(h-12 ml-1)',
        },
        showPumaLogo: {
            true: css$1 `
        &:after {
          content: ' ';
          display: block;
          background-color: currentColor;
        }
      `,
            false: 'hidden',
        },
    },
});
const pumaLogoStyle = style({
    variants: {
        size: {
            sm: 'w-6 h-6',
            base: 'w-8 h-8',
            lg: 'w-10 h-10',
            xl: 'w-12 h-12',
        },
    },
});
const Collab = ({ size = 'base', collabLogo, showPumaLogo, dataTestId, children, invert = false, className, }) => {
    return (React.createElement("div", { className: tw$1(className, collabStyle$1({ size, invert })), "data-uds-child": "collab", "data-test-id": dataTestId },
        React.createElement("span", { className: tw$1('font-bold uppercase', collabLogo ? 'sr-only' : ''), "data-test-id": dataTestId && `${dataTestId}-copy` }, children),
        collabLogo ? (React.createElement(React.Fragment, null,
            showPumaLogo ? (React.createElement("div", { className: tw$1('flex', logoWrapper({ showPumaLogo, size })) },
                React.createElement("div", { className: tw$1(pumaLogoStyle({ size })) },
                    React.createElement(Icon, { aspectRatio: 1, name: "puma-logo", "data-test-id": dataTestId && `${dataTestId}-puma-logo` })))) : null,
            React.createElement("img", { src: collabLogo, alt: "", "data-test-id": dataTestId && `${dataTestId}-logo`, className: tw$1(imageStyle({ size })) }))) : null));
};

const HeroContext = createContext(undefined);
const HeroProvider = ({ children, value, }) => React.createElement(HeroContext.Provider, { value: value }, children);
const useHeroContext = () => {
    const context = useContext(HeroContext);
    if (!context) {
        throw new Error('useHeroContext must be used within a HeroProvider');
    }
    return context;
};

/**
 * HEADING SIZE MAPS
 */
const HEADING_SIZE_MAP_OVERLAY_1 = [
    { width: 0, size: 'heading-lg' },
    { width: 640, size: 'heading-xl' },
    { width: 1024, size: 'heading-2xl' },
    { width: 1280, size: 'heading-3xl' },
    { width: 1920, size: 'heading-4xl' },
];
const HEADING_SIZE_MAP_OVERLAY_2 = [
    { width: 0, size: 'heading-lg' },
    { width: 640, size: 'heading-xl' },
    { width: 1024, size: 'heading-2xl' },
    { width: 1280, size: 'heading-3xl' },
    { width: 1920, size: 'heading-3xl' },
];
const HEADING_SIZE_MAP_OVERLAY_3 = [
    { width: 0, size: 'heading-lg' },
    { width: 640, size: 'heading-xl' },
    { width: 1024, size: 'heading-2xl' },
    { width: 1280, size: 'heading-2xl' },
    { width: 1920, size: 'heading-3xl' },
];
const HEADING_SIZE_MAP_SPLIT_1 = [
    { width: 0, size: 'heading-lg' },
    { width: 640, size: 'heading-lg' },
    { width: 1024, size: 'heading-xl' },
    { width: 1280, size: 'heading-2xl' },
    { width: 1600, size: 'heading-3xl' },
    { width: 1920, size: 'heading-4xl' },
];
const HEADING_SIZE_MAP_SPLIT_2 = [
    { width: 0, size: 'heading-base' },
    { width: 640, size: 'heading-base' },
    { width: 1024, size: 'heading-lg' },
    { width: 1280, size: 'heading-xl' },
    { width: 1600, size: 'heading-2xl' },
    { width: 1920, size: 'heading-3xl' },
];
/**
 * DESCRIPTION SIZE MAPS
 */
const DESCRIPTION_SIZE_MAP_OVERLAY_1 = [
    { width: 0, size: 'mt-2 heading-sm' },
    { width: 1024, size: 'mt-3 heading-base' },
    { width: 1920, size: 'mt-4 heading-lg' },
];
const DESCRIPTION_SIZE_MAP_OVERLAY_2 = [
    { width: 0, size: 'mt-2 heading-sm' },
    { width: 1024, size: 'mt-3 heading-base' },
    { width: 1920, size: 'mt-4 heading-lg' },
];
const DESCRIPTION_SIZE_MAP_OVERLAY_3 = [
    { width: 0, size: 'mt-2 heading-sm' },
    { width: 1024, size: 'mt-3 heading-base' },
    { width: 1920, size: 'mt-4 heading-lg' },
];
const DESCRIPTION_SIZE_MAP_SPLIT_1 = [
    { width: 0, size: 'mt-2 heading-sm' },
    { width: 768, size: 'mt-3 heading-sm' },
    { width: 1280, size: 'mt-4 heading-base' },
    { width: 1920, size: 'mt-4 heading-lg' },
];
const DESCRIPTION_SIZE_MAP_SPLIT_2 = [
    { width: 0, size: 'mt-2 heading-sm' },
    { width: 1024, size: 'mt-3 heading-base' },
    { width: 1920, size: 'mt-4 heading-lg' },
];
/** Utility function */
const findSize = (data, width) => {
    let result;
    data.every(item => {
        if (item.width > width) {
            return false;
        }
        result = item.size;
        return true;
    });
    return result;
};
/**
 * Content Padding
 */
const SPLIT_CONTENT_PADDING_MAP = {
    '1920': {
        start: 'py-12 px-10',
        end: 'py-12 pl-12 pr-10',
    },
    '1440': {
        start: 'py-12 px-10',
        end: 'py-12 px-10',
    },
    '1280': {
        start: 'py-10 px-8',
        end: 'py-10 px-8',
    },
    '1024': {
        start: 'py-10 px-8',
        end: 'py-10 px-8',
    },
    '768': {
        start: 'py-8 px-6',
        end: 'py-8 px-6',
    },
    '640': {
        start: 'py-6 px-6',
        end: 'py-6 px-6',
    },
    '540': {
        start: 'py-6 px-6',
        end: 'py-6 px-6',
    },
    _: {
        start: 'py-4 px-4',
        end: 'py-4 px-4',
    },
};
const OVERLAY_CONTENT_PADDING_MAP = {
    '1920': 'py-12 px-10',
    '1440': 'py-12 px-10',
    '1280': 'py-10 px-10',
    '1024': 'py-10 px-10',
    '768': 'py-8 px-8',
    '640': 'py-8 px-4',
    '540': 'py-6 px-4',
    _: 'py-4 px-4',
};
/**
 * Layout
 */
const LAYOUT_MAP_OVERLAY = {
    '1280': {
        media: ['1 / span 12', 1],
        content_start: ['2 / span 5', 1],
        content_center: ['4 / span 6', 1],
        content_end: ['8 / span 5', 1],
    },
    '768': {
        media: ['1 / span 12', 1],
        content_start: ['2 / span 6', 1],
        content_center: ['4 / span 6', 1],
        content_end: ['6 / span 6', 1],
    },
    '640': {
        media: ['1 / span 12', 1],
        content_start: ['2 / span 6', 1],
        content_center: ['4 / span 6', 1],
        content_end: ['6 / span 6', 1],
    },
    _: {
        media: ['1 / span 12', 1],
        content_start: ['2 / span 10', 1],
        content_center: ['2 / span 10', 1],
        content_end: ['2 / span 10', 1],
    },
};
const LAYOUT_MAP_SPLIT = {
    '1280': {
        media_start: ['span 7', 1],
        media_end: ['6 / span 7', 1],
        content_start: ['2 / span 4', 1],
        content_end: ['8 / span 4', 1],
    },
    '768': {
        media_start: ['span 7', 1],
        media_end: ['6 / span 7', 1],
        content_start: ['2 / span 4', 1],
        content_end: ['8 / span 4', 1],
    },
    '640': {
        media_start: ['span 6', 1],
        media_end: ['7 / span 6', 1],
        content_start: ['span 6', 1],
        content_end: ['7 / span 6', 1],
    },
    _: {
        media_start: ['1 / span 12', 1],
        media_end: ['1 / span 12', 1],
        content_start: ['1 / span 12', 2],
        content_end: ['1 / span 12', 2],
    },
};

const useLayoutStyles = ({ layout, width, }) => {
    const getGridStyles = () => {
        const mapKey = width >= 1280
            ? '1280'
            : width >= 768
                ? '768'
                : width >= 640
                    ? '640'
                    : '_';
        if (layout === 'split') {
            const config = LAYOUT_MAP_SPLIT[mapKey];
            return css `
        &
          > [data-uds-child='hero-content']
          + [data-uds-child='hero-media-container'] {
          grid-column: ${config.media_end[0]};
          grid-row: ${config.media_end[1]};
        }
        & > [data-uds-child='hero-media-container'] {
          grid-column: ${config.media_start[0]};
          grid-row: ${config.media_start[1]};
        }

        &
          > [data-uds-child='hero-media-container']
          + [data-uds-child='hero-content'] {
          grid-column: ${config.content_end[0]};
          grid-row: ${config.content_end[1]};
        }
        & > [data-uds-child='hero-content'] {
          grid-column: ${config.content_start[0]};
          grid-row: ${config.content_start[1]};
        }
      `;
        }
        if (layout === 'overlay') {
            const config = LAYOUT_MAP_OVERLAY[mapKey];
            return css `
        & > [data-uds-child='hero-media-container'] {
          grid-column: ${config.media[0]};
          grid-row: ${config.media[1]};
        }
        & > [data-uds-child='hero-content'][data-uds-justify='start'] {
          grid-column: ${config.content_start[0]};
          grid-row: ${config.content_start[1]};
        }
        & > [data-uds-child='hero-content'][data-uds-justify='center'] {
          grid-column: ${config.content_center[0]};
          grid-row: ${config.content_center[1]};
        }
        & > [data-uds-child='hero-content'][data-uds-justify='end'] {
          grid-column: ${config.content_end[0]};
          grid-row: ${config.content_end[1]};
        }
      `;
        }
    };
    const getPaddingStyles = () => {
        const mapKey = width >= 1920
            ? '1920'
            : width >= 1440
                ? '1440'
                : width >= 1280
                    ? '1280'
                    : width >= 1024
                        ? '1024'
                        : width >= 768
                            ? '768'
                            : width >= 640
                                ? '640'
                                : width >= 540
                                    ? '540'
                                    : '_';
        if (layout === 'split') {
            const config = SPLIT_CONTENT_PADDING_MAP[mapKey];
            return css `
        & > [data-uds-child='hero-content'] {
          @apply ${config.start};
        }
        &
          > [data-uds-child='hero-media-container']
          + [data-uds-child='hero-content'] {
          @apply ${config.end};
        }
      `;
        }
        if (layout === 'overlay') {
            const config = OVERLAY_CONTENT_PADDING_MAP[mapKey];
            return css `
        & > [data-uds-child='hero-content'] {
          @apply ${config};
        }
      `;
        }
    };
    return [getGridStyles(), getPaddingStyles()];
};

const MAX_CONTENT_WIDTH = 'screens.3xl';
const heroStyles = style({
    variants: {
        invert: {
            true: 'bg-neutral-100',
            false: 'bg-neutral-0',
        },
    },
});
/**
 * The HeroRoot component is the base component used to render a hero section.
 *
 * @example
 * ```
 * <HeroRoot
 *   layout="split"
 *   level={1}
 * >
 *   <HeroMediaContainer>
 *     <HeroMedia>
 *       <img src="image.jpg" />
 *     </HeroMedia>
 *   </HeroMediaContainer>
 *   <HeroContent>
 *     <HeroHeading>Hero Title</HeroHeading>
 *     <HeroDescription>Hero Description</HeroDescription>
 *   </HeroContent>
 * </HeroRoot>
 * ```
 *
 */
const HeroRoot = ({ layout = 'overlay', level: levelProp = 1, className, dataTestId, children, asChild, invert, ...rest }) => {
    const Comp = asChild ? Slot : 'article';
    const { ref, width } = useResizeObserver();
    const level = layout === 'split' && levelProp === 3 ? 2 : levelProp;
    const contextValue = { layout, level, width, invert, dataTestId };
    const layoutStyles = useLayoutStyles({ layout, width });
    return (React.createElement(HeroProvider, { value: contextValue },
        React.createElement(Comp, { className: tw('relative grid w-full', css `
            grid-template-columns:
              1fr
              repeat(10, min(10%, calc(${theme(MAX_CONTENT_WIDTH)} * 0.1)))
              1fr;
          `, heroStyles({ invert }), ...layoutStyles, className), "data-uds-child": "hero", "data-test-id": dataTestId, ref: ref, ...rest }, children)));
};

/**
 * The HeroMediaContainer component is used to wrap the HeroMedia component.
 * - It should be rendered directly inside the HeroRoot component, alongside the HeroContent component.
 *
 * @example
 * ```
 * <HeroMediaContainer>
 *   <HeroMedia>
 *     <img src="image.jpg" />
 *   </HeroMedia>
 * </HeroMediaContainer>
 * ```
 */
const HeroMediaContainer = ({ className, children, ...rest }) => {
    const { dataTestId } = useHeroContext();
    return (React.createElement("div", { className: tw$2('relative h-full', className), "data-test-id": dataTestId && `${dataTestId}-media-container`, "data-uds-child": "hero-media-container", ...rest }, children));
};

const useHeroRatio = () => {
    const { layout, level, width } = useHeroContext();
    if (layout === 'split') {
        if (width > 768) {
            return level === 1 ? 0.75 : 0.562;
        }
        else if (width > 640) {
            return 1;
        }
        else {
            return level === 1 ? 1 : 0.75;
        }
    }
    if (layout === 'overlay') {
        if (width > 1024) {
            return level === 1 ? 0.5 : level === 2 ? 0.428 : 0.25;
        }
        else if (width > 640) {
            return level === 1 ? 1 : level === 2 ? 0.75 : 0.333;
        }
        else {
            return level === 1 ? 1.5 : level === 2 ? 1.25 : 0.5;
        }
    }
};
/**
 * The HeroMedia component is used to render the media of a hero section.
 * - It should be rendered directly inside the HeroMediaContainer component.
 * - Insert an image or video element as a child.
 * - Overlay layouts includes a vignette effect to improve readability
 *   - Can be disabled by setting `vignette` to `false`.
 * - Internally uses the AspectRatio component.
 *
 * @example
 * ```
 * <HeroMedia vignette>
 *   <img src="image.jpg" />
 * </HeroMedia>
 * ```
 */
const HeroMedia = ({ vignette = true, className, ...rest }) => {
    const { layout, width, dataTestId } = useHeroContext();
    const ratio = useHeroRatio();
    const overlayVignette = width > 1024 ? 'left' : width > 640 ? 'bottom_left' : 'bottom';
    return (React.createElement(MediaContainer, { ...rest, dataTestId: dataTestId && `${dataTestId}-media`, ratio: ratio, vignette: layout === 'overlay' && vignette ? overlayVignette : false, className: tw$2('h-full', className), overflow: true }));
};

const outerStyles = style({
    variants: {
        justify: {
            start: 'text-left rtl:text-right',
            center: 'text-center',
            end: 'text-right rtl:text-left',
        },
    },
});
const contentStyles = style({
    variants: {
        justify: {
            start: 'items-start text-left rtl:text-right',
            center: 'items-center text-center',
            end: 'items-end text-right rtl:text-left',
        },
    },
});
/**
 * The HeroContent component is used to render the content of a hero section.
 *
 * - It should be rendered directly inside the HeroRoot component.
 * - For split layouts, it will be positioned on the opposite side of the HeroMediaContainer.
 *   - Place before or after the HeroMediaContainer component, depending on the desired layout.
 * - For overlay layouts, it will be positioned on top of the hero media.
 *
 * @example
 * ```
 * <HeroContent>
 *   <HeroHeading>Hero Title</HeroHeading>
 *   <HeroDescription>Hero Description</HeroDescription>
 * </HeroContent>
 * ```
 */
const HeroContent = ({ children, justify = 'start', className, ...rest }) => {
    const { layout, width, dataTestId } = useHeroContext();
    return (React.createElement("div", { "data-test-id": dataTestId && `${dataTestId}-content`, "data-uds-child": "hero-content", "data-uds-justify": justify, className: tw$1('relative max-w-screen-3xl', 'w-full flex', width < 1024 && layout === 'overlay' ? 'items-end' : 'items-center', outerStyles({ justify }), className), ...rest },
        React.createElement("div", { className: tw$1('flex flex-1 flex-col', contentStyles({ justify })) }, children)));
};

/**
 * HeroHeading is used to render a heading for the hero.
 * - It should be rendered directly inside the HeroContent component.
 *
 * @example
 * ```
 * <HeroHeading>Hero Heading</HeroHeading>
 * ```
 */
const HeroHeading = ({ className, children, color = 'base', transform = 'uppercase', ...rest }) => {
    const { dataTestId, width, layout, level, invert } = useHeroContext();
    const sizeClassName = useMemo(() => {
        switch (true) {
            case layout === 'overlay' && level === 1:
                return findSize(HEADING_SIZE_MAP_OVERLAY_1, width);
            case layout === 'overlay' && level === 2:
                return findSize(HEADING_SIZE_MAP_OVERLAY_2, width);
            case layout === 'overlay' && level === 3:
                return findSize(HEADING_SIZE_MAP_OVERLAY_3, width);
            case layout === 'split' && level === 1:
                return findSize(HEADING_SIZE_MAP_SPLIT_1, width);
            case layout === 'split' && level === 2:
                return findSize(HEADING_SIZE_MAP_SPLIT_2, width);
        }
    }, [width, layout, level]);
    return (React.createElement(Heading, { "data-test-id": dataTestId && `${dataTestId}-heading`, "data-uds-child": "hero-heading", transform: transform, className: tw$2(sizeClassName, className), invert: invert, color: color, ...rest }, children));
};

/**
 * HeroDescription is used to render a description for the hero.
 * - It should be rendered directly inside the HeroContent component.
 * - It should follow a HeroHeading component.
 *
 * @example
 * ```
 * <HeroDescription>Hero Description</HeroDescription>
 * ```
 */
const HeroDescription = ({ transform = 'uppercase', color = 'base', weight = '400', className, children, ...rest }) => {
    const { dataTestId, width, layout, level, invert } = useHeroContext();
    const sizeClassName = useMemo(() => {
        switch (true) {
            case layout === 'overlay' && level === 1:
                return findSize(DESCRIPTION_SIZE_MAP_OVERLAY_1, width);
            case layout === 'overlay' && level === 2:
                return findSize(DESCRIPTION_SIZE_MAP_OVERLAY_2, width);
            case layout === 'overlay' && level === 3:
                return findSize(DESCRIPTION_SIZE_MAP_OVERLAY_3, width);
            case layout === 'split' && level === 1:
                return findSize(DESCRIPTION_SIZE_MAP_SPLIT_1, width);
            case layout === 'split' && level === 2:
                return findSize(DESCRIPTION_SIZE_MAP_SPLIT_2, width);
        }
    }, [width, layout, level]);
    return (React.createElement(Heading, { "data-test-id": dataTestId && `${dataTestId}-description`, "data-uds-child": "hero-description", transform: transform, className: tw$2(sizeClassName, className), color: color, weight: weight, invert: invert, ...rest }, children));
};

/**
 * Use the HeroActionList component to display a list of actions in a Hero.
 * - The container, will apply the correct spacing and alignment based on the configuration of the parent HeroRoot.
 *
 * @example
 * ```
 * <HeroActionList>
 *   <LinkCta label="Shop Sports" href="/sports" />
 *   <LinkCta label="Shop Mens Shoes" href="/mens-shoes" />
 * </HeroActionList>
 * ```
 */
const HeroActionList = ({ className, children, ...rest }) => {
    const { dataTestId, invert } = useHeroContext();
    return (React.createElement("div", { ...rest, className: tw$2('flex flex-wrap max-w-max items-baseline gap-x-4 gap-y-2 mt-6 children:flex-1', className), "data-uds-child": "hero-actions", "data-test-id": dataTestId && `${dataTestId}-actions` }, React.Children.map(children, child => {
        return React.cloneElement(child, {
            invert: child.props?.invert ?? invert,
        });
    })));
};

/**
 * HeroFootnote is used to render a footnote for the hero.
 * - It should be rendered directly inside the HeroContent component.
 *
 * @example
 * ```
 * <HeroFootnote>Hero Footnote</HeroFootnote>
 * ```
 */
const HeroFootnote = ({ className, children, size = 'sm', color = 'base', ...rest }) => {
    const { dataTestId, invert } = useHeroContext();
    return (React.createElement(Text, { "data-test-id": dataTestId && `${dataTestId}-footnote`, "data-uds-child": "hero-footnote", className: tw$2('mt-4', className), size: size, color: color, invert: invert, asChild: true, ...rest }, children));
};

/**
 * HeroCollab is a component that is used to display a collaboration between PUMA and another brand.
 *
 * Uses the Float internally, and can be used directly as a child of the HeroMediaContainer or the HeroContent.
 *
 * It is positioned absolutely by default, but can be positioned relatively by setting the `inline` prop to `true`.
 *
 * @example
 * ```jsx
 * <HeroRoot>
 *   <HeroMediaContainer>
 *     <HeroCollab showPumaLogo>Brand</HeroCollab>
 *     <HeroMedia>
 *       ...
 *     </HeroMedia>
 *   </HeroMediaContainer>
 *   <HeroContent>
 *     ...
 *   </HeroContent>
 * </HeroRoot>
 * ```
 *
 * @example
 * ```
 * <HeroRoot>
 *   <HeroMediaContainer>
 *     ...
 *   </HeroMediaContainer>
 *   <HeroContent>
 *     <HeroCollab showPumaLogo inline>Brand</HeroCollab>
 *     <HeroHeading>Heading</HeroHeading>
 *     <HeroDescription>Description</HeroDescription>
 *   </HeroContent>
 * </HeroRoot>
 * ```
 */
const HeroCollab = ({ inline = false, align = 'top', justify = 'end', inset, insetX, insetY, showPumaLogo = true, size, invert, collabLogo, className, children, }) => {
    const { dataTestId, invert: contextInvert, width } = useHeroContext();
    return (React.createElement(Float, { align: align, justify: justify, invert: invert ?? contextInvert, inset: inset, insetY: insetY ?? inline
            ? 'none'
            : width > 1024
                ? 'base'
                : width > 640
                    ? 'sm'
                    : 'xs', insetX: insetX ?? inline
            ? 'none'
            : width > 1024
                ? 'md'
                : width > 640
                    ? 'base'
                    : 'sm', asChild: true },
        React.createElement(Collab, { showPumaLogo: showPumaLogo, collabLogo: collabLogo, dataTestId: dataTestId && `${dataTestId}-collab`, invert: invert ?? contextInvert, size: size || (width > 1280 ? 'xl' : 'base'), className: tw$1(inline &&
                'relative! top-auto bottom-auto left-auto right-auto transform-none text-left rtl:text-right', className) }, children)));
};

/**
 * QuiltRootContext is used to pass configuration to all Quilt components. It's mounted internally by the QuiltRoot, and will not be used directly by consumers.
 */
const QuiltRootContext = React.createContext(null);
const useQuiltContext = () => {
    const context = useContext(QuiltRootContext);
    if (!context) {
        throw new Error('useQuiltContext must be used within a QuiltRootContext');
    }
    return context;
};

/**
 * QuiltRoot is the root component for the Quilt layout system. It provides a responsive grid system for laying out content, and can be used to create a variety of layouts. It is designed to easily configure a layout that can switch betweeen a stack, row, mesh, scroll, or masonry layout, at different breakpoints.
 *
 * @example
 *
 * ```jsx
 * <QuiltRoot
 *   config={{
 *      _: { layout: "stack", gap: "xs" },
 *     md: { layout: "row", gap: "sm" },
 *     lg: { layout: "masonry", gap: "base" }
 *   }}
 * >
 *   <QuiltViewport>
 *     <Tile />
 *     <Tile />
 *     <Tile />
 *   </QuiltViewport>
 *   <QuiltScrollPrev>
 *     <button>Prev</button>
 *   </QuiltScrollPrev>
 *   <QuiltScrollNext>
 *     <button>Next</button>
 *   </QuiltScrollNext>
 * </QuiltRoot>
 * ```
 */
const QuiltRoot = (props) => {
    const { config = {}, className, dataTestId, children, asChild, ref: forwardRef, ...rest } = props;
    const Comp = asChild ? Slot : 'section';
    const { ref, isOverflowing, hasPrev, hasNext, scrollToNext, scrollToPrev } = useHorizontalScroll({
        infinite: false,
        items: Children.count(children),
    });
    const layoutConfig = 'layout' in config ? { _: config } : config;
    return (React.createElement(QuiltRootContext.Provider, { value: {
            dataTestId,
            config: layoutConfig,
            viewportRef: ref,
            isOverflowing,
            hasPrev,
            hasNext,
            scrollToNext,
            scrollToPrev,
        } },
        React.createElement(Comp, { ...rest, ref: forwardRef, "data-test-id": dataTestId, className: tw$2('w-full relative', className) }, children)));
};

/**
 * A clone of the GridCell component for backwards compatibility. This component will be removed in a future release.
 */
const QuiltCell = GridCell;

const getMeshStyles = (props) => {
    const { columns, limit, gapX, gapY, mql } = props;
    return css `
    @media ${mql} {
      display: grid;
      grid-template-columns: repeat(${columns}, minmax(0px, 1fr));
      column-gap: ${UDS_GAP_VALUES[gapX]};
      padding-bottom: 0;
      row-gap: ${UDS_GAP_VALUES[gapY]};
      & > :nth-child(n) {
        display: grid;
      }
      & > :nth-child(n + ${limit + 1}) {
        display: none;
      }
    }
  `;
};
const getScrollStyles = (props) => {
    const { length, columns, limit, mql, gapX, gapY } = props;
    const layoutColumns = length < limit ? length : limit;
    const visibleColumns = length < columns ? length : columns;
    const columnSize = visibleColumns === 1
        ? '100%'
        : `calc(
    ${100 / visibleColumns}% - ${(visibleColumns - 1) / visibleColumns} *
      ${UDS_GAP_VALUES[gapX]}
  )`;
    return css `
    @media ${mql} {
      display: grid;
      column-gap: ${UDS_GAP_VALUES[gapX]};
      row-gap: ${UDS_GAP_VALUES[gapY]};
      padding-bottom: 0.625rem;
      overflow-x: auto;
      scroll-snap-type: x mandatory;
      grid-template-columns: repeat(${layoutColumns}, ${columnSize});

      & > :nth-child(n) {
        display: grid;
        scroll-snap-align: start;
      }
      & > :nth-child(n + ${limit + 1}) {
        display: none;
      }
    }
  `;
};
const cellDefaultProps = {
    colSpan: 1,
    rowSpan: 1,
    colStart: 'auto',
    rowStart: 'auto',
};
const tall = { colSpan: 1, rowSpan: 2, colStart: 'auto', rowStart: 'auto' };
const wide = { colSpan: 2, rowSpan: 1, colStart: 'auto', rowStart: 'auto' };
const masonryCellProps = {
    start: [
        tall,
        null,
        null,
        null,
        tall,
        null,
        tall,
        null,
        null,
        null,
        tall,
        null,
    ],
    end: [null, tall, null, tall, null, null, null, tall, null, tall, null, tall],
    top: [wide, null, null, wide, null, null, wide, null, null, wide, null, null],
    bottom: [
        null,
        null,
        wide,
        null,
        null,
        wide,
        null,
        null,
        wide,
        null,
        null,
        wide,
    ],
};
const getMasonryStyles = (props) => {
    const { gapX, gapY, limit, mql } = props;
    return css `
    @media ${mql} {
      display: grid;
      grid-template-columns: repeat(2, minmax(0px, 1fr));
      column-gap: ${UDS_GAP_VALUES[gapX]};
      row-gap: ${UDS_GAP_VALUES[gapY]};
      padding-bottom: 0;
      & > :nth-child(n) {
        display: grid;
      }

      & > :nth-child(n + ${limit + 1}) {
        display: none;
      }
    }
  `;
};
const useQuiltStyles = (props) => {
    const childrenLength = Children.count(props.children);
    const quilt = useMemo(() => {
        const className = Object.keys(props.config).map((breakpoint) => {
            const breakPointConfig = props.config[breakpoint];
            const twScreen = tw.theme(`screens`)[breakpoint];
            const mql = typeof twScreen === 'string'
                ? `screen and (min-width: ${twScreen})`
                : twScreen.raw;
            const gapX = breakPointConfig.gapX ?? breakPointConfig.gap ?? 'auto';
            const gapY = breakPointConfig.gapY ?? breakPointConfig.gap ?? 'auto';
            if (breakPointConfig.layout === 'masonry') {
                return getMasonryStyles({
                    align: breakPointConfig.align || 'start',
                    limit: breakPointConfig.limit || childrenLength,
                    gapX: gapX,
                    gapY: gapY,
                    length: childrenLength,
                    breakpoint: breakpoint,
                    mql,
                });
            }
            if (breakPointConfig.layout === 'mesh') {
                return getMeshStyles({
                    columns: breakPointConfig.columns || 3,
                    limit: breakPointConfig.limit || 999,
                    gapX: gapX,
                    gapY: gapY,
                    length: childrenLength,
                    breakpoint,
                    mql,
                });
            }
            if (breakPointConfig.layout === 'row') {
                return getMeshStyles({
                    columns: breakPointConfig.columns || childrenLength,
                    limit: breakPointConfig.columns,
                    gapX: gapX,
                    gapY: gapY,
                    length: childrenLength,
                    breakpoint,
                    mql,
                });
            }
            if (breakPointConfig.layout === 'stack') {
                return getMeshStyles({
                    columns: 1,
                    limit: breakPointConfig.limit,
                    gapX: gapX,
                    gapY: gapY,
                    length: childrenLength,
                    breakpoint,
                    mql,
                });
            }
            if (breakPointConfig.layout === 'scroll') {
                return getScrollStyles({
                    columns: breakPointConfig.columns || 3,
                    limit: breakPointConfig.limit || childrenLength,
                    gapX: gapX,
                    gapY: gapY,
                    length: childrenLength,
                    breakpoint,
                    mql,
                });
            }
        });
        const cellProps = Array.from(new Array(childrenLength)).map((_, i) => {
            const el = {
                colSpan: {},
                rowSpan: {},
                colStart: {},
                rowStart: {},
            };
            Object.keys(props.config).forEach((breakpoint) => {
                const breakPointConfig = props.config[breakpoint];
                if (breakPointConfig.layout === 'masonry') {
                    const masonryLayout = masonryCellProps[breakPointConfig.align || 'start'];
                    el.colSpan[breakpoint] = (masonryLayout[i] || cellDefaultProps)['colSpan'];
                    el.rowSpan[breakpoint] = (masonryLayout[i] || cellDefaultProps)['rowSpan'];
                    el.colStart[breakpoint] = (masonryLayout[i] || cellDefaultProps)['colStart'];
                    el.rowStart[breakpoint] = (masonryLayout[i] || cellDefaultProps)['rowStart'];
                }
                else {
                    el.colSpan[breakpoint] = 1;
                    el.rowSpan[breakpoint] = 1;
                    el.colStart[breakpoint] = 'auto';
                    el.rowStart[breakpoint] = 'auto';
                }
            });
            return el;
        });
        return { className, cellProps };
    }, [JSON.stringify(props.config), childrenLength]);
    return quilt;
};
const ViewportInner = React.forwardRef(({ nodes, asChild, ...rest }, ref) => {
    return asChild ? (React.createElement(React.Fragment, null, cloneElement(nodes, { ...rest, ref }))) : (React.createElement(Fragment, { ...rest }));
});
ViewportInner.displayName = 'ViewportInner';
/**
 * QuiltViewport is the content container for all Quilt components
 *
 * It is responsible for setting up the grid layout and applying the correct styles to its children. Its configuration is inherited from the QuiltRoot component.
 *
 * @example
 *```jsx
 * <QuiltRoot>
 *   <QuiltViewport>
 *     <Tile />
 *     <Tile />
 *     <Tile />
 *   </QuiltViewport>
 * </QuiltRoot>
 * ```
 */
const QuiltViewport = (props) => {
    const { dataTestId, config, viewportRef } = useQuiltContext();
    const { asChild, children, className, ...rest } = props;
    const Comp = asChild ? Slot : 'div';
    const quiltChildren = asChild
        ? children.props.children
        : children;
    const { className: containerClassName, cellProps } = useQuiltStyles({
        config: config,
        children: quiltChildren,
    });
    return (React.createElement(Comp, { ...rest, ref: viewportRef, className: tw(containerClassName, props.className), tabIndex: 0, "data-test-id": dataTestId ? `${dataTestId}-viewport` : null, "data-uds-child": "quilt-viewport" },
        React.createElement(ViewportInner, { asChild: asChild, nodes: props.children }, Children.map(quiltChildren, (child, index) => {
            const isQuiltCell = child.type.displayName === 'QuiltCell' ||
                child.type.displayName === 'GridCell';
            return isQuiltCell ? (cloneElement(child, {
                ...cellProps[index],
                ...child.props,
            })) : (React.createElement(QuiltCell, { ...cellProps[index] }, child));
        }))));
};

const getResponsiveStyle = (config) => {
    return Object.keys(config).reduce((res, breakpoint) => {
        const breakPointConfig = config[breakpoint];
        const breakpointLayout = breakPointConfig.layout;
        const [layoutType] = breakpointLayout.split('-');
        if (layoutType !== 'scroll') {
            res.push(`${breakpoint}:hidden`);
        }
        else {
            res.push(`${breakpoint}:block`);
        }
        return res;
    }, []);
};
/**
 * QuiltScrollPrev is a navigation higher-order-component that applies functionality to a child button, allowing it to scroll the viewport to the previous page of tiles.
 */
const QuiltScrollPrev = ({ className, children }) => {
    const { dataTestId, config, hasPrev, scrollToPrev } = useQuiltContext();
    return (React.createElement("div", { "data-test-id": dataTestId ? `${dataTestId}-prev-scroll-nav` : null, "data-uds-child": "quilt-scroll-prev", className: tw(getResponsiveStyle(config), hasPrev ? 'pointer-events-all' : 'pointer-events-none', className) }, Children.map(children, child => cloneElement(child, {
        onClick: scrollToPrev,
        'data-uds-enabled': hasPrev,
    }))));
};
/**
 * QuiltScrollNext is a navigation higher-order-component that applies functionality to a child button, allowing it to scroll the viewport to the next page of tiles.
 */
const QuiltScrollNext = ({ className, children }) => {
    const { dataTestId, config, hasNext, scrollToNext } = useQuiltContext();
    return (React.createElement("div", { "data-test-id": dataTestId ? `${dataTestId}-next-scroll-nav` : null, "data-uds-child": "quilt-scroll-next", className: tw(getResponsiveStyle(config), hasNext ? 'pointer-events-all' : 'pointer-events-none', className) }, Children.map(children, child => cloneElement(child, {
        onClick: scrollToNext,
        'data-uds-enabled': hasNext,
    }))));
};

/**
 * Share context between TileRoot and child elements
 *
 * @internal
 */
const TileRootContext = React.createContext(null);
const useTileContext = () => {
    const context = useContext(TileRootContext);
    if (!context) {
        throw new Error('useTileContext must be used within a TileRootContext');
    }
    return context;
};
/**
 * Share context between TileRoot and child elements
 *
 * @internal
 */
const TileContentContext = React.createContext(null);
const useContentContext = () => {
    const context = useContext(TileContentContext);
    return context || {};
};

const tileStyle = style({
    variants: {
        layout: {
            overlay: css `
        & [data-uds-child='tile-content'] {
          @apply row-start-1;
        }
      `,
            stack: css `
        & [data-uds-child='tile-content'] {
          @apply row-start-2;
        }
      `,
        },
        fill: {
            content: css `
        grid-template-rows: min-content auto;
      `,
            media: css `
        grid-template-rows: auto min-content;
      `,
        },
        invert: {
            true: 'text-white',
            false: 'text-neutral',
        },
        size: {
            xxs: '',
            xs: '',
            sm: '',
            base: '',
            md: '',
            lg: '',
            xl: '',
            '2xl': '',
        },
    },
    matches: [
        {
            layout: 'overlay',
            fill: 'content',
            use: css `
        grid-template-rows: auto min-content;
      `,
        },
    ],
});
/**
 * Tile Compound pattern. A Tile is a container that holds content related to a single subject. It can be used to display a product, a category post, a marketing campaign, or any other subject.
 *
 * @example
 * ```jsx
 * <TileRoot>
 *  <TileMedia />
 *  <TileContent />
 *    <TileBody>
 *      <TileHeading/>
 *      <TileDescription/>
 *    </TileBody>
 *    <TileFooter>
 *      <TileActionList>
 *        <Link label="Shop Running"/>
 *      </TileActionList>
 *    </TileFooter>
 *   <TileContent>
 * </TileRoot>
 * ```
 */
const TileRoot = ({ className, dataTestId, children, asChild, invert = false, fill = 'media', layout = 'overlay', size = 'auto', ref: forwardRef, ...rest }) => {
    const Comp = asChild ? Slot : 'article';
    const { ref, width } = useResizeObserver();
    const sharedRef = useSharedRef(forwardRef, ref);
    const appliedSize = (size === 'auto'
        ? width > 768
            ? '2xl'
            : width > 599
                ? 'xl'
                : width > 504
                    ? 'lg'
                    : width > 390
                        ? 'md'
                        : width > 319
                            ? 'base'
                            : width > 255
                                ? 'sm'
                                : width > 199
                                    ? 'xs'
                                    : 'xxs'
        : size);
    return (React.createElement(TileRootContext.Provider, { value: { invert, width, layout, size: appliedSize, dataTestId } },
        React.createElement(Comp, { ...rest, ref: sharedRef, "data-test-id": dataTestId, className: tw$2(className, tileStyle({ fill, layout, invert }), 'grid relative w-full', css `
            & [data-uds-child='media-container'] {
              grid-row: 1;
              grid-column: 1;
            }

            & [data-uds-child='tile-content'] {
              grid-column: 1;
            }

            & [data-uds-child='link-inv-block'] {
              @apply z-[1];
            }

            & [data-uds-child='tile-footer'] {
              @apply z-[2];
            }

            & [data-uds-child='tile-actions'] {
              @apply z-[2];
            }
          `) }, children)));
};

/**
 * TileMedia is used to display an image or video in a tile, but also to determine the aspect ratio and overall shape of a Tile.
 *
 * Internally uses the AspectRatio and includes a vignette effect to improve readability.
 *
 * @example
 * ```jsx
 * <TileRoot>
 *  <TileMedia>
 *    <img src="https://cdn.jpg" />
 *  </TileMedia>
 *  <TileContent />
 *</TileRoot>
 * ```
 */
const TileMedia = React.forwardRef(function TileMedia(props, ref) {
    const { dataTestId } = useTileContext();
    return (React.createElement(MediaContainer, { dataTestId: dataTestId && `${dataTestId}-media`, ...props, ref: ref }));
});

const contentStyle = style({
    variants: {
        position: {
            overlay: css$1 `
        grid-row-start: 1 !important;
      `,
            stack: css$1 `
        grid-row-start: 2 !important;
      `,
        },
        insetX: {
            true: '',
            false: '',
        },
        insetBottom: {
            true: '',
            false: '',
        },
        size: {
            xxs: 'px-2 pt-2',
            xs: 'px-4 pt-4',
            sm: 'px-4 pt-4',
            base: 'px-4 pt-4',
            md: 'px-4 pt-4',
            lg: 'px-6 pt-5',
            xl: 'px-6 pt-5',
            '2xl': 'px-8 pt-7',
        },
        invert: {
            true: 'text-white',
            false: 'text-neutral',
        },
    },
    matches: [
        {
            position: 'stack',
            insetBottom: undefined,
            use: 'pb-0',
        },
        {
            position: 'stack',
            insetX: undefined,
            use: 'px-0',
        },
        {
            size: 'xxs',
            position: 'overlay',
            use: 'px-2',
        },
        {
            size: 'xs',
            position: 'overlay',
            use: 'px-4',
        },
        {
            size: 'sm',
            position: 'overlay',
            use: 'px-4',
        },
        {
            size: 'base',
            position: 'overlay',
            use: 'px-4',
        },
        {
            size: 'md',
            position: 'overlay',
            use: 'px-4',
        },
        {
            size: 'lg',
            position: 'overlay',
            use: 'px-6',
        },
        {
            size: 'xl',
            position: 'overlay',
            use: 'px-6',
        },
        {
            size: '2xl',
            position: 'overlay',
            use: 'px-8',
        },
        {
            size: 'xxs',
            position: 'overlay',
            use: 'pb-2',
        },
        {
            size: 'xs',
            position: 'overlay',
            use: 'pb-4',
        },
        {
            size: 'sm',
            position: 'overlay',
            use: 'pb-4',
        },
        {
            size: 'base',
            position: 'overlay',
            use: 'pb-4',
        },
        {
            size: 'md',
            position: 'overlay',
            use: 'pb-4',
        },
        {
            size: 'lg',
            position: 'overlay',
            use: 'pb-5',
        },
        {
            size: 'xl',
            position: 'overlay',
            use: 'pb-5',
        },
        {
            size: '2xl',
            position: 'overlay',
            use: 'pb-7',
        },
        {
            size: 'xxs',
            insetX: true,
            use: 'px-2',
        },
        {
            size: 'xs',
            insetX: true,
            use: 'px-4',
        },
        {
            size: 'sm',
            insetX: true,
            use: 'px-4',
        },
        {
            size: 'base',
            insetX: true,
            use: 'px-4',
        },
        {
            size: 'md',
            insetX: true,
            use: 'px-4',
        },
        {
            size: 'lg',
            insetX: true,
            use: 'px-6',
        },
        {
            size: 'xl',
            insetX: true,
            use: 'px-6',
        },
        {
            size: '2xl',
            insetX: true,
            use: 'px-8',
        },
        {
            size: 'xxs',
            insetBottom: true,
            use: 'pb-2',
        },
        {
            size: 'xs',
            insetBottom: true,
            use: 'pb-4',
        },
        {
            size: 'sm',
            insetBottom: true,
            use: 'pb-4',
        },
        {
            size: 'base',
            insetBottom: true,
            use: 'pb-4',
        },
        {
            size: 'md',
            insetBottom: true,
            use: 'pb-4',
        },
        {
            size: 'lg',
            insetBottom: true,
            use: 'pb-5',
        },
        {
            size: 'xl',
            insetBottom: true,
            use: 'pb-5',
        },
        {
            size: '2xl',
            insetBottom: true,
            use: 'pb-7',
        },
        {
            insetX: false,
            use: 'px-0',
        },
        {
            insetBottom: false,
            use: 'pb-0',
        },
    ],
    defaults: {
        insetX: undefined,
        insetBottom: undefined,
    },
});
const innerContentStyle = style({
    variants: {
        align: {
            top: 'justify-start',
            middle: 'justify-center',
            bottom: 'justify-end',
            'space-between': 'justify-between h-full!',
        },
        justify: {
            start: 'items-start text-left rtl:text-right',
            center: 'items-center text-center',
            end: 'items-end text-right rtl:text-left ',
        },
        size: {
            xxs: 'gap-3',
            xs: 'gap-4',
            sm: 'gap-4',
            base: 'gap-4',
            md: 'gap-5',
            lg: 'gap-7',
            xl: 'gap-7',
            '2xl': 'gap-8',
        },
    },
});
/**
 * @example
 * ```jsx
 * <TileRoot>
 *  <TileMedia />
 *  <TileContent />
 *    <TileBody>
 *    <TileFooter/>
 *   <TileContent>
 * </TileRoot>
 * ```
 */
const TileContent = ({ className, children, position, invert, justify = 'start', align = 'bottom', insetX = true, insetBottom, interactive = true, ...rest }) => {
    const { invert: invertContext, layout, width, size, dataTestId, } = useTileContext();
    const appliedInvert = invert ?? invertContext;
    return (React.createElement(TileContentContext.Provider, { value: {
            invert: appliedInvert,
            justify,
            align,
            insetX,
            position,
            width,
        } },
        React.createElement("div", { ...rest, "data-uds-child": "tile-content", "data-test-id": dataTestId && `${dataTestId}-content`, className: tw$2(className, !interactive && 'pointer-events-none', 'relative z-1 flex w-full', contentStyle({
                position: position || layout,
                invert: appliedInvert,
                size,
                insetX,
                insetBottom,
            })) },
            React.createElement("div", { "data-uds-child": "tile-content-inner", className: tw$2('relative flex flex-1 flex-col w-full', innerContentStyle({ justify, align, size })) }, children))));
};

const TILE_SIZE_TO_FLOAT_INSET = {
    none: 'none',
    xxs: 'xs',
    xs: 'sm',
    sm: 'sm',
    base: 'sm',
    md: 'sm',
    lg: 'base',
    xl: 'base',
    '2xl': 'md',
};
/**
 * TileFloat is a floating content container, that can be positioned anywhere on a Tile.
 *
 * Use it to render badges, icons, buttons and other floating content.
 *
 * @example
 * ```jsx
 * <TileRoot>
 *   <TileFloat>
 *     <Badge icon="start" label="Exclusive"/>
 *   </TileFloat>
 *   <TileMedia />
 *   <TileContent />
 * </TileRoot>
 * ```
 */
const TileFloat = ({ children, ...rest }) => {
    const { dataTestId, size } = useTileContext();
    const contentContext = useContentContext();
    const isInsideContent = !is.emptyObject(contentContext);
    const tileSize = useResponsiveValue(size);
    return (React.createElement(Float, { dataTestId: dataTestId && `${dataTestId}-float`, inset: !isInsideContent && TILE_SIZE_TO_FLOAT_INSET[tileSize], ...rest }, children));
};

const bodyStyle = style({
    base: 'w-full flex flex-col',
    variants: {
        size: {
            xxs: 'gap-none max-w-[95%]',
            xs: 'gap-1 max-w-[95%]',
            sm: 'gap-1 max-w-[95%]',
            base: 'gap-1 max-w-[90%]',
            md: 'gap-1 max-w-[90%]',
            lg: 'gap-2 max-w-[85%]',
            xl: 'gap-2 max-w-[85%]',
            '2xl': 'gap-2 max-w-[40rem]',
        },
        justify: {
            start: 'items-start text-left rtl:text-right',
            center: 'items-center text-center',
            end: 'items-end text-right rtl:text-left',
        },
    },
});
/**
 * The TileBody is a container for the main content of the Tile. Usually TileHeading, and TileDescription elements. It only applies vertical rhythm and content alignment, based on the configuration of the parent TileRoot.
 *
 * @example
 * ```jsx
 * <TileRoot>
 *  <TileMedia />
 *  <TileContent />
 *    <TileBody>
 *      <TileHeading/>
 *      <TileDescription/>
 *    </TileBody>
 *    <TileFooter>
 *      <TileActionList>
 *        <Link label="Shop Running"/>
 *      </TileActionList>
 *    </TileFooter>
 *  </TileRoot>
 * ```
 */
const TileBody = ({ className, children, ...rest }) => {
    const { dataTestId, size } = useTileContext();
    const { justify } = useContentContext();
    return (React.createElement("div", { ...rest, "data-uds-child": "tile-body", "data-test-id": dataTestId && `${dataTestId}-body`, className: tw$2(className, bodyStyle({ size, justify })) }, children));
};

const footerStyle = style({
    variants: {
        size: {
            xxs: 'gap-y-1',
            xs: 'gap-y-1',
            sm: 'gap-y-2',
            base: 'gap-y-2',
            md: 'gap-y-2',
            lg: 'gap-y-2',
            xl: 'gap-y-3',
            '2xl': 'gap-y-3',
        },
        justify: {
            start: 'items-start text-left rtl:text-right',
            center: 'items-center text-center',
            end: 'items-end text-right rtl:text-left',
        },
    },
});
/**
 * The footer of a Tile. It is used to display actions or other content. Often a footnote
 *
 * @example
 * ```jsx
 * <TileRoot>
 *  <TileMedia />
 *  <TileContent>
 *    <TileBody/>
 *    <TileFooter>
 *      <TileActionList>
 *        <Link label="Shop Running"/>
 *      </TileActionList>
 *    </TileFooter>
 *  <TileContent>
 * </TileRoot>
 * ```
 */
const TileFooter = ({ className, children, justify, ...rest }) => {
    const { size, dataTestId } = useTileContext();
    const { justify: contextJustify } = useContentContext();
    return (React.createElement("div", { ...rest, className: tw$2('relative w-full flex flex-col', footerStyle({ size, justify: justify ?? contextJustify }), className), "data-uds-child": "tile-footer", "data-test-id": dataTestId && `${dataTestId}-footer` }, children));
};

/**
 * Use the TileActionList component to display a list of actions in a Tile. The container, will apply the correct spacing and alignment based on the configuration of the parent TileRoot.
 *
 * @example
 * ```jsx
 * <TileRoot>
 *  <TileMedia />
 *  <TileContent />
 *    <TileBody>
 *      <TileHeading/>
 *      <TileDescription/>
 *    </TileBody>
 *    <TileFooter>
 *      <TileActionList>
 *        <Link label="Shop Running"/>
 *      </TileActionList>
 *    </TileFooter>
 *  </TileRoot>
 * ```
 */
const TileActionList = ({ className, children, ...rest }) => {
    const { dataTestId } = useTileContext();
    const { invert } = useContentContext();
    return (React.createElement("div", { ...rest, className: tw$2('flex items-baseline gap-x-4 gap-y-2', className), "data-uds-child": "tile-actions", "data-test-id": dataTestId && `${dataTestId}-actions` }, React.Children.map(children, child => {
        return React.cloneElement(child, {
            invert: child.props?.invert ?? invert,
        });
    })));
};

const tileHeadingSize = style({
    variants: {
        size: {
            xxs: 'heading-xxs',
            xs: 'heading-sm',
            sm: 'heading-sm',
            base: 'heading-base',
            md: 'heading-lg',
            lg: 'heading-xl',
            xl: 'heading-2xl',
            '2xl': 'heading-3xl',
        },
    },
});
const tileCondensedSizeStyle = style({
    variants: {
        size: {
            xxs: 'heading-sm',
            xs: 'heading-base',
            sm: 'heading-base',
            base: 'heading-lg',
            md: 'heading-xl',
            lg: 'heading-2xl',
            xl: 'heading-3xl',
            '2xl': 'heading-4xl',
        },
    },
});
/**
 * The TileHeading component is used to render a heading specifically inline Tiles.
 *
 * It's a wrapper around the Heading component, but implements a custom size scale, proportional to the TileRoot size.
 */
const TileHeading = React.forwardRef((props, forwardedRef) => {
    const { asChild, balance, size, weight = 700, condensed = false, transform = 'uppercase', srOnly, css, className, children, color, invert, ...rest } = props;
    const { dataTestId, size: contextSize } = useTileContext();
    const Comp = balance
        ? Balancer
        : typeof children !== 'string' && asChild
            ? Slot
            : 'h3';
    return (React.createElement(Comp, { ref: forwardedRef, className: tw$1(condensed
            ? tileCondensedSizeStyle({ size: size ?? contextSize })
            : tileHeadingSize({ size: size ?? contextSize }), fontStyle({
            condensed,
            weight,
            transform,
            srOnly,
        }), colorStyle({
            color,
            invert,
        }), className), "data-uds-child": "tile-heading", "data-test-id": dataTestId && `${dataTestId}-heading`, ...rest }, asChild ? children : balance ? React.createElement("span", null, children) : children));
});
TileHeading.displayName = 'TileHeading';

const descriptionSizeStyle = style({
    variants: {
        size: {
            xxs: 'text-xs',
            xs: 'text-sm',
            sm: 'text-base',
            base: 'text-base',
            md: 'text-xl',
            lg: 'text-xl',
            xl: 'text-2xl ',
            '2xl': 'text-2xl ',
        },
    },
});
/**
 * The TileDescription component is used to render body copy specifically inline Tiles. Usually within the TileBody.
 *
 * It's a wrapper around the Text component, but implements a custom size scale, proportional to the TileRoot size.
 */
const TileDescription = ({ asChild, balance, weight = 400, condensed = false, transform, srOnly, className, children, color, invert, size, ...rest }) => {
    const { dataTestId, size: contextSize } = useTileContext();
    const BalancedWrapper = balance ? Balancer : asChild ? Slot : Fragment;
    const elProps = {
        ...rest,
        className: tw$2(descriptionSizeStyle({ size: size ?? contextSize }), fontStyle({
            condensed,
            weight,
            transform,
            srOnly,
        }), colorStyle({
            color,
            invert,
        }), className),
        'data-test-id': dataTestId && `${dataTestId}-description`,
        'data-uds-child': 'tile-description',
    };
    return asChild ? (React.createElement(BalancedWrapper, { ...elProps }, children)) : (React.createElement(BalancedWrapper, null,
        React.createElement("p", { ...elProps }, children)));
};

const footnoteSizeStyle = style({
    variants: {
        size: {
            xxs: 'text-xxs',
            xs: 'text-xxs',
            sm: 'text-xs',
            base: 'text-sm',
            md: 'text-sm',
            lg: 'text-base',
            xl: 'text-base ',
            '2xl': 'text-base ',
        },
    },
});
/**
 * The TileFootnote component is used to render a footnote specifically inline Tiles. Usually within the TileFooter.
 *
 * It's a wrapper around the Text component, but implements a custom size scale, proportional to the TileRoot size.
 */
const TileFootnote = ({ asChild, balance, weight = 700, condensed = false, transform = 'uppercase', srOnly, className, children, color, invert, size, ...rest }) => {
    const { size: contextSize, dataTestId } = useTileContext();
    const Comp = asChild ? Slot : 'p';
    return (React.createElement(Comp, { ...rest, className: tw$2(footnoteSizeStyle({ size: size ?? contextSize }), fontStyle({
            condensed,
            weight,
            transform,
            srOnly,
        }), colorStyle({
            color,
            invert,
        }), className), "data-test-id": dataTestId && `${dataTestId}-footnote`, "data-uds-child": "tile-footnote" }, children));
};

const collabStyle = style({
    variants: {
        inline: {
            true: 'relative',
            false: 'absolute z-20',
        },
    },
    matches: [
        {
            inline: true,
            use: 'relative! top-auto bottom-auto left-auto right-auto transform-none text-left rtl:text-right',
        },
    ],
});
const TILE_COLLAB_SIZE_MAP = {
    xxs: 'sm',
    xs: 'sm',
    sm: 'sm',
    base: 'base',
    md: 'base',
    lg: 'base',
    xl: 'xl',
    '2xl': 'xl',
};
/**
 * TileCollab is a component that is used to display a collaboration between PUMA and another brand.
 *
 * Uses the TileFloat internally, and can be used directly as a child of the TileRoot, or the TileContent.
 *
 * It is positioned absolutely by default, but can be positioned relatively by setting the inline prop to true.
 *
 * @example
 * ```jsx
 * <TileRoot>
 *   <TileCollab showPumaLogo>Vogue</TileCollab>
 *   <TileMedia />
 *   <TileContent />
 * </TileRoot>
 * ```
 */
const TileCollab = ({ children, invert, inline = false, align = 'top', justify = 'end', showPumaLogo = true, collabLogo, className, size, }) => {
    const { dataTestId, size: contextSize } = useTileContext();
    const { invert: contextInvert } = useContentContext();
    const responsiveSize = useResponsiveValue(size ?? contextSize);
    return (React.createElement(TileFloat, { align: align, justify: justify, invert: invert ?? contextInvert, asChild: true },
        React.createElement(Collab, { showPumaLogo: showPumaLogo, collabLogo: collabLogo, dataTestId: dataTestId && `${dataTestId}-collab`, size: TILE_COLLAB_SIZE_MAP[responsiveSize], invert: invert ?? contextInvert, className: tw$1(collabStyle({ inline }), className) }, children)));
};

const RibbonContext = createContext(undefined);
const RibbonProvider = ({ children, value, }) => React.createElement(RibbonContext.Provider, { value: value }, children);
const useRibbonContext = () => {
    const context = useContext(RibbonContext);
    if (!context) {
        throw new Error('useRibbonContext must be used within a RibbonProvider');
    }
    return context;
};

const SIZE_MAP = {
    sm: 'h-6',
    base: 'h-8',
    lg: 'h-10',
};
const GAP_MAP = {
    none: 0,
    xxs: 1,
    xs: 2,
    sm: 4,
    base: 6,
    lg: 8,
    xl: 12,
    '2xl': 16,
    '3xl': 20,
    '4xl': 28,
    '5xl': 36,
};
const SPEED_MAP = {
    slow: 4,
    base: 8,
    fast: 16,
};
const DEFAULT_GRADIENT = `linear-gradient(
  140deg,
  #de99f1 11.32%,
  #f6d2c8 16.18%,
  #f5e2c8 20.68%,
  #bde8d9 27.82%,
  #fffbf1 38.05%,
  #edc7c6 45.26%,
  #ecabc7 51.43%,
  #c7b2e5 61.12%,
  #e2ecf8 64.34%,
  #a9e8dd 72.25%,
  #eedfc5 79.63%,
  #e5b0c9 84.17%,
  #de99f1 88.59%
)`;

/**
 * The ribbon component is used to display a horizontal list of items, with
 * optional dividers between each item.
 *
 * @example
 *
 * ```
 * <Ribbon>
 *   <RibbonBackground />
 *   <RibbonScroller>
 *     <RibbonItem>Item 1</RibbonItem>
 *     <RibbonItem>Item 2</RibbonItem>
 *     <RibbonItem>Item 3</RibbonItem>
 *   </RibbonScroller>
 * </Ribbon>
 * ```
 */
const RibbonRoot = ({ size = 'base', gap = 'base', speed = 'base', divider, children, className, dataTestId, ...rest }) => {
    const gapAsRem = tw$2.theme(`space`)[GAP_MAP[gap]];
    const { ref, width } = useResizeObserver();
    const style = useResponsiveProp(size, value => SIZE_MAP[value]);
    return (React.createElement(RibbonProvider, { value: {
            divider: divider === false
                ? null
                : divider ?? (React.createElement(Icon, { name: "puma-logo", size: "2xl", dataTestId: dataTestId ? `${dataTestId}-divider` : undefined })),
            speed,
            size,
            width,
            dataTestId,
        } },
        React.createElement("div", { className: tw$2(`relative flex items-center w-full gap-[var(--gap)] select-none overflow-hidden`, style, 
            // Gap is applied as a custom property to the ribbon, so that it can be
            // used by the ribbon scroller animation
            css `
            --gap: ${gapAsRem};
          `, className), ref: ref, "data-test-id": dataTestId, "data-uds-child": "ribbon", ...rest }, children)));
};

const useRibbonSpeed = (speed, elementWidth) => {
    const { devicePreferences } = useUdsContext();
    const speedAsNumber = isNumber(speed) ? speed : SPEED_MAP[speed];
    const appliedSpeed = devicePreferences?.prefersReducedMotion
        ? Math.min(speedAsNumber, SPEED_MAP.slow)
        : speedAsNumber;
    return (elementWidth / (appliedSpeed * 10)).toFixed(2);
};

const SCROLL_ANIMATION$1 = keyframes({
    from: {
        transform: 'translateX(0)',
    },
    to: {
        transform: `translateX(50%)`,
    },
});
/**
 * Renders a ribbon gradient background which can be animated or static.
 *
 * @example
 * ```
 * <RibbonGradientBackground
 *   gradient="linear-gradient(90deg, #fff 0%, #ccc 50%, #fff 100%)"
 *   animate
 * />
 * ```
 */
const RibbonGradientBackground = ({ className, animate = true, gradient, speed, }) => {
    const { speed: ribbonSpeed, dataTestId } = useRibbonContext();
    const ref = useRef(null);
    const width = ref.current?.offsetWidth || 0;
    const animationSpeed = useRibbonSpeed(speed || ribbonSpeed, width);
    return (React.createElement("div", { ref: ref, className: tw(`absolute top-0 right-0 bottom-0 w-[200%] h-full overflow-hidden`, gradient
            ? css `
              background: ${gradient}, ${gradient};
              background-size: 50% 100%;
              background-position: 0 0, 100%, 0;
            `
            : css `
              background: linear-gradient(
                  0deg,
                  rgba(255, 255, 255, 1),
                  rgba(255, 255, 255, 1)
                ),
                ${DEFAULT_GRADIENT}, ${DEFAULT_GRADIENT};
              background-size: 100% 100%, 50% 100%, 50% 100%;
              background-blend-mode: soft-light, normal, normal;
              background-position: 0 0, 0 0, 100%, 0;
            `, css `
          background-repeat: no-repeat;
        `, animate &&
            css `
            animation: ${SCROLL_ANIMATION$1} ${animationSpeed}s linear infinite;
          `, className), "data-test-id": dataTestId ? `${dataTestId}-gradient-background` : undefined, "data-uds-child": "ribbon-gradient-background" }));
};

/**
 * A group of ribbon items
 *
 * @internal Used by the `RibbonScroller` component
 */
const RibbonItemGroup = forwardRef(function RibbonItemGroup({ children, isDuplicate, dataTestId }, ref) {
    return (React.createElement("div", { className: `flex flex-shrink-0 gap-[var(--gap)] items-center`, "aria-hidden": isDuplicate ? 'true' : undefined, "data-test-id": dataTestId, "data-uds-child": "ribbon-item-group", ref: ref }, children));
});

const SCROLL_ANIMATION = keyframes({
    from: {
        transform: 'translateX(0)',
    },
    to: {
        transform: `translateX(calc((-100% - var(--gap)) / 2))`,
    },
});
/**
 * Renders ribbon items and scrolls them horizontally.
 *
 * @example
 * ```
 * <RibbonScroller>
 *   <RibbonItem>Item 1</RibbonItem>
 *   <RibbonItem>Item 2</RibbonItem>
 *   <RibbonItem>Item 3</RibbonItem>
 * </RibbonScroller>
 * ```
 */
const RibbonScroller = ({ children }) => {
    const id = useId();
    const { speed, divider, width, dataTestId } = useRibbonContext();
    const [duplicateItemGroupCount, setDuplicateItemGroupCount] = useState(0);
    const originalItemRef = useRef(null);
    const scrollerRef = useRef(null);
    const scrollerWidth = scrollerRef.current?.scrollWidth || 0;
    const animationSpeed = useRibbonSpeed(speed, scrollerWidth);
    // Insert divider between each child
    const items = Children.map(children, child => (React.createElement(React.Fragment, null,
        child,
        divider &&
            cloneElement(divider, {
                role: 'separator',
            }))));
    // Calculate how many duplicate item groups we need to fill the ribbon
    useEffect(() => {
        if (width && originalItemRef.current) {
            const itemGroupsPerViewport = Math.ceil(width / originalItemRef.current.offsetWidth);
            // We need 2 times as many items as we can fit in the viewport
            // minus 1 to account for the original item group
            setDuplicateItemGroupCount(itemGroupsPerViewport * 2 - 1);
        }
    }, [width]);
    return (React.createElement("div", { ref: scrollerRef, className: tw(`relative flex gap-[var(--gap)] flex-shrink-0 items-center min-w-[100%]`, css `
          animation: ${SCROLL_ANIMATION} ${animationSpeed}s linear infinite;
        `), "data-test-id": dataTestId ? `${dataTestId}-scroller` : undefined, "data-uds-child": "ribbon-scroller" },
        React.createElement(RibbonItemGroup, { ref: originalItemRef, dataTestId: dataTestId ? `${dataTestId}-original-item-group` : undefined }, items),
        [...new Array(duplicateItemGroupCount)].map((_, i) => (React.createElement(RibbonItemGroup, { key: `${id}-${i}`, dataTestId: dataTestId ? `${dataTestId}-duplicate-item-group` : undefined, isDuplicate: true }, items)))));
};

const testSizes = {
    sm: 'text-xs',
    base: 'text-sm',
    lg: 'text-base',
};
/**
 * Renders a single item in a ribbon.
 *
 * @example
 * ```
 * <RibbonItem>Item text</RibbonItem>
 * ```
 */
const RibbonItem = ({ children, className, size, dataTestId, ...rest }) => {
    const { size: ribbonSize } = useRibbonContext();
    const style = useResponsiveProp(size || ribbonSize, value => testSizes[value]);
    return (React.createElement("div", { className: tw$2('relative flex-shrink-0 uppercase font-bold', style, className), "data-test-id": dataTestId, "data-uds-child": "ribbon-item", ...rest }, children));
};

/**
 * Wrap an element to automatically focus it when it is mounted.
 *
 * Warning: Use cautiously, to manage focus in popovers and dialogs or as a result of an action.
 *
 * @example
 * ```
 * <AutoFocus>
 *   <input />
 * </AutoFocus>
 * ```
 */
const AutoFocus = ({ children }) => {
    const ref = useRef(null);
    useEffect(() => {
        if (ref.current) {
            if (!(ref.current instanceof HTMLAnchorElement ||
                ref.current instanceof HTMLButtonElement ||
                ref.current instanceof HTMLInputElement ||
                ref.current instanceof HTMLSelectElement ||
                ref.current instanceof HTMLTextAreaElement)) {
                ref.current.tabIndex = -1;
            }
            ref.current.focus();
        }
    }, []);
    return React.createElement(Slot, { ref: ref }, children);
};

export { AccordionChevron, AccordionContent, AccordionContext, AccordionItem, AccordionItemContext, AccordionRoot, AccordionTrigger, Alert, AlertDialogAction, AlertDialogBody, AlertDialogCancel, AlertDialogContent, AlertDialogContext, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogHeading, AlertDialogProvider, AlertDialogRoot, AlertDialogTrigger, Anchor, AspectRatio, AutoFocus, AvatarGraphic, AvatarIcon, AvatarImage, AvatarLabel, AvatarRoot, Badge, Balancer, BalancerProvider, Bleed, Breadcrumb, Button, ButtonCta, ButtonIcon, ButtonInvBlock, ButtonText, ButtonUnderline, Callout, CalloutBody, CalloutIcon, CalloutRoot, CalloutText, Card, CarouselChevronButton, CarouselDots, CarouselPageOf, CarouselPeekButton, CarouselProgress, CarouselRoot, CarouselSlide, CarouselThumb, CarouselThumbList, CarouselViewport, Chip, Collab, CollapsibleChevron, CollapsibleContent, CollapsibleContext, CollapsibleRoot, CollapsibleTrigger, Container, DISPLAY_SIZE_CONFIG, DialogBody, DialogContent, DialogContentInner, DialogContext, DialogDescription, DialogFooter, DialogHeader, DialogHeading, DialogProvider, DialogRoot, DialogTrigger, Display, Divider, DotAnimation, Elevated, ErrorIcon, ErrorText, Fieldset, FilterSelect, FilterSelectInner, FilterToggle, Float, Grid, GridCell, Heading, HeroActionList, HeroCollab, HeroContent, HeroDescription, HeroFootnote, HeroHeading, HeroMedia, HeroMediaContainer, HeroRoot, ICON_SIZES, Icon, Image, InputCheckbox, InputCombobox, InputDateSelect, InputLabel, InputPassword, InputRadio, InputSelect, InputSwitch, InputText, InputTextArea, InputWrapper, Inset, Link, LinkCta, LinkIcon, LinkInvBlock, LinkText, LinkUnderline, LiveRegion, MediaContainer, PAGE_OF_SIZES, PLACEHOLDER_VALUE, PopoverBody, PopoverClose, PopoverContent, PopoverHeader, PopoverRoot, PopoverTrigger, ProgressStepper, QuiltCell, QuiltRoot, QuiltScrollNext, QuiltScrollPrev, QuiltViewport, RibbonGradientBackground, RibbonItem, RibbonRoot, RibbonScroller, SearchContent, SearchForm, SearchListbox, SearchOption, SearchRoot, SearchTrigger, SelectChevron, SelectContext, SelectHiddenInput, SelectProvider, SelectRoot, SelectValue, Separator, Sidenav, SidenavContext, SidenavLink, Spacer, Stack, SupportText, SupportTextContainer, TabsContent, TabsList, TabsRoot, TabsTrigger, Text, TileActionList, TileBody, TileCollab, TileContent, TileDescription, TileFloat, TileFooter, TileFootnote, TileHeading, TileMedia, TileRoot, ToastAction, ToastClose, ToastDescription, ToastIcon, ToastPrimitives, ToastProvider, ToastRoot, ToastTitle, ToastViewport, ToggleButton, ToggleButtonChip, ToggleButtonIcon, ToggleButtonImage, ToggleButtonSwitch, ToggleButtonText, ToggleButtonThumb, ToggleChevron, ToggleGroupItem, ToggleGroupItemChip, ToggleGroupItemIcon, ToggleGroupItemImage, ToggleGroupItemSwitch, ToggleGroupItemText, ToggleGroupItemThumb, ToggleGroupRoot, TooltipContent, TooltipProvider, TooltipRoot, TooltipTrigger, VideoPlayer, accordionItemStyle, accordionRootStyle, badgeStyle, collapsibleRootStyle, contentStyle$1 as contentStyle, fadeOut, footerStyle, iconSizeStyle, useAccordionContext, useAccordionItemContext, useAlertDialogContext, useCollapsibleContext, useDialogContext, useImageLoadingStatus, useInputDivStyle, useInputIconSlots, useMinMaxLength, useSelectContext, useTextStyle };
//# sourceMappingURL=index.js.map
